import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { Fragment, useState, useRef, useEffect, useCallback, useMemo } from "react";
import { Home, Bookmark, User, Camera, ArrowLeft, ArrowRight, Search, ChevronRight, Loader2, Sparkles, Trash2, ArrowLeftRight, Plus, X, Check, RefreshCw, Shirt, Instagram, Music2, Image as ImageIcon, Lightbulb, LayoutGrid, Smartphone, MapPin, MapPinOff, Share2, Lock, ExternalLink, Store, Globe, Info, AlertCircle } from "lucide-react";
import landingBeforeImg from "@/assets/landing-before.jpg";
import landingAfterImg from "@/assets/landing-after.jpg";
import landingStyledImg from "@/assets/landing-styled.webp";
import landingReferenceImg from "@/assets/landing-reference.webp";
import { supabase } from "@/integrations/supabase/client";
import { signInWithApple, signInWithGoogle } from "@/lib/auth";
import { isAndroid } from "@/lib/nativeAuth";
import { DesktopBanner } from "@/components/DesktopBanner";
import { toast } from "sonner";
import type { Session } from "@supabase/supabase-js";
import { trackEvent, setSignupTrigger, consumeSignupTrigger, pushDataLayer, getStoredUtms, setGuestHandleForAnalytics } from "@/lib/analytics";
import { debugLog, debugWarn } from "@/lib/debugLog";
import { FeedbackWidget } from "@/components/FeedbackWidget";
import {
  InlineSignupSheet,
  type SignupTrigger,
  setPendingSignupAction,
  consumePendingSignupAction,
} from "@/components/InlineSignupSheet";
import { Wardrobe, WardrobeGuestEmpty, type WardrobeItem } from "@/components/Wardrobe";
import { SavedScreen, type SavedOutfitRow } from "@/components/saved/SavedScreen";
import { GuestAccount } from "@/components/account/GuestAccount";
import { SavedLooksGrid, type SavedLookRow } from "@/components/SavedLooksGrid";
import { LookbookActionButtons } from "@/components/LookbookActionButtons";
import { Skeleton } from "@/components/ui/skeleton";
import { GrwmOnboarding } from "@/components/onboarding/GrwmOnboarding";
import { LookbookLoadingSilhouette } from "@/components/LookbookLoadingSilhouette";
import PurchaseConfidenceCard, {
  PurchaseConfidenceCardSkeleton,
  selectHeroPieceFromItems,
  type BrandFitEntry,
} from "@/components/PurchaseConfidenceCard";
import PurchaseFeedbackCard from "@/components/PurchaseFeedbackCard";
import { readGuestModelProfile, clearGuestModelProfile } from "@/lib/profileFieldOptions";
import { normaliseBrand } from "@/lib/brandRegistry";
import { subscribeAccountFocus, requestAccountFocus, type AccountFocusRequest } from "@/lib/accountFocus";
import { WardrobeBgButton } from "@/components/WardrobeImg";
import {
  Lb3ShoppingList,
  type Lb3Outcome,
  type Lb3OwnedItem,
  type Lb3GapItem,
} from "@/components/lb3/Lb3ShoppingList";
import { Lb3EmptyWardrobeState } from "@/components/lb3/Lb3EmptyWardrobeState";
import { VersatilityCard } from "@/components/results/VersatilityCard";
import { resolveLb3State, LB3_WARDROBE_THRESHOLD } from "@/lib/resolveLb3State";
import { ImageLightbox } from "@/components/ImageLightbox";
import { EnhancedImageViewer } from "@/components/EnhancedImageViewer";
import { ResultsTopNav } from "@/components/results/ResultsTopNav";
import { ReferenceImageCard } from "@/components/results/ReferenceImageCard";
import { UNIFIED_PENDING } from "@/lib/featureFlags";
import { invokeGenerator } from "@/lib/invokeGenerator";

import { useIssueNumber } from "@/hooks/useIssueNumber";
import { getSizeLabelForItem, type ActiveSize } from "@/lib/sizeLabels";
import { getCurrentCoords } from "@/lib/nativeLocation";
import { Capacitor } from "@capacitor/core";
import { configureNativeChrome, hideSplash } from "@/lib/nativeChrome";
import BottomNav, { type BottomNavTab } from "@/components/nav/BottomNav";
import AddOutfitSheet from "@/components/nav/AddOutfitSheet";
import UrlPasteSheet from "@/components/nav/UrlPasteSheet";
import TextDescriptionSheet from "@/components/nav/TextDescriptionSheet";
import GuestLimitPaywall from "@/components/nav/GuestLimitPaywall";
import SocialPlatformSheet from "@/components/nav/SocialPlatformSheet";
import SocialHowToSheet from "@/components/nav/SocialHowToSheet";
import { loadSampleOutfit } from "@/lib/sampleOutfit";
import UrlPendingResults from "@/components/results/UrlPendingResults";
import TextPendingResults from "@/components/results/TextPendingResults";
import ResultsLoadFailedSlot from "@/components/results/ResultsLoadFailedSlot";
import SharePendingResults from "@/components/results/SharePendingResults";
import PendingStripSlot from "@/components/results/PendingStripSlot";
import { useOutfitMetaPolling, type OutfitMeta } from "@/hooks/useOutfitMetaPolling";
import NativeHome from "@/components/native/NativeHome";
import OnboardingOverlay from "@/components/onboarding/OnboardingOverlay";
import { useOnboardingState } from "@/hooks/useOnboardingState";
import { StyleYourselfResult } from "@/components/wardrobe/StyleYourselfResult";

import { RESULTS_ANALYTICS_EVENTS } from "@/lib/resultsAnalytics";
import { triggerHaptic } from "@/lib/nativeHaptics";
import { consumePendingShare, addShareReceivedListener } from "@/lib/shareIntent";
import { handleSharePayload, onSharedOutfit } from "@/lib/shareHandler";
import { App as CapacitorApp } from "@capacitor/app";
// Wave 3B — Commit 3ab: offline shopping UX primitives. Home toggle is wired
// via NativeHome.shoppingSection below; result-side Badge / Chip usage
// (with the "Online only" fallback label rendered by OfflineShoppingChip)
// lands as part of the Lb3 shopping list integration.
import OfflineShoppingToggle from "@/components/shopping/OfflineShoppingToggle";
import OfflineShoppingBadge from "@/components/shopping/OfflineShoppingBadge";
import OfflineShoppingChip from "@/components/shopping/OfflineShoppingChip";
import StoresExpandedSheet from "@/components/shopping/StoresExpandedSheet";
import { useOfflineShoppingLocation } from "@/hooks/useOfflineShoppingLocation";
import {
  buildStableIdOrder,
  applyStableIdOrder,
} from "@/lib/offlineShoppingSort";
import {
  pickNearestStore,
  hasOfflineShoppingData,
  type OutfitItemOfflineShopping,
  type OfflineShoppingStore,
} from "@/hooks/useOutfitItems";




export const Route = createFileRoute("/")({
  component: Like2FitApp,
  head: () => ({
    meta: [
      { title: "Like2Fit — See Any Outfit On You Before You Buy" },
      { name: "description", content: "Share any outfit you love and see it on your body — at your size and budget. Find what you already own, shop the rest, and buy with confidence." },
      { name: "keywords", content: "ai outfit finder, find clothes from picture, shop the look app, ai stylist app, outfit generator ai, ai wardrobe, identify clothes from photo" },
      { name: "robots", content: "index, follow, max-image-preview:large" },
      { httpEquiv: "content-language", content: "en" },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://like2fit.com/" },
      { property: "og:title", content: "Like2Fit — See Any Outfit On You Before You Buy" },
      { property: "og:description", content: "Share any outfit you love and see it on your body, at your size and budget. Stop guessing before you buy." },
      { property: "og:image", content: "https://like2fit.com/og-image.jpg" },
      { property: "og:locale", content: "en_GB" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "Like2Fit — See Any Outfit On You Before You Buy" },
      { name: "twitter:description", content: "Share any outfit you love and see it on your body, at your size and budget. Stop guessing before you buy." },
      { name: "twitter:image", content: "https://like2fit.com/og-image.jpg" },
    ],
    links: [
      { rel: "canonical", href: "https://like2fit.com/" },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "SoftwareApplication",
          name: "Like2Fit",
          url: "https://like2fit.com/",
          applicationCategory: "LifestyleApplication",
          operatingSystem: "Web",
          description: "AI outfit finder — upload any outfit photo and shop every item across 100s of retailers at your budget.",
          offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
          inLanguage: "en",
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebSite",
          name: "Like2Fit",
          url: "https://like2fit.com/",
          inLanguage: "en",
        }),
      },
    ],
  }),

});

type Screen = "landing" | "native-home" | "auth" | "onboard" | "upload" | "results" | "inspo" | "saved" | "wardrobe" | "account";
type Gender = "male" | "female" | "other";

const BUDGET_OPTIONS: { label: string; value: number }[] = [
  { label: "Under £30", value: 25 },
  { label: "£30-£75", value: 50 },
  { label: "£75-£150", value: 100 },
  { label: "£150-£300", value: 200 },
  { label: "£300-£500", value: 400 },
  { label: "£500+", value: 600 },
  { label: "Any budget", value: -1 },
];
const DEFAULT_BUDGET = 100;

// Categories excluded from outfit cost totals (accessories, jewellery, etc.).
// Shared between LB1 "Shop this look" total and LB2 "Styled for you" total
// so both surfaces sum the same set of items. Rows still display these items;
// only the summed total excludes them.
const EXCLUDED_CATEGORIES = new Set([
  "bag", "accessory", "accessories",
  "jewellery", "jewelry", "hat",
  "sunglasses", "watch", "belt",
  "scarf", "tie", "pocket square",
]);
const isExcludedFromBudget = (category: string | null | undefined): boolean => {
  const cat = (category ?? "").toLowerCase().trim();
  return EXCLUDED_CATEGORIES.has(cat);
};

type RetailerLink = { retailer: string; url: string };
type ShoppingProduct = {
  title: string;
  price: string;
  source: string;
  link: string;
  thumbnail: string;
  match_type?: "visual" | "text";
  size_confirmed?: boolean | null;
  note?: string;
  extracted_price?: number | null;
};
type TierKey = "budget" | "mid" | "premium";
type TierProducts = Partial<Record<TierKey, ShoppingProduct | null>>;
type DetectedItem = {
  id: string;
  name: string;
  category: string;
  color_hex: string | null;
  style_tags: string[] | null;
  search_query: string | null;
  retailer_urls: RetailerLink[] | null;
  products: ShoppingProduct[] | null;
  tiers: TierProducts | null;
  position: number;
  item_type?: string | null;
  is_layer_added?: boolean | null;
  reason?: string | null;
  // A-7a PR 5b/5c — structured garment attributes captured by vision.
  // Optional + nullable; only top/dress/outerwear/bottom rows populate most;
  // bag/shoes/accessory rows are mostly null per category gating.
  silhouette?: string | null;
  length?: string | null;
  neckline?: string | null;
  sleeve_length?: string | null;
  fit?: string | null;
  fabric_weight?: string | null;
  structure?: string | null;
  // Wave 3B — Commit 3d: offline-shopping resolver outputs.
  brand?: string | null;
  offline_shopping_tier_1?: OfflineShoppingStore[] | null;
  offline_shopping_tier_2?: OfflineShoppingStore[] | null;
  offline_shopping_brand?: string | null;
  offline_shopping_source?: string | null;
  offline_shopping_resolved_at?: string | null;
};

type AnalysisMode = "find_look" | "style_item";

const SENSITIVE_CATEGORIES = [
  "swimwear",
  "beachwear",
  "lingerie",
  "underwear",
  "nightwear",
  "sleepwear",
];

const SENSITIVE_NAME_TERMS = [
  "bikini",
  "swimsuit",
  "lingerie",
  "bra ",
  "underwear",
  "thong",
  "corset",
  "swimwear",
  "swim ",
];

function containsSensitiveItems(items: DetectedItem[]): boolean {
  return items.some((item) => {
    const categoryMatch = SENSITIVE_CATEGORIES.some((c) =>
      item.category?.toLowerCase().includes(c),
    );
    const nameMatch = SENSITIVE_NAME_TERMS.some((t) =>
      item.name?.toLowerCase().includes(t),
    );
    return categoryMatch || nameMatch;
  });
}

type OutfitRow = {
  id: string;
  image_url: string;
  image_path: string | null;
  image_paths?: string[] | null;
  created_at: string;
  analysis_mode?: AnalysisMode | null;
  occasion?: string | null;
  style_summary?: string | null;
  title?: string | null;
  is_saved?: boolean | null;
};

async function signImage(path: string | null | undefined): Promise<string | null> {
  // Null-tolerant: url/text input methods may bootstrap an outfit row with
  // image_path === null. Returning null lets callers fall through cleanly
  // without a try/catch. See Phase BACKGROUND-ANALYSIS-P1a.5-ii.
  if (!path) return null;
  const { data, error } = await supabase.storage
    .from("outfits")
    .createSignedUrl(path, 60 * 60);
  if (error) {
    console.error("createSignedUrl failed", error);
    return null;
  }
  return data?.signedUrl ?? null;
}

async function compressImage(file: File): Promise<File> {
  return new Promise((resolve) => {
    try {
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      if (!ctx) return resolve(file);
      const img = new window.Image();
      img.onload = () => {
        const maxDim = 1200;
        let width = img.width;
        let height = img.height;
        if (width > maxDim || height > maxDim) {
          if (width > height) {
            height = (height / width) * maxDim;
            width = maxDim;
          } else {
            width = (width / height) * maxDim;
            height = maxDim;
          }
        }
        canvas.width = width;
        canvas.height = height;
        ctx.drawImage(img, 0, 0, width, height);
        canvas.toBlob(
          (blob) => {
            if (!blob) return resolve(file);
            const baseName = file.name.replace(/\.[^.]+$/, "");
            resolve(new File([blob], `${baseName}.jpg`, { type: "image/jpeg" }));
          },
          "image/jpeg",
          0.85,
        );
      };
      img.onerror = () => resolve(file);
      img.src = URL.createObjectURL(file);
    } catch {
      resolve(file);
    }
  });
}



const BRANDS = [
  "Depop", "ASOS", "Lululemon",
  "Nike", "Urban Outfitters", "Chanel",
  "Gap", "Zara", "Amazon",
  "Prada", "Shein", "Dior",
  "Gucci", "Revolve", "Louis Vuitton",
  "H&M", "Uniqlo", "Adidas",
];

const CORAL = "var(--l2f-coral)";

function getGuestSessionIdLocal(): string | null {
  if (typeof window === "undefined") return null;
  try {
    return localStorage.getItem("guest_session_id");
  } catch {
    return null;
  }
}

// Hex → colour name (mirrors supabase/functions/analyse-outfit/index.ts).
function hexToColourName(hex: string | null | undefined): string | null {
  if (!hex) return null;
  const rgb = {
    r: parseInt((hex.replace("#", "")).slice(0, 2), 16),
    g: parseInt((hex.replace("#", "")).slice(2, 4), 16),
    b: parseInt((hex.replace("#", "")).slice(4, 6), 16),
  };
  if (isNaN(rgb.r) || isNaN(rgb.g) || isNaN(rgb.b)) return null;
  const { r, g, b } = rgb;
  if (r < 40 && g < 40 && b < 40) return "black";
  if (r > 215 && g > 215 && b > 215) return "white";
  const maxDiff = Math.max(r, g, b) - Math.min(r, g, b);
  if (maxDiff < 30) {
    if (r < 100) return "dark grey";
    if (r < 170) return "grey";
    return "light grey";
  }
  const max = Math.max(r, g, b);
  if (r === max && r > 150) {
    if (g > 100 && b < 100) return "orange";
    if (g > 150 && b > 150) return "pink";
    if (g < 80 && b < 80) return "red";
    if (g > 80 && b < 80) return "coral";
    return "red";
  }
  if (g === max && g > 100) {
    if (r > 150 && b < 100) return "yellow";
    if (r < 100 && b < 100) return "green";
    if (r > 100 && b > 100) return "mint";
    if (r < 80 && b > 80) return "teal";
    return "green";
  }
  if (b === max && b > 100) {
    if (r > 100 && g < 100) return "purple";
    if (r > 150 && g > 150) return "light blue";
    if (r < 80 && g < 80) return "navy";
    if (r < 100 && g > 100) return "teal";
    if (r > 100 && g > 100) return "blue";
    return "blue";
  }
  if (r > g && r > b && g > b) {
    if (r > 150) return "beige";
    return "brown";
  }
  if (r > g && b > g) {
    if (r > 180 && b > 100) return "pink";
    return "purple";
  }
  return null;
}

function colourNameToDisplay(name: string): string {
  return name
    .split(" ")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

const STYLE_COLOUR_OPTIONS: { name: string; hex: string }[] = [
  { name: "black", hex: "#1a1a1a" },
  { name: "white", hex: "#F5F5F5" },
  { name: "navy", hex: "#2D4A7A" },
  { name: "beige", hex: "#C9B99A" },
  { name: "red", hex: "#CC2222" },
  { name: "brown", hex: "#8B4513" },
  { name: "grey", hex: "#4A4A4A" },
  { name: "pink", hex: "#E8A0B0" },
  { name: "green", hex: "#2D6A4F" },
  { name: "camel", hex: "#C19A6B" },
];




function EmailCaptureBanner({
  outfitId,
  lookbookImageUrl,
  guestSessionId,
  onRequestSignup,
}: {
  outfitId: string;
  lookbookImageUrl: string | null;
  guestSessionId: string | null;
  onRequestSignup: (trigger: SignupTrigger) => void;
}) {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [shake, setShake] = useState(false);
  const [sentTo, setSentTo] = useState<string | null>(null);



  const isValid = (e: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e.trim());

  const submit = async () => {
    setError(null);
    if (!isValid(email)) {
      setError("Please enter a valid email");
      setShake(true);
      setTimeout(() => setShake(false), 450);
      return;
    }
    setLoading(true);
    try {
      
      const { data, error: invokeErr } = await supabase.functions.invoke(
        "send-look-email",
        {
          body: {
            email: email.trim().toLowerCase(),
            outfit_id: outfitId,
            guest_session_id: guestSessionId,
            lookbook_image_url: lookbookImageUrl,
          },
        },
      );
      if (invokeErr || (data && (data as { error?: string }).error)) {
        throw new Error(
          (data as { error?: string })?.error ?? invokeErr?.message ?? "send_failed",
        );
      }
      trackEvent("email_captured", {
        is_guest: true,
        guest_session_id: guestSessionId,
        outfit_id: outfitId,
      });
      setSentTo(email.trim().toLowerCase());
    } catch {
      setError("Something went wrong. Try again.");
    } finally {
      setLoading(false);
    }
  };

  if (sentTo) {
    return (
      <div
        style={{
          background: "var(--l2f-success-bg)",
          border: "0.5px solid var(--l2f-success-border)",
          borderRadius: "var(--radius)",
          padding: "14px 16px",
          marginTop: 12,
        }}
      >
        <div className="flex items-start gap-2.5">
          <Check size={20} style={{ color: "var(--l2f-success-ink-soft)", marginTop: 1 }} strokeWidth={3} />
          <div className="flex-1">
            <div style={{ fontSize: 14, fontWeight: 500, color: "var(--l2f-success-ink)" }}>
              Check your inbox!
            </div>
            <div style={{ fontSize: 12, color: "var(--l2f-success-ink-soft)", marginTop: 3 }}>
              We've sent your look to {sentTo}. Sign up free to save unlimited looks.
            </div>
            <button
              onClick={() => {
                trackEvent("email_capture_followup_signup_clicked", {
                  outfit_id: outfitId,
                  guest_session_id: guestSessionId,
                });
                onRequestSignup("email_capture");
              }}
              style={{
                marginTop: 8,
                fontSize: 12,
                color: CORAL,
                fontWeight: 500,
                background: "none",
                border: "none",
                padding: 0,
                cursor: "pointer",
              }}
            >
              Create free account →
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        background: "var(--l2f-cream)",
        border: "1px solid var(--l2f-danger-border)",
        borderRadius: "var(--radius)",
        padding: "16px",
        marginTop: 12,
      }}
    >
      <div style={{ fontSize: 15, fontWeight: 600, color: "var(--l2f-danger-ink)" }}>
        Save this look
      </div>
      <div
        className="flex items-center gap-2"
        style={{
          marginTop: 10,
          animation: shake ? "ecbShake 0.4s" : undefined,
        }}
      >
        <input
          type="email"
          value={email}
          disabled={loading}
          onChange={(e) => setEmail(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              trackEvent("email_capture_submitted", {
                outfit_id: outfitId,
                placement: "lb3_section",
                guest_session_id: guestSessionId,
              });
              void submit();
            }
          }}
          placeholder="your@email.com"
          style={{
            flex: 1,
            fontSize: 13,
            border: "0.5px solid #F0997B",
            borderRadius: 8,
            background: "white",
            padding: "8px 10px",
            outline: "none",
          }}
        />
        <button
          onClick={() => {
            trackEvent("email_capture_submitted", {
              outfit_id: outfitId,
              placement: "lb3_section",
              guest_session_id: guestSessionId,
            });
            void submit();
          }}
          disabled={loading}
          aria-label="Save this look"
          style={{
            background: CORAL,
            borderRadius: 999,
            padding: "8px 18px",
            border: "none",
            cursor: loading ? "default" : "pointer",
            opacity: loading ? 0.7 : 1,
            color: "white",
            fontSize: 13,
            fontWeight: 600,
            whiteSpace: "nowrap",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 6,
          }}
        >
          {loading ? (
            <Loader2 size={14} color="white" className="animate-spin" />
          ) : (
            <>Save</>
          )}
        </button>
      </div>
      {error && (
        <div style={{ fontSize: 12, color: "var(--destructive)", marginTop: 6 }}>{error}</div>
      )}
      <style>{`@keyframes ecbShake { 0%,100%{transform:translateX(0)} 25%{transform:translateX(-4px)} 75%{transform:translateX(4px)} }`}</style>
    </div>
  );
}


function WardrobeEmptyIllustration({ variant }: { variant: "guest" | "empty" }) {
  return (
    <div
      style={{
        background: "var(--color-background-secondary, #FAFAFA)",
        border: "1.5px dashed var(--color-border-secondary, #E5E5E5)",
        borderRadius: "var(--radius)",
        padding: "28px 20px 22px",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 14,
      }}
    >
      <div style={{ display: "flex", alignItems: "flex-end", gap: 12, height: 86 }}>
        <div style={{ width: 42, height: 70, background: "#E8DDD2", borderRadius: 6 }} />
        <div style={{ width: 42, height: 86, background: "#D9C9B8", borderRadius: 6 }} />
        {variant === "guest" ? (
          <div style={{ width: 42, height: 60, background: "#F0E6D9", borderRadius: 6 }} />
        ) : (
          <div
            style={{
              width: 42,
              height: 70,
              border: "1.5px dashed #C0B5A6",
              borderRadius: 6,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#A89888",
              fontSize: 22,
              fontWeight: 300,
            }}
          >
            +
          </div>
        )}
      </div>
      <div style={{ display: "flex", gap: 5 }}>
        <span style={{ width: 4, height: 4, borderRadius: 999, background: "#C9BFB2" }} />
        <span style={{ width: 4, height: 4, borderRadius: 999, background: "#C9BFB2" }} />
        <span style={{ width: 4, height: 4, borderRadius: 999, background: "#C9BFB2" }} />
      </div>
      <p style={{ fontSize: 12, color: "#8B7F70", margin: 0, letterSpacing: "0.02em" }}>
        {variant === "guest" ? "Your wardrobe lives here" : "Your wardrobe is empty"}
      </p>
    </div>
  );
}


const OCCASIONS: { emoji: string; label: string }[] = [
  { emoji: "🌅", label: "Casual Day Out" },
  { emoji: "🏠", label: "Working From Home" },
  { emoji: "🛒", label: "Running Errands" },
  { emoji: "💼", label: "Office / Work" },
  { emoji: "🤝", label: "Business Meeting" },
  { emoji: "🍽️", label: "Brunch / Lunch" },
  { emoji: "🌙", label: "Date Night" },
  { emoji: "🎉", label: "Party / Night Out" },
  { emoji: "💒", label: "Wedding / Formal" },
  { emoji: "🎓", label: "Graduation / Ceremony" },
  { emoji: "🏋️", label: "Gym / Workout" },
  { emoji: "🏃", label: "Outdoor / Sports" },
  { emoji: "🎵", label: "Festival / Concert" },
  { emoji: "✈️", label: "Travel" },
  { emoji: "👘", label: "Loungewear / Home" },
];

function occasionEmoji(label: string | null | undefined): string {
  const found = OCCASIONS.find((o) => o.label === label);
  return found?.emoji ?? "📅";
}

type SignupTarget = "results" | "saved" | "account";

const TAB_TO_PARAM: Partial<Record<Screen, string>> = {
  upload: "get-the-look",
  saved: "wardrobe",
  account: "account",
};
const PARAM_TO_TAB: Record<string, Screen> = {
  "get-the-look": "upload",
  "wardrobe": "saved",
  "account": "account",
};

// ───────────────────────── First-visit explainer screens ─────────────────────────
function SmwIntroScreen({ onDismiss }: { onDismiss: () => void }) {
  const ACCENT = "#185FA5";
  const TINT = "#E6F1FB";
  return (
    <div
      className="fixed inset-0 z-[200] overflow-y-auto"
      style={{ background: "var(--l2f-cream)" }}
    >
      <div className="min-h-full w-full flex flex-col">
        <div
          className="flex-1 px-6 pb-10 max-w-md mx-auto w-full flex flex-col"
          style={{ paddingTop: "calc(var(--safe-top) + 24px)" }}
        >
          <div
            className="l2f-section-label"
            style={{ color: ACCENT, marginBottom: 16 }}
          >
            LIKE2FIT · STYLE MY WARDROBE
          </div>
          <h1
            style={{
              fontFamily: "var(--font-display)",
              fontStyle: "italic",
              fontWeight: 400,
              fontSize: 38,
              lineHeight: 1.05,
              letterSpacing: "-0.01em",
              color: "var(--l2f-ink)",
              marginBottom: 28,
            }}
          >
            One piece you love.<br />
            A whole look to buy.
          </h1>

          {/* Visual: one item → paired items */}
          <div
            className="rounded-2xl p-5 mb-8 flex items-center gap-3"
            style={{ background: TINT, border: `1px solid ${ACCENT}22` }}
          >
            <div
              className="w-16 h-16 rounded-xl flex items-center justify-center text-2xl shrink-0"
              style={{ background: "#fff", border: `1.5px solid ${ACCENT}` }}
            >
              👕
            </div>
            <div
              className="text-2xl shrink-0"
              style={{ color: ACCENT, fontFamily: "var(--font-display)", fontStyle: "italic" }}
            >
              +
            </div>
            <div className="flex gap-2 flex-1">
              <div className="flex-1 aspect-square rounded-xl flex items-center justify-center text-xl" style={{ background: "#fff" }}>👖</div>
              <div className="flex-1 aspect-square rounded-xl flex items-center justify-center text-xl" style={{ background: "#fff" }}>👟</div>
              <div className="flex-1 aspect-square rounded-xl flex items-center justify-center text-xl" style={{ background: "#fff" }}>👜</div>
            </div>
          </div>

          <ul className="space-y-4 mb-10" style={{ fontFamily: "var(--font-body, 'Work Sans')", fontSize: 16, lineHeight: 1.5, color: "#3a3a3a" }}>
            <li className="flex gap-3">
              <span style={{ color: ACCENT, fontWeight: 700 }}>01</span>
              <span>Upload <b>one item</b> you already own.</span>
            </li>
            <li className="flex gap-3">
              <span style={{ color: ACCENT, fontWeight: 700 }}>02</span>
              <span>We suggest pieces to <b>buy</b> that pair with it.</span>
            </li>
            <li className="flex gap-3">
              <span style={{ color: ACCENT, fontWeight: 700 }}>03</span>
              <span>Swap, tweak, and shop the whole look.</span>
            </li>
          </ul>

          <div className="mt-auto pt-4">
            <button
              onClick={onDismiss}
              className="w-full rounded-full py-4 text-base font-semibold transition active:scale-[0.98]"
              style={{ background: ACCENT, color: "#fff", letterSpacing: "0.01em" }}
            >
              Got it, let's go →
            </button>
            <button
              onClick={onDismiss}
              className="w-full mt-3 py-2 text-sm underline"
              style={{ color: "var(--muted-foreground)" }}
            >
              I already know this
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function WardrobeIntroScreen({ onDismiss }: { onDismiss: () => void }) {
  const ACCENT = "var(--l2f-success-ink-soft)";
  const TINT = "var(--l2f-success-bg)";
  return (
    <div
      className="fixed inset-0 z-[200] overflow-y-auto"
      style={{ background: "var(--l2f-cream)" }}
    >
      <div className="min-h-full w-full flex flex-col">
        <div
          className="flex-1 px-6 pb-10 max-w-md mx-auto w-full flex flex-col"
          style={{ paddingTop: "calc(var(--safe-top) + 24px)" }}
        >
          <div
            className="l2f-section-label"
            style={{ color: ACCENT, marginBottom: 16 }}
          >
            LIKE2FIT · YOUR WARDROBE
          </div>
          <h1
            style={{
              fontFamily: "var(--font-display)",
              fontStyle: "italic",
              fontWeight: 400,
              fontSize: 38,
              lineHeight: 1.05,
              letterSpacing: "-0.01em",
              color: "var(--l2f-ink)",
              marginBottom: 28,
            }}
          >
            Your clothes.<br />
            Your daily outfit.
          </h1>

          <div
            className="rounded-2xl p-5 mb-8 grid grid-cols-3 gap-3"
            style={{ background: TINT, border: `1px solid ${ACCENT}22` }}
          >
            {["👗", "🧥", "👖", "👚", "👟", "👜"].map((e, i) => (
              <div
                key={i}
                className="aspect-square rounded-xl flex items-center justify-center text-2xl"
                style={{ background: "#fff" }}
              >
                {e}
              </div>
            ))}
          </div>

          <ul className="space-y-4 mb-10" style={{ fontFamily: "var(--font-body, 'Work Sans')", fontSize: 16, lineHeight: 1.5, color: "#3a3a3a" }}>
            <li className="flex gap-3">
              <span style={{ color: ACCENT, fontWeight: 700 }}>01</span>
              <span>Snap the clothes you <b>already own</b>.</span>
            </li>
            <li className="flex gap-3">
              <span style={{ color: ACCENT, fontWeight: 700 }}>02</span>
              <span>Generate outfits from your wardrobe — <b>no shopping</b>.</span>
            </li>
            <li className="flex gap-3">
              <span style={{ color: ACCENT, fontWeight: 700 }}>03</span>
              <span>Track what you wear and unlock style insights.</span>
            </li>
          </ul>

          <div className="mt-auto pt-4">
            <button
              onClick={onDismiss}
              className="w-full rounded-full py-4 text-base font-semibold transition active:scale-[0.98]"
              style={{ background: ACCENT, color: "#fff", letterSpacing: "0.01em" }}
            >
              Set up my wardrobe →
            </button>
            <button
              onClick={onDismiss}
              className="w-full mt-3 py-2 text-sm underline"
              style={{ color: "var(--muted-foreground)" }}
            >
              Skip
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Like2FitApp() {
  const isNative = Capacitor.isNativePlatform();
  const hasOutfitInUrl =
    typeof window !== "undefined" &&
    new URLSearchParams(window.location.search).has("outfit");
  const [screen, setScreen] = useState<Screen>(
    hasOutfitInUrl ? "results" : isNative ? "native-home" : "landing"
  );
  const [activeTab, setActiveTab] = useState<BottomNavTab>("home");
  // NAV-STATE-SAVED-1 — derive BottomNav's lit tab from `screen`.
  // Transient screens (results / upload / onboard / auth / landing)
  // leave the last non-transient tab lit so returning from a results
  // view shows the tab that opened it.
  useEffect(() => {
    const mapped: BottomNavTab | null =
      screen === "native-home" ? "home"
      : screen === "saved" ? "saved"
      : screen === "wardrobe" ? "wardrobe"
      : screen === "account" ? "profile"
      : null;
    if (mapped) setActiveTab(mapped);
  }, [screen]);
  // LB4-RECENTS-SAVED-ROUTING — wardrobe_combinator rows from Recents
  // and BottomNav Saved mount StyleYourselfResult as a fixed overlay
  // (BottomNav stays visible). backTarget controls where the back
  // chevron returns the user.
  const [lb4Detail, setLb4Detail] = useState<{
    outfitId: string;
    imageUrl: string | null;
    backTarget: "native-home" | "saved";
  } | null>(null);
  const [addSheetOpen, setAddSheetOpen] = useState(false);
  const [urlSheetOpen, setUrlSheetOpen] = useState(false);
  const [urlPendingHostname, setUrlPendingHostname] = useState<string | null>(null);
  const [urlPendingStage, setUrlPendingStage] = useState<"fetching" | "analysing">("fetching");
  const [textSheetOpen, setTextSheetOpen] = useState(false);
  const [socialPlatformSheetOpen, setSocialPlatformSheetOpen] = useState(false);
  const [socialHowToSheetOpen, setSocialHowToSheetOpen] = useState(false);
  const [socialSelectedPlatform, setSocialSelectedPlatform] = useState<
    "tiktok" | "instagram" | "pinterest" | "facebook" | null
  >(null);
  const [textPendingOutfitId, setTextPendingOutfitId] = useState<string | null>(null);
  const [textPendingDescription, setTextPendingDescription] = useState<string>("");
  const [textPendingOccasion, setTextPendingOccasion] = useState<string | null>(null);
  const [textPendingSeason, setTextPendingSeason] = useState<string | null>(null);
  const [currentInputMethod, setCurrentInputMethod] = useState<string | null>(null);
  const [currentTextDescription, setCurrentTextDescription] = useState<string | null>(null);
  const [bootstrapping, setBootstrapping] = useState(true);
  const [session, setSession] = useState<Session | null>(null);

  // Wave 4: 3-screen value walkthrough (first-run intro).
  const onboardingState = useOnboardingState(session?.user.id ?? null);
  const [guestHandle, setGuestHandle] = useState<string | null>(null);
  const [guestSearchCount, setGuestSearchCount] = useState<number>(0);
  const [guestCreatedAt, setGuestCreatedAt] = useState<string | null>(null);
  const [guestOutfitCount, setGuestOutfitCount] = useState<number>(0);
  const [guestSavedCount, setGuestSavedCount] = useState<number>(0);
  const guestResumedFiredRef = useRef(false);
  const [paywallOpen, setPaywallOpen] = useState(false);
  // Per-outfit guard so a single outfit's LB1 success only increments once.
  const outfitsCountedRef = useRef<Set<string>>(new Set());
  const [gender, setGender] = useState<Gender | null>(null);
  const [modelProfile, setModelProfile] = useState<{
    model_for?: string | null;
    model_setup_complete?: boolean | null;
    model_gender?: string | null;
    model_height?: string | null;
    model_body_shape?: string | null;
    model_body_size?: string | null;
    model_skin_tone?: string | null;
    model_hair?: string | null;
    model_preferred_variation?: string | null;
  } | null>(null);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [uploadedImg, setUploadedImg] = useState<string | null>(null);
  const [styleFiles, setStyleFiles] = useState<File[]>([]);
  const [styleImgs, setStyleImgs] = useState<string[]>([]);
  const [analysing, setAnalysing] = useState(false);
  const [currentOutfitId, setCurrentOutfitId] = useState<string | null>(null);
  // Phase BACKGROUND-ANALYSIS-P1a.5-v — failed-load surface flag.
  // Flipped true by instant-mount call sites (share L1293, photo upload
  // L1805) when viewOutfit returns { ok: false }. Cleared on every
  // viewOutfit entry (top of fn). Drives the ResultsLoadFailed render
  // branch added at L~3446 area. Hydration failures only — distinct
  // from analysis_status === 'failed' (DB-driven; 1a.5-vi territory).
  const [loadFailed, setLoadFailed] = useState(false);
  // Phase BACKGROUND-ANALYSIS-P1a.5-vi — expectsLb3 mirrors the
  // orchestrator's unfiltered wardrobe_items count
  // (analyse-outfit/index.ts:2890 queries WITHOUT is_active filter), so
  // PendingStripSlot's terminal predicate matches reality even when the
  // user has soft-deleted wardrobe rows. Per-user, not per-outfit.
  const [lb3WardrobeCount, setLb3WardrobeCount] = useState<number | null>(null);
  const [resultsRenderKey, setResultsRenderKey] = useState(0);
  // F37.5-SHARE-FLOW-COMPLETION-FIX (Phase 1): share-intent pending state.
  const [sharePendingOutfitId, setSharePendingOutfitId] = useState<string | null>(null);
  const [sharedPreviewDataUrl, setSharedPreviewDataUrl] = useState<string | null>(null);
  const [isSampleMode, setIsSampleMode] = useState<boolean>(false);
  const [lookbookReady, setLookbookReady] = useState(false);
  const [currentItems, setCurrentItems] = useState<DetectedItem[]>([]);
  const [currentImg, setCurrentImg] = useState<string | null>(null);
  const [currentImages, setCurrentImages] = useState<string[]>([]);
  const [currentMode, setCurrentMode] = useState<AnalysisMode>("find_look");
  const [currentOccasion, setCurrentOccasion] = useState<string | null>(null);
  const [currentStyleSummary, setCurrentStyleSummary] = useState<string | null>(null);
  const [currentDetectedGender, setCurrentDetectedGender] = useState<"men" | "women" | "unisex" | null>(null);
  const [currentIsSaved, setCurrentIsSaved] = useState<boolean>(false);
  const [currentTitle, setCurrentTitle] = useState<string | null>(null);
  const [typicalBudget, setTypicalBudget] = useState<number | null>(null);
  const [uploadBudget, setUploadBudget] = useState<number | null>(null);
  const [budgetTop, setBudgetTop] = useState<number | null>(null);
  const [budgetBottom, setBudgetBottom] = useState<number | null>(null);
  const [budgetShoes, setBudgetShoes] = useState<number | null>(null);
  const userSelectedBudgetRef = useRef(false);
  const [uploadMode, setUploadMode] = useState<AnalysisMode>("find_look");
  const [selectedOccasion, setSelectedOccasion] = useState<string | null>(null);
  const [tourStep, setTourStep] = useState<number>(0);
  const [signupSheet, setSignupSheet] = useState<SignupTrigger | null>(null);
  const [wardrobeItems, setWardrobeItems] = useState<WardrobeItem[]>([]);
  const [lastKnownCity, setLastKnownCity] = useState<string | null>(null);
  const [todayWeather, setTodayWeather] = useState<string | null>(null);
  const [wardrobeLoaded, setWardrobeLoaded] = useState(false);
  const [pendingPostAuthScreen, setPendingPostAuthScreen] = useState<Screen | null>(null);
  const pendingActionRef = useRef<(() => void) | null>(null);
  const postOnboardingActionRef = useRef<(() => void) | null>(null);
  const [accountFocus, setAccountFocus] = useState<AccountFocusRequest | null>(null);
  useEffect(() => {
    return subscribeAccountFocus((req) => {
      setAccountFocus(req);
      setScreen("account");
    });
  }, []);

  // Native chrome: configure status bar + hide splash once React has mounted.
  useEffect(() => {
    void configureNativeChrome();
    void hideSplash();
  }, []);

  // F37.5-ANDROID-SHARE-INTENT: drain pending share on boot + listen for
  // warm-launch shares (onNewIntent) and for App.resume (Android sometimes
  // delivers SEND to a backgrounded singleTask process before the JS bridge
  // is ready). When a shared outfit is dispatched, swap to the results
  // screen with the new outfit_id — same end-state as in-app submissions.
  useEffect(() => {
    const unsubOutfit = onSharedOutfit((detail) => {
      const { outfitId, kind, previewDataUrl, hostname, text, imagePath, sourceUrl } = detail;
      console.log("[share-debug-v2.1] onSharedOutfit handler entry", {
        outfitId,
        kind,
        hasImagePath: !!imagePath,
        screen,
        currentOutfitId: currentOutfitIdRef.current,
      });

      // F.5h-v2.1 — Defensive cleanup for share-while-results-mounted stuck
      // state. Radix/vaul can leave `body { pointer-events: none }` and
      // related scroll-lock attrs when a sheet transition is interrupted by
      // app backgrounding (Android share intent fires during close anim).
      // Reset is idempotent: no-op on cold-start first share.
      if (typeof document !== "undefined") {
        document.body.style.pointerEvents = "";
        document.body.style.removeProperty("overflow");
        document.body.removeAttribute("data-scroll-locked");
      }
      const previousOutfitId = currentOutfitIdRef.current;
      if (previousOutfitId && previousOutfitId !== outfitId) {
        forceNextResultsRefreshRef.current = true;
        toast.success("Saved to Recents", { duration: 2000 });
      }


      // F.5h-v2 — Instant-mount path. When the share already carries an
      // image (image share, or URL share with iOS-extension og:image preview
      // that uploaded successfully), skip SharePendingResults/UrlPendingResults
      // entirely and mount Results immediately via viewOutfit. Items are
      // empty at share-tap time; Results' internal poller (line ~7503) is
      // guest-aware and fills them in as analyse-outfit produces rows.
      //
      // P1a.5-iii extension (UNIFIED_PENDING-gated): URL-without-preview and
      // text shares also instant-mount, surfacing the URL / text hero variants
      // from 1a.5-i. Flag OFF (production today) keeps these on the legacy
      // pending-screen fall-through below — no behavior change.
      //
      // Items pre-fetch acts purely as a network/RLS health probe: a non-null
      // result (including []) proves the read path works → safe to mount with
      // skeletons. On read failure we fall through to the existing pending
      // screen which has its own polling + recovery semantics.
      const useInstantMount =
        (!!imagePath && (kind === "image" || kind === "social-url" || kind === "generic-url")) ||
        (UNIFIED_PENDING && !imagePath && (kind === "generic-url" || kind === "social-url")) ||
        (UNIFIED_PENDING && kind === "text");

      if (useInstantMount) {
        void (async () => {
          console.log("[share-debug-v2.1] IIFE entry", { outfitId, kind, hasImagePath: !!imagePath });
          const { data: { user } } = await supabase.auth.getUser();
          const isGuest = !user;
          console.log("[share-debug-v2.1] after auth.getUser", { isGuest });
          let items: DetectedItem[] | null = null;
          try {
            if (isGuest) {
              const { data, error } = await (supabase as any).rpc(
                "get_guest_outfit_items",
                { p_outfit_id: outfitId },
              );
              if (!error) items = (data as DetectedItem[] | null) ?? [];
            } else {
              const { data, error } = await supabase
                .from("detected_items")
                .select("*")
                .eq("outfit_id", outfitId)
                .order("position", { ascending: true });
              if (!error) items = (data as DetectedItem[] | null) ?? [];
            }
          } catch (err) {
            console.warn("[share-debug] items pre-fetch failed", err);
            items = null;
          }
          console.log("[share-debug-v2.1] after items fetch", {
            itemsIsNull: items === null,
            itemsLen: items?.length ?? -1,
          });

          if (items === null) {
            console.log("[share-debug-v2.1] fallback path — calling resetResultsState");
            beginResultsRefresh(outfitId);
            setCurrentOutfitId(outfitId);
            currentOutfitIdRef.current = outfitId;
            setLookbookReady(false);
            if (kind === "generic-url") {
              setUrlPendingHostname(hostname ?? "the web");
              setUrlPendingStage("fetching");
            } else if (kind === "text") {
              setTextPendingOutfitId(outfitId);
              setTextPendingDescription(text ?? "");
              setTextPendingOccasion(null);
              setTextPendingSeason(null);
            } else {
              setSharedPreviewDataUrl(previewDataUrl ?? null);
              setSharePendingOutfitId(outfitId);
            }
            setScreen("results");
            return;
          }

          // Build a synthetic OutfitRow with every field viewOutfit reads.
          // Three variants by share kind:
          //   (a) image present       → image_path + signed:// sentinel,
          //                             input_method "share_intent" (legacy).
          //   (b) url, no image       → null image fields, source_url set,
          //                             input_method "url" so the widened
          //                             mount gate (1a.5-ii) accepts it and
          //                             the URL hero variant renders.
          //   (c) text                → null image fields, text_description
          //                             set, input_method "text".
          // viewOutfit (1a.5-ii) is null-tolerant on image_path; signImage
          // skips the RPC when path is null.
          //
          // Deviation noted for approval: the persisted outfit row keeps
          // input_method "share_intent" (createOutfitRow in shareHandler).
          // Only the synthetic row passed to viewOutfit carries the more
          // specific "url" / "text" value. This keeps row writes unchanged
          // under flag OFF and defers row-level reclassification to a later
          // sub-phase.
          const hasImage = !!imagePath;
          const isText = kind === "text";
          const isUrl = kind === "generic-url" || kind === "social-url";
          const syntheticRow = {
            id: outfitId,
            image_path: hasImage ? imagePath : null,
            image_url: hasImage ? `signed://${imagePath}` : null,
            image_paths: hasImage ? [imagePath] : null,
            analysis_mode: null,
            occasion: null,
            style_summary: null,
            detected_gender: null,
            input_method: hasImage ? "share_intent" : isText ? "text" : isUrl ? "url" : "share_intent",
            text_description: isText ? (text ?? null) : null,
            source_url: !hasImage && isUrl ? (sourceUrl ?? null) : null,
            is_saved: false,
            title: null,
            lookbook_image_url: null,
            lookbook_status: null,
            lookbook_lb2_url: null,
            lookbook_lb3_url: null,
            lookbook_image_urls: null,
          } as unknown as OutfitRow;
          setLookbookReady(false);
          console.log("[share-debug-v2.1] about to call viewOutfit (instant-mount)", { outfitId, kind });
          const viewResult = await viewOutfit(syntheticRow, items);
          console.log("[share-debug-v2.1] viewOutfit await returned (instant-mount)", { outfitId, ok: viewResult.ok });
          // Phase BACKGROUND-ANALYSIS-P1a.5-v — hydration failure on
          // instant-mount: surface the failed-load screen instead of
          // perpetual skeletons. Manually seed the outfit id + screen
          // since viewOutfit's setters never fired on the failure path.
          if (!viewResult.ok) {
            setCurrentOutfitId(outfitId);
            currentOutfitIdRef.current = outfitId;
            setLoadFailed(true);
            setScreen("results");
            trackEvent("outfit_load_failed", {
              outfit_id: outfitId,
              reason: viewResult.reason,
              source: "share_instant_mount",
              kind,
            });
          }
        })();
        return;
      }


      // Legacy fallback path — no image available at share-tap time. Routes
      // to the per-kind pending screen for UX parity with in-app paste/type
      // flows (text echo, hostname echo, image preview).
      beginResultsRefresh(outfitId);
      setCurrentOutfitId(outfitId);
      currentOutfitIdRef.current = outfitId;
      setLookbookReady(false);
      if (kind === "generic-url") {
        setUrlPendingHostname(hostname ?? "the web");
        setUrlPendingStage("fetching");
      } else if (kind === "text") {
        setTextPendingOutfitId(outfitId);
        setTextPendingDescription(text ?? "");
        setTextPendingOccasion(null);
        setTextPendingSeason(null);
      } else {
        // 'image' | 'social-url' (and unknown — back-compat). In practice
        // 'image' always carries imagePath (handleSharedImage sets it after
        // upload), so this branch fires for social-url-without-preview only.
        setSharedPreviewDataUrl(previewDataUrl ?? null);
        setSharePendingOutfitId(outfitId);
      }
      setScreen("results");
    });


    let cancelled = false;
    const drain = async () => {
      console.log("[share-debug] BOOT/RESUME — draining pending share");
      const payload = await consumePendingShare();
      console.log("[share-debug] drain payload type=", payload.type);
      if (!cancelled) await handleSharePayload(payload);
    };
    void drain();

    let listenerHandle: { remove: () => Promise<void> } | null = null;
    void addShareReceivedListener((payload) => {
      console.log("[share-debug] shareReceived event in React layer, type=", payload.type);
      void handleSharePayload(payload);
    }).then((h) => {
      listenerHandle = h;
    });

    const resumeHandle = CapacitorApp.addListener("resume", () => {
      console.log("[share-debug] CapacitorApp.resume fired");
      void drain();
      // F.5f — broadcast resume so active pending pollers (URL / text /
      // share image) re-evaluate immediately. iOS WebView lock/unlock does
      // not always fire `visibilitychange`, so the polling watchers can sit
      // idle even after the server finished. This event is idempotent: each
      // poller's internal `cancelled` flag and ready-gate guarantee no
      // double-transition with the 2s interval already in flight.
      if (typeof window !== "undefined") {
        window.dispatchEvent(new Event("l2f:resume"));
      }
    });

    return () => {
      cancelled = true;
      unsubOutfit();
      void listenerHandle?.remove();
      void resumeHandle.then((h) => h.remove());
    };
    // resetResultsState / currentOutfitIdRef are stable refs/closures here;
    // mount-only intentionally.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  // Upload via Capacitor Camera plugin (native sheet handler). Falls back
  // to the existing file input on web by clicking the hidden <input>.


  const fileRef = useRef<HTMLInputElement>(null);
  const currentOutfitIdRef = useRef<string | null>(null);
  const forceNextResultsRefreshRef = useRef(false);
  const screenRef = useRef<Screen>("landing");
  const skipPushRef = useRef(false);
  const recoverOutfitRef = useRef<((id: string) => Promise<void>) | null>(null);
  const recoveringRef = useRef(false);
  const authed = !!session;
  const isGuest = !session;


  // Upload screen tabs + wardrobe picker state
  const [styleTab, setStyleTab] = useState<"new_look" | "saved">("new_look");
  const [getTheLookTab, setGetTheLookTab] = useState<"upload" | "saved">("upload");
  const [savedStyleCount, setSavedStyleCount] = useState(0);
  const [savedGetTheLookCount, setSavedGetTheLookCount] = useState(0);
  const [showWardrobePicker, setShowWardrobePicker] = useState(false);
  const [uploadedWardrobeItem, setUploadedWardrobeItem] = useState<WardrobeItem | null>(null);
  const [isWardrobeSource, setIsWardrobeSource] = useState(false);
  const [savedLooksRefreshKey, setSavedLooksRefreshKey] = useState(0);

  // ---- F26 native home tab data ----
  type NativeHomeProfileFields = {
    model_setup_complete: boolean | null;
    size_uk_number: string | null;
    size_letter: string | null;
    size_top_letter: string | null;
    size_shoe: string | null;
    budget_top: number | null;
    home_onboarding_card_dismissed: boolean | null;
    personalisation_enabled: boolean | null;
  };
  const [nativeHomeProfile, setNativeHomeProfile] =
    useState<NativeHomeProfileFields | null>(null);
  const [recentAnalysesNh, setRecentAnalysesNh] = useState<Array<{
    id: string;
    lookbook_image_url: string | null;
    thumbnail_url: string | null;
    image_url: string | null;
    hero_item_name: string | null;
    title: string | null;
    occasion: string | null;
    hero_item_category: string | null;
    created_at: string;
    lookbook_status: string | null;
    input_method?: string | null;
  }>>([]);
  const [unsavedGuestAnalysis, setUnsavedGuestAnalysis] = useState<{
    id: string;
    lookbook_status: string | null;
    lookbook_image_url: string | null;
    thumbnail_url: string | null;
    image_url: string | null;
    hero_item_name: string | null;
    title: string | null;
    style_summary: string | null;
    created_at: string;
  } | null>(null);
  const [unsavedGuestCount, setUnsavedGuestCount] = useState(0);
  const [wardrobeItemsCount, setWardrobeItemsCount] = useState(0);


  // First-visit explainer flags. Default seen=true so we never flash for
  // unknown state; we explicitly set false once we know it's a first visit.
  const [smwIntroSeen, setSmwIntroSeen] = useState<boolean>(true);
  const [wardrobeIntroSeen, setWardrobeIntroSeen] = useState<boolean>(true);
  const [showSmwIntro, setShowSmwIntro] = useState<boolean>(false);
  const [showWardrobeIntro, setShowWardrobeIntro] = useState<boolean>(false);

  // Hydrate guest SMW flag from localStorage on mount.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const seen = window.localStorage.getItem("l2f_smw_intro_seen");
    setSmwIntroSeen(seen === "true");
  }, []);

  // Load saved-outfit counts for the tab badges (authed users only)
  useEffect(() => {
    if (!session?.user.id) return;
    const uid = session.user.id;
    let cancelled = false;
    (async () => {
      const [styleRes, findRes] = await Promise.all([
        supabase
          .from("outfits")
          .select("*", { count: "exact", head: true })
          .eq("user_id", uid)
          .eq("is_saved", true)
          .eq("analysis_mode", "style_item"),
        supabase
          .from("outfits")
          .select("*", { count: "exact", head: true })
          .eq("user_id", uid)
          .eq("is_saved", true)
          .eq("analysis_mode", "find_look"),
      ]);
      if (cancelled) return;
      setSavedStyleCount(styleRes.count ?? 0);
      setSavedGetTheLookCount(findRes.count ?? 0);
    })();
    return () => { cancelled = true; };
  }, [session?.user.id, savedLooksRefreshKey]);

  // Load wardrobe items for authed users so the upload-screen picker has data.
  useEffect(() => {
    if (!session?.user.id || wardrobeLoaded) return;
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from("wardrobe_items")
        .select("*")
        .eq("user_id", session.user.id)
        .eq("is_active", true)
        .order("created_at", { ascending: false });
      if (cancelled) return;
      setWardrobeItems((data as WardrobeItem[]) ?? []);
      setWardrobeLoaded(true);
    })();
    return () => { cancelled = true; };
  }, [session?.user.id, wardrobeLoaded]);

  // Phase BACKGROUND-ANALYSIS-P1a.5-vi — wardrobe count for expectsLb3.
  // Unfiltered (no is_active) to mirror analyse-outfit/index.ts:2890.
  // Per-user, not per-outfit — soft-delete edits handled on next session.
  useEffect(() => {
    if (!session?.user.id) { setLb3WardrobeCount(null); return; }
    let cancelled = false;
    void (async () => {
      const { count } = await supabase
        .from("wardrobe_items")
        .select("id", { count: "exact", head: true })
        .eq("user_id", session.user.id);
      if (!cancelled) setLb3WardrobeCount(count ?? 0);
    })();
    return () => { cancelled = true; };
  }, [session?.user.id]);

  // Single source of truth for outfit meta polling. Both PendingStripSlot
  // (header strip) and Results (hero-variant spinner gate) read from this —
  // no double 5s interval.
  const resultsMeta = useOutfitMetaPolling(currentOutfitId, !session, (lb3WardrobeCount ?? 0) > 0);

  // ---- F26: Native home tab data loader ----
  // Loads recent analyses, profile flags, wardrobe count for authed users,
  // and the most recent guest analysis for guests. Re-runs on session change
  // and savedLooksRefreshKey bumps.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (session?.user.id) {
        const uid = session.user.id;
        const [profRes, recentsRes, wardrobeRes] = await Promise.all([
          (supabase.from("profiles") as any)
            .select(
              "model_setup_complete, size_uk_number, size_letter, size_top_letter, size_shoe, budget_top, home_onboarding_card_dismissed, personalisation_enabled",
            )
            .eq("id", uid)
            .maybeSingle(),
          // Phase 1a.5-vi: SECURITY INVOKER RPC filters out parent
          // outfits whose regeneration child has reached
          // analysis_status='complete' (clean Recents; DB record
          // preserved). Returns exactly N post-filter rows.
          (supabase.rpc as any)("get_recent_outfits_filtered", {
            p_user_id: uid,
            p_limit: 5,
          }),
          supabase
            .from("wardrobe_items")
            .select("id", { count: "exact", head: true })
            .eq("user_id", uid)
            .eq("is_active", true),
        ]);
        if (cancelled) return;
        setNativeHomeProfile(
          (profRes.data as NativeHomeProfileFields | null) ?? null,
        );
        setRecentAnalysesNh((recentsRes.data as any) ?? []);
        setWardrobeItemsCount(wardrobeRes.count ?? 0);
        setUnsavedGuestAnalysis(null);
        setUnsavedGuestCount(0);
      } else {
        // Guest path
        const guestId =
          typeof window !== "undefined"
            ? localStorage.getItem("guest_session_id")
            : null;
        if (!guestId) {
          setUnsavedGuestAnalysis(null);
          setUnsavedGuestCount(0);
          setRecentAnalysesNh([]);
          setNativeHomeProfile(null);
          return;
        }
        const { data } = await (supabase.rpc as any)(
          "get_guest_recent_outfits",
          { p_guest_session_id: guestId, p_limit: 5 },
        );
        if (cancelled) return;
        const rows = (data as any[] | null) ?? [];
        const first = rows[0] ?? null;
        setUnsavedGuestAnalysis(first);
        setUnsavedGuestCount(rows.length);
        setRecentAnalysesNh(rows);
        setNativeHomeProfile(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [session?.user.id, savedLooksRefreshKey, screen]);

  // ---- F26: Realtime subscription for in-flight LB1 guest analysis ----
  useEffect(() => {
    if (
      !unsavedGuestAnalysis ||
      unsavedGuestAnalysis.lookbook_status === "ready"
    )
      return;
    const id = unsavedGuestAnalysis.id;
    const channel = supabase
      .channel(`outfit-${id}`)
      .on(
        "postgres_changes",
        {
          event: "UPDATE",
          schema: "public",
          table: "outfits",
          filter: `id=eq.${id}`,
        },
        (payload) => {
          const next = payload.new as any;
          setUnsavedGuestAnalysis((prev) =>
            prev && prev.id === next.id ? { ...prev, ...next } : prev,
          );
        },
      )
      .subscribe();
    return () => {
      void supabase.removeChannel(channel);
    };
  }, [unsavedGuestAnalysis?.id, unsavedGuestAnalysis?.lookbook_status]);




  // Load wardrobe items when user opens the Wardrobe tab.
  useEffect(() => {
    if (screen !== "saved" || !session?.user.id || wardrobeLoaded) return;
    let cancelled = false;
    (async () => {
      const { data, error } = await supabase
        .from("wardrobe_items")
        .select("*")
        .eq("user_id", session.user.id)
        .eq("is_active", true)
        .order("created_at", { ascending: false });
      if (cancelled) return;
      if (error) { console.error("[wardrobe] load failed", error); return; }
      setWardrobeItems((data as WardrobeItem[]) ?? []);
      setWardrobeLoaded(true);
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [screen, session?.user.id, wardrobeLoaded]);


  const [resultsCentreButton, setResultsCentreButton] = useState<CentreButtonConfig | null>(null);
  const handleCentreButtonChange = useCallback((config: CentreButtonConfig | null) => {
    setResultsCentreButton(config);
  }, []);

  // Lifted Results state so it survives tab navigation (Results unmounts when screen !== "results").
  const [currentLookbookUrl, setCurrentLookbookUrl] = useState<string | null>(null);
  const [currentLookbookUrls, setCurrentLookbookUrls] = useState<string[] | null>(null);
  const [currentOutfitDirty, setCurrentOutfitDirty] = useState<boolean>(false);
  const handleLookbookUrlChange = useCallback((url: string | null) => {
    setCurrentLookbookUrl(url);
  }, []);
  const handleOutfitDirtyChange = useCallback((dirty: boolean) => {
    setCurrentOutfitDirty(dirty);
  }, []);
  const resetResultsState = useCallback(() => {
    setCurrentLookbookUrl(null);
    setCurrentLookbookUrls(null);
    setCurrentOutfitDirty(false);
    setUrlPendingHostname(null);
    setUrlPendingStage("fetching");
    setTextPendingOutfitId(null);
    setTextPendingDescription("");
    setTextPendingOccasion(null);
    setTextPendingSeason(null);
    // Bug 2 fix — clear the full outfit surface so a fresh outfit can't
    // inherit stale fields (LB1 image, reference card, items, mode) from
    // the previous outfit while the watcher is still hydrating.
    setCurrentOutfitId(null);
    currentOutfitIdRef.current = null;
    setCurrentImg(null);
    setCurrentImages([]);
    setCurrentItems([]);
    setCurrentInputMethod(null);
    setCurrentTextDescription(null);
    setCurrentOccasion(null);
    setCurrentStyleSummary(null);
    setCurrentDetectedGender(null);
    setCurrentIsSaved(false);
    setCurrentTitle(null);
    setIsSampleMode(false);
  }, []);
  const beginResultsRefresh = useCallback((nextOutfitId: string | null) => {
    const previousOutfitId = currentOutfitIdRef.current;
    const wasResults = screenRef.current === "results";
    const shouldForceRefresh = forceNextResultsRefreshRef.current;
    forceNextResultsRefreshRef.current = false;
    resetResultsState();
    if (shouldForceRefresh && wasResults && previousOutfitId && nextOutfitId && previousOutfitId !== nextOutfitId) {
      setResultsRenderKey((key) => key + 1);
    }
  }, [resetResultsState]);


  // URL flow watcher: poll the outfits row until the wrapper edge fn fills
  // image_url, then hydrate via viewOutfit. Handles three failure modes:
  //   1. Cleanup on unmount/back-nav — cancelled flag + clearInterval + a
  //      visibility listener removed in the same return.
  //   2. Background → foreground — visibilitychange triggers an immediate
  //      poll on resume (iOS setInterval throttles when backgrounded).
  //   3. Indefinite polling — 60s wall-clock guard bails to home with a toast.
  useEffect(() => {
    if (!urlPendingHostname || !currentOutfitId) return;
    const id = currentOutfitId;
    const startedAt = Date.now();
    const MAX_DURATION_MS = 60_000;
    const isDev =
      typeof window !== "undefined" && window.location.hostname !== "like2fit.com";
    let cancelled = false;

    const bail = (reason: "failed" | "timeout") => {
      cancelled = true;
      setUrlPendingHostname(null);
      setUrlPendingStage("fetching");
      if (reason === "failed") {
        toast.error("Couldn't process this URL");
      } else {
        toast.error(
          isGuest
            ? "Still working on this — your look will appear here when it's ready."
            : "This is taking longer than expected. We'll keep working on it — check Recents in a minute.",
        );
      }
      setScreen(isNative ? "native-home" : "upload");
    };

    const poll = async () => {
      if (cancelled) return;
      if (isDev) debugLog("[F37.2 watcher] tick", id);
      if (Date.now() - startedAt > MAX_DURATION_MS) {
        bail("timeout");
        return;
      }
      // Guests have no anon SELECT policy on `outfits` — a direct read
      // silently returns null and the watcher never transitions. Use the
      // SECURITY DEFINER RPC `get_guest_outfit` for guest sessions; authed
      // users keep the direct read so RLS continues to gate per user.
      let data: Record<string, unknown> | null = null;
      let error: unknown = null;
      if (isGuest) {
        const result = await (supabase as any).rpc("get_guest_outfit", { p_id: id });
        const rows = result.data as Record<string, unknown>[] | null;
        data = Array.isArray(rows) && rows.length > 0 ? rows[0] : null;
        error = result.error;
      } else {
        const result = await supabase
          .from("outfits")
          .select("*")
          .eq("id", id)
          .maybeSingle();
        data = (result.data as Record<string, unknown> | null) ?? null;
        error = result.error;
      }
      if (cancelled || error || !data) return;
      const row = data as unknown as OutfitRow & {
        image_url: string | null;
        lookbook_status: string | null;
        analysis_status: string | null;
        hero_item_id: string | null;
      };
      if (row.lookbook_status === "failed") {
        bail("failed");
        return;
      }
      // Stage 2: image landed, detection still running → flip caption.
      if (row.image_url && row.analysis_status !== "complete") {
        setUrlPendingStage("analysing");
      }
      // Ready gate (F37.URL-UX): image_url alone. Transition to results screen
      // as soon as og:image is in storage (~10-15s); items / hero / lookbooks
      // reveal progressively on the results page (matches screenshot flow UX).
      // Detected_items INSERTs + outfits UPDATEs are picked up by the realtime
      // + poll backstops inside ResultsScreen (see L6889+ and L6235+).
      const ready = !!row.image_url;
      if (ready) {
        cancelled = true;
        // Bug 2 fix — hydrate new outfit state BEFORE releasing the pending
        // shell, so Results never mounts with stale outfit fields.
        // Phase 1a.5-v — capture viewOutfit result; on hydration failure
        // flip loadFailed so Results renders the failed-load surface
        // instead of perpetual skeletons. currentOutfitId is already set
        // (it's a watcher dep), so no manual seeding needed.
        const viewResult = await viewOutfit(row);
        setUrlPendingHostname(null);
        setUrlPendingStage("fetching");
        if (!viewResult.ok) {
          setLoadFailed(true);
          setScreen("results");
          trackEvent("outfit_load_failed", {
            outfit_id: id,
            reason: viewResult.reason,
            source: "url_watcher",
          });
        }
      }

    };

    void poll();
    const intervalId = window.setInterval(() => { void poll(); }, 2000);
    const onVisibility = () => {
      if (document.visibilityState === "visible") void poll();
    };
    const onResume = () => { void poll(); };
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("l2f:resume", onResume);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("l2f:resume", onResume);
    };
    // viewOutfit / isNative are stable for this purpose; intentional dep list.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlPendingHostname, currentOutfitId]);

  // F37.3 text-flow watcher. Separate from URL watcher per per-flow-gate rule.
  // Gate: analysis_status === 'items_ready' | 'complete'. Guest reads use the
  // get_guest_outfit SECURITY DEFINER RPC (anon has no SELECT on outfits).
  useEffect(() => {
    if (!textPendingOutfitId) return;
    const id = textPendingOutfitId;
    const guest = !session;
    const startedAt = Date.now();
    const MAX_DURATION_MS = 60_000;
    let cancelled = false;

    const bail = (reason: "failed" | "timeout") => {
      cancelled = true;
      setTextPendingOutfitId(null);
      if (reason === "failed") {
        toast.error(
          guest
            ? "Couldn't process that — please try a different description"
            : "Couldn't process that — please try again",
        );
      } else {
        toast.message(
          guest
            ? "Still working on this — your look will appear here when it's ready."
            : "This is taking longer than expected. We'll keep working on it — check Recents in a minute.",
        );
      }
      setScreen(isNative ? "native-home" : "upload");
    };

    const poll = async () => {
      if (cancelled) return;
      if (Date.now() - startedAt > MAX_DURATION_MS) {
        bail("timeout");
        return;
      }
      let row: Record<string, unknown> | null = null;
      if (guest) {
        const result = await (supabase as any).rpc("get_guest_outfit", { p_id: id });
        const rows = result.data as Record<string, unknown>[] | null;
        row = Array.isArray(rows) && rows.length > 0 ? rows[0] : null;
      } else {
        const result = await supabase
          .from("outfits")
          .select("*")
          .eq("id", id)
          .maybeSingle();
        row = (result.data as Record<string, unknown> | null) ?? null;
      }
      if (cancelled || !row) return;
      const status = (row as { analysis_status?: string | null }).analysis_status ?? null;
      if (status === "failed") {
        bail("failed");
        return;
      }
      if (status === "items_ready" || status === "complete") {
        cancelled = true;
        // Bug 2 fix — hydrate before clearing the pending shell.
        // Phase 1a.5-v — on hydration failure stay on the pending shell
        // and toast; do NOT clear textPendingOutfitId. The next poll
        // tick will retry. Matches table row for L1889 in brief.
        const viewResult = await viewOutfit(row as unknown as OutfitRow);
        if (!viewResult.ok) {
          toast.error("Couldn't load outfit.");
          trackEvent("outfit_load_failed", {
            outfit_id: id,
            reason: viewResult.reason,
            source: "text_watcher",
          });
          cancelled = false; // allow next poll to retry
          return;
        }
        setTextPendingOutfitId(null);
      }

    };

    void poll();
    const intervalId = window.setInterval(() => { void poll(); }, 2000);
    const onVisibility = () => {
      if (document.visibilityState === "visible") void poll();
    };
    const onResume = () => { void poll(); };
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("l2f:resume", onResume);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("l2f:resume", onResume);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [textPendingOutfitId, session, isNative]);


  useEffect(() => {
    const routeForSession = async (s: Session | null) => {
      if (!s) return;
      const urlParams = typeof window !== "undefined"
        ? new URLSearchParams(window.location.search)
        : null;
      const tabParam = urlParams?.get("tab") ?? null;
      const screenFromUrl: Screen | null = tabParam
        ? (PARAM_TO_TAB[tabParam] ?? null)
        : null;
      const { data: profile } = await supabase
        .from("profiles")
        .select("onboarded_at, gender, typical_budget, budget_top, budget_bottom, budget_shoes, seen_onboarding, model_for, model_setup_complete, model_gender, model_height, model_body_shape, model_body_size, model_skin_tone, model_hair, model_preferred_variation, last_known_city, last_known_city_updated_at, smw_intro_seen, wardrobe_intro_seen, deleted_at")
        .eq("id", s.user.id)
        .maybeSingle();
      const profileAny = profile as Record<string, unknown> | null;
      // Soft-delete gate: if this account was marked for deletion, sign the
      // user out immediately and bounce them back to landing with a notice.
      // Restore is manual via support@like2fit.com within 30 days.
      if (profileAny?.deleted_at) {
        await supabase.auth.signOut();
        toast.error(
          "This account has been deleted. Email support@like2fit.com within 30 days to restore.",
          { duration: 8000 },
        );
        setScreen("landing");
        return;
      }

      const existingCity = (profileAny?.last_known_city as string | null) ?? null;
      const lastCityUpdated = (profileAny?.last_known_city_updated_at as string | null) ?? null;
      if (existingCity) {
        setLastKnownCity(existingCity);

        void (async () => {
          try {
            const weatherResponse = await fetch(
              `https://wttr.in/${encodeURIComponent(existingCity)}?format=j1`,
              { signal: AbortSignal.timeout(5000) },
            );
            const weatherData = await weatherResponse.json();
            const current = weatherData?.current_condition?.[0];
            if (current) {
              const tempC = parseInt(current.temp_C ?? "15");
              const code = parseInt(current.weatherCode ?? "800");
              const weather =
                code >= 200 && code < 600
                  ? "rainy"
                  : code >= 600 && code < 700
                  ? "cold"
                  : tempC <= 5
                  ? "cold"
                  : tempC <= 15
                  ? "cool"
                  : tempC <= 25
                  ? "sunny"
                  : "hot";
              setTodayWeather(weather);
            }
          } catch {
            // silent fail — weather stays on default
          }
        })();
      }
      // Once-per-12h IP → city/weather detection
      void (async () => {
        try {
          if (lastCityUpdated) {
            const hoursSince = (Date.now() - new Date(lastCityUpdated).getTime()) / (1000 * 60 * 60);
            if (hoursSince < 12) return;
          }
          let userIp: string | null = null;
          try {
            const ipResponse = await fetch("https://api.ipify.org?format=json", {
              signal: AbortSignal.timeout(3000),
            });
            const ipData = await ipResponse.json();
            userIp = ipData.ip ?? null;
          } catch {
            debugLog("IP fetch failed, proceeding without IP");
          }
          const { data } = await supabase.functions.invoke("detect-city-weather", {
            body: { user_id: s.user.id, ip: userIp },
          });
          if (data?.city) {
            setLastKnownCity(data.city as string);
            if (data.today_weather) setTodayWeather(data.today_weather as string);
          }
        } catch (err) {
          console.error("City detection failed:", err);
        }
      })();
      setGender((profile?.gender as Gender) ?? null);
      const p = profile as Record<string, unknown> | null;
      setModelProfile(p ? {
        model_for: (p.model_for as string | null) ?? null,
        model_setup_complete: (p.model_setup_complete as boolean | null) ?? null,
        model_gender: (p.model_gender as string | null) ?? null,
        model_height: (p.model_height as string | null) ?? null,
        model_body_shape: (p.model_body_shape as string | null) ?? null,
        model_body_size: (p.model_body_size as string | null) ?? null,
        model_skin_tone: (p.model_skin_tone as string | null) ?? null,
        model_hair: (p.model_hair as string | null) ?? null,
        model_preferred_variation: (p.model_preferred_variation as string | null) ?? null,
      } : null);
      setTypicalBudget(profile?.typical_budget ?? null);
      if (!userSelectedBudgetRef.current) {
        setUploadBudget(profile?.typical_budget ?? null);
      }
      setBudgetTop((p?.budget_top as number | null) ?? null);
      setBudgetBottom((p?.budget_bottom as number | null) ?? null);
      setBudgetShoes((p?.budget_shoes as number | null) ?? null);
      setSmwIntroSeen(!!(p?.smw_intro_seen as boolean | null));
      const dbSeen = !!(p?.wardrobe_intro_seen as boolean | null);
      setWardrobeIntroSeen(dbSeen);
      if (dbSeen) {
        try {
          localStorage.setItem("l2f_wardrobe_intro_seen", "true");
        } catch {
          // ignore
        }
      }
      const onboarded = !!profile?.onboarded_at;
      if (onboarded && !(profile as any)?.seen_upload_tour) {
        setTourStep(1);
      }

      // Transfer all guest outfits on signup
      const guestId = typeof window !== "undefined" ? localStorage.getItem("guest_session_id") : null;
      let resolvedHandle: string | null = guestHandle;
      if (guestId) {
        await (supabase as any).rpc("claim_guest_outfits", { p_guest_session_id: guestId });
        // PQ-3d: transfer guest handle to profiles.username (no-op if already set)
        const { error: claimHandleErr } = await (supabase as any).rpc("claim_guest_session", {
          p_guest_session_id: guestId,
        });
        if (claimHandleErr) {
          console.error("[claim_guest_session] failed", claimHandleErr);
        }
        localStorage.removeItem("guest_session_id");
        const trigger = consumeSignupTrigger();
        trackEvent("signup_completed", { trigger });
      } else if (typeof window !== "undefined") {
        // PQ-3d cold signup: no guest session ever existed. Generate a handle
        // on the fly and assign it as the username so every user gets one.
        try {
          const sid =
            (typeof crypto !== "undefined" && "randomUUID" in crypto
              ? (crypto as any).randomUUID()
              : `g_${Date.now()}_${Math.random().toString(36).slice(2)}`) as string;
          const { data: genData, error: genErr } = await (supabase as any).rpc(
            "get_or_create_guest_session",
            { p_guest_session_id: sid },
          );
          if (genErr || !genData || genData.length === 0) {
            console.error("[cold-signup] handle generation failed", genErr);
          } else {
            resolvedHandle = (genData?.[0]?.guest_handle as string | null) ?? resolvedHandle;
            const { error: claimErr } = await (supabase as any).rpc("claim_guest_session", {
              p_guest_session_id: sid,
            });
            if (claimErr) {
              console.error("[cold-signup claim_guest_session] failed", claimErr);
            }
          }
        } catch (e) {
          console.error("[cold-signup] unexpected error", e);
        }
      }

      // PHASE-0: Fire Welcome email. Edge function dedupes via
      // profiles.welcome_sent_at, so this is safe to call on every signup
      // completion (including subsequent logins where it becomes a no-op).
      try {
        const u = session?.user ?? (await supabase.auth.getUser()).data.user;
        if (u?.email) {
          await supabase.functions.invoke("send-welcome-email", {
            body: {
              user_id: u.id,
              email: u.email,
              handle: resolvedHandle ?? null,
            },
          });
          trackEvent("welcome_email_triggered", { user_id: u.id });
        }
      } catch (e) {
        console.error("[welcome-email] invoke failed", e);
      }

      // Migrate guest size profile (sessionStorage) into the user's profile row.
      if (typeof window !== "undefined") {
        try {
          const storedSize = sessionStorage.getItem("l2f_guest_size");
          if (storedSize) {
            const gs = JSON.parse(storedSize) as GuestSizeProfile;
            if (gs?.gender) {
              await (supabase.from("profiles") as any)
                .update({
                  size_gender: gs.gender,
                  size_uk_number: gs.uk_number ?? null,
                  size_top_letter: gs.top_letter ?? null,
                  size_top_uk_number: gs.gender === "women" ? (gs.top_uk_number ?? null) : null,
                  size_letter: gs.gender === "women" ? (gs.top_letter ?? null) : null,
                  size_shoe: gs.gender === "women" ? (gs.shoe ?? null) : null,
                  size_shoe_mens: gs.gender === "men" ? (gs.shoe_mens ?? null) : null,
                  size_trouser_waist: gs.trouser_waist ?? null,
                  size_trouser_length: gs.trouser_length ?? null,
                })
                .eq("id", s.user.id);
              sessionStorage.removeItem("l2f_guest_size");
            }
          }
        } catch { /* ignore */ }

        // Migrate guest model profile (height / body_shape / body_size) collected
        // inline on the confidence card. Set model_gender from the most recent
        // claimed outfit's detected_gender when available; otherwise leave null.
        try {
          const guestModel = readGuestModelProfile();
          if (guestModel && (guestModel.height || guestModel.body_shape || guestModel.body_size)) {
            let modelGender: string | null = null;
            try {
              const { data: recent } = await (supabase as any)
                .from("outfits")
                .select("detected_gender")
                .eq("user_id", s.user.id)
                .not("detected_gender", "is", null)
                .order("created_at", { ascending: false })
                .limit(1)
                .maybeSingle();
              modelGender = recent?.detected_gender ?? null;
            } catch { /* ignore — gender stays null */ }

            const updates: Record<string, unknown> = {
              model_height: guestModel.height ?? null,
              model_body_shape: guestModel.body_shape ?? null,
              model_body_size: guestModel.body_size ?? null,
            };
            if (modelGender) updates.model_gender = modelGender;

            await (supabase.from("profiles") as any)
              .update(updates)
              .eq("id", s.user.id);
            trackEvent("guest_progressive_profile_migrated", {
              has_height: !!guestModel.height,
              has_body_shape: !!guestModel.body_shape,
              has_body_size: !!guestModel.body_size,
              model_gender: modelGender,
            });
            clearGuestModelProfile();
          }
        } catch { /* ignore */ }
      }

      // Reset guest lookbook daily counter — authenticated users have no limit.
      if (typeof window !== "undefined") {
        localStorage.removeItem("lookbook_guest_count");
        localStorage.removeItem("lookbook_guest_date");
      }

      // Mark any matching email captures as converted.
      const newEmail = s.user.email?.toLowerCase() ?? null;
      if (newEmail) {
        await supabase
          .from("email_captures")
          .update({ converted_to_user_id: s.user.id })
          .eq("email", newEmail)
          .is("converted_to_user_id", null);
      }

      // Consume pending inline-signup action (covers OAuth redirect round-trip).
      const pending = consumePendingSignupAction();
      if (pending) {
        const runPending = async () => {
          if (pending.trigger === "save_outfit" && pending.outfitId) {
            await supabase
              .from("outfits")
              .update({ is_saved: true, updated_at: new Date().toISOString() } as never)
              .eq("id", pending.outfitId);
            setCurrentIsSaved(true);
            setScreen("results");
            return;
          }
          if (pending.trigger === "saved_tab") { setScreen("saved"); return; }
          if (pending.trigger === "account_tab") { setScreen("account"); return; }
          if (pending.trigger === "lookbook_limit") { setScreen("results"); return; }
        };

        if (!profile?.seen_onboarding) {
          // Run onboarding first; defer the pending action until the tour finishes.
          postOnboardingActionRef.current = () => { void runPending(); };
          setScreen("onboard");
          return;
        }
        await runPending();
        return;
      }

      const rawTarget = typeof window !== "undefined"
        ? (localStorage.getItem("pending_signup_target") as SignupTarget | null)
        : null;
      const target = rawTarget ?? "results";
      if (typeof window !== "undefined") localStorage.removeItem("pending_signup_target");
      // If onboarding has not been completed yet, force onboarding first —
      // a stale pending_signup_target must not bypass the onboarding flow.
      if (rawTarget && !profile?.seen_onboarding) {
        setScreen("onboard");
        return;
      }
      if (target === "saved") {
        setScreen("saved");
        return;
      }
      if (target === "account") {
        setScreen("account");
        return;
      }
      if (target === "results" && currentOutfitIdRef.current) {
        setScreen("results");
        return;
      }

      if (recoveringRef.current) return;

      setScreen((cur) => {
        if (cur !== "landing" && cur !== "auth") return cur;
        if (!onboarded) return "onboard";
        if (screenFromUrl) return screenFromUrl;
        return "upload";
      });
    };
    debugLog("[bootstrap] start", { isNative: Capacitor.isNativePlatform() });
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, s) => {
      debugLog("[bootstrap] auth state change", event, !!s);
      setSession(s);
      void routeForSession(s);
      debugLog("[bootstrap] setFalse (via onAuthStateChange)");
      setBootstrapping(false);
    });
    supabase.auth.getSession()
      .then(({ data }) => {
        debugLog("[bootstrap] getSession resolved", !!data.session);
        setSession(data.session);
        void routeForSession(data.session);
        debugLog("[bootstrap] setFalse (via getSession)");
        setBootstrapping(false);
      })
      .catch((err) => {
        console.error("[bootstrap] getSession rejected — flipping anyway:", err);
        setBootstrapping(false);
      });
    return () => subscription.unsubscribe();
  }, []);

  // Render-time trace: shows whether bootstrapping ever flips to false.
  debugLog("[bootstrap] bootstrapping value =", bootstrapping);

  // PQ-3a — Guest session row + readable handle.
  // Runs once we know the user is unauthenticated. Reuses existing
  // localStorage.guest_session_id so other consumers keep working.
  useEffect(() => {
    if (bootstrapping) return;
    if (session) return;
    if (typeof window === "undefined") return;
    if (guestHandle) return;
    let cancelled = false;
    try {
      const sid = localStorage.getItem("guest_session_id") ?? crypto.randomUUID();
      localStorage.setItem("guest_session_id", sid);
      (supabase as any)
        .rpc("get_or_create_guest_session", { p_guest_session_id: sid })
        .then(({ data, error }: { data: any; error: any }) => {
          if (cancelled) return;
          if (error) {
            console.error("[guest-session] init failed", error);
            return;
          }
          if (Array.isArray(data) && data.length > 0) {
            const searchCount = data[0].search_count ?? 0;
            const handle = data[0].guest_handle ?? null;
            const createdAt = data[0].created_at ?? null;
            setGuestSearchCount(searchCount);
            setGuestHandle(handle);
            setGuestHandleForAnalytics(handle);
            setGuestCreatedAt(createdAt);

            // PQ-4: returning-guest detection. Fire once per cold load when
            // search_count > 0 OR session is older than 24h. Fresh first
            // visits (count == 0 AND createdAt == now()) intentionally skip.
            if (!guestResumedFiredRef.current && createdAt) {
              const ageMs = Date.now() - new Date(createdAt).getTime();
              const days = ageMs / (1000 * 60 * 60 * 24);
              const isReturning = searchCount > 0 || days > 1;
              if (isReturning) {
                guestResumedFiredRef.current = true;
                trackEvent("guest_session_resumed", {
                  handle,
                  days_since_first_visit: Math.floor(days),
                  search_count: searchCount,
                });
              }
            }
          }
        });
    } catch (err) {
      console.error("[guest-session] init threw", err);
    }
    return () => { cancelled = true; };
  }, [bootstrapping, session, guestHandle]);

  // PQ-4: pull guest outfit/saved counts when the Account tab is opened so
  // the GuestAccount component can render an accurate stats line.
  useEffect(() => {
    if (session) return;
    if (screen !== "account") return;
    if (typeof window === "undefined") return;
    const sid = localStorage.getItem("guest_session_id");
    if (!sid) return;
    let cancelled = false;
    (async () => {
      try {
        const { data, error } = await (supabase as unknown as {
          rpc: (name: string, args: Record<string, unknown>) => Promise<{ data: unknown; error: unknown }>;
        }).rpc("get_guest_stats", { p_guest_session_id: sid });
        if (cancelled || error) return;
        const row = Array.isArray(data) && data.length > 0 ? (data[0] as { outfit_count?: number; saved_count?: number }) : null;
        if (row) {
          setGuestOutfitCount(row.outfit_count ?? 0);
          setGuestSavedCount(row.saved_count ?? 0);
        }
      } catch (err) {
        console.error("[guest-stats] failed", err);
      }
    })();
    return () => { cancelled = true; };
  }, [session, screen]);


  useEffect(() => {
    if (screenRef.current === "results" && screen !== "results") {
      setLookbookReady(false);
    }
    screenRef.current = screen;
  }, [screen]);

  useEffect(() => {
    setLookbookReady(false);
  }, [currentOutfitId]);

  useEffect(() => {
    const pushable: Screen[] = ["results", "upload", "saved", "account"];
    if (!pushable.includes(screen)) return;
    if (skipPushRef.current) {
      skipPushRef.current = false;
      return;
    }
    let path = "/";
    if (screen === "results" && currentOutfitId) {
      path = `/?outfit=${currentOutfitId}`;
    } else if (TAB_TO_PARAM[screen]) {
      path = `/?tab=${TAB_TO_PARAM[screen]}`;
    }
    window.history.pushState({ screen, outfitId: currentOutfitId }, "", path);
  }, [screen, currentOutfitId]);

  useEffect(() => {
    const handler = (event: PopStateEvent) => {
      const params = new URLSearchParams(window.location.search);
      const pathOutfitId = params.get("outfit");
      const tabParam = params.get("tab");
      if (pathOutfitId) {
        if (currentOutfitIdRef.current === pathOutfitId) {
          setScreen("results");
        } else {
          void recoverOutfitRef.current?.(pathOutfitId);
        }
        return;
      }
      if (screenRef.current === "results") {
        setUploadedImg((prev) => {
          if (prev) URL.revokeObjectURL(prev);
          return null;
        });
        setUploadedFile(null);
        setSelectedOccasion(null);
      }
      const prevScreen = event.state?.screen as Screen | undefined;
      if (prevScreen && prevScreen !== "landing" && prevScreen !== "auth") {
        setScreen(prevScreen);
        return;
      }
      if (tabParam) {
        const tab = PARAM_TO_TAB[tabParam];
        if (tab) {
          setScreen(tab);
          return;
        }
      }
      setScreen("upload");
    };
    window.addEventListener("popstate", handler);
    return () => window.removeEventListener("popstate", handler);
  }, []);

  const handleFile = (f?: File) => {
    if (!f) return;
    setUploadedImg((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return URL.createObjectURL(f);
    });
    setUploadedFile(f);
    if (fileRef.current) {
      fileRef.current.value = "";
    }
  };

  const clearUploadState = () => {
    userSelectedBudgetRef.current = false;
    setUploadBudget(null);
    setUploadedImg((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
    setUploadedFile(null);
    setSelectedOccasion(null);
  };

  const MAX_STYLE_IMAGES = 5;
  const MAX_IMAGE_BYTES = 10 * 1024 * 1024;

  const addStyleFiles = (files: FileList | File[]) => {
    const incoming = Array.from(files);
    const remaining = MAX_STYLE_IMAGES - styleFiles.length;
    const accepted: File[] = [];
    for (const f of incoming) {
      if (accepted.length >= remaining) break;
      if (!/^image\/(png|jpe?g)$/i.test(f.type)) {
        toast.error(`${f.name}: only PNG or JPG allowed.`);
        continue;
      }
      if (f.size > MAX_IMAGE_BYTES) {
        toast.error(`${f.name}: max 10MB.`);
        continue;
      }
      accepted.push(f);
    }
    if (accepted.length === 0) return;
    setStyleFiles((prev) => [...prev, ...accepted]);
    setStyleImgs((prev) => [...prev, ...accepted.map((f) => URL.createObjectURL(f))]);
  };

  const removeStyleAt = (idx: number) => {
    setStyleFiles((prev) => prev.filter((_, i) => i !== idx));
    setStyleImgs((prev) => {
      const url = prev[idx];
      if (url) URL.revokeObjectURL(url);
      return prev.filter((_, i) => i !== idx);
    });
  };

  const clearStyle = () => {
    styleImgs.forEach((u) => URL.revokeObjectURL(u));
    setStyleFiles([]);
    setStyleImgs([]);
    setSelectedOccasion(null);
  };

  const handleAnalyse = useCallback(async (budgetOverride?: number, reuseItems?: DetectedItem[], reuseGender?: "men" | "women" | "unisex" | null) => {
    const isStyle = uploadMode === "style_item";
    const wardrobeSource = isStyle && isWardrobeSource && !!uploadedWardrobeItem;
    if (isStyle ? (!wardrobeSource && styleFiles.length === 0) : !uploadedFile) return;

    setAnalysing(true);
    trackEvent("analysis_started", {
      mode: uploadMode,
      occasion: isStyle ? selectedOccasion ?? null : null,
    });
    // GTM: user has just submitted an upload / triggered analysis.
    // Fire only on a fresh user-initiated analysis (skip silent regenerate
    // paths that reuse previously detected items).
    if (!reuseItems) {
      pushDataLayer({ event: "upload_started" });
    }
    try {
      const guest = !session;
      let folderId: string;
      let userIdForRow: string | null = null;
      let guestId: string | null = null;

      if (guest) {
        guestId = localStorage.getItem("guest_session_id") || crypto.randomUUID();
        localStorage.setItem("guest_session_id", guestId);
        folderId = `guests/${guestId}`;
      } else {
        userIdForRow = session!.user.id;
        folderId = session!.user.id;
      }

      let imagePaths: string[] = [];
      if (wardrobeSource) {
        // Wardrobe-sourced item: no new upload. Use the item's storage path if available.
        const wardrobePath = uploadedWardrobeItem?.image_path ?? null;
        imagePaths = wardrobePath ? [wardrobePath] : [];
      } else if (isStyle) {
        const compressedStyleFiles = await Promise.all(styleFiles.map((f) => compressImage(f)));
        const uploads = await Promise.all(
          compressedStyleFiles.map(async (file, idx) => {
            const ext = file.name.split(".").pop() || "jpg";
            const p = `${folderId}/${crypto.randomUUID()}_${idx}.${ext}`;
            const { error } = await supabase.storage
              .from("outfits")
              .upload(p, file, { contentType: file.type, upsert: false });
            return error ? null : p;
          }),
        );
        imagePaths = uploads.filter((p): p is string => !!p);
        if (imagePaths.length !== compressedStyleFiles.length) {
          toast.error("Some images failed to upload. Please try again.");
          setAnalysing(false);
          return;
        }
      } else {
        const compressedFile = await compressImage(uploadedFile!);
        const ext = compressedFile.name.split(".").pop() || "jpg";
        const p = `${folderId}/${crypto.randomUUID()}.${ext}`;
        const { error: upErr } = await supabase.storage
          .from("outfits")
          .upload(p, compressedFile, { contentType: compressedFile.type, upsert: false });
        if (upErr) throw upErr;
        imagePaths = [p];
      }



      const primaryPath = imagePaths[0] ?? null;
      let signedUrl: string | null = null;
      if (primaryPath) {
        signedUrl = await signImage(primaryPath);
      }
      if (!signedUrl && wardrobeSource) {
        signedUrl = uploadedWardrobeItem?.image_url ?? null;
      }
      if (!signedUrl) throw new Error("Could not sign image URL");

      const newOutfitId = crypto.randomUUID();
      const { error: insErr } = await supabase
        .from("outfits")
        .insert({
          id: newOutfitId,
          user_id: userIdForRow,
          image_url: wardrobeSource && !primaryPath
            ? (uploadedWardrobeItem?.image_url ?? signedUrl)
            : `signed://${primaryPath}`,
          image_path: primaryPath,
          image_paths: imagePaths,
          is_guest: guest,
          guest_session_id: guestId,
          analysis_mode: uploadMode,
          occasion: isStyle ? selectedOccasion : null,
          // P1a.5-iv: authoritative write. Previously relied on the
          // migration default ('screenshot'); making it explicit so every
          // row-insert site sets input_method at write time. Value
          // preserved as 'screenshot' — no historical reclassification.
          input_method: "screenshot",
        } as any);
      if (insErr) throw insErr;
      const outfit = { id: newOutfitId };


      const selectedBudget = budgetOverride ?? uploadBudget ?? -1;

      let sizeProfile: Record<string, unknown> | null = null;
      if (!guest && session?.user?.id) {
        const { data: userProfile } = await (supabase.from("profiles") as any)
          .select(
            "model_gender, size_gender, size_uk_number, size_letter, size_shoe, size_top_letter, size_top_uk_number, size_trouser_waist, size_trouser_length, size_shoe_mens",
          )
          .eq("id", session.user.id)
          .maybeSingle();
        if (userProfile) {
          const rawProfile = {
            gender:
              (userProfile as { model_gender?: string | null }).model_gender ??
              userProfile.size_gender ??
              null,
            uk_number: userProfile.size_uk_number,
            letter_size: userProfile.size_letter,
            shoe_womens: userProfile.size_shoe,
            top_letter: userProfile.size_top_letter,
            trouser_waist: userProfile.size_trouser_waist,
            trouser_length: userProfile.size_trouser_length,
            shoe_mens: userProfile.size_shoe_mens,
          };
          const hasSize = Object.values(rawProfile).some(
            (v) => v !== null && v !== undefined,
          );
          sizeProfile = hasSize ? rawProfile : null;
        }

      }

      let fnData: any;
      if (isStyle) {
        const { data, error: fnErr, quotaExceeded } = await invokeGenerator<any>("style-my-item", {
          source: "style-yourself",
          body: {
            image_path: primaryPath,
            image_paths: imagePaths,
            outfit_id: outfit.id,
            user_budget: selectedBudget,
            size_profile: sizeProfile,
            occasion: selectedOccasion,
            wardrobe_item_id: wardrobeSource ? (uploadedWardrobeItem?.id ?? null) : null,
            wardrobe_item_image_url: wardrobeSource ? (uploadedWardrobeItem?.image_url ?? null) : null,
          },
        });
        if (quotaExceeded) return;
        if (fnErr) throw fnErr;
        fnData = data;

      } else {

        const skipDetection = Array.isArray(reuseItems) && reuseItems.length > 0;
        const { data, error: fnErr, quotaExceeded } = await invokeGenerator<any>("analyse-outfit", {
          source: "photo",
          body: {
            source_type: "photo",
            image_path: primaryPath,
            image_url: signedUrl,
            outfit_id: outfit.id,
            budget: selectedBudget,
            user_budget: selectedBudget,
            budget_top: budgetTop,
            budget_bottom: budgetBottom,
            budget_shoes: budgetShoes,
            is_guest: guest,
            guest_session_id: guestId,
            size_profile: sizeProfile,
            skip_detection: skipDetection,
            detected_items: skipDetection
              ? reuseItems!.map((it) => ({
                  name: it.name,
                  category: it.category,
                  color_hex: it.color_hex ?? null,
                  style_tags: it.style_tags ?? [],
                  search_query: it.search_query ?? it.name,
                }))
              : undefined,
            detected_gender: skipDetection ? (reuseGender ?? null) : undefined,
          },
        });
        if (quotaExceeded) return;
        if (fnErr) throw fnErr;
        fnData = data;

      }

      const allSigned = await Promise.all(imagePaths.map((p) => signImage(p)));
      const signedImages = allSigned.filter((u): u is string => !!u);

      setLookbookReady(false);
      resetResultsState();
      setCurrentOutfitId(outfit.id);
      currentOutfitIdRef.current = outfit.id;
      setCurrentImg(signedUrl);
      setCurrentImages(signedImages.length > 0 ? signedImages : [signedUrl]);
      const detected = (fnData?.items ?? []) as DetectedItem[];
      setCurrentItems(detected);
      setCurrentMode(uploadMode);
      setCurrentOccasion(isStyle ? selectedOccasion : null);
      setCurrentStyleSummary(fnData?.style_summary ?? null);
      const rawGender = (fnData?.gender ?? null) as string | null;
      const normalizedGender: "men" | "women" | "unisex" | null =
        rawGender === "men" || rawGender === "women" || rawGender === "unisex" ? rawGender : null;
      setCurrentDetectedGender(normalizedGender);
      if (normalizedGender) {
        void supabase
          .from("outfits")
          .update({ detected_gender: normalizedGender } as never)
          .eq("id", outfit.id);
      }
      setCurrentIsSaved(false);
      setCurrentTitle(null);
      userSelectedBudgetRef.current = false;
      setScreen("results");
      if (wardrobeSource) {
        setUploadedWardrobeItem(null);
        setIsWardrobeSource(false);
      }
      trackEvent("analysis_completed", {
        mode: uploadMode,
        item_count: detected.length,
        gender: fnData?.gender ?? null,
      });
    } catch (err) {
      console.error(err);
      toast.error("Analysis failed. Please try again.");
    } finally {
      setAnalysing(false);
    }
  }, [uploadedFile, styleFiles, session, uploadBudget, uploadMode, selectedOccasion, isWardrobeSource, uploadedWardrobeItem]);


  const handleSaveOutfit = async () => {
    if (!session) {
      // PQ-4: guest save branch — flip is_saved via SECURITY DEFINER RPC
      // instead of bouncing to the signup sheet. Falls back to signup only
      // if we somehow lack a guest session id.
      const guestSid =
        typeof window !== "undefined"
          ? localStorage.getItem("guest_session_id")
          : null;
      if (!currentOutfitId) return;
      if (!guestSid) {
        trackEvent("signup_prompted", { trigger: "save_outfit" });
        setPendingSignupAction({ trigger: "save_outfit", outfitId: currentOutfitId });
        pendingActionRef.current = () => { void handleSaveOutfit(); };
        setSignupSheet("save_outfit");
        return;
      }
      if (currentIsSaved) return;
      const { data, error } = await (supabase as unknown as {
        rpc: (name: string, args: Record<string, unknown>) => Promise<{ data: unknown; error: unknown }>;
      }).rpc("save_guest_outfit", {
        p_outfit_id: currentOutfitId,
        p_guest_session_id: guestSid,
      });
      if (error || data !== true) {
        console.error("[guest-save] failed", error);
        toast.error("Couldn't save outfit.");
        return;
      }
      setCurrentIsSaved(true);
      setSavedLooksRefreshKey((k) => k + 1);
      setGuestSavedCount((n) => n + 1);
      trackEvent("outfit_saved", {
        mode: currentMode,
        occasion: currentOccasion ?? null,
        is_guest: true,
      });
      toast.success("Saved ✓", {
        style: { background: CORAL, color: "white", border: "none" },
        duration: 2000,
      });
      return;
    }
    if (!currentOutfitId) return;
    if (currentIsSaved) return;

    // Auto-generate a title if none exists yet.
    let titleToSet = currentTitle;
    if (!titleToSet) {
      if (currentMode === "style_item") {
        const firstOwned = currentItems.find((i) => i.item_type === "owned") ?? currentItems[0];
        const base = firstOwned?.name ?? "Your items";
        titleToSet = currentOccasion ? `${base} — ${currentOccasion}` : base;
      } else {
        titleToSet = currentItems[0]?.name ?? "Outfit";
      }
    }

    const { error } = await supabase
      .from("outfits")
      .update({ is_saved: true, title: titleToSet, updated_at: new Date().toISOString() } as any)
      .eq("id", currentOutfitId);
    if (error) {
      toast.error("Couldn't save outfit.");
      return;
    }
    setCurrentIsSaved(true);
    setCurrentTitle(titleToSet);
    if (currentMode === "style_item") {
      setSavedStyleCount((p) => p + 1);
    } else {
      setSavedGetTheLookCount((p) => p + 1);
    }
    setSavedLooksRefreshKey((k) => k + 1);

    trackEvent("outfit_saved", {
      mode: currentMode,
      occasion: currentOccasion ?? null,
    });
    toast.success("Saved to your library ✓", {
      style: { background: CORAL, color: "white", border: "none" },
      duration: 2000,
    });
  };

  // Phase BACKGROUND-ANALYSIS-P1a.5-v — viewOutfit returns a discriminated
  // result so instant-mount call sites can distinguish a hydration
  // failure (RLS / network / data shape) from a successful entry and
  // surface the ResultsLoadFailed component instead of perpetual skeletons.
  // Existing union members beyond items_fetch_failed are reserved for
  // future failure paths (1a.5-vi may flip status-driven failures here).
  type ViewOutfitResult =
    | { ok: true }
    | { ok: false; reason: "items_fetch_failed" | "outfit_missing" | "rls_denied" };

  const viewOutfit = async (
    outfit: OutfitRow,
    preloadedItems?: DetectedItem[] | null,
  ): Promise<ViewOutfitResult> => {
    console.log("[share-debug-v2.1] viewOutfit entry", {
      outfitId: outfit.id,
      hasPreloadedItems: preloadedItems != null,
      currentOutfitIdBefore: currentOutfitIdRef.current,
    });
    // Clear any prior failed-load state at entry. If this hydration also
    // fails, the caller re-flips it true after our return.
    setLoadFailed(false);
    let items: DetectedItem[] | null = preloadedItems ?? null;
    if (!items) {
      // GUEST-RECENTS-TAP-COSMETIC-1: anon has no SELECT on detected_items;
      // branch on outfit.is_guest to use the SECURITY DEFINER RPC for guests.
      const outfitIsGuest = (outfit as { is_guest?: boolean }).is_guest === true;
      if (outfitIsGuest) {
        const { data, error } = await (supabase as any).rpc(
          "get_guest_outfit_items",
          { p_outfit_id: outfit.id },
        );
        if (error) {
          toast.error("Couldn't load outfit.");
          return { ok: false, reason: "items_fetch_failed" };
        }
        items = ((data as DetectedItem[] | null) ?? []) as DetectedItem[];
      } else {
        const { data, error } = await supabase
          .from("detected_items")
          .select("*")
          .eq("outfit_id", outfit.id)
          .order("position", { ascending: true });
        if (error) {
          toast.error("Couldn't load outfit.");
          return { ok: false, reason: "items_fetch_failed" };
        }
        items = (data ?? []) as DetectedItem[];
      }
    }
    // Phase BACKGROUND-ANALYSIS-P1a.5-ii: tolerate null image_path/image_url.
    // For url/text input methods the outfit row may bootstrap with no image.
    // signImage(null) returns null; Promise.all([]) resolves to [];
    // setCurrentImg(null) is accepted by Results (text path already does this).
    const signedUrl = outfit.image_path ? await signImage(outfit.image_path) : null;
    console.log("[share-debug-v2.1] viewOutfit after signImage", {
      outfitId: outfit.id,
      signedUrlOk: !!signedUrl,
    });
    const paths = (outfit.image_paths && outfit.image_paths.length > 0)
      ? outfit.image_paths
      : (outfit.image_path ? [outfit.image_path] : []);
    const allSigned = await Promise.all(paths.map((p) => signImage(p)));
    console.log("[share-debug-v2.1] viewOutfit after Promise.all signImage", {
      outfitId: outfit.id,
      signedCount: allSigned.filter(Boolean).length,
      pathsCount: paths.length,
    });
    setCurrentImages(allSigned.filter((u): u is string => !!u));
    console.log("[share-debug-v2.1] viewOutfit about to call resetResultsState", {
      outfitId: outfit.id,
    });
    beginResultsRefresh(outfit.id);
    setCurrentOutfitId(outfit.id);
    currentOutfitIdRef.current = outfit.id;
    setCurrentImg(signedUrl ?? outfit.image_url ?? null);
    setCurrentItems(items);
    const loadedMode = (outfit.analysis_mode as AnalysisMode) ?? "find_look";
    setCurrentMode(loadedMode);
    setUploadMode(loadedMode);
    setCurrentOccasion(outfit.occasion ?? null);
    setCurrentStyleSummary(outfit.style_summary ?? null);
    const loadedGender = (outfit as { detected_gender?: string | null }).detected_gender ?? null;
    setCurrentDetectedGender(
      loadedGender === "men" || loadedGender === "women" || loadedGender === "unisex"
        ? loadedGender
        : null,
    );
    setCurrentInputMethod((outfit as { input_method?: string | null }).input_method ?? null);
    setCurrentTextDescription((outfit as { text_description?: string | null }).text_description ?? null);
    setCurrentIsSaved(outfit.is_saved ?? true);
    setCurrentTitle(outfit.title ?? null);
    // Restore persisted lookbook (async-job pattern). If still pending, leave
    // it null — the user can re-trigger; we don't auto-resume polling here.
    const persistedUrl = (outfit as { lookbook_image_url?: string | null }).lookbook_image_url ?? null;
    const persistedStatus = (outfit as { lookbook_status?: string | null }).lookbook_status ?? null;
    if (persistedUrl && persistedStatus === "ready") {
      setCurrentLookbookUrl(persistedUrl);
    }
    const persistedLb2Url = (outfit as { lookbook_lb2_url?: string | null }).lookbook_lb2_url ?? null;
    const persistedLb3Url =
      (outfit as { lookbook_lb3_url?: string | null }).lookbook_lb3_url ?? null;
    const rawUrls = (outfit as { lookbook_image_urls?: unknown }).lookbook_image_urls;
    if (Array.isArray(rawUrls) && rawUrls.length > 0) {
      const validUrls = rawUrls.filter(
        (v): v is string => typeof v === "string" && v.length > 0,
      );
      if (validUrls.length > 0) {
        setCurrentLookbookUrls(validUrls);
      }
    }
    if (
      (persistedLb2Url || persistedLb3Url) &&
      (
        !Array.isArray(rawUrls) ||
        (persistedLb2Url && !(rawUrls as unknown[])[1]) ||
        (persistedLb3Url && !(rawUrls as unknown[])[2])
      )
    ) {
      const lb1FromArray = Array.isArray(rawUrls) && typeof (rawUrls as unknown[])[0] === "string"
        ? ((rawUrls as unknown[])[0] as string)
        : null;
      const lb2FromArray = Array.isArray(rawUrls) && typeof (rawUrls as unknown[])[1] === "string"
        ? ((rawUrls as unknown[])[1] as string)
        : null;
      const lb1Url = persistedUrl ?? lb1FromArray ?? null;
      const lb2Url = persistedLb2Url ?? lb2FromArray ?? null;
      // Preserve index positions: [0]=lb1, [1]=lb2, [2]=lb3. Use empty string
      // for missing slots so Results can read index [2] for lb3.
      const next: string[] = [];
      if (lb1Url) next[0] = lb1Url;
      if (lb2Url) next[1] = lb2Url;
      if (persistedLb3Url) next[2] = persistedLb3Url;
      // Convert sparse array to dense with empty strings for missing.
      const dense: string[] = [];
      for (let i = 0; i <= 2; i++) dense[i] = next[i] ?? "";
      // Trim trailing empties but keep middle gaps.
      while (dense.length > 0 && dense[dense.length - 1] === "") dense.pop();
      if (dense.length > 0) setCurrentLookbookUrls(dense);
    }
    setScreen("results");
    console.log("[share-debug-v2.1] viewOutfit end — all setters fired", {
      outfitId: outfit.id,
    });
    return { ok: true };
  };

  // HOME-DEMO-CARD-1 — Shared sample-outfit hydration handler. Lifted from
  // SocialHowToSheet's inline closure so both the social flow and the new
  // HomeDemoCard can trigger the same path. `source` is attached to the
  // sample_outfit_viewed analytics event for funnel attribution.
  const handleTrySample = useCallback(
    async (source: "social_flow" | "home_demo_card") => {
      const result = await loadSampleOutfit(supabase);
      if (!result.ok) {
        toast.error("Sample outfit not available right now");
        console.error("[sample] load failed:", result.error);
        return;
      }
      const viewResult = await viewOutfit(
        result.outfit as unknown as OutfitRow,
        result.items as unknown as DetectedItem[],
      );
      if (!viewResult.ok) {
        toast.error("Sample outfit not available right now");
        trackEvent("outfit_load_failed", {
          outfit_id: result.outfit.id,
          reason: viewResult.reason,
          source: "sample_outfit",
        });
        return;
      }
      setIsSampleMode(true);
      trackEvent("sample_outfit_viewed", {
        source,
        platform: source === "social_flow" ? socialSelectedPlatform : null,
        sample_outfit_id: result.outfit.id,
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [socialSelectedPlatform],
  );

  const handleUploadScreenshot = useCallback(async () => {
    // PQ-3b: guest paywall gate fires BEFORE camera + edge fn.
    if (!session && guestSearchCount >= 5) {
      setAddSheetOpen(false);
      setPaywallOpen(true);
      return;
    }
    setAddSheetOpen(false);
    if (!isNative) {
      fileRef.current?.click();
      return;
    }
    try {
      const { Camera } = await import("@capacitor/camera");

      const status = await Camera.checkPermissions();
      if (status.photos === "prompt" || status.photos === "prompt-with-rationale") {
        const requested = await Camera.requestPermissions({ permissions: ["photos"] });
        if (requested.photos !== "granted" && requested.photos !== "limited") {
          toast.error("Photo access required. Enable in Settings → Like2Fit → Photos.");
          return;
        }
      } else if (status.photos === "denied") {
        toast.error("Photo access was denied. Enable in Settings → Like2Fit → Photos.");
        return;
      }

      const result = await Camera.pickImages({ limit: 1, quality: 90 });
      const photo = result.photos?.[0];
      if (!photo?.webPath) return;
      const resp = await fetch(photo.webPath);
      const blob = await resp.blob();
      const ext = (photo.format || "jpg").toLowerCase();
      const file = new File([blob], `upload.${ext}`, {
        type: blob.type || `image/${ext === "jpg" ? "jpeg" : ext}`,
      });

      // SCREENSHOT-INSTANT-MOUNT-1: collapse "Get the look" intermediate
      // screen. Compress + upload + insert synchronously, then mount Results
      // via viewOutfit with a synthetic row; fire analyse-outfit in the
      // background. Mirrors text/URL instant-mount precedent.
      const guest = !session;
      const guestId = guest
        ? (localStorage.getItem("guest_session_id") || (() => {
            const id = crypto.randomUUID();
            localStorage.setItem("guest_session_id", id);
            return id;
          })())
        : null;
      const folderId = guest ? `guests/${guestId}` : session!.user.id;

      const compressed = await compressImage(file);
      const fileExt = compressed.name.split(".").pop() || "jpg";
      const primaryPath = `${folderId}/${crypto.randomUUID()}.${fileExt}`;
      const { error: upErr } = await supabase.storage
        .from("outfits")
        .upload(primaryPath, compressed, { contentType: compressed.type, upsert: false });
      if (upErr) {
        console.error("[handleUploadScreenshot] upload failed:", upErr);
        toast.error("Upload failed. Please try again.");
        return;
      }

      const signedUrl = await signImage(primaryPath);
      if (!signedUrl) {
        toast.error("Couldn't prepare image. Please try again.");
        return;
      }

      const newOutfitId = crypto.randomUUID();
      const { error: insErr } = await supabase
        .from("outfits")
        .insert({
          id: newOutfitId,
          user_id: guest ? null : session!.user.id,
          image_url: `signed://${primaryPath}`,
          image_path: primaryPath,
          image_paths: [primaryPath],
          is_guest: guest,
          guest_session_id: guestId,
          analysis_mode: "find_look",
          occasion: null,
          input_method: "screenshot",
        } as any);
      if (insErr) {
        console.error("[handleUploadScreenshot] insert failed:", insErr);
        toast.error("Couldn't start analysis. Please try again.");
        return;
      }

      const syntheticRow = {
        id: newOutfitId,
        image_path: primaryPath,
        image_url: `signed://${primaryPath}`,
        image_paths: [primaryPath],
        analysis_mode: "find_look",
        occasion: null,
        style_summary: null,
        detected_gender: null,
        input_method: "screenshot",
        text_description: null,
        source_url: null,
        is_saved: false,
        title: null,
        lookbook_image_url: null,
        lookbook_status: "pending",
        lookbook_lb2_url: null,
        lookbook_lb3_url: null,
        lookbook_image_urls: null,
      } as unknown as OutfitRow;

      setLookbookReady(false);
      const viewResult = await viewOutfit(syntheticRow, []);
      if (!viewResult.ok) {
        setCurrentOutfitId(newOutfitId);
        currentOutfitIdRef.current = newOutfitId;
        setLoadFailed(true);
        setScreen("results");
        trackEvent("outfit_load_failed", {
          outfit_id: newOutfitId,
          reason: viewResult.reason,
          source: "in_app_screenshot",
        });
        return;
      }

      // Background invoke — mirrors handleAnalyse non-style branch.
      // TODO(future): extend OutfitMeta polling to include detected_gender
      // + style_summary so image-flow instant-mounts (share-image + this
      // screenshot path) populate these in Results post-mount.
      void (async () => {
        const selectedBudget = uploadBudget ?? -1;
        let sizeProfile: Record<string, unknown> | null = null;
        if (!guest && session?.user?.id) {
          const { data: userProfile } = await (supabase.from("profiles") as any)
            .select(
              "model_gender, size_gender, size_uk_number, size_letter, size_shoe, size_top_letter, size_top_uk_number, size_trouser_waist, size_trouser_length, size_shoe_mens",
            )
            .eq("id", session.user.id)
            .maybeSingle();
          if (userProfile) {
            const rawProfile = {
              gender:
                (userProfile as { model_gender?: string | null }).model_gender ??
                userProfile.size_gender ??
                null,
              uk_number: userProfile.size_uk_number,
              letter_size: userProfile.size_letter,
              shoe_womens: userProfile.size_shoe,
              top_letter: userProfile.size_top_letter,
              trouser_waist: userProfile.size_trouser_waist,
              trouser_length: userProfile.size_trouser_length,
              shoe_mens: userProfile.size_shoe_mens,
            };
            const hasSize = Object.values(rawProfile).some(
              (v) => v !== null && v !== undefined,
            );
            sizeProfile = hasSize ? rawProfile : null;
          }
        }
        const { error: fnErr, quotaExceeded } = await invokeGenerator("analyse-outfit", {
          source: "photo-reuse",
          body: {
            source_type: "photo",
            image_path: primaryPath,
            image_url: signedUrl,
            outfit_id: newOutfitId,
            budget: selectedBudget,
            user_budget: selectedBudget,
            budget_top: budgetTop,
            budget_bottom: budgetBottom,
            budget_shoes: budgetShoes,
            is_guest: guest,
            guest_session_id: guestId,
            size_profile: sizeProfile,
            skip_detection: false,
          },
        });
        if (quotaExceeded) return;
        if (fnErr) {
          console.error("[handleUploadScreenshot] analyse-outfit invoke failed:", fnErr);
        }

      })();
    } catch (err) {
      console.error("[NativeHome] pickImages failed:", err);
      toast.error("Couldn't pick photo. Try again or check permissions.");
    }
  }, [
    isNative,
    session,
    guestSearchCount,
    uploadBudget,
    budgetTop,
    budgetBottom,
    budgetShoes,
    viewOutfit,
  ]);


  const recoverOutfit = useCallback(async (outfitId: string) => {
    try {
      // Try direct read first (works for authenticated owners via RLS).
      let outfit: OutfitRow | null = null;
      const direct = await supabase
        .from("outfits")
        .select("*")
        .eq("id", outfitId)
        .maybeSingle();
      outfit = (direct.data as OutfitRow | null) ?? null;

      // Fallback: anonymous visitors must go through the by-id guest RPC
      // (broad anon SELECT was removed to block enumeration of all guest outfits).
      if (!outfit) {
        const { data: guestRows } = await (supabase as any).rpc("get_guest_outfit", {
          p_id: outfitId,
        });
        outfit = Array.isArray(guestRows) && guestRows.length > 0
          ? (guestRows[0] as OutfitRow)
          : null;
      }

      if (!outfit) {
        window.history.replaceState({}, "", "/");
        recoveringRef.current = false;
        return;
      }

      // Items: try direct read first, fall back to guest RPC.
      let items: DetectedItem[] | null = null;
      const itemsDirect = await supabase
        .from("detected_items")
        .select("*")
        .eq("outfit_id", outfitId)
        .order("position", { ascending: true });
      items = (itemsDirect.data as DetectedItem[] | null) ?? null;
      if (!items || items.length === 0) {
        const { data: guestItems } = await (supabase as any).rpc("get_guest_outfit_items", {
          p_outfit_id: outfitId,
        });
        items = (guestItems as DetectedItem[] | null) ?? null;
      }

      if (!items || items.length === 0) {
        window.history.replaceState({}, "", "/");
        recoveringRef.current = false;
        return;
      }
      // viewOutfit hydrates all results state and calls setScreen("results").
      // Phase 1a.5-v — recoverOutfit: deep-link recovery. On hydration
      // failure, toast + return to home; don't surface failed-load UI
      // (user opened from a URL, not from a just-created flow).
      const viewResult = await viewOutfit(outfit as OutfitRow, items);
      if (!viewResult.ok) {
        toast.error("Couldn't load that outfit.");
        trackEvent("outfit_load_failed", {
          outfit_id: outfitId,
          reason: viewResult.reason,
          source: "recover_outfit",
        });
        window.history.replaceState({}, "", "/");
      }
    } catch (err) {
      console.error("recoverOutfit failed:", err);
      window.history.replaceState({}, "", "/");
    } finally {
      recoveringRef.current = false;
    }
  }, []);

  useEffect(() => {
    recoverOutfitRef.current = recoverOutfit;
  }, [recoverOutfit]);

  // On mount, if URL has ?outfit={outfitId}, attempt to recover.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    const outfitId = params.get("outfit");
    if (outfitId) {
      recoveringRef.current = true;
      skipPushRef.current = true; // don't push a duplicate history entry
      void recoverOutfit(outfitId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Campaign landing-page entry: /get-the-look and /your-wardrobe set a
  // sessionStorage flag before redirecting here. Honor it once on mount.
  useEffect(() => {
    if (typeof window === "undefined") return;
    let entry: string | null = null;
    try {
      entry = sessionStorage.getItem("l2f_campaign_entry");
      if (entry) sessionStorage.removeItem("l2f_campaign_entry");
    } catch {
      /* ignore */
    }
    if (!entry) return;

    if (entry === "get-the-look" || entry === "new-job") {
      setUploadMode("find_look");
      setScreen("upload");
      return;
    }
    if (entry === "wardrobe") {
      if (session?.user.id) {
        setScreen("saved");
      } else {
        setPendingPostAuthScreen("saved");
        setScreen("auth");
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const goAuthFor = (target: SignupTarget) => {
    if (typeof window !== "undefined") {
      localStorage.setItem("pending_signup_target", target);
    }
    setScreen("auth");
  };
  const goAuth = () => goAuthFor("results");
  const handleLogout = async () => {
    // Bug 2 fix — clear outfit state before sign-out so a subsequent guest
    // session can't inherit the prior user's outfit (LB1 image, reference
    // card, etc.) while a fresh outfit is being hydrated.
    resetResultsState();
    await supabase.auth.signOut();
    setScreen(Capacitor.isNativePlatform() ? "native-home" : "landing");
  };


  const openInlineSignup = (
    trigger: SignupTrigger,
    onSuccess: () => void,
    pending?: Parameters<typeof setPendingSignupAction>[0],
  ) => {
    trackEvent("signup_prompted", { trigger });
    if (pending) setPendingSignupAction(pending);
    pendingActionRef.current = onSuccess;
    setSignupSheet(trigger);
  };

  /**
   * PQ-3b: Guest paywall gate. Returns true if submission is allowed
   * (authed user, or guest under the 5-search limit). Returns false and
   * opens the paywall when a guest hits the cap. Callers should NOT invoke
   * any analysis edge fn when this returns false.
   */
  const tryStartAnalysis = useCallback((): boolean => {
    if (session) return true;
    if (guestSearchCount >= 5) {
      setPaywallOpen(true);
      return false;
    }
    return true;
  }, [session, guestSearchCount]);

  /**
   * PQ-3b: increments guest search_count once per outfit when LB1 first
   * becomes renderable. Skipped for authed users and same-outfit re-fires.
   */
  const handleGuestLb1Success = useCallback((outfitId: string) => {
    if (session) return;
    if (outfitsCountedRef.current.has(outfitId)) return;
    const guestSessionId = typeof window !== "undefined"
      ? localStorage.getItem("guest_session_id")
      : null;
    if (!guestSessionId) return;
    outfitsCountedRef.current.add(outfitId);
    void (async () => {
      const { data: newCount, error } = await (supabase as any).rpc(
        "increment_guest_search",
        { p_guest_session_id: guestSessionId },
      );
      if (error) {
        console.error("[PQ-3b] increment_guest_search failed:", error);
        // Roll back guard so a retry can attempt again.
        outfitsCountedRef.current.delete(outfitId);
        return;
      }
      if (typeof newCount === "number" && newCount >= 0) {
        setGuestSearchCount(newCount);
      }
    })();
  }, [session]);

  const handleTabChange = (next: TabKey) => {
    if (screen === "upload" && next !== "find" && next !== "style") {
      userSelectedBudgetRef.current = false;
    }
    // PQ-4: web tab gates dropped — guests can navigate to Saved + Account.
    // The underlying screens render their own guest-aware states.
    if (next === "find") {
      // Return to results if a prior find_look analysis exists and we're not already on results.
      if (currentOutfitId && currentMode === "find_look" && screen !== "results" && currentImg) {
        setUploadMode("find_look");
        setScreen("results");
        return;
      }
      setUploadMode("find_look");
      setSelectedOccasion(null);
      setScreen("upload");
      return;
    }
    if (next === "style") {
      // Return to results if a prior style_item analysis exists and we're not already on results.
      if (currentOutfitId && currentMode === "style_item" && screen !== "results" && currentImg) {
        setUploadMode("style_item");
        setScreen("results");
        return;
      }
      if (!smwIntroSeen) {
        setShowSmwIntro(true);
        return;
      }
      setUploadMode("style_item");
      setScreen("upload");
      return;
    }
    if (next === "saved" && session && !wardrobeIntroSeen) {
      let localSeen = false;
      try {
        localSeen = localStorage.getItem("l2f_wardrobe_intro_seen") === "true";
      } catch {
        // localStorage unavailable; fall through to default behavior.
      }
      if (localSeen) {
        setWardrobeIntroSeen(true);
        setScreen("saved");
      } else {
        setScreen("saved");
        setShowWardrobeIntro(true);
      }
      return;
    }
    setScreen(next);
  };

  const dismissSmwIntro = async () => {
    if (session?.user.id) {
      void supabase.from("profiles").update({ smw_intro_seen: true } as never).eq("id", session.user.id);
    } else if (typeof window !== "undefined") {
      window.localStorage.setItem("l2f_smw_intro_seen", "true");
    }
    setSmwIntroSeen(true);
    setShowSmwIntro(false);
    setUploadMode("style_item");
    setScreen("upload");
  };

  const dismissWardrobeIntro = async () => {
    setShowWardrobeIntro(false);
    setWardrobeIntroSeen(true);
    try {
      localStorage.setItem("l2f_wardrobe_intro_seen", "true");
    } catch {
      // localStorage may be unavailable (private mode, quota); fail silently.
    }
    if (session?.user.id) {
      try {
        const { error } = await supabase
          .from("profiles")
          .update({ wardrobe_intro_seen: true } as never)
          .eq("id", session.user.id);
        if (error) {
          console.error("[wardrobe-intro] DB write failed", error);
        }
      } catch (err) {
        console.error("[wardrobe-intro] DB write threw", err);
      }
    }
  };

  const handleSignupSuccess = async () => {
    const fn = pendingActionRef.current;
    pendingActionRef.current = null;
    setSignupSheet(null);

    // Resolve the freshly created session and check onboarding state.
    const { data: userData } = await supabase.auth.getUser();
    const newUserId = userData?.user?.id ?? null;
    let needsOnboarding = false;
    if (newUserId) {
      const { data: profile } = await supabase
        .from("profiles")
        .select("seen_onboarding")
        .eq("id", newUserId)
        .maybeSingle();
      needsOnboarding = !profile?.seen_onboarding;
    }

    if (needsOnboarding) {
      // Defer the original action until onboarding completes.
      postOnboardingActionRef.current = fn;
      setScreen("onboard");
      return;
    }

    if (pendingPostAuthScreen) {
      const target = pendingPostAuthScreen;
      setPendingPostAuthScreen(null);
      setScreen(target);
      return;
    }

    if (fn) {
      // Delay so onAuthStateChange has set session before fn re-runs.
      setTimeout(fn, 50);
    }
  };

  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col items-center md:bg-[#F9F6F3]">
      {bootstrapping && (
        <div
          aria-hidden
          style={{
            position: "fixed",
            inset: 0,
            background: "var(--background)",
            zIndex: 9999,
            pointerEvents: "none",
          }} />
      )}
      <DesktopBanner />
      <div className="w-full max-w-md md:max-w-lg min-h-screen flex flex-col relative pb-20 bg-background md:border-l md:border-r md:border-[#F0EBE6]">
        {screen === "landing" && !isNative && (
          <Landing
            onTryFree={() => {
              setUploadMode("find_look");
              setCurrentMode("find_look");
              setScreen("upload");
            }}
            onLogin={goAuth}
          />
        )}
        {screen === "native-home" && (
          <NativeHome
            isAuthenticated={!!session}
            guestHandle={guestHandle}
            profile={nativeHomeProfile}
            recentAnalyses={recentAnalysesNh}
            unsavedGuestAnalysis={unsavedGuestAnalysis as any}
            unsavedGuestCount={unsavedGuestCount}
            savedLooksCount={savedStyleCount + savedGetTheLookCount}
            wardrobeItemsCount={wardrobeItemsCount}
            lb3WardrobeCount={lb3WardrobeCount}
            onCardBCtaTap={() => {
              if (!session) {
                trackEvent("home_card_b_signup_tapped", {});
                openInlineSignup("home_card_b", () => {
                  setSavedLooksRefreshKey((k) => k + 1);
                });
              } else {
                trackEvent("home_card_b_wardrobe_tapped", {
                  count: lb3WardrobeCount ?? 0,
                });
                setScreen("wardrobe");
              }
            }}
            onPersonalisationUpsellTap={() => {
              trackEvent("home_personalisation_upsell_tapped", {});
              requestAccountFocus("personalisation");
            }}
            onTryDemoTap={() => {
              trackEvent("home_demo_card_tapped", {});
              void handleTrySample("home_demo_card");
            }}
            onSignInClick={() => setScreen("auth")}
            onAddOutfitClick={() => setAddSheetOpen(true)}
            onUploadScreenshotClick={() => { void handleUploadScreenshot(); }}
            onUnsavedPreviewTap={() => {
              if (!unsavedGuestAnalysis) return;
              trackEvent("home_unsaved_preview_tapped", {
                outfit_id: unsavedGuestAnalysis.id,
                lookbook_status: unsavedGuestAnalysis.lookbook_status,
              });
              openInlineSignup(
                "unsaved_preview",
                () => {
                  toast.success("Your look is saved.");
                  setSavedLooksRefreshKey((k) => k + 1);
                },
                { trigger: "unsaved_preview", outfitId: unsavedGuestAnalysis.id },
              );
            }}
            onUnsavedRetry={(outfitId) => {
              trackEvent("home_unsaved_retry_tapped", { outfit_id: outfitId });
              toast.info("Retry coming soon — open the look to try again.");
            }}
            onRecentTap={async (id) => {
              trackEvent("home_recent_row_tapped", { outfit_id: id });
              // GUEST-RECENTS-TAP-COSMETIC-1: anon has no SELECT on outfits;
              // guests must go through the SECURITY DEFINER RPC.
              let row: OutfitRow | null = null;
              if (isGuest) {
                const { data } = await (supabase as any).rpc("get_guest_outfit", { p_id: id });
                const arr = (data as OutfitRow[] | null) ?? [];
                row = arr.length > 0 ? arr[0] : null;
              } else {
                const { data } = await supabase
                  .from("outfits")
                  .select("*")
                  .eq("id", id)
                  .maybeSingle();
                row = (data as OutfitRow | null) ?? null;
              }
              if (row) {
                // LB4-RECENTS-SAVED-ROUTING — wardrobe_combinator rows have
                // no analyse-outfit shape; route directly to the LB4 result
                // overlay instead of viewOutfit.
                const rowAny = row as OutfitRow & {
                  input_method?: string | null;
                  lookbook_image_url?: string | null;
                };
                if (rowAny.input_method === "wardrobe_combinator") {
                  setLb4Detail({
                    outfitId: rowAny.id,
                    imageUrl: rowAny.lookbook_image_url ?? null,
                    backTarget: "native-home",
                  });
                  return;
                }
                // Phase 1a.5-v — recent tap. On hydration failure
                // toast and stay on home; do NOT enter Results.
                const viewResult = await viewOutfit(row);
                if (!viewResult.ok) {
                  toast.error("Couldn't load that outfit.");
                  trackEvent("outfit_load_failed", {
                    outfit_id: id,
                    reason: viewResult.reason,
                    source: "recent_tap",
                  });
                  return;
                }
                setScreen("results");
              }
            }}
            onSavedPillTap={() => {
              trackEvent("home_saved_pill_tapped", {});
              setScreen("saved");
            }}
            shoppingSection={
              <OfflineShoppingToggle
                userId={session?.user?.id ?? null}
                guestSessionId={
                  session?.user?.id
                    ? null
                    : typeof window !== "undefined"
                      ? localStorage.getItem("guest_session_id")
                      : null
                }
              />
            }
          />
        )}
        {screen === "auth" && <Auth />}
        {screen === "onboard" && (
          <GrwmOnboarding
            session={session}
            onComplete={({ typicalBudget, budgetTop, budgetBottom, budgetShoes }) => {
              setTypicalBudget(typicalBudget);
              setUploadBudget(typicalBudget);
              setBudgetTop(budgetTop);
              setBudgetBottom(budgetBottom);
              setBudgetShoes(budgetShoes);
              setScreen("upload");
              setTourStep(1);
            }}
            onAbandon={() => {
              setScreen("upload");
            }}
          />
        )}
        {screen === "upload" && (
          <Upload
            uploadedImg={uploadedImg}
            styleImgs={styleImgs}
            analysing={analysing}
            budget={uploadBudget}
            onChangeBudget={(v) => {
              userSelectedBudgetRef.current = true;
              setUploadBudget(v);
            }}
            onPick={() => fileRef.current?.click()}
            onPickStyle={(files) => addStyleFiles(files)}
            onRemoveStyle={removeStyleAt}
            onAnalyse={() => { void handleAnalyse(); }}
            onClear={clearUploadState}
            onClearStyle={clearStyle}
            mode={uploadMode}
            onChangeMode={(m) => {
              setUploadMode(m);
              if (m === "find_look") setSelectedOccasion(null);
            }}
            selectedOccasion={selectedOccasion}
            onSelectOccasion={setSelectedOccasion}
            isGuest={isGuest}
            userId={session?.user.id ?? null}
            wardrobeItems={wardrobeItems}
            styleTab={styleTab}
            onChangeStyleTab={setStyleTab}
            getTheLookTab={getTheLookTab}
            onChangeGetTheLookTab={setGetTheLookTab}
            savedStyleCount={savedStyleCount}
            savedGetTheLookCount={savedGetTheLookCount}
            showWardrobePicker={showWardrobePicker}
            onShowWardrobePicker={setShowWardrobePicker}
            uploadedWardrobeItem={uploadedWardrobeItem}
            isWardrobeSource={isWardrobeSource}
            onPickWardrobeItem={(item) => {
              setShowWardrobePicker(false);
              if (!item.image_url) {
                toast.error("This item has no image. Upload a photo instead.");
                return;
              }
              setUploadedWardrobeItem(item);
              setIsWardrobeSource(true);
            }}
            onClearWardrobeSource={() => {
              setUploadedWardrobeItem(null);
              setIsWardrobeSource(false);
            }}
            onViewSavedOutfit={(o) => viewOutfit(o as OutfitRow)}
            savedLooksRefreshKey={savedLooksRefreshKey}
          />

        )}
        {screen === "results" && urlPendingHostname && (
          <UrlPendingResults
            hostname={urlPendingHostname}
            stage={urlPendingStage}
            outfitId={currentOutfitId}
            isGuest={!session}
            onBack={() => {
              setUrlPendingHostname(null);
              setUrlPendingStage("fetching");
              clearUploadState();
              setScreen(isNative ? "native-home" : "upload");
            }}
          />
        )}
        {screen === "results" && textPendingOutfitId && (
          <TextPendingResults
            textDescription={textPendingDescription}
            occasion={textPendingOccasion}
            season={textPendingSeason}
            outfitId={textPendingOutfitId}
            isGuest={!session}
            onBack={() => {
              setTextPendingOutfitId(null);
              setTextPendingDescription("");
              setTextPendingOccasion(null);
              setTextPendingSeason(null);
              clearUploadState();
              setScreen(isNative ? "native-home" : "upload");
            }}
          />
        )}
        {screen === "results" && sharePendingOutfitId && (
          <SharePendingResults
            outfitId={sharePendingOutfitId}
            previewDataUrl={sharedPreviewDataUrl}
            isGuest={!session}
            onReady={(row, items) => {
              setSharePendingOutfitId(null);
              setSharedPreviewDataUrl(null);
              void viewOutfit(
                row as unknown as OutfitRow,
                items as unknown as DetectedItem[],
              );
            }}
            onBack={() => {
              setSharePendingOutfitId(null);
              setSharedPreviewDataUrl(null);
              clearUploadState();
              setScreen(isNative ? "native-home" : "upload");
            }}
          />
        )}
        {/* Phase BACKGROUND-ANALYSIS-P1a.5-v/-vi — failed-load surface
            with live retry wiring. Takes precedence over the regular
            Results render branch when an instant-mount viewOutfit
            hydration aborted. */}
        {screen === "results" && loadFailed && (
          <ResultsLoadFailedSlot
            outfitId={currentOutfitId}
            isGuest={!session}
            onBack={() => {
              setLoadFailed(false);
              clearUploadState();
              setScreen(isNative ? "native-home" : "upload");
            }}
            onRetryDispatched={(newId: string) => {
              setLoadFailed(false);
              void (async () => {
                const { data } = await supabase
                  .from("outfits")
                  .select("*")
                  .eq("id", newId)
                  .maybeSingle();
                if (data) await viewOutfit(data as OutfitRow);
              })();
            }}
          />
        )}
        {screen === "results" && !loadFailed && !urlPendingHostname && !textPendingOutfitId && !sharePendingOutfitId && (currentImg || currentInputMethod === "text" || currentInputMethod === "url") && (
          <Fragment key={`${currentOutfitId ?? "no-outfit"}:${resultsRenderKey}`}>
            <Results
              img={currentImg}
              inputMethod={currentInputMethod}
              textDescription={currentTextDescription}
              meta={resultsMeta}
              headerSlot={
                <PendingStripSlot
                  outfitId={currentOutfitId}
                  meta={resultsMeta}
                  expectsLb3={(lb3WardrobeCount ?? 0) > 0}
                  onRetryDispatched={(newId: string) => {
                    void (async () => {
                      const { data } = await supabase
                        .from("outfits")
                        .select("*")
                        .eq("id", newId)
                        .maybeSingle();
                      if (data) await viewOutfit(data as OutfitRow);
                    })();
                  }}
                />
              }
              images={currentImages}
              items={currentItems}
              outfitId={currentOutfitId}
              userId={session?.user.id ?? null}
              userBudget={uploadBudget ?? typicalBudget ?? null}
              onBack={() => { clearUploadState(); setScreen(isNative ? "native-home" : "upload"); }}
              onSave={handleSaveOutfit}
              isGuest={!session}
              onRequestSignup={(trigger) => {
                if (trigger === "lookbook_limit") {
                  openInlineSignup("lookbook_limit", () => {
                    // User is now authenticated; lookbook will retry on next tap.
                  });
                } else if (trigger === "email_capture") {
                  openInlineSignup(
                    "email_capture",
                    () => {
                      void handleSaveOutfit();
                      toast.success("Welcome! Your look has been saved ✨");
                    },
                    { trigger: "save_outfit", outfitId: currentOutfitId },
                  );
                } else {
                  openInlineSignup(
                    "save_outfit",
                    () => { void handleSaveOutfit(); },
                    { trigger: "save_outfit", outfitId: currentOutfitId },
                  );
                }
              }}
              mode={currentMode}
              occasion={currentOccasion}
              styleSummary={currentStyleSummary}
              detectedGender={currentDetectedGender}
              isSaved={currentIsSaved}
              onGoToStyle={() => {
                clearUploadState();
                setUploadMode("style_item");
                setScreen("upload");
              }}
              onLookbookReadyChange={setLookbookReady}
              onCentreButtonChange={handleCentreButtonChange}
              initialLookbookUrl={currentLookbookUrl}
              initialOutfitDirty={currentOutfitDirty}
              onLookbookUrlChange={handleLookbookUrlChange}
              onOutfitDirtyChange={handleOutfitDirtyChange}
              typicalBudget={typicalBudget}
              budgetTop={budgetTop}
              budgetBottom={budgetBottom}
              budgetShoes={budgetShoes}

              onOpenWardrobe={() => setScreen("saved")}
              initialLookbookImageUrls={currentLookbookUrls}
              modelProfile={modelProfile}
              wardrobeItems={wardrobeItems}
              isSample={isSampleMode}
              onSampleExit={() => {
                trackEvent("sample_cta_tapped", {
                  sample_outfit_id: currentOutfitId,
                });
                resetResultsState();
                setScreen(isNative ? "native-home" : "upload");
              }}
              onLb1Success={handleGuestLb1Success}
            />


            <FeedbackWidget
              key={currentOutfitId ?? "no-outfit"}
              outfitId={currentOutfitId}
              analysisMode={currentMode}
              userId={session?.user.id ?? null}
              lookbookReady={lookbookReady}
            />
          </Fragment>
        )}
        {screen === "saved" && (
          <SavedScreen
            userId={session?.user.id ?? null}
            guestSessionId={typeof window !== "undefined" ? localStorage.getItem("guest_session_id") : null}
            guestHandle={guestHandle}
            onViewOutfit={(o: SavedOutfitRow) => {
              // LB4-RECENTS-SAVED-ROUTING — wardrobe_combinator rows mount
              // the LB4 result overlay; back returns to Saved tab.
              if (o.input_method === "wardrobe_combinator") {
                setLb4Detail({
                  outfitId: o.id,
                  imageUrl: o.lookbook_image_url ?? null,
                  backTarget: "saved",
                });
                return;
              }
              void viewOutfit(o as unknown as OutfitRow);
            }}
            onGuestSignup={() =>
              openInlineSignup("saved_tab", () => setScreen("saved"), { trigger: "saved_tab" })
            }
            onGoHome={() => {
              setScreen(isNative ? "native-home" : "upload");
            }}
          />
        )}
        {screen === "wardrobe" && (
          session
            ? <Wardrobe
                userId={session.user.id}
                wardrobeItems={wardrobeItems}
                setWardrobeItems={setWardrobeItems}
                onViewOutfit={(o) => viewOutfit(o as OutfitRow)}
                userProfile={{
                  gender: gender ?? "women",
                  model_for: modelProfile?.model_for ?? null,
                  model_setup_complete: modelProfile?.model_setup_complete ?? null,
                  model_gender: modelProfile?.model_gender ?? null,
                  model_height: modelProfile?.model_height ?? null,
                  model_body_shape: modelProfile?.model_body_shape ?? null,
                  model_body_size: modelProfile?.model_body_size ?? null,
                  model_skin_tone: modelProfile?.model_skin_tone ?? null,
                  model_hair: modelProfile?.model_hair ?? null,
                  model_preferred_variation: modelProfile?.model_preferred_variation ?? null,
                  model_preferred_variation_description: modelProfile?.model_preferred_variation
                    ? (VARIATION_DESCRIPTIONS[modelProfile.model_preferred_variation as VariationKey] ?? null)
                    : null,
                }}
                onGoToShopping={() => {
                  setScreen("upload");
                  setCurrentMode("find_look");
                }}
                lastKnownCity={lastKnownCity}
                todayWeather={todayWeather}
              />
            : <WardrobeGuestEmpty onSignup={() => openInlineSignup("saved_tab", () => setScreen("wardrobe"), { trigger: "saved_tab" })} />
        )}
        {screen === "inspo" && <Inspo gender={gender} />}
        {screen === "account" && (
          session ? (
            <Account
              email={session.user.email ?? ""}
              userId={session.user.id}
              typicalBudget={typicalBudget}
              budgetTop={budgetTop}
              budgetBottom={budgetBottom}
              budgetShoes={budgetShoes}
              onBudgetSaved={(b, perCat) => {
                setTypicalBudget(b);
                setUploadBudget(b);
                if (perCat) {
                  setBudgetTop(perCat.top);
                  setBudgetBottom(perCat.bottom);
                  setBudgetShoes(perCat.shoes);
                }
              }}
              onLogout={handleLogout}
              focusRequest={accountFocus}
              onShowIntroAgain={() => {
                trackEvent("onboarding_v2_show_again", {});
                void onboardingState.reset();
              }}


            />
          ) : (
            <GuestAccount
              handle={guestHandle}
              createdAt={guestCreatedAt}
              outfitCount={guestOutfitCount}
              savedCount={guestSavedCount}
              onSignup={() => openInlineSignup("account_tab", () => setScreen("account"), { trigger: "account_tab" })}
            />)
        )}

        {showSmwIntro && <SmwIntroScreen onDismiss={dismissSmwIntro} />}
        {showWardrobeIntro && <WardrobeIntroScreen onDismiss={dismissWardrobeIntro} />}

        {/* Wave 4: 3-screen value walkthrough — NATIVE ONLY.
            Gated on Capacitor native platform (never renders on
            like2fit.com marketing site). Belt-and-braces: also excludes
            landing/auth/onboard screens. Persists via useOnboardingState. */}
        {isNative && !bootstrapping && onboardingState.shouldShow && screen !== "landing" && screen !== "auth" && screen !== "onboard" && (
          <OnboardingOverlay
            onDone={() => {
              void onboardingState.complete();
            }}
          />
        )}



        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(e) => handleFile(e.target.files?.[0])}
        />

        {!isNative && (authed || isGuest) && screen !== "auth" && screen !== "landing" && screen !== "onboard" && (
          <TabBar
            current={
              screen === "saved"
                ? "saved"
                : screen === "account"
                  ? "account"
                  : screen === "results"
                    ? currentMode === "style_item"
                      ? "style"
                      : "find"
                    : uploadMode === "style_item"
                      ? "style"
                      : "find"
            }
            onChange={handleTabChange}
            centreButton={
              screen === "results" && resultsCentreButton !== null
                ? resultsCentreButton
                : screen === "saved" || screen === "account"
                  ? {
                      state: "disabled" as const,
                      label: "",
                      onTap: () => {},
                      disabled: true,
                    }
                  : {
                      state: "upload" as const,
                      label: "upload",
                      onTap: () => {
                        if (screen === "upload") {
                          fileRef.current?.click();
                          return;
                        }
                        if (uploadMode !== "style_item") {
                          setUploadMode("find_look");
                        }
                        setScreen("upload");
                      },
                      disabled: false,
                    }
            }
          />
        )}

        {tourStep > 0 && session && screen === "upload" && (
          <OnboardingTour
            step={tourStep}
            onNext={() => setTourStep((s) => s + 1)}
            onFinish={async () => {
              setTourStep(0);
              await (supabase as any)
                .from("profiles")
                .update({ seen_upload_tour: true })
                .eq("id", session.user.id);
              if (typeof window !== "undefined") {
                localStorage.removeItem("pending_signup_target");
              }
              const pending = postOnboardingActionRef.current;
              postOnboardingActionRef.current = null;
              if (pending) setTimeout(pending, 50);
            }}
          />
        )}

        <InlineSignupSheet
          isOpen={signupSheet !== null}
          trigger={signupSheet ?? "save_outfit"}
          onClose={() => { setSignupSheet(null); pendingActionRef.current = null; }}
          onSuccess={handleSignupSuccess}
        />

        {isNative && screen !== "onboard" && screen !== "auth" && (
          <>
            <BottomNav
              activeTab={activeTab}
              onTabChange={(tab) => {
                // LB4-RECENTS-SAVED-ROUTING — any tab change unmounts the
                // LB4 detail overlay so the destination tab is visible.
                setLb4Detail(null);
                
                if (tab === "home") setScreen("native-home");
                else if (tab === "saved") setScreen("saved");
                else if (tab === "wardrobe") setScreen("wardrobe");
                else if (tab === "profile") setScreen("account");
              }}
              onAddOutfitTap={() => {
                setLb4Detail(null);
                if (screen === "results") {
                  setScreen("native-home");
                }
                setAddSheetOpen(true);
              }}
              savedBadgeCount={0}
            />
            {lb4Detail && (
              <div
                style={{
                  position: "fixed",
                  top: 0,
                  left: 0,
                  right: 0,
                  height:
                    "calc(100dvh - var(--safe-top, 0px) - var(--safe-bottom, 0px) - 12px - 78px)",
                  zIndex: 50,
                  background: "#FFFCF8",
                }}
              >
                <StyleYourselfResult
                  outfitId={lb4Detail.outfitId}
                  imageUrl={lb4Detail.imageUrl}
                  loading={!lb4Detail.imageUrl}
                  initiallySaved
                  hideTryAnother
                  onBack={() => {
                    const target = lb4Detail.backTarget;
                    setLb4Detail(null);
                    if (target === "saved") {
                      
                      setScreen("saved");
                    } else {
                      
                      setScreen("native-home");
                    }
                  }}
                  onTryAnother={() => { /* unreachable: hideTryAnother */ }}
                  onTweakCombo={() => { /* not rendered from this surface */ }}
                />
              </div>
            )}
            <AddOutfitSheet
              open={addSheetOpen}
              onOpenChange={setAddSheetOpen}
              onUploadScreenshot={() => { void handleUploadScreenshot(); }}
              onPasteUrl={() => setUrlSheetOpen(true)}
              onOpenText={() => setTextSheetOpen(true)}
              onOpenSocial={() => setSocialPlatformSheetOpen(true)}
            />
            <SocialPlatformSheet
              open={socialPlatformSheetOpen}
              onOpenChange={setSocialPlatformSheetOpen}
              onSelectPlatform={(platform) => {
                setSocialSelectedPlatform(platform);
                setSocialPlatformSheetOpen(false);
                setSocialHowToSheetOpen(true);
              }}
            />
            <SocialHowToSheet
              open={socialHowToSheetOpen}
              onOpenChange={setSocialHowToSheetOpen}
              platform={socialSelectedPlatform}
              onTrySample={async () => {
                setSocialHowToSheetOpen(false);
                await handleTrySample("social_flow");
              }}
            />
            <UrlPasteSheet
              open={urlSheetOpen}
              onOpenChange={setUrlSheetOpen}
              userId={session?.user?.id ?? null}
              guestSessionId={typeof window !== "undefined" ? localStorage.getItem("guest_session_id") : null}
              canSubmit={tryStartAnalysis}
              onSubmitted={(outfitId, hostname) => {
                setUrlSheetOpen(false);
                if (typeof document !== "undefined") {
                  document.body.style.pointerEvents = "";
                  document.body.style.removeProperty("overflow");
                  document.body.removeAttribute("data-scroll-locked");
                }
                // IN-APP-VIEWOUTFIT-ALIGN-1: mirror share instant-mount pattern.
                // Do NOT pre-set currentOutfitId / screen before viewOutfit —
                // viewOutfit's internal beginResultsRefresh nulls them, producing
                // value→null→value transitions that fire React #185.
                if (UNIFIED_PENDING) {
                  // IN-APP-SHEET-DELAY-1: defer viewOutfit until after vaul's
                  // ~500ms exit animation completes to avoid Radix composeRefs
                  // race during portal unmount + Results mount.
                  setTimeout(() => {
                    void (async () => {
                      const syntheticRow = {
                        id: outfitId,
                        image_path: null,
                        image_url: null,
                        image_paths: null,
                        analysis_mode: null,
                        occasion: null,
                        style_summary: null,
                        detected_gender: null,
                        input_method: "url",
                        text_description: null,
                        source_url: null,
                        is_saved: false,
                        title: null,
                        lookbook_image_url: null,
                        lookbook_status: "pending",
                        lookbook_lb2_url: null,
                        lookbook_lb3_url: null,
                        lookbook_image_urls: null,
                      } as unknown as OutfitRow;
                      setLookbookReady(false);
                      const viewResult = await viewOutfit(syntheticRow, []);
                      if (!viewResult.ok) {
                        setCurrentOutfitId(outfitId);
                        currentOutfitIdRef.current = outfitId;
                        setLoadFailed(true);
                        setScreen("results");
                        trackEvent("outfit_load_failed", {
                          outfit_id: outfitId,
                          reason: viewResult.reason,
                          source: "in_app_url",
                        });
                      }
                    })();
                  }, 550);
                  return;
                }
                resetResultsState();
                setCurrentOutfitId(outfitId);
                currentOutfitIdRef.current = outfitId;
                setLookbookReady(false);
                setUrlPendingHostname(hostname);
                setScreen("results");
              }}
            />
            <TextDescriptionSheet
              open={textSheetOpen}
              onOpenChange={setTextSheetOpen}
              userId={session?.user?.id ?? null}
              guestSessionId={typeof window !== "undefined" ? localStorage.getItem("guest_session_id") : null}
              canSubmit={tryStartAnalysis}
              onSubmitted={({ outfitId, textDescription, occasion, season }) => {
                setTextSheetOpen(false);
                if (typeof document !== "undefined") {
                  document.body.style.pointerEvents = "";
                  document.body.style.removeProperty("overflow");
                  document.body.removeAttribute("data-scroll-locked");
                }
                // IN-APP-VIEWOUTFIT-ALIGN-1: mirror share instant-mount pattern.
                // See URL handler above for rationale.
                if (UNIFIED_PENDING) {
                  void season; // season is not persisted on OutfitRow; carried via insert in sheet.
                  // IN-APP-SHEET-DELAY-1: defer viewOutfit until after vaul's
                  // ~500ms exit animation completes to avoid Radix composeRefs
                  // race during portal unmount + Results mount.
                  setTimeout(() => {
                    void (async () => {
                      const syntheticRow = {
                        id: outfitId,
                        image_path: null,
                        image_url: null,
                        image_paths: null,
                        analysis_mode: null,
                        occasion,
                        style_summary: null,
                        detected_gender: null,
                        input_method: "text",
                        text_description: textDescription,
                        source_url: null,
                        is_saved: false,
                        title: null,
                        lookbook_image_url: null,
                        lookbook_status: "pending",
                        lookbook_lb2_url: null,
                        lookbook_lb3_url: null,
                        lookbook_image_urls: null,
                      } as unknown as OutfitRow;
                      setLookbookReady(false);
                      const viewResult = await viewOutfit(syntheticRow, []);
                      if (!viewResult.ok) {
                        setCurrentOutfitId(outfitId);
                        currentOutfitIdRef.current = outfitId;
                        setLoadFailed(true);
                        setScreen("results");
                        trackEvent("outfit_load_failed", {
                          outfit_id: outfitId,
                          reason: viewResult.reason,
                          source: "in_app_text",
                        });
                      }
                    })();
                  }, 550);
                  return;
                }
                resetResultsState();
                setCurrentOutfitId(outfitId);
                currentOutfitIdRef.current = outfitId;
                setLookbookReady(false);
                setTextPendingOutfitId(outfitId);
                setTextPendingDescription(textDescription);
                setTextPendingOccasion(occasion);
                setTextPendingSeason(season);
                setScreen("results");
              }}
            />
            {!session && (
              <GuestLimitPaywall
                open={paywallOpen}
                onOpenChange={setPaywallOpen}
                guestHandle={guestHandle}
                searchCount={guestSearchCount}
                onSignupSuccess={() => {
                  setPaywallOpen(false);
                  // Post-auth: claim_guest_outfits + count refresh fires from
                  // the existing onAuthStateChange handler.
                }}
                onEmailSignup={() => {
                  setPaywallOpen(false);
                  openInlineSignup(
                    "lookbook_limit",
                    () => {
                      // user is now authed; nothing else to do.
                    },
                  );
                }}
              />
            )}


          </>
        )}
      </div>
    </div>
  );
}

const LAND_INK = "#0A0A0A";
const LAND_CORAL = "var(--l2f-coral)";
const LAND_TINT = "var(--l2f-cream)";
const LAND_SERIF = "'Instrument Serif', 'Times New Roman', Georgia, serif";
const LAND_SANS = "'Work Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";

function useLandingFonts() {
  useEffect(() => {
    const id = "l2f-landing-fonts";
    if (typeof document === "undefined" || document.getElementById(id)) return;
    const link = document.createElement("link");
    link.id = id;
    link.rel = "stylesheet";
    link.href =
      "https://fonts.googleapis.com/css2?family=Instrument+Serif:ital@0;1&family=Work+Sans:wght@300;400;500;600&display=swap";
    document.head.appendChild(link);
  }, []);
}

function Landing({ onTryFree, onLogin }: { onTryFree: () => void; onLogin: () => void }) {
  useLandingFonts();

  useEffect(() => {
    trackEvent("homepage_viewed", {})
  }, [])

  return (
    <div className="flex-1 flex flex-col bg-background">
      {/* Nav */}
      <nav className="sticky top-0 z-50 bg-background/85 backdrop-blur-md border-b border-border/60 px-5 h-14 flex items-center justify-between">
        <span
          className="text-[18px] leading-none"
          style={{
            fontFamily: "var(--font-display)",
            fontStyle: "italic",
            color: "var(--l2f-ink)",
            letterSpacing: "-0.5px",
          }}
        >
          like2<span style={{ color: "var(--l2f-coral)" }}>fit</span>
        </span>
        <div className="flex gap-2 items-center">
          <button
            onClick={onLogin}
            className="text-[13px] font-medium px-4 py-2 rounded-full border border-border bg-background text-foreground hover:bg-muted transition-colors"
          >
            Sign in
          </button>
          <button
            onClick={onLogin}
            className="text-[13px] font-semibold px-4 py-2 rounded-full text-white shadow-sm"
            style={{ background: "var(--l2f-coral)" }}
          >
            Download
          </button>
        </div>
      </nav>

      {/* Hero */}
      <section className="px-6 pt-12 pb-2 bg-background">
        <p
          className="l2f-section-label"
          style={{ color: "var(--l2f-coral)", marginBottom: 10 }}
        >
          LIKE2FIT · TRY BEFORE YOU BUY
        </p>
        <h1
          className="l2f-screen-title"
          style={{ fontSize: 38, lineHeight: 1.05, marginBottom: 14 }}
        >
          Know if an outfit works for you
          <span style={{ color: "var(--l2f-coral)" }}> before you buy it.</span>
        </h1>

        <p className="text-[15px] leading-[1.6] text-muted-foreground mb-7">
          Share any reel or screenshot and see it on your body,
          at your size and budget.
        </p>

        <div className="flex flex-col gap-2.5 mb-3">
          <button
            onClick={() => {}}
            className="flex items-center justify-center gap-2.5 w-full py-3.5 rounded-2xl text-white text-[15px] font-semibold shadow-sm active:opacity-90 transition-opacity"
            style={{ background: "var(--l2f-ink)" }}
          >
            <i className="ti ti-brand-apple" style={{ fontSize: 20 }} aria-hidden="true" />
            Download on the App Store
          </button>

          <button
            onClick={() => {}}
            className="flex items-center justify-center gap-2.5 w-full py-3.5 rounded-2xl text-foreground text-[15px] font-semibold bg-card border border-border active:opacity-90 transition-opacity"
          >
            <i className="ti ti-brand-google-play" style={{ fontSize: 20 }} aria-hidden="true" />
            Get it on Google Play
          </button>
        </div>

        <p className="text-[12px] text-muted-foreground text-center mt-3 mb-3">
          Free · No measurements required
        </p>

        <p className="text-center mt-3 mb-0">
          <span
            onClick={onTryFree}
            className="text-[14px] font-semibold cursor-pointer inline-flex items-center gap-1"
            style={{ color: "var(--l2f-coral)" }}
          >
            or try it on web
            <i className="ti ti-arrow-right" style={{ fontSize: 14 }} aria-hidden="true" />
          </span>
        </p>
      </section>

      {/* Phone Mockup */}
      <section className="px-6 pt-10 pb-2">
        <div
          className="mx-auto rounded-[40px] p-2.5 shadow-[0_24px_60px_-20px_rgba(10,10,10,0.25)]"
          style={{
            maxWidth: 268,
            background: "linear-gradient(160deg, #E8E4DF 0%, #C9C2BA 100%)",
            border: "1px solid #B9B1A7",
          }}
        >
          <div
            className="rounded-[32px] overflow-hidden bg-white relative"
            style={{ boxShadow: "inset 0 0 0 1px rgba(0,0,0,0.04)" }}
          >
            {/* Notch */}
            <div className="absolute top-1.5 left-1/2 -translate-x-1/2 w-20 h-4 bg-black rounded-full z-10" />

            {/* Status bar */}
            <div className="flex items-center justify-between px-5 pt-2 pb-1 text-[10px] font-semibold text-black">
              <span>9:41</span>
              <span className="flex gap-1 items-center">
                <i className="ti ti-signal-5g" style={{ fontSize: 11 }} aria-hidden="true" />
                <i className="ti ti-battery-3" style={{ fontSize: 12 }} aria-hidden="true" />
              </span>
            </div>

            {/* Dark social feed half */}
            <div className="bg-black px-3.5 pt-3 pb-3.5 flex flex-col justify-between" style={{ minHeight: 200 }}>
              <div className="flex justify-between items-center">
                <div className="flex gap-3 text-white">
                  <span className="text-[11px] font-medium opacity-50">Following</span>
                  <span className="text-[12px] font-bold relative">
                    For You
                    <span className="absolute -bottom-1 left-0 right-0 h-0.5 bg-white rounded-full" />
                  </span>
                </div>
                <i className="ti ti-search text-white" style={{ fontSize: 15 }} aria-hidden="true" />
              </div>

              <div className="flex flex-col items-end gap-3">
                <div className="flex flex-col items-center gap-0.5 text-white">
                  <i className="ti ti-heart-filled" style={{ fontSize: 22, color: "var(--l2f-coral)" }} aria-hidden="true" />
                  <span className="text-[9px] font-semibold">128k</span>
                </div>
                <div className="flex flex-col items-center gap-0.5 text-white">
                  <i className="ti ti-message-circle" style={{ fontSize: 22 }} aria-hidden="true" />
                  <span className="text-[9px] font-semibold">2.4k</span>
                </div>
                <div className="flex flex-col items-center gap-0.5 text-white">
                  <i className="ti ti-share-3" style={{ fontSize: 22 }} aria-hidden="true" />
                  <span className="text-[9px] font-semibold">Share</span>
                </div>
              </div>

              {/* Share sheet */}
              <div
                className="bg-white rounded-2xl p-3 mt-2.5"
                style={{ boxShadow: "0 -8px 24px -10px rgba(0,0,0,0.6)" }}
              >
                <p className="text-[10px] text-muted-foreground text-center font-semibold mb-2.5 tracking-wide">
                  SHARE TO
                </p>
                <div className="flex gap-3 justify-center">
                  {[
                    { label: "Like2Fit", highlight: true },
                    { label: "WhatsApp", highlight: false },
                    { label: "Messages", highlight: false },
                  ].map(app => (
                    <div key={app.label} className="flex flex-col items-center gap-1">
                      <div
                        className="w-10 h-10 rounded-[13px] flex items-center justify-center"
                        style={{
                          background: app.highlight ? "var(--l2f-coral)" : "#F2F0EC",
                          boxShadow: app.highlight ? "0 6px 14px -4px rgba(255,92,92,0.55)" : "none",
                        }}
                      >
                        {app.highlight && (
                          <i className="ti ti-hanger text-white" style={{ fontSize: 18 }} aria-hidden="true" />
                        )}
                      </div>
                      <span
                        className="text-[9px]"
                        style={{
                          color: app.highlight ? "var(--l2f-ink)" : "var(--muted-foreground)",
                          fontWeight: app.highlight ? 700 : 500,
                        }}
                      >
                        {app.label}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Result card half */}
            <div className="bg-white px-4 py-3.5">
              <div className="flex items-start justify-between mb-2">
                <p
                  style={{
                    fontFamily: "var(--font-display)",
                    fontStyle: "italic",
                    fontSize: 16,
                    lineHeight: 1.1,
                    color: "var(--l2f-ink)",
                  }}
                >
                  Brown leather jacket
                </p>
                <i
                  className="ti ti-bookmark"
                  style={{ fontSize: 14, color: "var(--muted-foreground)" }}
                  aria-hidden="true"
                />
              </div>

              <div
                className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 mb-2"
                style={{ background: "var(--l2f-cream)" }}
              >
                <i className="ti ti-info-circle" style={{ fontSize: 11, color: "var(--l2f-danger-ink)" }} aria-hidden="true" />
                <span className="text-[10px] font-semibold" style={{ color: "var(--l2f-danger-ink)" }}>
                  Fit: Medium confidence
                </span>
              </div>

              <p className="text-[9px] text-muted-foreground leading-snug mb-2.5">
                Based on: height · body shape · brand data
              </p>

              <div className="flex flex-col gap-1.5">
                {[
                  { icon: "ti-check", color: "#0F6E56", bg: "var(--l2f-success-bg)", text: "Similar to ASOS XL — fits you" },
                  { icon: "ti-check", color: "#0F6E56", bg: "var(--l2f-success-bg)", text: "Works with white trainers" },
                  { icon: "ti-alert-triangle", color: "#BA7517", bg: "#FAEFD9", text: "Slim fit — consider sizing up" },
                ].map((row, i) => (
                  <div key={i} className="flex items-start gap-1.5">
                    <span
                      className="rounded-full flex items-center justify-center shrink-0 mt-px"
                      style={{ width: 14, height: 14, background: row.bg }}
                    >
                      <i className={`ti ${row.icon}`} style={{ fontSize: 9, color: row.color }} aria-hidden="true" />
                    </span>
                    <span className="text-[10px] leading-snug" style={{ color: "var(--l2f-ink)" }}>
                      {row.text}
                    </span>
                  </div>
                ))}
              </div>

              <div className="mt-2.5 pt-2 border-t border-border/70 flex flex-wrap gap-x-2 gap-y-1">
                {["On your body", "Size available", "Within your budget"].map((line, i) => (
                  <div key={i} className="flex items-center gap-1">
                    <i className="ti ti-check" style={{ fontSize: 9, color: "#0F6E56" }} aria-hidden="true" />
                    <span className="text-[9px] font-medium text-muted-foreground">{line}</span>
                  </div>
                ))}
              </div>

              <div
                className="mt-2.5 rounded-xl flex items-center justify-center relative overflow-hidden"
                style={{
                  height: 78,
                  background: "linear-gradient(135deg, #F4EFE6 0%, #E8DFD0 100%)",
                }}
              >
                <i className="ti ti-user" style={{ fontSize: 28, color: "rgba(10,10,10,0.35)" }} aria-hidden="true" />
                <div
                  className="absolute bottom-1.5 right-1.5 rounded-md px-2 py-0.5 flex items-center gap-1"
                  style={{ background: "rgba(10,10,10,0.7)", backdropFilter: "blur(4px)" }}
                >
                  <span className="w-1 h-1 rounded-full bg-emerald-400" />
                  <span className="text-[8px] text-white font-semibold tracking-wide">
                    ON YOUR BODY
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <p className="text-[12px] text-muted-foreground text-center mt-4">
          From reel to result in under a minute
        </p>
      </section>

      {/* Problem */}
      <section className="px-6 pt-12 pb-4 bg-background">
        <p
          className="l2f-section-label"
          style={{ color: "var(--l2f-coral)", marginBottom: 8 }}
        >
          THE PROBLEM
        </p>
        <h2 className="l2f-screen-title" style={{ fontSize: 28, lineHeight: 1.1, marginBottom: 10 }}>
          Stop guessing before you buy
        </h2>

        <p className="text-[15px] leading-[1.6] text-muted-foreground mb-6">
          Every online purchase is a guess. Like2Fit ends the guessing.
        </p>

        <div className="rounded-3xl overflow-hidden border border-border bg-card shadow-sm">
          <div className="p-5">
            <p
              className="l2f-section-label"
              style={{ color: "var(--muted-foreground)", marginBottom: 8 }}
            >
              BEFORE
            </p>
            <p
              className="text-[15px] leading-[1.55] text-muted-foreground"
              style={{ fontFamily: "var(--font-display)", fontStyle: "italic" }}
            >
              "I love this outfit but I have no idea if it would suit me
              or where to buy it."
            </p>
          </div>

          <div className="flex items-center justify-center py-2 border-t border-b border-border bg-muted/30">
            <span
              className="w-7 h-7 rounded-full flex items-center justify-center"
              style={{ background: "var(--l2f-coral)" }}
            >
              <i className="ti ti-arrow-down text-white" style={{ fontSize: 14 }} aria-hidden="true" />
            </span>
          </div>

          <div className="p-5" style={{ background: "var(--l2f-cream)" }}>
            <p
              className="l2f-section-label"
              style={{ color: "var(--l2f-danger-ink-soft)", marginBottom: 8 }}
            >
              AFTER
            </p>
            <p
              className="text-[15px] leading-[1.55]"
              style={{
                fontFamily: "var(--font-display)",
                fontStyle: "italic",
                color: "var(--l2f-danger-ink)",
              }}
            >
              "Shared the reel, saw it on my body, bought just the jacket.
              Already owned the rest."
            </p>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="px-6 pt-12 pb-4 bg-background">
        <p
          className="l2f-section-label"
          style={{ color: "var(--l2f-coral)", marginBottom: 8 }}
        >
          HOW IT WORKS
        </p>
        <h2 className="l2f-screen-title" style={{ fontSize: 28, lineHeight: 1.1, marginBottom: 10 }}>
          Three taps to a real answer
        </h2>
        <p className="text-[15px] leading-[1.6] text-muted-foreground mb-6">
          Share a reel or upload a screenshot. Get your answer in seconds.
        </p>

        <div className="flex flex-col gap-3">
          {[
            {
              n: "1",
              title: "Share any outfit",
              body: "See something you love on TikTok, Instagram or Pinterest. Tap share and select Like2Fit.",
              icon: "ti-share-3",
            },
            {
              n: "2",
              title: "See it on your body",
              body: "Your height, shape and proportions. See it on your body and discover different ways to wear it.",
              icon: "ti-user-scan",
            },
            {
              n: "3",
              title: "Buy confidently",
              body: "Every item at your size and budget. See what you already own. Only buy what you need.",
              icon: "ti-shopping-bag-check",
            },
          ].map(step => (
            <div
              key={step.n}
              className="rounded-2xl bg-card border border-border p-4 flex items-start gap-4 shadow-sm"
            >
              <div className="relative shrink-0">
                <div
                  className="w-11 h-11 rounded-2xl flex items-center justify-center"
                  style={{ background: "var(--l2f-cream)" }}
                >
                  <i className={`ti ${step.icon}`} style={{ fontSize: 20, color: "var(--l2f-danger-ink-soft)" }} aria-hidden="true" />
                </div>
                <span
                  className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full flex items-center justify-center text-white text-[10px] font-bold"
                  style={{ background: "var(--l2f-coral)" }}
                >
                  {step.n}
                </span>
              </div>
              <div className="min-w-0 pt-0.5">
                <p
                  style={{
                    fontFamily: "var(--font-display)",
                    fontStyle: "italic",
                    fontSize: 19,
                    lineHeight: 1.15,
                    color: "var(--l2f-ink)",
                  }}
                >
                  {step.title}
                </p>
                <p className="text-[13px] leading-[1.55] text-muted-foreground mt-1">
                  {step.body}
                </p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Social Proof */}
      <section className="px-6 pt-12 pb-4 bg-background">
        <p
          className="l2f-section-label"
          style={{ color: "var(--l2f-coral)", marginBottom: 8 }}
        >
          WHAT PEOPLE SAY
        </p>
        <h2 className="l2f-screen-title" style={{ fontSize: 28, lineHeight: 1.1, marginBottom: 10 }}>
          From people who stopped guessing
        </h2>
        <p className="text-[15px] leading-[1.6] text-muted-foreground mb-6">
          Real reviews from the first Like2Fit users.
        </p>

        <div className="flex flex-col gap-3">
          {[
            {
              quote: "Shared a TikTok reel and saw exactly how the outfit would look on me. Bought just the jacket. Saved me from spending £200 on pieces I already owned.",
              name: "Maya",
              detail: "London · saved £200",
            },
            {
              quote: "Finally stopped buying things that look nothing like they did on the model. Now I check before I buy anything.",
              name: "Priya",
              detail: "Manchester · daily user",
            },
          ].map((t, i) => (
            <div
              key={i}
              className="rounded-3xl bg-card border border-border p-5 shadow-sm"
            >
              <div className="flex items-center gap-1 mb-3" style={{ color: "var(--l2f-coral)" }}>
                {[0, 1, 2, 3, 4].map(s => (
                  <i key={s} className="ti ti-star-filled" style={{ fontSize: 13 }} aria-hidden="true" />
                ))}
              </div>
              <p
                className="text-[15px] leading-[1.55] text-foreground"
                style={{ fontFamily: "var(--font-display)", fontStyle: "italic" }}
              >
                "{t.quote}"
              </p>
              <div className="mt-4 pt-4 border-t border-border/70 flex items-center gap-3">
                <div
                  className="w-9 h-9 rounded-full flex items-center justify-center text-white text-[13px] font-bold"
                  style={{ background: "var(--l2f-coral)" }}
                >
                  {t.name[0]}
                </div>
                <div className="min-w-0">
                  <p className="text-[13px] font-semibold text-foreground leading-tight">{t.name}</p>
                  <p className="text-[11px] text-muted-foreground mt-0.5">{t.detail}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Trust */}
      <section className="px-6 pt-12 pb-4 bg-background">
        <p
          className="l2f-section-label"
          style={{ color: "var(--l2f-coral)", marginBottom: 8 }}
        >
          PRIVACY
        </p>
        <h2 className="l2f-screen-title" style={{ fontSize: 28, lineHeight: 1.1, marginBottom: 18 }}>
          Your data stays private
        </h2>

        <div className="rounded-3xl bg-card border border-border shadow-sm overflow-hidden">
          {[
            { text: "Your profile belongs to you", icon: "ti-user-shield" },
            { text: "Photos and videos are never shared or sold", icon: "ti-lock" },
            { text: "Delete your account anytime", icon: "ti-trash" },
          ].map((row, i, arr) => (
            <div
              key={i}
              className={`flex items-center gap-3 px-5 py-4 ${i < arr.length - 1 ? "border-b border-border/70" : ""}`}
            >
              <div
                className="w-10 h-10 rounded-2xl flex items-center justify-center shrink-0"
                style={{ background: "var(--l2f-success-bg)" }}
              >
                <i className={`ti ${row.icon}`} style={{ fontSize: 18, color: "#0F6E56" }} aria-hidden="true" />
              </div>
              <span className="text-[14px] leading-snug text-foreground">{row.text}</span>
              <i
                className="ti ti-check ml-auto shrink-0"
                style={{ fontSize: 16, color: "#0F6E56" }}
                aria-hidden="true"
              />
            </div>
          ))}
        </div>
      </section>

      {/* Final CTA + Footer */}
      <section className="px-6 pt-12 pb-10 bg-background mt-2">
        <div
          className="rounded-[28px] p-7 relative overflow-hidden"
          style={{
            background: "linear-gradient(160deg, var(--l2f-cream) 0%, #FFE8DE 100%)",
            border: "1px solid #F4D6C8",
          }}
        >
          <div
            className="absolute -top-10 -right-10 w-40 h-40 rounded-full opacity-50"
            style={{ background: "radial-gradient(circle, rgba(255,92,92,0.35) 0%, transparent 70%)" }}
          />
          <p
            className="l2f-section-label relative"
            style={{ color: "var(--l2f-coral)", marginBottom: 8 }}
          >
            START FREE
          </p>
          <h2
            className="l2f-screen-title relative"
            style={{ fontSize: 30, lineHeight: 1.05, marginBottom: 10 }}
          >
            See yourself in it first.
          </h2>
          <p className="text-[15px] leading-[1.55] mb-6 relative" style={{ color: "#5A2E1B" }}>
            Download Like2Fit and stop guessing before you buy.
          </p>

          <div className="flex flex-col gap-2.5 mb-3 relative">
            <button
              onClick={() => {}}
              className="flex items-center justify-center gap-2.5 w-full py-3.5 rounded-2xl text-white text-[15px] font-semibold shadow-md active:opacity-90 transition-opacity"
              style={{ background: "var(--l2f-ink)" }}
            >
              <i className="ti ti-brand-apple" style={{ fontSize: 20 }} aria-hidden="true" />
              Download on the App Store
            </button>

            <button
              onClick={() => {}}
              className="flex items-center justify-center gap-2.5 w-full py-3.5 rounded-2xl text-foreground text-[15px] font-semibold bg-background border border-border active:opacity-90 transition-opacity"
            >
              <i className="ti ti-brand-google-play" style={{ fontSize: 20 }} aria-hidden="true" />
              Get it on Google Play
            </button>
          </div>

          <p className="text-[12px] text-center mt-2 relative" style={{ color: "#7A4530" }}>
            Free · No measurements required
          </p>
        </div>

        <div className="mt-6 flex items-center gap-3">
          <div className="flex-1 h-px bg-border" />
          <span className="text-[11px] uppercase tracking-wider text-muted-foreground font-medium">
            or web access
          </span>
          <div className="flex-1 h-px bg-border" />
        </div>

        <div className="mt-5 flex flex-col gap-2.5">
          <input
            type="email"
            placeholder="Or enter your email for web access"
            onChange={() => trackEvent("email_field_focused", {})}
            className="w-full px-4 py-3.5 text-[15px] rounded-2xl border border-border bg-card text-foreground placeholder:text-muted-foreground outline-none focus:border-[var(--l2f-coral)] transition-colors"
          />
          <button
            onClick={e => {
              const input = e.currentTarget.previousElementSibling as HTMLInputElement | null
              const email = input?.value
              if (email) {
                trackEvent("email_captured", { source: "homepage_cta" })
                onTryFree()
              }
            }}
            className="w-full py-3.5 rounded-2xl text-white text-[15px] font-semibold shadow-sm active:opacity-90 transition-opacity"
            style={{ background: "var(--l2f-coral)" }}
          >
            Get started free
          </button>
          <p className="text-[12px] text-muted-foreground text-center mt-1">
            No credit card required
          </p>
        </div>

        <footer className="mt-10 pt-6 border-t border-border flex justify-between items-center">
          <span
            style={{
              fontFamily: "var(--font-display)",
              fontStyle: "italic",
              fontSize: 18,
              color: "var(--l2f-ink)",
            }}
          >
            like2<span style={{ color: "var(--l2f-coral)" }}>fit</span>
          </span>
          <div className="flex gap-5">
            {([
              { label: "Privacy", to: "/privacy" },
              { label: "Terms", to: "/terms" },
              { label: "Retailers", to: "/" },
            ] as const).map(link => (
              <Link
                key={link.label}
                to={link.to}
                className="text-[12px] text-muted-foreground hover:text-foreground transition-colors"
              >
                {link.label}
              </Link>
            ))}
          </div>
        </footer>
      </section>
    </div>
  );
}

function Auth() {
  const [mode, setMode] = useState<"signup" | "login">("signup");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      if (mode === "signup") {
        const { data, error } = await supabase.auth.signUp({
          email,
          password,
        });
        if (error) throw error;
        if (!data.session) {
          throw new Error("Account created but session was not established. Please log in.");
        }
      } else {
        const { error } = await supabase.auth.signInWithPassword({ email, password });
        if (error) throw error;
      }
    } catch (err: any) {
      setError(err.message ?? "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  const googleSignIn = async () => {
    setError(null);
    const result = await signInWithGoogle();
    if (result.error) setError(result.error.message ?? "Google sign-in failed");
  };

  const appleSignIn = async () => {
    setError(null);
    const result = await signInWithApple();
    if (result.error) setError(result.error.message ?? "Apple sign-in failed");
  };

  return (
    <div className="flex-1 flex flex-col px-6 pt-16 pb-10">
      <div
        className="mb-10"
        style={{
          fontFamily: "var(--font-display)",
          fontStyle: "italic",
          fontWeight: 400,
          fontSize: 22,
          letterSpacing: "-0.5px",
          color: "var(--l2f-ink)",
        }}
      >
        Like2<span style={{ color: "var(--l2f-coral)" }}>Fit</span>
      </div>

      <p
        className="l2f-section-label"
        style={{ color: "var(--l2f-coral)", marginBottom: 8 }}
      >
        {mode === "signup" ? "LIKE2FIT · THE INVITATION" : "LIKE2FIT · WELCOME HOME"}
      </p>
      <h2 className="l2f-screen-title" style={{ fontSize: 36 }}>
        {mode === "signup" ? "Create your account" : "Welcome back"}
      </h2>
      <p className="mt-2 text-muted-foreground">
        {mode === "signup" ? "Start building your wardrobe." : "Log in to continue."}
      </p>

      <div className="mt-8 p-1 bg-secondary rounded-full flex">
        {(["signup", "login"] as const).map((m) => (
          <button
            key={m}
            onClick={() => setMode(m)}
            className={`flex-1 py-2.5 rounded-full text-sm font-medium transition ${
              mode === m ? "bg-background shadow-sm" : "text-muted-foreground"
            }`}
          >
            {m === "signup" ? "Sign Up" : "Log In"}
          </button>
        ))}
      </div>

      <button
        type="button"
        onClick={googleSignIn}
        className="mt-8 w-full py-4 rounded-full font-medium bg-white text-foreground relative flex items-center justify-center"
        style={{ border: "1px solid #E5E7EB", boxShadow: "0 1px 3px rgba(0,0,0,0.1)", fontWeight: 500 }}
      >
        <svg className="absolute left-5" width="20" height="20" viewBox="0 0 48 48" aria-hidden="true">
          <path fill="#FFC107" d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z"/>
          <path fill="#FF3D00" d="M6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 16.318 4 9.656 8.337 6.306 14.691z"/>
          <path fill="#4CAF50" d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238C29.211 35.091 26.715 36 24 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z"/>
          <path fill="#1976D2" d="M43.611 20.083H42V20H24v8h11.303c-.792 2.237-2.231 4.166-4.087 5.571.001-.001.002-.001.003-.002l6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z"/>
        </svg>
        Continue with Google
      </button>

      {!isAndroid() && (
        <button
          type="button"
          onClick={appleSignIn}
          className="mt-3 w-full py-4 rounded-full font-medium bg-black text-white relative flex items-center justify-center"
          style={{ fontWeight: 500 }}
        >
          <svg className="absolute left-5" width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <path d="M17.05 12.04c-.02-1.94 1.59-2.87 1.66-2.92-.91-1.33-2.32-1.51-2.82-1.53-1.2-.12-2.34.71-2.95.71-.61 0-1.55-.69-2.55-.67-1.31.02-2.52.76-3.2 1.94-1.36 2.36-.35 5.86 1 7.79.66.95 1.45 2.01 2.49 1.97 1-.04 1.38-.65 2.6-.65s1.56.65 2.62.63c1.08-.02 1.77-.96 2.43-1.92.76-1.1 1.08-2.17 1.1-2.22-.02-.01-2.11-.81-2.13-3.21zM15.13 6.07c.54-.66.91-1.57.81-2.49-.78.03-1.74.52-2.3 1.18-.5.58-.94 1.52-.82 2.41.88.07 1.77-.45 2.31-1.1z" />
          </svg>
          Continue with Apple
        </button>
      )}

      <div className="my-5 flex items-center gap-3 text-xs text-muted-foreground">
        <div className="flex-1 h-px bg-border" /> or <div className="flex-1 h-px bg-border" />
      </div>

      <form onSubmit={submit} className="space-y-4">
        <div>
          <label className="text-sm font-medium">Email</label>
          <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com"
            className="mt-1.5 w-full px-4 py-3.5 rounded-2xl bg-secondary border border-transparent focus:outline-none focus:border-foreground" />
        </div>
        <div>
          <label className="text-sm font-medium">Password</label>
          <input type="password" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••"
            className="mt-1.5 w-full px-4 py-3.5 rounded-2xl bg-secondary border border-transparent focus:outline-none focus:border-foreground" />
        </div>

        {error && <p className="text-sm" style={{ color: CORAL }}>{error}</p>}

        <button type="submit" disabled={loading}
          className="w-full py-3.5 rounded-full font-medium text-white shadow-lg shadow-[color:var(--l2f-coral)]/30 mt-4 disabled:opacity-60"
          style={{ background: CORAL }}>
          {loading ? "Please wait…" : mode === "signup" ? "Sign Up" : "Log In"}
        </button>
        {mode === "signup" && (
          <p className="text-[12px] text-center text-muted-foreground leading-snug pt-1">
            By creating an account, you agree to our{" "}
            <a href="/terms" target="_blank" rel="noopener noreferrer" className="underline" style={{ color: CORAL }}>
              Terms of Service
            </a>{" "}
            and{" "}
            <a href="/privacy" target="_blank" rel="noopener noreferrer" className="underline" style={{ color: CORAL }}>
              Privacy Policy
            </a>
            .
          </p>
        )}
      </form>
    </div>
  );
}

const FIND_STEPS = [
  { at: 0, emoji: "📸", text: "Reading your outfit...", sub: "Analysing colours, fabrics and styles" },
  { at: 4000, emoji: "✨", text: "Identifying every piece...", sub: "Breaking down each item in the look" },
  { at: 9000, emoji: "🔍", text: "Searching hundreds of retailers...", sub: "Finding similar pieces at your budget" },
  { at: 16000, emoji: "🛍️", text: "Almost ready...", sub: "Putting together your personalised results" },
];
const STYLE_STEPS = [
  { at: 0, emoji: "👗", text: "Checking your items...", sub: "Analysing what you already own" },
  { at: 4000, emoji: "✨", text: "Finding the perfect pairings...", sub: "Matching styles for your occasion" },
  { at: 9000, emoji: "🔍", text: "Searching hundreds of retailers...", sub: "Finding pieces that complement your look" },
  { at: 16000, emoji: "🛍️", text: "Almost ready...", sub: "Putting together your personalised suggestions" },
];

function AnalysisLoadingOverlay({ mode }: { mode: AnalysisMode }) {
  const steps = mode === "style_item" ? STYLE_STEPS : FIND_STEPS;
  const [stepIdx, setStepIdx] = useState(0);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const start = Date.now();
    const timers = steps.slice(1).map((s, i) =>
      setTimeout(() => setStepIdx(i + 1), s.at)
    );
    // animate progress 0 -> 90% over 20s
    setTimeout(() => setProgress(90), 50);
    return () => { timers.forEach(clearTimeout); void start; };
  }, []);

  const step = steps[stepIdx];
  const textShadow = "0px 1px 4px rgba(0,0,0,0.8)";

  return (
    <div
      className="absolute inset-0 flex flex-col items-center justify-center rounded-3xl"
      style={{ background: "rgba(0,0,0,0.65)" }}
    >
      <div
        className="rounded-full overflow-hidden"
        style={{
          width: "60%",
          height: 4,
          background: "rgba(255,255,255,0.3)",
          boxShadow: "0px 1px 3px rgba(0,0,0,0.5)",
        }}
      >
        <div
          style={{
            width: `${progress}%`,
            height: "100%",
            background: "var(--l2f-coral)",
            transition: "width 20s linear",
          }}
        />
      </div>

      <div
        key={stepIdx}
        className="mt-4 flex flex-col items-center text-center px-6 animate-[fade-in_0.5s_ease]"
      >
        <div
          style={{
            fontSize: 28,
            lineHeight: 1,
            filter: "drop-shadow(0px 1px 3px rgba(0,0,0,0.6))",
          }}
        >
          {step.emoji}
        </div>
        <p
          className="mt-2 font-medium text-white"
          style={{ fontSize: 16, textShadow }}
        >
          {step.text}
        </p>
        <p
          className="mt-1 text-white"
          style={{ fontSize: 13, opacity: 0.8, textShadow }}
        >
          {step.sub}
        </p>
      </div>

      <p
        className="mt-4 text-white text-center"
        style={{ fontSize: 12, opacity: 0.6, textShadow }}
      >
        This usually takes 15-25 seconds
      </p>
    </div>
  );
}



function Upload({
  uploadedImg, styleImgs, analysing, budget, onChangeBudget,
  onPick, onPickStyle, onRemoveStyle, onAnalyse, onClear, onClearStyle,
  mode, onChangeMode, selectedOccasion, onSelectOccasion,
  
  isGuest, userId, wardrobeItems,
  styleTab, onChangeStyleTab, getTheLookTab, onChangeGetTheLookTab,
  savedStyleCount, savedGetTheLookCount,
  showWardrobePicker, onShowWardrobePicker,
  uploadedWardrobeItem, isWardrobeSource,
  onPickWardrobeItem, onClearWardrobeSource,
  onViewSavedOutfit, savedLooksRefreshKey,
}: {
  uploadedImg: string | null;
  styleImgs: string[];
  analysing: boolean;
  budget: number | null;
  onChangeBudget: (b: number) => void;
  onPick: () => void;
  onPickStyle: (files: FileList) => void;
  onRemoveStyle: (idx: number) => void;
  onAnalyse: () => void;
  onClear: () => void;
  onClearStyle: () => void;
  mode: AnalysisMode;
  onChangeMode: (m: AnalysisMode) => void;
  selectedOccasion: string | null;
  onSelectOccasion: (o: string) => void;
  isGuest: boolean;
  userId: string | null;
  wardrobeItems: WardrobeItem[];
  styleTab: "new_look" | "saved";
  onChangeStyleTab: (t: "new_look" | "saved") => void;
  getTheLookTab: "upload" | "saved";
  onChangeGetTheLookTab: (t: "upload" | "saved") => void;
  savedStyleCount: number;
  savedGetTheLookCount: number;
  showWardrobePicker: boolean;
  onShowWardrobePicker: (v: boolean) => void;
  uploadedWardrobeItem: WardrobeItem | null;
  isWardrobeSource: boolean;
  onPickWardrobeItem: (item: WardrobeItem) => void;
  onClearWardrobeSource: () => void;
  onViewSavedOutfit: (o: SavedLookRow) => void;
  savedLooksRefreshKey: number;
}) {

  const styleInputRef = useRef<HTMLInputElement>(null);
  const isStyle = mode === "style_item";
  const MAX_STYLE = 5;
  const hasStyleImages = styleImgs.length > 0;
  const canAnalyse = !analysing && (isStyle
    ? hasStyleImages && !!selectedOccasion
    : !!uploadedImg);

  const openStylePicker = () => styleInputRef.current?.click();

  const findCards = [
    { Icon: Music2, label: "TikTok video" },
    { Icon: Instagram, label: "Instagram post" },
    { Icon: ImageIcon, label: "Any screenshot" },
  ];
  const styleCards = [
    { Icon: Shirt, label: "On a hanger" },
    { Icon: LayoutGrid, label: "Flat lay" },
    { Icon: Smartphone, label: "Product photo" },
  ];
  const cards = isStyle ? styleCards : findCards;
  const tipText = isStyle
    ? "1–5 items max. One item per photo works best"
    : "Best results: full body shot showing the complete outfit";
  const atMax = isStyle && styleImgs.length >= MAX_STYLE;

  const activeTab: "primary" | "saved" = isStyle
    ? (styleTab === "saved" ? "saved" : "primary")
    : (getTheLookTab === "saved" ? "saved" : "primary");
  const savedCount = isStyle ? savedStyleCount : savedGetTheLookCount;
  const primaryLabel = isStyle ? "New look" : "Upload";

  return (
    <div className="flex-1 flex flex-col px-6 pt-12">
      <p
        className="l2f-section-label"
        style={{ color: "var(--l2f-coral)", marginBottom: 6 }}
      >
        {isStyle ? "LIKE2FIT · THE STYLIST" : "LIKE2FIT · THE LOOK"}
      </p>
      <h2 className="l2f-screen-title">
        {isStyle ? "Style my wardrobe" : "Get the look"}
      </h2>
      <p className="text-muted-foreground mt-1" style={{ fontSize: 15 }}>
        {isStyle
          ? "Upload items you own — we'll build the perfect outfit around them"
          : "Upload any outfit and we'll find similar items at your budget"}
      </p>

      {!isGuest && userId && (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 4,
            background: "hsl(var(--secondary))",
            borderRadius: 10,
            padding: 3,
            marginTop: 12,
            marginBottom: 4,
          }}
        >
          <button
            onClick={() => isStyle ? onChangeStyleTab("new_look") : onChangeGetTheLookTab("upload")}
            style={{
              background: activeTab === "primary" ? "var(--l2f-coral)" : "transparent",
              color: activeTab === "primary" ? "white" : "hsl(var(--muted-foreground))",
              border: "none", borderRadius: 8, padding: "7px 0",
              fontSize: 12, fontWeight: 500, cursor: "pointer",
            }}
          >
            {primaryLabel}
          </button>
          <button
            onClick={() => isStyle ? onChangeStyleTab("saved") : onChangeGetTheLookTab("saved")}
            style={{
              background: activeTab === "saved" ? "hsl(var(--background))" : "transparent",
              color: activeTab === "saved" ? "hsl(var(--foreground))" : "hsl(var(--muted-foreground))",
              border: "none", borderRadius: 8, padding: "7px 0",
              fontSize: 12, fontWeight: 500, cursor: "pointer",
            }}
          >
            {savedCount > 0 ? `Saved · ${savedCount}` : "Saved"}
          </button>
        </div>
      )}

      {activeTab === "saved" && userId ? (
        <div className="mt-4">
          <SavedLooksGrid
            userId={userId}
            analysisMode={isStyle ? "style_item" : "find_look"}
            onViewOutfit={onViewSavedOutfit}
            onGetNewLook={() => isStyle ? onChangeStyleTab("new_look") : onChangeGetTheLookTab("upload")}
            refreshKey={savedLooksRefreshKey}
          />
        </div>
      ) : (<>

      {isStyle && isWardrobeSource && uploadedWardrobeItem && (
        <div style={{
          background: "hsl(var(--secondary))", borderRadius: 12, padding: 10,
          border: "0.5px solid hsl(var(--border))", display: "flex",
          alignItems: "center", gap: 10, marginTop: 12, marginBottom: 4,
        }}>
          <div style={{
            width: 52, height: 60, borderRadius: 10, flexShrink: 0,
            background: uploadedWardrobeItem.color_hex ?? "hsl(var(--muted))",
            backgroundImage: uploadedWardrobeItem.image_url ? `url(${uploadedWardrobeItem.image_url})` : undefined,
            backgroundSize: "cover", backgroundPosition: "center",
          }} />
          <div style={{ flex: 1 }}>
            <p style={{ fontSize: 12, fontWeight: 500, margin: "0 0 2px" }}>{uploadedWardrobeItem.name}</p>
            <p className="text-muted-foreground" style={{ fontSize: 12, margin: 0, textTransform: "capitalize" }}>
              {uploadedWardrobeItem.category} · from your wardrobe
            </p>
          </div>
          <button onClick={onClearWardrobeSource} style={{
            background: "transparent", border: "none", fontSize: 12,
            color: "hsl(var(--muted-foreground))", cursor: "pointer", padding: 4,
          }}>✕</button>
        </div>
      )}


      {isStyle ? (
        <div className="mt-6">
          {!hasStyleImages ? (
            <button
              onClick={openStylePicker}
              disabled={analysing}
              className="w-full aspect-[4/5] rounded-3xl border-2 border-dashed border-neutral-300 bg-secondary/60 flex flex-col items-center justify-center gap-3 hover:border-foreground transition px-6"
            >
              <div className="w-14 h-14 rounded-2xl bg-background flex items-center justify-center shadow-sm">
                <Camera size={40} className="text-muted-foreground" />
              </div>
              <div className="text-center">
                <p className="text-foreground" style={{ fontSize: 16, fontWeight: 500 }}>
                  Upload 1–5 clothing items
                </p>
                <p className="text-foreground" style={{ fontSize: 13, marginTop: 4 }}>
                  Start with your hero piece — the item to build around
                </p>
              </div>
              <span
                className="mt-2 inline-flex items-center gap-1 rounded-full text-white"
                style={{ background: CORAL, fontSize: 14, fontWeight: 500, padding: "10px 20px" }}
              >
                + Add photos
              </span>
            </button>
          ) : analysing ? (
            <div className="relative w-full aspect-[4/5] rounded-3xl overflow-hidden bg-secondary">
              <img
                src={styleImgs[0]}
                alt="Uploaded item"
                className="absolute inset-0 w-full h-full object-cover"
              />
              <AnalysisLoadingOverlay mode="style_item" />
            </div>
          ) : (
            <div>
              <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1">
                {styleImgs.map((src, idx) => (
                  <div key={idx} className="relative shrink-0">
                    <img
                      src={src}
                      alt={`Item ${idx + 1}`}
                      className="w-20 h-20 rounded-lg object-cover bg-secondary"
                    />
                    <button
                      onClick={() => onRemoveStyle(idx)}
                      aria-label="Remove image"
                      className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-white shadow flex items-center justify-center text-neutral-500 text-xs font-medium border border-neutral-200"
                    >×</button>
                  </div>
                ))}
                {styleImgs.length < MAX_STYLE && (
                  <button
                    onClick={openStylePicker}
                    disabled={atMax}
                    className="shrink-0 w-20 h-20 rounded-lg border-2 border-dashed border-neutral-300 flex flex-col items-center justify-center text-neutral-500 disabled:opacity-40"
                  >
                    <span className="text-xl leading-none">+</span>
                    <span className="text-[12px] mt-0.5 font-medium">Add item</span>
                  </button>
                )}
              </div>
              <div className="mt-2 flex items-center justify-between gap-2">
                <p
                  className="flex-1 text-center"
                  style={{ fontSize: 13, color: atMax ? "var(--l2f-coral)" : undefined }}
                >
                  {atMax
                    ? "Maximum 5 items reached"
                    : `${styleImgs.length} of ${MAX_STYLE} items added`}
                </p>
                <button onClick={onClearStyle} className="text-xs font-medium text-muted-foreground">
                  Clear all
                </button>
              </div>
            </div>
          )}

          <input
            ref={styleInputRef}
            type="file"
            accept="image/png,image/jpeg"
            multiple
            className="hidden"
            onChange={(e) => {
              if (e.target.files && e.target.files.length > 0) {
                onPickStyle(e.target.files);
                e.target.value = "";
              }
            }}
          />
        </div>
      ) : (
        <button
          onClick={onPick}
          disabled={analysing}
          className="mt-6 w-full aspect-[4/5] rounded-3xl border-2 border-dashed border-neutral-300 bg-secondary/60 flex flex-col items-center justify-center gap-3 hover:border-foreground transition overflow-hidden relative px-6"
        >
          {uploadedImg ? (
            <>
              <img  loading="lazy" decoding="async"src={uploadedImg} alt="Uploaded outfit" className="absolute inset-0 w-full h-full object-cover" />
              {!analysing && (
                <button
                  onClick={(e) => { e.stopPropagation(); onClear(); }}
                  className="absolute top-3 right-3 bg-background/90 rounded-full px-3 py-1.5 text-xs font-medium"
                >
                  Replace
                </button>
              )}
              {analysing && <AnalysisLoadingOverlay mode="find_look" />}
            </>
          ) : (
            <>
              <div className="w-14 h-14 rounded-2xl bg-background flex items-center justify-center shadow-sm">
                <Camera size={40} className="text-muted-foreground" />
              </div>
              <div className="text-center">
                <p className="text-foreground" style={{ fontSize: 16, fontWeight: 500 }}>
                  Upload an outfit screenshot
                </p>
                <p className="text-foreground" style={{ fontSize: 13, marginTop: 4 }}>
                  From TikTok, Instagram, Pinterest or anywhere online
                </p>
              </div>
              <span
                className="mt-2 inline-flex items-center gap-1 rounded-full text-white"
                style={{ background: CORAL, fontSize: 14, fontWeight: 500, padding: "10px 20px" }}
              >
                + Choose photo
              </span>
            </>
          )}
        </button>
      )}

      {(isStyle ? !hasStyleImages : !uploadedImg) && !analysing && (
        <div className="mt-5">
          <p className="text-muted-foreground/70" style={{ fontSize: 12, marginBottom: 6 }}>
            {isStyle ? "Any of these work:" : "Works great with:"}
          </p>
          <div className="grid grid-cols-3 gap-2">
            {cards.map(({ Icon, label }) => (
              <div
                key={label}
                className="flex flex-col items-center justify-center text-center bg-secondary/60 rounded-lg"
                style={{
                  border: "0.5px solid hsl(var(--border))",
                  padding: "10px 8px",
                }}
              >
                <Icon size={22} className="text-foreground" />
                <span className="text-muted-foreground/70 mt-1" style={{ fontSize: 12 }}>
                  {label}
                </span>
              </div>
            ))}
          </div>

          <div
            className="mt-3 flex flex-row gap-2 items-start rounded-lg"
            style={{
              background: "var(--l2f-cream)",
              padding: "10px 12px",
            }}
          >
            <Lightbulb size={16} style={{ color: "var(--l2f-danger-ink-soft)", flexShrink: 0, marginTop: 1 }} />
            <p style={{ fontSize: 13, color: "var(--l2f-danger-ink-soft)" }}>{tipText}</p>
          </div>
        </div>
      )}

      {isStyle && hasStyleImages && !analysing && (
        <div className="mt-5">
          <h3 className="text-base font-medium">Where are you wearing it?</h3>
          <p className="text-xs text-muted-foreground">This helps us suggest the right pairings</p>
          <div className="mt-3 grid grid-cols-3 gap-2">
            {OCCASIONS.map((o) => {
              const sel = selectedOccasion === o.label;
              return (
                <button
                  key={o.label}
                  onClick={() => onSelectOccasion(o.label)}
                  className="relative p-2 rounded-xl bg-white text-center"
                  style={{
                    border: sel ? `2px solid ${CORAL}` : "1px solid #E5E7EB",
                  }}
                >
                  {sel && (
                    <span
                      className="absolute top-1 right-1 w-4 h-4 rounded-full text-white text-[12px] flex items-center justify-center"
                      style={{ background: CORAL }}
                    >✓</span>
                  )}
                  <div className="text-2xl">{o.emoji}</div>
                  <div className="text-[12px] font-medium mt-1 leading-tight">{o.label}</div>
                </button>
              );
            })}
          </div>
        </div>
      )}


      <button
        disabled={!canAnalyse}
        onClick={onAnalyse}
        className="mt-4 w-full py-3.5 rounded-full font-medium text-white shadow-lg shadow-[color:var(--l2f-coral)]/30 disabled:opacity-40 disabled:shadow-none flex items-center justify-center gap-2"
        style={{ background: CORAL }}
      >
        {analysing && <Loader2 size={16} className="animate-spin" />}
        {analysing
          ? (isStyle ? "Finding…" : "Analysing…")
          : (isStyle ? "Find Pairings" : "Analyse Outfit")}
      </button>


      {isStyle && activeTab === "primary" && !isWardrobeSource && wardrobeItems.length > 0 && (
        <>
          <div style={{ display: "flex", alignItems: "center", gap: 8, margin: "10px 0" }}>
            <div style={{ flex: 1, height: 0.5, background: "hsl(var(--border))" }} />
            <p className="text-muted-foreground" style={{ fontSize: 12, margin: 0 }}>or</p>
            <div style={{ flex: 1, height: 0.5, background: "hsl(var(--border))" }} />
          </div>
          <button
            onClick={() => onShowWardrobePicker(true)}
            style={{
              background: "hsl(var(--secondary))", borderRadius: 12, padding: "10px 12px",
              border: "0.5px solid hsl(var(--border))", display: "flex",
              alignItems: "center", justifyContent: "space-between", cursor: "pointer",
              marginBottom: 8, width: "100%",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <Shirt size={14} className="text-muted-foreground" />
              <p style={{ fontSize: 12, margin: 0 }}>Pick from your wardrobe</p>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
              {wardrobeItems.slice(0, 3).map((item) => (
                <div key={item.id} style={{
                  width: 20, height: 20, borderRadius: 8,
                  background: item.color_hex ?? "hsl(var(--muted))",
                  border: "0.5px solid hsl(var(--border))",
                }} />
              ))}
              {wardrobeItems.length > 3 && (
                <p className="text-muted-foreground" style={{ fontSize: 11, margin: 0 }}>
                  +{wardrobeItems.length - 3}
                </p>
              )}
            </div>
          </button>
        </>
      )}

      {showWardrobePicker && (
        <div className="fixed inset-0 z-50 flex items-end justify-center" onClick={() => onShowWardrobePicker(false)}>
          <div className="absolute inset-0 bg-black/40" />
          <div className="relative w-full max-w-md bg-background rounded-t-3xl max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="mx-auto mt-3 h-1.5 w-12 rounded-full bg-muted" />
            <div className="px-5 pt-4 pb-6">
              <div className="flex items-center justify-between mb-3">
                <p style={{ fontSize: 12, fontWeight: 500 }}>Pick an item to style</p>
                <button onClick={() => onShowWardrobePicker(false)} className="text-muted-foreground" style={{ fontSize: 16 }}>✕</button>
              </div>
              <div className="grid grid-cols-4 gap-2">
                {wardrobeItems.map((item) => (
                  <WardrobeBgButton
                    key={item.id}
                    item={item}
                    onClick={() => onPickWardrobeItem(item)}
                    className="text-left"
                    style={{
                      background: item.color_hex ?? "hsl(var(--muted))",
                      borderRadius: 10, aspectRatio: "1", overflow: "hidden",
                      border: "0.5px solid hsl(var(--border))",
                      backgroundSize: "cover", backgroundPosition: "center",
                      position: "relative",
                    }}
                  >
                    <div style={{
                      position: "absolute", bottom: 0, left: 0, right: 0,
                      background: "rgba(0,0,0,0.5)", color: "white",
                      fontSize: 11, padding: "3px 8px", textAlign: "center",
                      whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                    }}>{item.name}</div>
                  </WardrobeBgButton>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      </>)}
    </div>


  );
}

const SWAP_OPTIONS: Record<string, string[]> = {
  bottom: ["Mini Skirt", "Midi Skirt", "Maxi Skirt", "Shorts", "Straight Leg Trousers", "Wide Leg Trousers", "Joggers"],
  top: ["Sleeveless Top", "Short Sleeve", "Long Sleeve", "Crop Top", "Oversized", "Fitted Blouse", "Knit / Jumper"],
  shoes: ["Heels", "Flat Sandals", "Trainers / Sneakers", "Boots", "Loafers", "Mules"],
  outerwear: ["Blazer", "Denim Jacket", "Leather Jacket", "Overshirt", "Trench Coat", "Cardigan", "Oversized Coat", "Hoodie", "Puffer Jacket"],
  accessory: ["Belt", "Handbag", "Clutch", "Sunglasses", "Hat", "Scarf", "Jewellery"],
  dress: ["Mini Dress", "Midi Dress", "Maxi Dress", "Wrap Dress", "Bodycon", "A-line"],
};

function pluralize(s: string): string {
  if (/s$/i.test(s)) return s;
  if (/y$/i.test(s)) return s.replace(/y$/i, "ies");
  return s + "s";
}

function pickDefaultSwap(category: string, currentName: string): string {
  const opts = SWAP_OPTIONS[category.toLowerCase()] ?? [];
  if (opts.length === 0) return "";
  const lower = (currentName ?? "").toLowerCase();
  const different = opts.find((o) => !lower.includes(o.toLowerCase().split(/[\s/]/)[0]));
  return different ?? opts[0];
}

function AddLayerCard({
  item,
  isAdded,
  onToggle,
  swap,
  onOpenSwap,
  onSelectSwap,
  onConfirmSwap,
  onCancelSwap,
  swappedTo,
  trackProductClick,
  trackManualSearch,
  manualSearchUrl,
}: {
  item: DetectedItem;
  isAdded: boolean;
  onToggle: (next: boolean) => void;
  swap?: { selected: string; loading: boolean };
  onOpenSwap: () => void;
  onSelectSwap: (opt: string) => void;
  onConfirmSwap: () => void;
  onCancelSwap: () => void;
  swappedTo?: string;
  trackProductClick: (item: DetectedItem, p: ShoppingProduct) => void;
  trackManualSearch: (item: DetectedItem) => void;
  manualSearchUrl: (item: DetectedItem) => string;
}) {
  const loading = item.products == null;
  const products = item.products;
  const swapOpts = SWAP_OPTIONS.outerwear;
  const isSwapping = !!swap;

  if (loading && !isAdded) {
    return (
      <div
        className="rounded-2xl p-4 bg-secondary/40 animate-pulse"
        style={{ height: 64 }}
        aria-label="Loading optional layer"
      />
    );
  }

  if (!isAdded) {
    return (
      <button
        onClick={() => onToggle(true)}
        className="w-full flex items-center justify-between rounded-2xl bg-white text-left"
        style={{
          border: "0.5px dashed #D1D5DB",
          padding: "14px 16px",
        }}
      >
        <div className="flex items-center gap-3">
          {swappedTo ? (
            <Check size={18} style={{ color: CORAL }} />
          ) : (
            <Plus size={18} style={{ color: CORAL }} />
          )}
          <div>
            <p className="font-medium text-sm flex items-center gap-2">
              {swappedTo ? "Layer added" : "Add a layer"}
              {swappedTo && (
                <span
                  className="inline-block rounded-full"
                  style={{ width: 6, height: 6, background: CORAL }}
                />
              )}
            </p>
            <p className="text-muted-foreground" style={{ fontSize: 12 }}>
              Jacket, coat or overshirt
            </p>
          </div>
        </div>
        {!swappedTo && (
          <span
            className="rounded-full"
            style={{
              background: "#F3F4F6",
              color: "#6B7280",
              fontSize: 12,
              padding: "3px 9px",
            }}
          >
            Optional
          </span>
        )}
      </button>
    );
  }

  return (
    <div
      className="rounded-2xl border p-4 shadow-sm relative"
      style={{
        background: "var(--card)",
        borderColor: isSwapping ? CORAL : "var(--border)",
        borderWidth: isSwapping ? 2 : 1,
        animation: "layerExpand 0.3s ease",
      }}
    >
      <button
        onClick={() => onToggle(false)}
        className="absolute top-3 right-3 w-7 h-7 rounded-full flex items-center justify-center text-muted-foreground"
        aria-label="Remove layer"
      >
        <X size={16} />
      </button>

      {isSwapping ? (
        <div>
          <p className="text-[12px] font-medium uppercase tracking-wider" style={{ color: CORAL }}>
            OUTERWEAR — swap for?
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            {swapOpts.map((opt) => {
              const selected = swap!.selected === opt;
              return (
                <button
                  key={opt}
                  onClick={() => onSelectSwap(opt)}
                  disabled={swap!.loading}
                  className="rounded-full transition-colors"
                  style={{
                    background: selected ? CORAL : "#FFFFFF",
                    color: selected ? "#FFFFFF" : "#111827",
                    border: selected ? `1px solid ${CORAL}` : "1px solid #D1D5DB",
                    fontSize: 12,
                    padding: "5px 10px",
                  }}
                >
                  {opt}
                </button>
              );
            })}
          </div>
          <button
            onClick={onConfirmSwap}
            disabled={swap!.loading || !swap!.selected}
            className="mt-4 w-full py-2.5 rounded-full text-sm font-medium text-white flex items-center justify-center gap-2 disabled:opacity-60"
            style={{ background: CORAL }}
          >
            {swap!.loading && <Loader2 size={14} className="animate-spin" />}
            {swap!.loading ? `Finding ${pluralize(swap!.selected)}…` : `Find ${pluralize(swap!.selected)} →`}
          </button>
          {!swap!.loading && (
            <button onClick={onCancelSwap} className="mt-2 w-full text-xs text-muted-foreground">
              Cancel
            </button>
          )}
        </div>
      ) : (
        <>
          <div className="flex items-center gap-4 pr-8">
            <div
              className="w-14 h-14 rounded-xl shrink-0 border border-border"
              style={{ background: item.color_hex ?? "#E5E5E5" }}
            />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <p className="text-[12px] font-medium uppercase tracking-wider" style={{ color: "#9CA3AF" }}>
                  {item.category}
                </p>
                <span
                  className="rounded-full"
                  style={{ background: "var(--l2f-cream)", color: "var(--l2f-danger-ink-soft)", fontSize: 12, padding: "2px 8px" }}
                >
                  Optional layer
                </span>
              </div>
              <p className="font-medium text-sm leading-tight mt-0.5 truncate">{item.name}</p>
              <p className="text-xs text-muted-foreground mt-0.5 truncate">
                {(item.style_tags ?? []).join(", ")}
              </p>
              {item.reason && (
                <p className="text-[12px] italic text-muted-foreground mt-1">{item.reason}</p>
              )}
            </div>
          </div>

          <div className="mt-2 flex justify-end">
            <button
              onClick={onOpenSwap}
              className="inline-flex items-center gap-1 rounded-full"
              style={{
                border: `1.5px solid ${CORAL}`,
                color: CORAL,
                padding: "6px 12px",
                fontSize: 12,
                fontWeight: 500,
              }}
            >
              <ArrowLeftRight size={13} />
              Swap this
            </button>
          </div>

          <div className="mt-4 -mx-4 px-4 overflow-x-auto">
            {products === null ? (
              <div className="flex gap-3">
                {[0, 1, 2].map((i) => (
                  <div key={i} className="w-32 shrink-0">
                    <div className="aspect-square rounded-xl bg-secondary animate-pulse" />
                    <div className="mt-2 h-3 rounded-xl bg-secondary animate-pulse" />
                  </div>
                ))}
              </div>
            ) : products.length === 0 ? (
              <a
                href={manualSearchUrl(item)}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackManualSearch(item)}
                className="inline-flex items-center gap-1 px-4 py-2 rounded-full text-xs font-medium border border-border"
                style={{ color: CORAL }}
              >
                Search manually <ChevronRight size={14} />
              </a>
            ) : (
              <div className="flex gap-3">
                {products.map((p, i) => (
                  <a
                    key={i}
                    href={p.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => trackProductClick(item, p)}
                    className="w-32 shrink-0 text-left block"
                  >
                    <div className="relative aspect-square rounded-xl overflow-hidden bg-secondary border border-border">
                      <img src={p.thumbnail} alt={p.title} className="w-full h-full object-cover" loading="lazy" />
                    </div>
                    <p className="mt-2 text-[12px] leading-tight font-medium line-clamp-2">{p.title}</p>
                    {p.price && (
                      <p className="mt-0.5 text-sm font-medium" style={{ color: CORAL }}>{p.price}</p>
                    )}
                    {p.source && (
                      <p className="text-[12px] text-muted-foreground truncate">{p.source}</p>
                    )}
                    {p.size_confirmed === true && (
                      <span className="inline-block mt-1 text-[12px] font-medium rounded-full" style={{ background: "#D1FAE5", color: "#065F46", padding: "3px 8px" }}>
                        ✓ Your size
                      </span>
                    )}
                  </a>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

async function buildWatermarkedLookbookBlob(imageUrl: string): Promise<Blob> {
  const img = new Image();
  img.crossOrigin = "anonymous";
  await new Promise<void>((resolve, reject) => {
    img.onload = () => resolve();
    img.onerror = () => reject(new Error("image_load_failed"));
    img.src = imageUrl;
  });
  const canvas = document.createElement("canvas");
  canvas.width = img.naturalWidth;
  canvas.height = img.naturalHeight;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("canvas_unavailable");
  ctx.drawImage(img, 0, 0);

  const text = "like2fit.com";
  const fontSize = Math.max(12, Math.round(canvas.width * 0.018));
  ctx.font = `${fontSize}px -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif`;
  const padX = Math.round(fontSize * 0.83);
  const padY = Math.round(fontSize * 0.33);
  const metrics = ctx.measureText(text);
  const textW = metrics.width;
  const textH = fontSize;
  const pillW = textW + padX * 2;
  const pillH = textH + padY * 2;
  const margin = Math.round(fontSize * 0.83);
  const x = canvas.width - pillW - margin;
  const y = canvas.height - pillH - margin;
  const r = pillH / 2;
  ctx.fillStyle = "rgba(0,0,0,0.45)";
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + pillW - r, y);
  ctx.arcTo(x + pillW, y, x + pillW, y + r, r);
  ctx.lineTo(x + pillW, y + pillH - r);
  ctx.arcTo(x + pillW, y + pillH, x + pillW - r, y + pillH, r);
  ctx.lineTo(x + r, y + pillH);
  ctx.arcTo(x, y + pillH, x, y + pillH - r, r);
  ctx.lineTo(x, y + r);
  ctx.arcTo(x, y, x + r, y, r);
  ctx.closePath();
  ctx.fill();
  ctx.fillStyle = "#ffffff";
  ctx.textBaseline = "middle";
  ctx.fillText(text, x + padX, y + pillH / 2);

  return await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob((b) => (b ? resolve(b) : reject(new Error("blob_failed"))), "image/png");
  });
}

function ShareLookbookButton({
  imageUrl,
  isGuest,
  userId,
  mode,
  guestSessionId,
}: {
  imageUrl: string;
  isGuest: boolean;
  userId: string | null;
  mode: AnalysisMode;
  guestSessionId: string | null;
}) {
  const [busy, setBusy] = useState(false);
  const onShare = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const blob = await buildWatermarkedLookbookBlob(imageUrl);
      const file = new File([blob], "like2fit-look.png", { type: "image/png" });
      const nav = navigator as Navigator & {
        canShare?: (data: ShareData) => boolean;
      };
      if (nav.share && nav.canShare && nav.canShare({ files: [file] })) {
        await nav.share({
          files: [file],
          title: "My Like2Fit look",
          text: "Found this outfit on Like2Fit — like2fit.com",
        });
      } else {
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = "like2fit-look.png";
        link.click();
        setTimeout(() => URL.revokeObjectURL(link.href), 1000);
      }
      trackEvent("lookbook_shared", {
        is_guest: isGuest,
        analysis_mode: mode,
        user_id: userId ?? null,
        guest_session_id: guestSessionId,
      });
    } catch (err) {
      const name = (err as { name?: string } | null)?.name;
      if (name !== "AbortError") {
        toast.error("Couldn't prepare image to share.");
      }
    } finally {
      setBusy(false);
    }
  };
  return (
    <button
      onClick={onShare}
      disabled={busy}
      aria-label="Share look"
      className="absolute flex items-center justify-center"
      style={{
        top: 10,
        right: 10,
        width: 36,
        height: 36,
        borderRadius: "50%",
        background: "rgba(0,0,0,0.45)",
        border: "none",
        cursor: "pointer",
      }}
    >
      {busy ? (
        <Loader2 size={17} style={{ color: "#ffffff" }} className="animate-spin" />
      ) : (
        <Share2 size={17} style={{ color: "#ffffff" }} />
      )}
    </button>
  );
}







const findCheaperProducts = (
  items: DetectedItem[],
  targetTotal: number | null,
): {
  overrides: Record<string, ShoppingProduct | null>;
  achievedTotal: number;
  savedAmount: number;
} => {
  const scoredItems = items.filter(
    (i) => i.tiers?.mid?.extracted_price != null,
  );
  const itemCount = scoredItems.length;
  const perItemBudget =
    targetTotal !== null && itemCount > 0 ? targetTotal / itemCount : null;
  const overrides: Record<string, ShoppingProduct | null> = {};
  let achievedTotal = 0;
  scoredItems.forEach((item) => {
    const currentProduct = item.tiers?.mid ?? null;
    const currentPrice = currentProduct?.extracted_price ?? 0;
    const candidates = (item.products ?? [])
      .filter(
        (p: ShoppingProduct) =>
          p.extracted_price != null &&
          (p.extracted_price ?? 0) > 0 &&
          !!p.link &&
          !!p.thumbnail &&
          (p.extracted_price ?? 0) < currentPrice,
      )
      .sort((a: ShoppingProduct, b: ShoppingProduct) => {
        if (a.size_confirmed && !b.size_confirmed) return -1;
        if (!a.size_confirmed && b.size_confirmed) return 1;
        return (a.extracted_price ?? 0) - (b.extracted_price ?? 0);
      });
    if (candidates.length === 0) {
      overrides[item.id] = null;
      achievedTotal += currentPrice;
      return;
    }
    let chosen = candidates[0];
    if (perItemBudget !== null) {
      const withinBudget = candidates.filter(
        (p: ShoppingProduct) => (p.extracted_price ?? 0) <= perItemBudget,
      );
      if (withinBudget.length > 0) {
        chosen = withinBudget[withinBudget.length - 1];
      }
    }
    overrides[item.id] = chosen;
    achievedTotal += chosen.extracted_price ?? currentPrice;
  });
  const currentTotal = scoredItems.reduce(
    (sum, item) => sum + (item.tiers?.mid?.extracted_price ?? 0),
    0,
  );
  return {
    overrides,
    achievedTotal: Math.round(achievedTotal),
    savedAmount: Math.round(currentTotal - achievedTotal),
  };
};


function Results({

  img, images = [], items: initialItems, outfitId, userId, userBudget, onBack, onSave,
  isGuest, onRequestSignup, mode = "find_look", occasion = null, styleSummary = null,
  detectedGender = null,
  isSaved = false, onGoToStyle, onLookbookReadyChange, onCentreButtonChange,
  initialLookbookUrl = null,
  initialOutfitDirty = false,
  onLookbookUrlChange,
  onOutfitDirtyChange,
  inputMethod = null,
  textDescription = null,
  typicalBudget = null,
  budgetTop = null,
  budgetBottom = null,
  budgetShoes = null,

  onOpenWardrobe,
  initialLookbookImageUrls = null,
  modelProfile = null,
  wardrobeItems = [],
  isSample = false,
  onSampleExit,
  onLb1Success,
  headerSlot = null,
  meta = null,




  
  
  
  
}: {
  img: string | null;
  inputMethod?: string | null;
  textDescription?: string | null;
  images?: string[];
  items: DetectedItem[];
  outfitId: string | null;
  userId: string | null;
  userBudget: number | null;
  onBack: () => void;
  onSave: () => void;
  isGuest: boolean;
  onRequestSignup: (trigger: SignupTrigger) => void;
  mode?: AnalysisMode;
  occasion?: string | null;
  styleSummary?: string | null;
  detectedGender?: "men" | "women" | "unisex" | null;
  isSaved?: boolean;
  onGoToStyle?: () => void;
  onLookbookReadyChange?: (ready: boolean) => void;
  onCentreButtonChange?: (config: CentreButtonConfig | null) => void;
  initialLookbookUrl?: string | null;
  initialOutfitDirty?: boolean;
  onLookbookUrlChange?: (url: string | null) => void;
  onOutfitDirtyChange?: (dirty: boolean) => void;
  typicalBudget?: number | null;
  budgetTop?: number | null;
  budgetBottom?: number | null;
  budgetShoes?: number | null;

  onOpenWardrobe?: () => void;
  initialLookbookImageUrls?: string[] | null;
  modelProfile?: {
    model_gender?: string | null;
    model_height?: string | null;
    model_body_shape?: string | null;
    model_body_size?: string | null;
    model_skin_tone?: string | null;
    model_hair?: string | null;
    model_preferred_variation?: string | null;
  } | null;
  wardrobeItems?: WardrobeItem[];
  /** When true, Results renders read-only sample preview UI (banner + Get-my-own CTA, no save/share/regenerate). Defaults to false → existing behavior. */
  isSample?: boolean;
  /** Called when the user taps the sample "Get my own outfit" CTA. Required only when isSample is true. */
  onSampleExit?: () => void;
  /** PQ-3b: fires once per outfit when LB1 first becomes renderable. */
  onLb1Success?: (outfitId: string) => void;
  /** Phase 1a.5-vi: pending/failed status strip injected by parent. */
  headerSlot?: React.ReactNode;
  /** Phase 1a.5-vi: outfit meta from parent's useOutfitMetaPolling. Single source of truth. */
  meta?: OutfitMeta | null;


}) {


  
  
  const [items, setItems] = useState<DetectedItem[]>(initialItems);
  const [lightboxImage, setLightboxImage] = useState<{ src: string; alt: string } | null>(null);
  const issueNumber = useIssueNumber(outfitId, isGuest);
  const openLightbox = (src: string, alt: string, source: string) => {
    if (!src) return;
    trackEvent("item_image_enlarged", {
      outfit_id: outfitId,
      source,
    });
    setLightboxImage({ src, alt });
  };
  
  
  // Fire results_view once per mount.
  const resultsViewFiredRef = useRef(false);
  useEffect(() => {
    if (resultsViewFiredRef.current) return;
    resultsViewFiredRef.current = true;
    trackEvent("results_view", {
      outfit_id: outfitId,
      mode,
      item_count: initialItems?.length ?? 0,
    });
  }, [outfitId, mode, initialItems]);

  const [cheaperOverrides, setCheaperOverrides] = useState<Record<string, ShoppingProduct | null>>({});
  const [cheaperTotal, setCheaperTotal] = useState<number | null>(null);
  const [originalTotal, setOriginalTotal] = useState<number | null>(null);
  const [cheaperNoneFound, setCheaperNoneFound] = useState(false);
  const [cheaperApplied, setCheaperApplied] = useState(false);
  const [cheaperBannerDismissed, setCheaperBannerDismissed] = useState(false);
  useEffect(() => {
    if (!cheaperNoneFound) return;
    const t = setTimeout(() => setCheaperNoneFound(false), 4000);
    return () => clearTimeout(t);
  }, [cheaperNoneFound]);
  const hasSensitiveItems = containsSensitiveItems(items);
  const [swapState, setSwapState] = useState<Record<string, { selected: string; loading: boolean }>>({});
  const isSwapping = Object.values(swapState).some((s) => s.loading);
  const [swappedFlag, setSwappedFlag] = useState<Record<string, string>>({});
  const [itemColourOverrides, setItemColourOverrides] = useState<
    Record<string, { colourName: string; product: ShoppingProduct | null; loading: boolean }>
  >({});

  const fetchColourVariant = async (item: DetectedItem, colourName: string) => {
    if (!item.search_query) return;
    setItemColourOverrides((prev) => ({
      ...prev,
      [item.id]: {
        colourName,
        product: prev[item.id]?.product ?? null,
        loading: true,
      },
    }));
    try {
      const query = `${item.search_query} ${colourName}`;
      const { data, error, quotaExceeded } = await invokeGenerator<any>("swap-pairing-item", {
        source: "swap-colour",
        body: {
          item_id: item.id,
          search_query: query,
          outfit_id: outfitId,
          colour_search: true,
          user_budget: userBudget,
          item_count: Math.max(items.length, 1),
        },
      });
      if (quotaExceeded) {
        setItemColourOverrides((prev) => ({
          ...prev,
          [item.id]: { colourName, product: null, loading: false },
        }));
        return;
      }
      if (error || !data?.product) {
        setItemColourOverrides((prev) => ({
          ...prev,
          [item.id]: { colourName, product: null, loading: false },
        }));
        return;
      }

      setItemColourOverrides((prev) => ({
        ...prev,
        [item.id]: { colourName, product: data.product as ShoppingProduct, loading: false },
      }));
    } catch {
      setItemColourOverrides((prev) => ({
        ...prev,
        [item.id]: { colourName, product: null, loading: false },
      }));
    }
  };

  const [isLayerAdded, setIsLayerAdded] = useState<boolean>(
    !!initialItems.find((i) => i.item_type === "optional")?.is_layer_added,
  );
  const [lookbookImageUrl, setLookbookImageUrl] = useState<string | null>(initialLookbookUrl);
  const [hasGenerated, setHasGenerated] = useState<boolean>(initialLookbookUrl != null);
  const [regenerateNonce, setRegenerateNonce] = useState(0);
  // Phase B-1: only auto-trigger first-gen when no persisted lookbook exists.
  // Opening a completed outfit from Recents/Saved/Wardrobe/share/deep-link/
  // refresh must NOT re-invoke generate-lookbook1 (gpt-image-2 cost). Manual
  // retry path uses setFirstGenNonce((n) => n + 1), which still works.
  const [firstGenNonce, setFirstGenNonce] = useState(initialLookbookUrl ? 0 : 1);
  const [primaryLoading, setPrimaryLoading] = useState(false);
  const [headlessLoading, setHeadlessLoading] = useState(false);
  const isLookbookLoading = primaryLoading || headlessLoading;
  const [crossSellDismissed, setCrossSellDismissed] = useState(false);
  const [genderConflict, setGenderConflict] = useState<
    { detectedGender: "men" | "women" | "unisex" } | null
  >(null);
  const [switchGenderNonce, setSwitchGenderNonce] = useState(0);
  const [temporaryGenderOverride, setTemporaryGenderOverride] = useState<
    "men" | "women" | "unisex" | null
  >(null);
  // Sub-build 1.5 Part 2 — persisted per-outfit gender override
  // (outfits.effective_gender). Sourced from DB on outfit load; updated
  // optimistically when the user clicks "Switch model". Used by
  // PurchaseConfidenceCard's fit-confidence call.
  const [outfitEffectiveGender, setOutfitEffectiveGender] = useState<
    "men" | "women" | "unisex" | "non-binary" | null
  >(null);
  const [outfitDirty, setOutfitDirty] = useState(initialOutfitDirty);
  const [lookbookImageUrls, setLookbookImageUrls] = useState<string[] | null>(
    initialLookbookImageUrls,
  );
  const lb2UrlsLoaded = useRef<boolean>(
    !!(initialLookbookImageUrls && initialLookbookImageUrls.length > 0),
  );
  
  const lb1Url = lookbookImageUrls?.[0] ?? lookbookImageUrl ?? null;
  const [lb2UrlDirect, setLb2UrlDirect] = useState<string | null>(null);
  const lb2Url = lookbookImageUrls?.[1] ?? lb2UrlDirect ?? null;
  const [lb2Loading, setLb2Loading] = useState(!(initialLookbookImageUrls && initialLookbookImageUrls[1]));
  const [lb2TimedOut, setLb2TimedOut] = useState(false);
  // F37.3 Phase A — Fix 2: surface LB1 failure with its own UI rather than
  // falling back to LB2's image. Driven by outfits.lookbook_status==='failed'.
  const [lb1Failed, setLb1Failed] = useState(false);
  // LB3-DISPLAY-RECOVERY-1: lb3 state derived from resultsMeta (single source
  // of truth via useOutfitMetaPolling). The bespoke 120s poller + 75s
  // watchdog are retired. `meta` is passed in as a prop from the parent.
  const lb3Url: string | null =
    meta?.lookbook_lb3_url ?? (initialLookbookImageUrls?.[2] ?? null);
  const lb3Outcome: Lb3Outcome | null =
    (meta?.lb3_outcome ?? null) as Lb3Outcome | null;
  const lb3OwnedItems: Lb3OwnedItem[] = Array.isArray(meta?.lb3_owned_items)
    ? (meta!.lb3_owned_items as Lb3OwnedItem[])
    : [];
  const lb3GapItems: Lb3GapItem[] = Array.isArray(meta?.lb3_gap_items)
    ? (meta!.lb3_gap_items as Lb3GapItem[])
    : [];
  const lb3TimedOut: boolean = meta?.analysis_status === "failed";
  const lb3Loading: boolean =
    !lb3Url && !lb3TimedOut && lb3Outcome !== "empty_wardrobe";
  const lb3OutcomeRenderedRef = useRef<string | null>(null);
  useEffect(() => {
    if (!outfitId || !lb3Outcome) return;
    const key = `${outfitId}:${lb3Outcome}`;
    if (lb3OutcomeRenderedRef.current === key) return;
    lb3OutcomeRenderedRef.current = key;
    trackEvent("lb3_outcome_rendered", {
      outfit_id: outfitId,
      outcome: lb3Outcome,
      owned_count: lb3OwnedItems.length,
      gap_count: lb3GapItems.length,
    });
  }, [outfitId, lb3Outcome, lb3OwnedItems.length, lb3GapItems.length]);

  // Sub-build 1.5 Part 2 — load persisted gender override for this outfit.
  // Auth users only; guests don't persist overrides per Part 2 scope.
  useEffect(() => {
    let cancelled = false;
    setOutfitEffectiveGender(null);
    if (!outfitId || isGuest || !userId) return;
    (async () => {
      const { data, error } = await supabase
        .from("outfits")
        .select("effective_gender")
        .eq("id", outfitId)
        .maybeSingle();
      if (cancelled || error || !data) return;
      const v = (data as { effective_gender?: string | null }).effective_gender ?? null;
      if (v === "men" || v === "women" || v === "unisex" || v === "non-binary") {
        setOutfitEffectiveGender(v);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [outfitId, isGuest, userId]);




  // Guest progressive onboarding regen state.
  const [guestRegenStatus, setGuestRegenStatus] = useState<
    "idle" | "loading" | "success" | "error"
  >("idle");
  const [guestRegenError, setGuestRegenError] = useState<string | null>(null);
  const [guestModelSetupOverride, setGuestModelSetupOverride] = useState<
    Record<string, unknown> | null
  >(null);
  // Ref-sync so LB2/LB3 regen helpers (and the LB2 poll that re-fires them
  // with no args) always see the latest guest override, matching how LB1
  // consumes guestModelSetupOverride via the LookbookGenerator prop.
  const guestModelSetupOverrideRef = useRef<Record<string, unknown> | null>(null);
  useEffect(() => {
    guestModelSetupOverrideRef.current = guestModelSetupOverride;
  }, [guestModelSetupOverride]);
  const [guestRegenBannerDismissed, setGuestRegenBannerDismissed] = useState(false);
  const guestRegenSnapshotRef = useRef<{
    lb1: string | null;
    lb2: string | null;
    lb3: string | null;
    at: number;
  } | null>(null);
  const [wardrobeEligibleCount, setWardrobeEligibleCount] = useState<number | null>(null);
  useEffect(() => {
    let cancelled = false;
    if (!userId || isGuest) {
      setWardrobeEligibleCount(0);
      return;
    }
    (async () => {
      try {
        const { count } = await supabase
          .from("wardrobe_items")
          .select("id", { count: "exact", head: true })
          .eq("user_id", userId)
          .eq("is_active", true)
          .eq("lb3_eligible", true);
        if (!cancelled) setWardrobeEligibleCount(count ?? 0);
      } catch {
        if (!cancelled) setWardrobeEligibleCount(0);
      }
    })();
    return () => { cancelled = true; };
  }, [userId, isGuest]);
  const [lb2RegenerateNonce, setLb2RegenerateNonce] = useState(0);
  const [lb2Items, setLb2Items] = useState<Array<{
    category: string;
    search_query: string;
    tiers: {
      budget: ShoppingProduct | null;
      mid: ShoppingProduct | null;
      premium: ShoppingProduct | null;
    };
  }> | null>(null);
  type StyledCostsItem = {
    category: string;
    result: {
      product: ShoppingProduct;
      overBudget: boolean;
      overBudgetBy: number;
      onlineFallback: boolean;
    };
    isHero: boolean;
    // Wave 3B — Commit 3h: detected_items row backing this styled-costs
    // row, used to render the per-item offline-shopping badge.
    itemRow: DetectedItem | null;
  };

  const getStyledCostsBreakdown = (): {
    items: StyledCostsItem[];
    total: number;
    hasHero: boolean;
  } => {
    const out: StyledCostsItem[] = [];

    // 1. Hero piece from detected_items
    const heroDetected = heroItemId ? items.find((i) => i.id === heroItemId) : null;
    if (heroDetected) {
      const product = heroDetected.tiers?.mid ?? null;
      if (product) {
        out.push({
          category: heroDetected.category,
          result: { product, overBudget: false, overBudgetBy: 0, onlineFallback: false },
          isHero: true,
          itemRow: heroDetected,
        });
      }
    }

    // 2. lb2 styling additions.
    for (const lb2 of lb2Items ?? []) {
      const mid = lb2.tiers?.mid ?? null;
      if (!mid) continue;
      // Wave 3B — Commit 3h: best-effort match to a detected_items row
      // by category so the LB2 row can render an offline-shopping badge.
      const lb2Row = items.find((it) => it.category === lb2.category) ?? null;
      out.push({
        category: lb2.category,
        result: { product: mid, overBudget: false, overBudgetBy: 0, onlineFallback: false },
        isHero: false,
        itemRow: lb2Row,
      });
    }



    const total = out.reduce((acc, x) => {
      if (isExcludedFromBudget(x.category)) return acc;
      return acc + (x.result.product.extracted_price ?? 0);
    }, 0);

    return { items: out, total, hasHero: out.some((x) => x.isHero) };
  };

  const guestSessionId =
    typeof window !== "undefined" ? localStorage.getItem("guest_session_id") : null;


  // Size profile for the size-prompt banner (signed-in users only).
  const [sizeProfile, setSizeProfile] = useState<SizeProfileRow | null>(null);
  const loadSizeProfile = useCallback(async () => {
    if (!userId || isGuest) return;
    const { data } = await (supabase.from("profiles") as any)
      .select(
        "size_gender, size_uk_number, size_letter, size_shoe, size_top_letter, size_top_uk_number, size_trouser_waist, size_trouser_length, size_shoe_mens, model_gender, model_height, model_body_shape, model_body_size",
      )
      .eq("id", userId)
      .maybeSingle();
    setSizeProfile((data as SizeProfileRow | null) ?? null);
  }, [userId, isGuest]);
  useEffect(() => { void loadSizeProfile(); }, [loadSizeProfile]);

  // ---- Delayed purchase-feedback prompt (≥48h after affiliate click, no outcome yet) ----
  const [pendingFeedback, setPendingFeedback] = useState<{
    outfit_id: string | null;
    item_name: string;
    item_category: string | null;
    retailer: string | null;
    product_url: string | null;
    detected_item_id: string | null;
  } | null>(null);

  const checkPendingFeedback = useCallback(async () => {
    if (!userId || isGuest) return;
    const cutoff = new Date(Date.now() - 48 * 60 * 60 * 1000).toISOString();
    const { data: clicks } = await (supabase.from("affiliate_clicks") as any)
      .select("*")
      .eq("user_id", userId)
      .lt("clicked_at", cutoff)
      .not("item_name", "is", null)
      .order("clicked_at", { ascending: false })
      .limit(3);
    if (!clicks?.length) return;
    for (const click of clicks) {
      const { data: existing } = await (supabase.from("purchase_outcomes") as any)
        .select("id")
        .eq("user_id", userId)
        .eq("item_name", click.item_name)
        .gte("created_at", click.clicked_at)
        .maybeSingle();
      if (!existing) {
        setPendingFeedback(click);
        return;
      }
    }
  }, [userId, isGuest]);

  useEffect(() => {
    void checkPendingFeedback();
  }, [checkPendingFeedback]);

  // Guest size profile — held in sessionStorage only until signup.
  const [guestSizeProfile, setGuestSizeProfile] = useState<GuestSizeProfile | null>(null);
  useEffect(() => {
    if (!isGuest || typeof window === "undefined") return;
    try {
      const stored = sessionStorage.getItem("l2f_guest_size");
      if (stored) setGuestSizeProfile(JSON.parse(stored) as GuestSizeProfile);
    } catch { /* ignore */ }
  }, [isGuest]);

  const activeSizeProfile: ActiveSize | null = isGuest
    ? toActiveFromGuest(guestSizeProfile)
    : toActiveFromRow(sizeProfile);

  // Fire styled_costs_rendered once per outfit when both data sources resolve.
  const styledCostsTrackedRef = useRef<string | null>(null);
  useEffect(() => {
    if (!outfitId || !lb2Url) return;
    if (styledCostsTrackedRef.current === outfitId) return;
    if (!lb2Items) return; // wait for fetch
    const breakdown = getStyledCostsBreakdown();
    if (breakdown.items.length === 0) return;
    styledCostsTrackedRef.current = outfitId;
    trackEvent("styled_costs_rendered", {
      outfit_id: outfitId,
      item_count: breakdown.items.length,
      total: breakdown.total,
      has_hero: breakdown.hasHero,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [outfitId, lb2Url, lb2Items, items]);



  // Purchase Confidence Card: hero piece + brand fit preferences.
  const lb1ScrollRef = useRef<HTMLDivElement | null>(null);
  // Wave 3B — Commit 3l: row-level scroll target for tier-tap.
  const lb1FirstRowRef = useRef<HTMLDivElement | null>(null);
  const handleSeeItOnYou = useCallback(() => {
    lb1ScrollRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, []);
  const [heroItemId, setHeroItemId] = useState<string | null>(null);
  useEffect(() => {
    if (!outfitId) { setHeroItemId(null); return; }
    let cancelled = false;
    let interval: ReturnType<typeof setInterval> | null = null;
    const read = async () => {
      try {
        let hero: string | null = null;
        if (isGuest) {
          // Guests can't SELECT outfits directly (no anon RLS policy).
          // Use the SECURITY DEFINER RPC to bypass RLS for the single row.
          const { data } = await (supabase as any).rpc("get_guest_outfit", { p_id: outfitId });
          const row = Array.isArray(data) && data.length > 0 ? data[0] : null;
          hero = ((row as { hero_item_id?: string | null } | null)?.hero_item_id) ?? null;
        } else {
          const { data } = await (supabase.from("outfits") as any)
            .select("hero_item_id")
            .eq("id", outfitId)
            .maybeSingle();
          hero = ((data as { hero_item_id?: string | null } | null)?.hero_item_id) ?? null;
        }
        if (!cancelled && hero) {
          setHeroItemId(hero);
          if (interval) { clearInterval(interval); interval = null; }
        }
      } catch { /* ignore */ }
    };
    void read();
    // Backstop poll (F37.URL-UX): URL flow may transition to results before
    // hero_item_id lands. Realtime UPDATE listener at L6235+ handles auth'd
    // users; this covers guests + missed events. Stops on first hero read.
    interval = setInterval(() => { void read(); }, 3000);
    // Hard cap so we never poll forever on a failed/stuck outfit.
    const stop = setTimeout(() => {
      if (interval) { clearInterval(interval); interval = null; }
    }, 120_000);
    return () => {
      cancelled = true;
      if (interval) clearInterval(interval);
      clearTimeout(stop);
    };
  }, [outfitId, isGuest]);
  const [brandFitTops, setBrandFitTops] = useState<BrandFitEntry[]>([]);
  const [brandFitBottoms, setBrandFitBottoms] = useState<BrandFitEntry[]>([]);
  const loadBrandFits = useCallback(async () => {
    if (!userId || isGuest) return;
    try {
      const { data } = await (supabase.from("profiles") as any)
        .select("brand_fit_tops, brand_fit_bottoms")
        .eq("id", userId)
        .maybeSingle();
      if (!data) return;
      const t = (data as any).brand_fit_tops;
      const b = (data as any).brand_fit_bottoms;
      setBrandFitTops(Array.isArray(t) ? (t as BrandFitEntry[]) : []);
      setBrandFitBottoms(Array.isArray(b) ? (b as BrandFitEntry[]) : []);
    } catch { /* ignore */ }
  }, [userId, isGuest]);
  useEffect(() => { void loadBrandFits(); }, [loadBrandFits]);
  const handleProfileUpdated = useCallback(() => {
    void loadBrandFits();
    void loadSizeProfile();
  }, [loadBrandFits, loadSizeProfile]);


  const heroItem = useMemo(() => {
    if (items.length === 0) return null;
    const fromId = heroItemId ? items.find((i) => i.id === heroItemId) : null;
    return fromId ?? selectHeroPieceFromItems(items);
  }, [items, heroItemId]);

  // Wave 3B — Commit 3d: offline-shopping wiring at the results level.
  // - shoppingLoc: user's stored home location + enabled preference.
  // - shoppingChipMode: local UI toggle between "nearby first" and "online only".
  // - showShoppingUx: gates chip + badge visibility (auth + enabled + fresh).
  // - offlineHeroItem: detected_items row for the PCC hero, carrying
  //   offline_shopping_tier_1/2 populated by the resolver.
  const shoppingLoc = useOfflineShoppingLocation(userId ?? null);
  const [shoppingChipMode, setShoppingChipMode] = useState<"online" | "nearby">(
    "online",
  );
  // Wave 3B — Commit 3f: one-shot sync from Home toggle. After first sync,
  // manual chip override is preserved for the rest of the session.
  const shoppingChipSyncedRef = useRef(false);
  useEffect(() => {
    if (shoppingChipSyncedRef.current || shoppingLoc.loading) return;
    setShoppingChipMode(shoppingLoc.enabled ? "nearby" : "online");
    shoppingChipSyncedRef.current = true;
  }, [shoppingLoc.loading, shoppingLoc.enabled]);
  const showShoppingUx =
    !isGuest && !!userId && shoppingLoc.enabled && shoppingLoc.isFresh;
  const [storesSheetItem, setStoresSheetItem] = useState<{
    item: Partial<OutfitItemOfflineShopping>;
    itemName: string | null;
    brand: string | null;
  } | null>(null);
  const offlineHeroItem = useMemo<Partial<OutfitItemOfflineShopping> | null>(
    () => {
      if (!showShoppingUx || !heroItem) return null;
      const row = items.find((i) => i.id === heroItem.id);
      if (!row) return null;
      return {
        offline_shopping_tier_1: row.offline_shopping_tier_1 ?? null,
        offline_shopping_tier_2: row.offline_shopping_tier_2 ?? null,
        offline_shopping_source: row.offline_shopping_source ?? null,
        offline_shopping_brand: row.offline_shopping_brand ?? null,
        offline_shopping_resolved_at: row.offline_shopping_resolved_at ?? null,
      };
    },
    [showShoppingUx, heroItem, items],
  );





  // Lift derived state up to parent so it survives unmount on tab navigation.
  useEffect(() => { onLookbookUrlChange?.(lookbookImageUrl); }, [lookbookImageUrl, onLookbookUrlChange]);
  useEffect(() => { onOutfitDirtyChange?.(outfitDirty); }, [outfitDirty, onOutfitDirtyChange]);

  // True only when every detected item has its products + tiers populated.
  const allItemsReady = useMemo(
    () =>
      items.length > 0 &&
      items.every((it) => it.products != null && it.tiers != null),
    [items],
  );

  // Derive contextual centre-button config and lift to parent for TabBar.
  useEffect(() => {
    if (!onCentreButtonChange) return;
    // Sample mode renders its own sticky CTA — suppress the centre button.
    if (isSample) {
      onCentreButtonChange(null);
      return;
    }
    // Suppress the centre button until product cards have finished loading,
    // but only before the first generation — never hide regenerate mid-session.
    if (!hasGenerated && !allItemsReady) {
      onCentreButtonChange({ state: "waiting", label: "waiting", onTap: () => {}, disabled: true });
      return;
    }
    if (isLookbookLoading) {
      onCentreButtonChange({
        state: "loading",
        label: hasGenerated ? "Regenerating…" : "Creating your look…",
        onTap: () => {},
        disabled: true,
      });
      return;
    }
    if (isSwapping) {
      onCentreButtonChange({ state: "swapping", label: "swapping", onTap: () => {}, disabled: true });
      return;
    }
    // Guest who has reached the daily lookbook limit → locked / sign-up CTA.
    if (isGuest && typeof window !== "undefined") {
      const today = new Date().toISOString().slice(0, 10);
      const storedDate = localStorage.getItem("lookbook_guest_date");
      const storedCount = parseInt(localStorage.getItem("lookbook_guest_count") ?? "0", 10);
      const count = storedDate === today ? storedCount : 0;
      if (count >= 10) {
        onCentreButtonChange({
          state: "locked",
          label: "sign up",
          onTap: () => onRequestSignup("lookbook_limit"),
          disabled: false,
        });
        return;
      }
    }
    const needsGeneration = !hasGenerated || outfitDirty;
    if (!needsGeneration) {
      onCentreButtonChange({ state: "done", label: "done", onTap: () => {}, disabled: true });
      return;
    }
    onCentreButtonChange({
      state: "regenerate",
      label: "Regenerate look",
      onTap: () => setRegenerateNonce((n) => n + 1),
      disabled: false,
    });
  }, [
    isLookbookLoading,
    isSwapping,
    hasGenerated,
    outfitDirty,
    allItemsReady,
    isGuest,
    isSample,
    onCentreButtonChange,
    onRequestSignup,
  ]);

  // Clear centre-button config when Results unmounts.
  useEffect(() => {
    return () => {
      onCentreButtonChange?.(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    onLookbookReadyChange?.(!!lookbookImageUrl);
  }, [lookbookImageUrl, onLookbookReadyChange]);

  // GTM: fire analysis_completed exactly once per outfit, the moment LB1's
  // image URL first becomes renderable. Covers both the realtime path
  // (authenticated users: lookbookImageUrls[0]) and the poll/invoke path
  // (guests + handleLookbookGenerated → lookbookImageUrl scalar), since
  // lb1Url is derived from both. Outfit-scoped ref guards against re-fires
  // on re-render and resets naturally when the user starts a new outfit.
  const lb1PinFiredFor = useRef<string | null>(null);
  useEffect(() => {
    if (!lb1Url || !outfitId) return;
    if (lb1PinFiredFor.current === outfitId) return;
    lb1PinFiredFor.current = outfitId;
    pushDataLayer({ event: "analysis_completed" });
    // PQ-3b: notify parent so guest search_count can increment (once per outfit).
    onLb1Success?.(outfitId);
  }, [lb1Url, outfitId, onLb1Success]);


  // Reset conflict state when a new outfit (new analysis) loads.
  // Note: outfitDirty is NOT reset here — it's seeded from initialOutfitDirty on mount
  // and the parent component resets the lifted value when starting a new analysis.
  useEffect(() => {
    setGenderConflict(null);
    setSwitchGenderNonce(0);
    setTemporaryGenderOverride(null);
  }, [outfitId]);

  // Reset lb2 state when outfit changes. LB3 state is derived from `meta`
  // (LB3-DISPLAY-RECOVERY-1) — no explicit reset needed.
  useEffect(() => {
    setLookbookImageUrls(null);
    lb2UrlsLoaded.current = false;
    setLb2Loading(false);
    setLb2TimedOut(false);
    setLb2Items(null);
    lb3OutcomeRenderedRef.current = null;
  }, [outfitId]);

  // Recovery safety net: if outfit was recovered and pipeline already
  // completed but no image exists, ensure poll/realtime pick it up.
  useEffect(() => {
    if (!outfitId || isGuest) return;
    const timer = setTimeout(async () => {
      try {
        const { data: readyCheck } = await (supabase.from("outfits") as any)
          .select("lb2_ready,lookbook_lb2_url,lookbook_lb3_url")
          .eq("id", outfitId)
          .maybeSingle();
        if (!readyCheck) return;
        if (
          (readyCheck as any).lb2_ready &&
          !(readyCheck as any).lookbook_lb2_url
        ) {
          debugLog("[recovery] lb2 ready but no image — triggering generation");
          setLb2Loading(true);
        }
        // LB3-DISPLAY-RECOVERY-1: LB3 readiness is now sourced from
        // resultsMeta polling; no manual recovery setter needed.
      } catch (err) {
        console.error("[recovery] readiness check failed:", err);
      }
    }, 1000);
    return () => clearTimeout(timer);
  }, [outfitId, isGuest, userId]);


  // Realtime: listen for lookbook_image_urls on the outfits row.
  useEffect(() => {
    if (!outfitId) return;
    const channel = supabase
      .channel(`outfits_lb_urls:${outfitId}`)
      .on(
        "postgres_changes",
        { event: "UPDATE", schema: "public", table: "outfits", filter: `id=eq.${outfitId}` },
        (payload) => {
          // F37.URL-UX: hero may land after the early transition to results.
          // Rehydrate heroItemId when the outfits row reports it.
          const newHero = (payload.new as { hero_item_id?: string | null }).hero_item_id;
          if (typeof newHero === "string" && newHero.length > 0) {
            setHeroItemId(newHero);
          }
          debugLog(
            "Realtime outfits fired:",
            "lb2_ready:",
            (payload.new as { lb2_ready?: boolean })
              .lb2_ready,
            "lb2Url:", lb2Url,
            "lb2Loading:", lb2Loading,
            "lookbook_image_urls:",
            (payload.new as {
              lookbook_image_urls?: unknown
            }).lookbook_image_urls
          );
          // F37.3 Phase A — Fix 2: preserve index positions so LB2's URL
          // never lands at slot [0] (where the LB1 card reads from).
          // Previously, filtering out empty values compressed [null, lb2Url]
          // to [lb2Url], causing LB1 to display LB2's image.
          const raw = (payload.new as { lookbook_image_urls?: unknown }).lookbook_image_urls;
          const positional = Array.isArray(raw)
            ? (raw as unknown[]).map((v) => (typeof v === "string" ? v : ""))
            : null;
          if (positional && positional.some((v) => v.length > 0)) {
            setLookbookImageUrls(positional);
            lb2UrlsLoaded.current = true;
            if (positional[1]) setLb2Loading(false);
          }
          const rawLb2Url = (payload.new as {
            lookbook_lb2_url?: string;
          }).lookbook_lb2_url;
          if (typeof rawLb2Url === "string" && rawLb2Url.length > 0) {
            setLb2UrlDirect(rawLb2Url);
            setLookbookImageUrls((prev) => {
              const lb1 = prev?.[0] ?? lb1Url ?? "";
              // Keep slot 0 empty (not LB2!) when LB1 is missing.
              return [lb1, rawLb2Url];
            });
            setLb2Loading(false);
            lb2UrlsLoaded.current = true;
          }
          const lookbookStatus = (payload.new as { lookbook_status?: string | null }).lookbook_status;
          if (lookbookStatus === "failed") {
            setLb1Failed(true);
          } else if (lookbookStatus === "ready") {
            setLb1Failed(false);
          }
          const rawItems = (payload.new as { lb2_items?: unknown }).lb2_items;
          if (rawItems) {
            setLb2Items(rawItems as typeof lb2Items);
          }
        },
      )
      .subscribe();
    return () => {
      void supabase.removeChannel(channel);
    };
  }, [outfitId]);

  // LB2 timeout: if LB1 has rendered but LB2 has not arrived in 45s, surface fallback.
  useEffect(() => {
    if (lb2Url) {
      setLb2TimedOut(false);
      return;
    }
    if (!lb2UrlsLoaded.current) return;
    if (!lb1Url) return;
    const timer = setTimeout(() => {
      if (!lb2Url) setLb2TimedOut(true);
    }, 45000);
    return () => clearTimeout(timer);
  }, [lb1Url, lb2Url, lb2UrlsLoaded.current]);

  // Recover LB2 when tab returns from background (phone lock, tab switch).
  // Realtime + polling can be suspended by the OS, leaving lb2Loading stuck.
  useEffect(() => {
    if (!outfitId) return;
    const handleVisibilityChange = async () => {
      if (document.visibilityState !== "visible") return;
      if (lb2Url) return;
      try {
        const { data } = await (supabase.from("outfits") as any)
          .select("lookbook_lb2_url, lookbook_image_urls, lb2_ready")
          .eq("id", outfitId)
          .maybeSingle();
        if (!data) return;
        const freshLb2Url =
          (data as any).lookbook_lb2_url ??
          (data as any).lookbook_image_urls?.[1] ??
          null;
        if (freshLb2Url) {
          debugLog("visibilitychange: recovered LB2 URL");
          setLb2UrlDirect(freshLb2Url);
          setLb2Loading(false);
          const lb1UrlFresh = (data as any).lookbook_image_urls?.[0] ?? null;
          if (lb1UrlFresh) {
            setLookbookImageUrls([lb1UrlFresh, freshLb2Url]);
          }
          return;
        }
      } catch (err) {
        console.error("visibilitychange check failed:", err);
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [outfitId, lb2Url, isGuest]);


  const resultsModelSetup = modelProfile
    ? {
        height: modelProfile.model_height ?? null,
        body_shape: modelProfile.model_body_shape ?? null,
        body_size: modelProfile.model_body_size ?? null,
        skin_tone: modelProfile.model_skin_tone ?? null,
        hair: modelProfile.model_hair ?? null,
        gender: modelProfile.model_gender ?? null,
        variation: modelProfile.model_preferred_variation ?? undefined,
        variation_description: modelProfile.model_preferred_variation
          ? (VARIATION_DESCRIPTIONS[modelProfile.model_preferred_variation as VariationKey]
              ?? "Clean white studio background, editorial fashion photography")
          : "Clean white studio background, editorial fashion photography",
      }
    : null;

  // Gender mismatch banner: profile gender vs detected outfit gender.
  const [genderMismatchDismissed, setGenderMismatchDismissed] = useState(false);
  const profileGender =
    (modelProfile?.model_gender as string | null | undefined) ??
    (sizeProfile as { size_gender?: string | null } | null)?.size_gender ??
    null;
  const detectedGenderFromAnalysis = detectedGender;
  const showGenderMismatchBanner =
    !genderMismatchDismissed &&
    profileGender !== null &&
    profileGender !== "non-binary" &&
    detectedGenderFromAnalysis !== null &&
    detectedGenderFromAnalysis !== "unisex" &&
    !(
      (profileGender === "women" || profileGender === "woman") &&
      detectedGenderFromAnalysis === "women"
    ) &&
    !(
      (profileGender === "men" || profileGender === "man") &&
      detectedGenderFromAnalysis === "men"
    );
  const genderMismatchMessage =
    profileGender === "men" || profileGender === "man"
      ? "This looks like a women's outfit — we're finding the men's version of each piece."
      : "This looks like a men's outfit — we're finding the women's version of each piece.";

  const regenerateLb2Only = async (opts?: { isManualRetry?: boolean; modelSetupOverride?: unknown }) => {
    if (!outfitId) return;
    setLb2Loading(true);
    setLb2TimedOut(false);
    if (opts?.isManualRetry) {
      setLb2RegenerateNonce((n) => n + 1);
    }
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const lb2Gender = (modelProfile?.model_gender as "men" | "women" | "unisex" | null | undefined) ?? detectedGender;
      debugLog(
        "Invoking lookbook with gender:",
        lb2Gender,
        "model_gender:",
        modelProfile?.model_gender ?? null,
        "size_gender:",
        (sizeProfile as { size_gender?: string | null } | null)?.size_gender ?? null,
      );
      await supabase.functions.invoke("generate-lookbook2", {
        body: {
          outfit_id: outfitId,
          variant: "lb2_only",
          items: tierItemsFor("mid").map((e) => e.item),
          gender: lb2Gender,
          model_setup: opts?.modelSetupOverride ?? guestModelSetupOverrideRef.current ?? resultsModelSetup,
          is_guest: isGuest,
          guest_session_id: guestSessionId,
        },
        headers: session?.access_token
          ? { Authorization: `Bearer ${session.access_token}` }
          : {},
      });
    } catch (e) {
      console.error("lb2 regen failed:", e);
      setLb2Loading(false);
      throw e;
    }
  };



  // ──────────────────────────────────────────────────────────────────────────
  // LB3 — Style with your wardrobe
  // ──────────────────────────────────────────────────────────────────────────
  const regenerateLb3Only = async (opts?: { modelSetupOverride?: unknown; allowGuest?: boolean }) => {
    if (!outfitId) return;
    if (isGuest && !opts?.allowGuest) return;
    // LB3-DISPLAY-RECOVERY-1: URL-aware short-circuit. If the row already
    // has an LB3 url, the previous dispatch landed — let resultsMeta
    // hydrate the UI on its next 5s tick instead of re-invoking.
    try {
      const { data: existing } = await supabase
        .from("outfits")
        .select("lookbook_lb3_url")
        .eq("id", outfitId)
        .maybeSingle();
      if (existing && (existing as { lookbook_lb3_url?: string | null }).lookbook_lb3_url) {
        debugLog("[lb3] regen short-circuit: url already present in DB");
        return;
      }
    } catch (e) {
      debugLog("[lb3] regen pre-fetch failed; proceeding to invoke", e);
    }
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const lb3Gender =
        (modelProfile?.model_gender as
          | "men"
          | "women"
          | "unisex"
          | null
          | undefined) ?? detectedGender;
      debugLog("[lb3] invoking with gender:", lb3Gender);
      await supabase.functions.invoke("generate-lookbook3", {
        body: {
          outfit_id: outfitId,
          gender: lb3Gender,
          model_setup: opts?.modelSetupOverride ?? guestModelSetupOverrideRef.current ?? resultsModelSetup,
          is_guest: isGuest,
          guest_session_id: guestSessionId,
          user_id: session?.user?.id ?? null,
        },
        headers: session?.access_token
          ? { Authorization: `Bearer ${session.access_token}` }
          : {},
      });
    } catch (e) {
      console.error("lb3 regen failed:", e);
      throw e;
    }
  };


  // ──────────────────────────────────────────────────────────────────────────
  // Guest progressive onboarding orchestrator. Wires the CTA on the
  // PurchaseConfidenceCard to all three lookbook regens, using the guest's
  // collected height/body_shape/body_size as model_setup.
  // ──────────────────────────────────────────────────────────────────────────
  const handleGuestRegenerate = useCallback(
    async (
      model: { height: string | null; body_shape: string | null; body_size: string | null },
      _size: string | null,
    ) => {
      if (!isGuest) return;
      if (!outfitId) return;
      const guestGender =
        detectedGender === "men" ? "men" : detectedGender === "women" ? "women" : "women";
      const override: Record<string, unknown> = {
        height: model.height ?? null,
        body_shape: model.body_shape ?? null,
        body_size: model.body_size ?? null,
        skin_tone: null,
        hair: null,
        gender: guestGender,
        variation: "editorial_studio",
        variation_description:
          "Clean white studio background, editorial fashion photography",
      };

      // Snapshot current URLs so the watcher can detect new ones.
      guestRegenSnapshotRef.current = {
        lb1: lb1Url,
        lb2: lb2Url,
        lb3: lb3Url,
        at: Date.now(),
      };
      setGuestModelSetupOverride(override);
      setGuestRegenError(null);
      setGuestRegenStatus("loading");
      setGuestRegenBannerDismissed(false);

      trackEvent("confidence_card_regen_started", {
        outfit_id: outfitId,
        user_type: "guest",
      });

      // Bump LB1 nonce → LookbookGenerator picks up guestModelSetupOverride.
      setRegenerateNonce((n) => n + 1);

      const results = await Promise.allSettled([
        regenerateLb2Only({ modelSetupOverride: override, isManualRetry: true }),
        regenerateLb3Only({ modelSetupOverride: override, allowGuest: true }),
      ]);
      const failed = results.filter((r) => r.status === "rejected");
      if (failed.length === results.length) {
        // Total failure (LB1 still has its own path; surface partial error).
        setGuestRegenStatus("error");
        setGuestRegenError("Couldn't update your lookbooks. Please try again.");
        trackEvent("confidence_card_regen_failed", {
          outfit_id: outfitId,
          user_type: "guest",
          reason: "all_failed",
        });
      }
      // success transition happens in watcher effect below.
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isGuest, outfitId, detectedGender, lb1Url, lb2Url, lb3Url],
  );

  // Watch URL updates to flip guest regen → success / timeout.
  useEffect(() => {
    if (guestRegenStatus !== "loading") return;
    const snap = guestRegenSnapshotRef.current;
    if (!snap) return;
    const lb1Changed = lb1Url && lb1Url !== snap.lb1;
    const lb2Changed = lb2Url && lb2Url !== snap.lb2;
    const lb3Changed = lb3Url && lb3Url !== snap.lb3;
    if (lb1Changed && lb2Changed && lb3Changed) {
      setGuestRegenStatus("success");
      trackEvent("confidence_card_regen_completed", {
        outfit_id: outfitId,
        user_type: "guest",
        partial: false,
        duration_ms: Date.now() - snap.at,
      });
    }
  }, [guestRegenStatus, lb1Url, lb2Url, lb3Url, outfitId]);

  // Timeout safety: after 90s, treat as partial-success if at least one URL
  // changed, otherwise error.
  useEffect(() => {
    if (guestRegenStatus !== "loading") return;
    const t = setTimeout(() => {
      const snap = guestRegenSnapshotRef.current;
      if (!snap) return;
      const anyChanged =
        (lb1Url && lb1Url !== snap.lb1) ||
        (lb2Url && lb2Url !== snap.lb2) ||
        (lb3Url && lb3Url !== snap.lb3);
      if (anyChanged) {
        setGuestRegenStatus("success");
        trackEvent("confidence_card_regen_completed", {
          outfit_id: outfitId,
          user_type: "guest",
          partial: true,
          duration_ms: Date.now() - snap.at,
        });
      } else {
        setGuestRegenStatus("error");
        setGuestRegenError("Taking longer than expected. Try again?");
        trackEvent("confidence_card_regen_failed", {
          outfit_id: outfitId,
          user_type: "guest",
          reason: "timeout",
        });
      }
    }, 90000);
    return () => clearTimeout(t);
  }, [guestRegenStatus, lb1Url, lb2Url, lb3Url, outfitId]);


  // LB3-DISPLAY-RECOVERY-1: 75s watchdog + bespoke 120s poller retired.
  // lb3Url / lb3Outcome / lb3_owned_items / lb3_gap_items now derive from
  // resultsMeta (useOutfitMetaPolling) — single source of truth.
  // lb3TimedOut derives from resultsMeta.analysis_status === "failed".



  // Initial outfits row read: hydrate lb2_items and auto-fire lb2_only
  // generation if lb2_ready is already true but LB2 hasn't been generated.
  useEffect(() => {
    if (!outfitId) return;
    let cancelled = false;
    let attempts = 0;
    const MAX_ATTEMPTS = 60; // ~180s; generation observed at ~122s, needs headroom
    const POLL_INTERVAL_MS = 3000;
    const poll = async () => {
      if (cancelled) return;
      attempts++;
      let outfitRow: {
        lb2_items?: unknown;
        lb2_ready?: boolean | null;
        lookbook_image_urls?: unknown;
        lookbook_lb2_url?: string | null;
      } | null = null;

      const direct = await supabase
        .from("outfits")
        .select("lb2_items, lb2_ready, lookbook_image_urls, lookbook_lb2_url")
        .eq("id", outfitId)
        .maybeSingle();
      outfitRow = direct.data ?? null;

      // Guests can't read the outfits row directly under RLS — fall back to
      // the by-id guest RPC (mirrors the LB1 recovery path).
      if (!outfitRow) {
        const { data: guestRows } = await (supabase as any).rpc("get_guest_outfit", {
          p_id: outfitId,
        });
        outfitRow = Array.isArray(guestRows) && guestRows.length > 0
          ? guestRows[0]
          : null;
      }

      // Sample outfits aren't guest rows — fall back to the sample RPC
      // (allows the guest sample flow to read persisted LB urls without
      // owning the row).
      if (!outfitRow) {
        const { data: sampleRows } = await (supabase as any).rpc("get_sample_outfit", {
          p_id: outfitId,
        });
        outfitRow = Array.isArray(sampleRows) && sampleRows.length > 0
          ? sampleRows[0]
          : null;
      }


      if (cancelled || !outfitRow) return;
      const rawItems = (outfitRow as { lb2_items?: unknown }).lb2_items;
      if (rawItems) {
        setLb2Items(rawItems as typeof lb2Items);
      }
      const urls = (outfitRow as { lookbook_image_urls?: unknown }).lookbook_image_urls;
      const existingLb2 = Array.isArray(urls)
        ? (urls as unknown[])[1]
        : undefined;
      const rawLb2Url = (outfitRow as {
        lookbook_lb2_url?: string
      }).lookbook_lb2_url;
      if (typeof rawLb2Url === "string" && rawLb2Url.length > 0) {
        debugLog(
          "Poll found lb2:",
          rawLb2Url,
        );
        debugLog(
          "lb2 poll: lb2Url found:",
          rawLb2Url.slice(0, 60)
        );
        // Always pin LB2 to index [1] — never [0]. If LB1 hasn't populated [0]
        // yet (common for guests, who have no realtime channel and rely solely
        // on this poll), the old code wrote LB2 into [0] (LB1's slot), leaving
        // LB2's slot empty and showing LB2's image in LB1's card.
        setLookbookImageUrls((prev) => {
          const next = Array.isArray(prev) ? [...prev] : [];
          next[1] = rawLb2Url; // LB2 always lives at [1]
          return next;
        });
        // Belt-and-braces: set lb2UrlDirect too, so the LB2 card's fallback
        // (lb2Url = lookbookImageUrls[1] ?? lb2UrlDirect) can surface the image
        // even if the array is momentarily sparse. Mirrors the realtime path.
        setLb2UrlDirect(rawLb2Url);
        setLb2Loading(false);
        lb2UrlsLoaded.current = true;
        return;
      }
      const lb2Ready = (outfitRow as { lb2_ready?: boolean }).lb2_ready === true;
      if (attempts >= MAX_ATTEMPTS) {
        debugLog("lb2 poll: max attempts reached, surfacing timeout");
        setLb2Loading(false);
        setLb2TimedOut(true);
        return;
      }
      debugLog("lb2 poll: attempt", attempts, "lb2_ready:", lb2Ready, "waiting...");
      setTimeout(poll, POLL_INTERVAL_MS);
    };
    void poll();
    return () => {
      cancelled = true;
    };
  }, [outfitId, isGuest, lb2RegenerateNonce]);




  const handleLookbookGenerated = (url: string | null) => {
    setLookbookImageUrl(url);
    setPrimaryLoading(false);
    if (url) {
      setHasGenerated(true);
      setOutfitDirty(false);
    }
  };

  useEffect(() => {
    setItems(initialItems);
    const opt = initialItems.find((i) => i.item_type === "optional");
    if (opt) setIsLayerAdded(!!opt.is_layer_added);
    setCheaperOverrides({});
    setCheaperTotal(null);
    setOriginalTotal(null);
    setCheaperApplied(false);
    setCheaperNoneFound(false);
    setCheaperBannerDismissed(false);
  }, [initialItems]);


  // Subscribe to live updates while the edge function fills in shopping results.
  // IMPORTANT: depend only on outfitId — depending on `items` would tear down
  // and re-subscribe on every update, dropping events in between.
  useEffect(() => {
    if (!outfitId) return;

    setItemColourOverrides({});
    let cancelled = false;


    const mergeRow = (updated: DetectedItem) => {
      // Upsert: update if present, append if new. Required for URL flow's
      // early-transition gate (F37.URL-UX) where items list starts empty
      // and is populated by INSERT events.
      setItems((prev) => {
        const idx = prev.findIndex((it) => it.id === updated.id);
        if (idx === -1) {
          const next = [...prev, updated];
          next.sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
          return next;
        }
        const next = prev.slice();
        next[idx] = { ...next[idx], ...updated };
        return next;
      });
    };

    const channel = supabase
      .channel(`detected_items:${outfitId}`)
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "detected_items", filter: `outfit_id=eq.${outfitId}` },
        (payload) => mergeRow(payload.new as DetectedItem),
      )
      .subscribe();

    // Backstop: poll the DB while anything is still pending, in case a realtime
    // event fired between the initial fetch and channel subscribe, or got dropped.
    // Guests have no anon SELECT policy on detected_items — if the direct read
    // returns empty/null, fall back to the by-outfit-id guest RPC.
    const poll = async () => {
      let data: DetectedItem[] | null = null;
      const direct = await supabase
        .from("detected_items")
        .select("*")
        .eq("outfit_id", outfitId)
        .order("position", { ascending: true });
      data = (direct.data as DetectedItem[] | null) ?? null;
      if ((!data || data.length === 0) && isGuest) {
        const { data: guestRows } = await (supabase as any).rpc("get_guest_outfit_items", {
          p_outfit_id: outfitId,
        });
        data = (guestRows as DetectedItem[] | null) ?? null;
      }
      if (cancelled || !data || data.length === 0) return;
      // Upsert each fetched row (URL flow may start with prev=[]).
      setItems((prev) => {
        const byId = new Map(prev.map((it) => [it.id, it]));
        for (const row of data as DetectedItem[]) {
          const existing = byId.get(row.id);
          byId.set(row.id, existing ? { ...existing, ...row } : row);
        }
        return Array.from(byId.values()).sort(
          (a, b) => (a.position ?? 0) - (b.position ?? 0),
        );
      });
    };

    // Cap empty polls to avoid infinite ticks if analysis never produces items
    // (e.g. failed URL). 45 ticks × 2s = 90s — beyond which the outfit is
    // either marked failed or the user has navigated away.
    let emptyTicks = 0;
    const MAX_EMPTY_TICKS = 45;
    const interval = setInterval(() => {
      setItems((prev) => {
        if (prev.length === 0) {
          emptyTicks += 1;
          if (emptyTicks >= MAX_EMPTY_TICKS) {
            clearInterval(interval);
          } else {
            void poll();
          }
          return prev;
        }
        const stillPending = prev.some((it) => it.products == null || it.tiers == null);
        if (!stillPending) {
          clearInterval(interval);
        } else {
          void poll();
        }
        return prev;
      });
    }, 2000);

    // Kick off an immediate refetch to catch updates that landed before subscribe.
    void poll();

    return () => {
      cancelled = true;
      clearInterval(interval);
      supabase.removeChannel(channel);
    };
  }, [outfitId, isGuest]);

  const trackProductClick = (item: DetectedItem, product: ShoppingProduct) => {
    // GTM: Pinterest buy_tap. Push BEFORE any navigation so the redirect
    // doesn't drop the event. Include value + currency only when a numeric
    // price is available; omit both fields otherwise.
    const price = typeof product.extracted_price === "number" ? product.extracted_price : null;
    const utms = getStoredUtms();
    pushDataLayer(
      price != null
        ? { event: "buy_tap", value: price, currency: "GBP", ...utms }
        : { event: "buy_tap", ...utms },
    );
    trackEvent("product_clicked", {
      item_name: item.name,
      retailer: product.source || "unknown",
      price: product.price ?? null,
      mode,
    });
    if (!userId) return;
    void supabase.from("affiliate_clicks").insert({
      user_id: userId,
      outfit_id: outfitId,
      item_name: item.name,
      retailer: product.source || "unknown",
      search_query: item.search_query ?? item.name,
      item_category: item.category ?? null,
      product_url: product.link ?? null,
      detected_item_id: item.id ?? null,
    });
  };

  const trackManualSearch = (item: DetectedItem) => {
    if (!userId) return;
    void supabase.from("affiliate_clicks").insert({
      user_id: userId,
      outfit_id: outfitId,
      item_name: item.name,
      retailer: "ASOS",
      search_query: item.search_query ?? item.name,
      item_category: item.category ?? null,
      product_url: null,
      detected_item_id: item.id ?? null,
    });
  };

  const manualSearchUrl = (item: DetectedItem) =>
    `https://www.asos.com/search/?q=${encodeURIComponent(item.search_query ?? item.name)}`;

  const optionalItem = items.find((it) => it.item_type === "optional") ?? null;

  // Items considered for tier totals and Shop This Look.
  // The optional outerwear is only included when the user has expanded the layer card.
  const tierEligibleItems = items.filter((it) => {
    if (it.item_type === "optional") return isLayerAdded;
    return it.item_type !== "owned";
  });

  const tiersLoading = tierEligibleItems.some((it) => it.tiers == null);

  // Active tier — lifted from PurchaseConfidenceCard so the LB1 items list
  // flips together with the hero. PCC owns the setter via onTierChange.
  const [activeTier, setActiveTier] = useState<TierKey>("mid");
  // Reset when outfit changes (heroItem null = no outfit yet).
  useEffect(() => {
    if (items.length === 0) setActiveTier("mid");
  }, [outfitId, items.length]);
  // Fire analytics when the active tier flips (skip the initial mount).
  const prevTierRef = useRef<TierKey>("mid");
  const tierDidMountRef = useRef(false);
  useEffect(() => {
    const from = prevTierRef.current;
    if (from === activeTier) return;
    const itemsBefore = tierEligibleItems.filter((it) => it.tiers?.[from] != null).length;
    const itemsAfter = tierEligibleItems.filter((it) => it.tiers?.[activeTier] != null).length;
    trackEvent("lb1_items_tier_changed", {
      outfit_id: outfitId,
      from_tier: from,
      to_tier: activeTier,
      items_before_count: itemsBefore,
      items_after_count: itemsAfter,
      items_dropped_count: Math.max(0, itemsBefore - itemsAfter),
    });
    prevTierRef.current = activeTier;
    // Wave 3B — Commit 3k Part C: on user-initiated tier tap, scroll the
    // viewport to the LB1 shopping list so the newly selected alternative
    // is visible. Gate on tierDidMountRef to skip the initial mount + the
    // programmatic reset when the outfit changes (items.length === 0).
    if (!tierDidMountRef.current) {
      tierDidMountRef.current = true;
      return;
    }
    requestAnimationFrame(() => {
      lb1FirstRowRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTier]);

  const parsePrice = (s?: string | null): number => {
    if (!s) return 0;
    const m = s.replace(/,/g, "").match(/(\d+(\.\d+)?)/);
    return m ? parseFloat(m[1]) : 0;
  };

  // SERP-MATCH-FIX-3a — fall back to `your_pick` when the requested tier
  // slot is empty. Prevents items from being silently dropped from the
  // itemlist when the backend low-confidence refill only populated
  // your_pick. Hero `your_pick_is_caveat` already surfaces the "Closest
  // we found" badge on PurchaseConfidenceCard.
  const tierItemsFor = (tier: TierKey): { item: DetectedItem; product: ShoppingProduct }[] =>
    tierEligibleItems
      .map((it) => ({ item: it, product: it.tiers?.[tier] ?? (it.tiers as TierProducts & { your_pick?: ShoppingProduct | null })?.your_pick ?? null }))
      .filter((x): x is { item: DetectedItem; product: ShoppingProduct } => !!x.product);


  // Display list for "Shop This Look". In instore mode each entry is either a
  // kept product (matches a nearby store, or has an override) or null (filtered
  // out → render fallback card). In online mode this is identical to tierItemsFor.
  type DisplayEntry = {
    item: DetectedItem;
    product: ShoppingProduct | null;
    overBudget: boolean;
    overBudgetBy: number;
    onlineFallback: boolean;
  };
  const tierItemsForDisplay = (tier: TierKey): DisplayEntry[] => {
    return tierItemsFor(tier).map((e) => ({
      ...e,
      overBudget: false,
      overBudgetBy: 0,
      onlineFallback: false,
    }));
  };



  const [localSaved, setLocalSaved] = useState(isSaved);
  useEffect(() => { setLocalSaved(isSaved); }, [isSaved]);

  const handleBookmark = () => {
    if (localSaved) return;
    setLocalSaved(true);
    onSave();
  };

  const totalPairings = items.filter((i) => i.item_type === "pairing").length || 1;

  const openSwap = (item: DetectedItem) => {
    const def = pickDefaultSwap(item.category, item.name);
    setSwapState((s) => ({ ...s, [item.id]: { selected: def, loading: false } }));
  };
  const cancelSwap = (id: string) => {
    setSwapState((s) => {
      const n = { ...s };
      delete n[id];
      return n;
    });
  };
  const selectSwap = (id: string, opt: string) => {
    setSwapState((s) => ({ ...s, [id]: { ...(s[id] ?? { selected: opt, loading: false }), selected: opt } }));
  };
  const confirmSwap = async (item: DetectedItem) => {
    const st = swapState[item.id];
    if (!st || !st.selected) return;
    setSwapState((s) => ({ ...s, [item.id]: { ...st, loading: true } }));
    // mark product placeholders
    setItems((prev) => prev.map((it) => it.id === item.id ? { ...it, products: null, tiers: null, name: st.selected } : it));

    let sizeProfile: any = null;
    if (userId) {
      const { data: up } = await (supabase.from("profiles") as any)
        .select("model_gender, size_gender, size_uk_number, size_letter, size_shoe, size_top_letter, size_top_uk_number, size_trouser_waist, size_trouser_length, size_shoe_mens")
        .eq("id", userId).maybeSingle();
      if (up) {
        sizeProfile = {
          gender: up.model_gender ?? up.size_gender ?? null,
          uk_number: up.size_uk_number, letter_size: up.size_letter,
          shoe_womens: up.size_shoe, top_letter: up.size_top_letter,
          trouser_waist: up.size_trouser_waist, trouser_length: up.size_trouser_length,
          shoe_mens: up.size_shoe_mens,
        };
      }
    }

    const excludedItems = Array.isArray(item.products)
      ? (item.products as Array<{ link?: string }>)
          .map((p) => p?.link)
          .filter((l): l is string => !!l)
      : [];

    const { data, error, quotaExceeded } = await invokeGenerator<any>("swap-pairing-item", {
      source: "swap-type",
      body: {
        swap_type: st.selected,
        original_item: item,
        item_id: item.id,
        occasion: occasion ?? "Casual Day Out",
        gender: detectOutfitGender(items),
        user_budget: userBudget,
        size_profile: sizeProfile,
        total_pairings: totalPairings,
        excluded_items: excludedItems,
      },
    });
    if (quotaExceeded) {
      setSwapState((s) => ({ ...s, [item.id]: { ...st, loading: false } }));
      return;
    }
    if (error || !data) {
      toast.error("Swap failed. Please try again.");
      setSwapState((s) => ({ ...s, [item.id]: { ...st, loading: false } }));
      return;
    }

    setItems((prev) => prev.map((it) => it.id === item.id ? {
      ...it,
      name: (data as any).name ?? st.selected,
      reason: (data as any).reason ?? it.reason,
      search_query: (data as any).search_query ?? it.search_query,
      products: (data as any).products ?? [],
      tiers: (data as any).tiers ?? it.tiers,
    } : it));
    setSwappedFlag((f) => ({ ...f, [item.id]: st.selected }));
    setOutfitDirty(true);
    trackEvent("swap_used", {
      category: item.category,
      from: item.name,
      to: st.selected,
      mode,
    });
    cancelSwap(item.id);
  };

  const toggleLayer = (next: boolean) => {
    setIsLayerAdded(next);
    
    if (optionalItem) {
      void (supabase.from("detected_items") as any)
        .update({ is_layer_added: next })
        .eq("id", optionalItem.id);
    }
    if (next) {
      trackEvent("layer_added", {
        outerwear_type: optionalItem?.name ?? null,
      });
    }
  };



  return (
    <>
    <ResultsTopNav
      issueNumber={issueNumber}
      isSaved={localSaved}
      onBack={onBack}
      onBookmarkTap={handleBookmark}
      hideBookmark={isSample}
    />
    {headerSlot}
    {isSample && (
      <div
        style={{
          background: "#FAECE7",
          borderBottom: "0.5px solid #E8E2D9",
          padding: "12px 18px",
          display: "flex",
          alignItems: "flex-start",
          gap: 12,
        }}
      >
        <div
          style={{
            width: 24,
            height: 24,
            borderRadius: 6,
            background: "#C84B2F",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#FFFCF8",
            flexShrink: 0,
          }}
        >
          <i className="ti ti-sparkles" style={{ fontSize: 14 }} aria-hidden="true" />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 500, color: "#2C2820", lineHeight: "18px" }}>
            This is a sample outfit
          </div>
          <div style={{ fontSize: 11, color: "#6B6560", lineHeight: "16px", marginTop: 2 }}>
            Share from your favorite app to create your own
          </div>
        </div>
      </div>
    )}
    <div className="flex-1 flex flex-col px-[14px] pt-4" style={{ paddingBottom: isSample ? 160 : 120 }}>
      {/*
        Phase 1a.5-i — Results-mounted hero variants for url/text inputs.
        Gated by UNIFIED_PENDING (currently false → dead code). Flipped on
        in 1a.5-iii/iv when share + in-app sheet flows are rewired to
        instant-mount and Results learns to mount without an image_url.
        Visuals ported verbatim from UrlPendingResults.tsx (L63-113) and
        TextPendingResults.tsx (L61-131; skeleton rows L133-147 excluded).
        Keyframes are scope-renamed (`l2f-results-*`) to avoid colliding
        with the legacy pending screens while both coexist.
      */}
      {UNIFIED_PENDING && inputMethod === "url" && meta?.analysis_status !== "complete" && meta?.analysis_status !== "failed" ? (
        <>
          <style>{`
            @keyframes l2f-results-url-pending-pulse {
              0%, 100% { opacity: 1; }
              50% { opacity: 0.55; }
            }
            @keyframes l2f-results-url-pending-spin {
              to { transform: rotate(360deg); }
            }
          `}</style>
          <div
            style={{
              width: "100%",
              aspectRatio: "3 / 4",
              background: "#FFFCF8",
              border: "0.5px solid #E8E2D9",
              borderRadius: 20,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 14,
              padding: 24,
              textAlign: "center",
              animation: "l2f-results-url-pending-pulse 1.6s ease-in-out infinite",
            }}
          >
            <i
              className="ti ti-loader-2"
              style={{
                fontSize: 28,
                color: "#C84B2F",
                opacity: 0.7,
                animation: "l2f-results-url-pending-spin 1s linear infinite",
              }}
            />
            <div
              style={{
                fontSize: 19,
                fontStyle: "italic",
                fontFamily:
                  "var(--font-serif), 'Instrument Serif', Georgia, serif",
                color: "#2C2820",
                lineHeight: 1.2,
              }}
            >
              Fetching from the web…
            </div>
            <div
              className="l2f-type-caption"
              style={{
                color: "#6B6560",
                maxWidth: 240,
                lineHeight: 1.5,
              }}
            >
              Pulling the look from the page. This usually takes 5–15 seconds.
            </div>
          </div>
        </>
      ) : UNIFIED_PENDING && inputMethod === "text" && meta?.analysis_status !== "complete" && meta?.analysis_status !== "failed" ? (
        <>
          <style>{`
            @keyframes l2f-results-text-pending-pulse {
              0%, 100% { opacity: 1; }
              50% { opacity: 0.55; }
            }
            @keyframes l2f-results-text-pending-spin { to { transform: rotate(360deg); } }
          `}</style>
          {(() => {
            const td = textDescription ?? "";
            const preview = td.length > 80 ? td.slice(0, 77) + "…" : td;
            const chips = [occasion].filter((c): c is string => !!c);
            return (
              <div
                style={{
                  width: "100%",
                  aspectRatio: "3 / 4",
                  background: "#FAECE7",
                  border: "0.5px solid #E8E2D9",
                  borderRadius: 20,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 16,
                  padding: 28,
                  textAlign: "center",
                  animation: "l2f-results-text-pending-pulse 1.6s ease-in-out infinite",
                }}
              >
                <i
                  className="ti ti-loader-2"
                  style={{
                    fontSize: 28,
                    color: "#C84B2F",
                    opacity: 0.7,
                    animation: "l2f-results-text-pending-spin 1s linear infinite",
                  }}
                />
                <div
                  style={{
                    fontSize: 22,
                    fontStyle: "italic",
                    fontFamily:
                      "var(--font-serif), 'Instrument Serif', Georgia, serif",
                    color: "#2C2820",
                    lineHeight: 1.25,
                    maxWidth: 280,
                  }}
                >
                  “{preview}”
                </div>
                <div
                  className="l2f-type-caption"
                  style={{
                    color: "#6B6560",
                    maxWidth: 240,
                    lineHeight: 1.5,
                  }}
                >
                  Crafting your look…
                </div>
                {chips.length > 0 && (
                  <div style={{ display: "flex", gap: 6, flexWrap: "wrap", justifyContent: "center" }}>
                    {chips.map((c) => (
                      <span
                        key={c}
                        style={{
                          padding: "4px 10px",
                          borderRadius: 999,
                          background: "#FFFCF8",
                          fontSize: 11,
                          color: "#4A4540",
                          textTransform: "capitalize",
                          border: "0.5px solid #E8E2D9",
                        }}
                      >
                        {c}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            );
          })()}
        </>
      ) : inputMethod === "text" ? (
        <ReferenceImageCard
          variant="text"
          textDescription={textDescription ?? ""}
          occasion={occasion}
        />
      ) : img ? (
        <ReferenceImageCard
          imageUrl={img}
          onTap={() => openLightbox(img, "Your input", "reference")}
        />
      ) : null}



      {(() => {
        const ownedItemsList = items.filter((i) => i.item_type === "owned");
        if (mode !== "style_item" || ownedItemsList.length === 0) return null;

        return (
          <div className="mt-4">
            <p className="text-[12px] font-medium tracking-widest text-muted-foreground uppercase mb-2">
              You're wearing
            </p>
            <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1">
              {ownedItemsList.map((it) => (
                <div
                  key={it.id}
                  className="shrink-0 flex items-center gap-2 rounded-full bg-secondary pl-1 pr-3 py-1"
                  style={{ border: "0.5px solid var(--border)" }}
                >
                  <span
                    className="w-7 h-7 rounded-full shrink-0"
                    style={{
                      background: it.color_hex ?? "var(--muted)",
                      border: "0.5px solid var(--border)",
                    }}
                    aria-hidden
                  />
                  <span className="text-xs font-medium text-foreground capitalize whitespace-nowrap">
                    {it.name}
                  </span>
                </div>
              ))}
            </div>
          </div>
        );
      })()}

      {mode === "style_item" && (styleSummary || occasion) && (
        <div className="mt-3 flex flex-wrap gap-2">
          {styleSummary && (
            <span className="px-3 py-1 rounded-full text-xs font-medium text-white" style={{ background: CORAL }}>
              {styleSummary}
            </span>
          )}
          {occasion && (
            <span className="px-3 py-1 rounded-full text-xs font-medium bg-secondary text-foreground">
              {occasionEmoji(occasion)} {occasion}
            </span>
          )}
        </div>
      )}

      {showGenderMismatchBanner && (
        <div
          className="mt-3 flex items-start gap-3 rounded-md px-3 py-2 text-xs text-muted-foreground"
          style={{ background: "var(--muted)", border: "0.5px solid var(--border)" }}
          role="status"
        >
          <span className="flex-1 leading-relaxed">{genderMismatchMessage}</span>
          <button
            type="button"
            onClick={() => setGenderMismatchDismissed(true)}
            aria-label="Dismiss"
            className="shrink-0 text-muted-foreground/70 hover:text-foreground transition-colors"
          >
            ×
          </button>
        </div>
      )}

      {/* Delayed purchase-feedback prompt */}
      {pendingFeedback && (
        <PurchaseFeedbackCard
          outfitId={pendingFeedback.outfit_id ?? outfitId ?? ""}
          itemName={pendingFeedback.item_name}
          itemCategory={pendingFeedback.item_category ?? "item"}
          retailer={pendingFeedback.retailer}
          productUrl={pendingFeedback.product_url}
          detectedItemId={pendingFeedback.detected_item_id}
          onDismiss={() => setPendingFeedback(null)}
          onComplete={() => {
            setPendingFeedback(null);
            void checkPendingFeedback();
          }}
        />
      )}

      {/* Wave 3B — Commit 3j: offline-shopping chip relocated to just above
          the LB1 "This look costs approximately" panel (see below). */}



      {/* Purchase Confidence Card — instant signal above LB1.
          F27 2B.2: wrapped in matching card shell (visual only).
          PurchaseConfidenceCard internals are unchanged — same props, same
          mount lifecycle, same guest progressive onboarding pathway. */}
      <div
        style={{
          background: "#FFFCF8",
          border: "0.5px solid #E8E2D9",
          borderRadius: 20,
          margin: "12px 0",
          overflow: "hidden",
        }}
      >
        {items.length === 0 ? (
          <PurchaseConfidenceCardSkeleton />
        ) : (
          <PurchaseConfidenceCard
            heroItem={heroItem}
            sizeProfile={(sizeProfile ? { ...(sizeProfile as object), user_id: userId } : userId ? { user_id: userId } : null) as Record<string, unknown> | null}
            brandFitTops={brandFitTops}
            brandFitBottoms={brandFitBottoms}
            isGuest={isGuest}
            detectedGender={detectedGender}
            guestGender={(() => {
              // PR 4b — prefer outfit-detected gender, fall back to
              // progressive-onboarding stored gender so guest brand lookups
              // (brand_fit_profiles / brand_size_charts) resolve effectiveGender.
              if (detectedGender === "men" || detectedGender === "women") return detectedGender;
              const g = guestSizeProfile?.gender;
              if (g === "men" || g === "women") return g;
              return null;
            })()}
            outfitId={outfitId}
            effectiveGender={outfitEffectiveGender}
            onGuestRegenerate={handleGuestRegenerate}
            guestRegenStatus={guestRegenStatus}
            guestRegenError={guestRegenError}
            onProfileUpdated={handleProfileUpdated}
            onTierChange={(t) => setActiveTier(t as TierKey)}

          />
        )}
      </div>



      {/* Sticky signup nudge after successful guest regen */}
      {isGuest && guestRegenStatus === "success" && !guestRegenBannerDismissed && (
        <div
          role="region"
          aria-label="Save your personalised lookbooks"
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            zIndex: 60,
            background: "var(--l2f-cream)",
            borderBottom: "1px solid rgba(0,0,0,0.06)",
            padding: "10px 14px",
            display: "flex",
            alignItems: "center",
            gap: 10,
            fontFamily: "var(--font-body)",
          }}
        >
          <span style={{ fontSize: 13, color: "var(--l2f-danger-ink)", flex: 1, lineHeight: 1.35 }}>
            <strong style={{ fontWeight: 700, display: "block" }}>
              Want it to look exactly like you?
            </strong>
            <span style={{ color: "rgba(113,43,19,0.75)" }}>
              Add skin tone, hair and more
            </span>
          </span>
          <button
            type="button"
            onClick={() => {
              trackEvent("guest_regen_nudge_converted", { outfit_id: outfitId });
              onRequestSignup("customise_model");
            }}
            style={{
              background: "var(--l2f-coral)",
              color: "white",
              border: "none",
              borderRadius: 999,
              padding: "8px 14px",
              fontSize: 12,
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            Sign up
          </button>
          <button
            type="button"
            aria-label="Dismiss"
            onClick={() => {
              trackEvent("guest_regen_nudge_dismissed", { outfit_id: outfitId });
              setGuestRegenBannerDismissed(true);
            }}
            style={{
              background: "transparent",
              border: "none",
              fontSize: 18,
              cursor: "pointer",
              color: "var(--l2f-danger-ink)",
              lineHeight: 1,
              padding: 4,
            }}
          >
            ×
          </button>
        </div>
      )}

      {/* LB1 — The look */}
      <div
        ref={lb1ScrollRef}
        className="mt-6"
        style={{
          background: "#FFFCF8",
          border: "0.5px solid #E8E2D9",
          borderRadius: 20,
          margin: "0 0 12px",
          padding: 16,
          overflow: "hidden",
        }}
      >
        <div className="flex items-start justify-between mb-2">
          <div>
            <p
              style={{
                fontSize: 11,
                textTransform: "uppercase",
                letterSpacing: "0.08em",
                fontWeight: 500,
                color: "#9B958E",
                margin: 0,
              }}
            >
              THE LOOK
            </p>
            <p style={{ fontSize: 13, color: "#4A4540", marginTop: 2 }}>
              Here's how this look could look on you
            </p>
          </div>
          <div className="flex gap-2 items-center">
            {!isGuest && lb1Url && (
              <button
                onClick={() => setRegenerateNonce((n) => n + 1)}
                disabled={isLookbookLoading}
                className="w-8 h-8 rounded-full flex items-center justify-center"
                style={{ background: "rgba(255,92,92,0.1)" }}
                aria-label="Regenerate look"
              >
                <RefreshCw size={14} style={{ color: "var(--l2f-coral)" }} />
              </button>
            )}
            {/* PQ-1: floating ShareLookbookButton removed — per-card share lives on the LB1 card itself */}
          </div>
        </div>
        <div className="relative w-full">
          {lb1Url ? (
            <div className="relative w-full" style={{ aspectRatio: "3 / 4" }}>
              <img
                src={lb1Url}
                alt="AI generated lookbook"
                className="absolute inset-0 w-full h-full object-cover"
                style={{ borderRadius: "var(--radius)" }}
              />
              <span
                className="absolute bottom-2 right-2 text-xs"
                style={{
                  background: "rgba(44,40,32,0.85)",
                  color: "#F5F0EB",
                  borderRadius: 20,
                  padding: "4px 10px",
                  fontSize: 10,
                  fontWeight: 500,
                }}
              >
                Like2Fit · On you
              </span>
              {!isSample && (
                <LookbookActionButtons
                  imageUrl={lb1Url}
                  lookbook="LB1"
                  badgeText="Like2Fit · On you"
                  filename="like2fit-on-you.jpg"
                  outfitId={outfitId}
                />
              )}
            </div>
          ) : (
            <>
              {hasSensitiveItems && (
                <div
                  style={{
                    background: "var(--secondary)",
                    border: "0.5px solid var(--border)",
                    borderRadius: "var(--radius)",
                    padding: "8px 12px",
                    marginBottom: 8,
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 8,
                  }}
                >
                  <Info size={14} style={{ color: "#9CA3AF", flexShrink: 0, marginTop: 1 }} />
                  <div>
                    <p style={{ fontSize: 12, fontWeight: 500, color: "var(--foreground)", margin: "0 0 2px" }}>
                      Lookbook may not be available
                    </p>
                    <p style={{ fontSize: 12, color: "#6B7280", margin: 0, lineHeight: 1.5 }}>
                      This outfit contains items that may not be supported for model generation. You can still find and buy all items below.
                    </p>
                  </div>
                </div>
              )}
              {lb1Failed ? (
                <div
                  style={{
                    background: "#FFFCF8",
                    border: "0.5px solid #E8E2D9",
                    borderRadius: 12,
                    aspectRatio: "3 / 4",
                    width: "100%",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 8,
                    padding: 16,
                  }}
                  role="status"
                  aria-label="Lookbook generation failed"
                >
                  <i
                    className="ti ti-photo-off"
                    style={{ fontSize: 32, color: "#9B958E" }}
                  />
                  <div style={{ fontSize: 12, color: "#6B6560", fontWeight: 500 }}>
                    Couldn't generate
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setLb1Failed(false);
                      setFirstGenNonce((n) => n + 1);
                    }}
                    style={{
                      background: "transparent",
                      border: "none",
                      padding: 0,
                      fontSize: 11,
                      color: "#C84B2F",
                      cursor: "pointer",
                      fontFamily: "inherit",
                    }}
                  >
                    Tap to retry
                  </button>
                </div>
              ) : (
                <LookbookLoadingSilhouette
                  modelProfile={modelProfile}
                  label="The look"
                  messages={[
                    "Finding your exact look...",
                    "Matching each piece...",
                    "Styling your model...",
                    "Almost ready...",
                  ]}
                />
              )}
              <div style={{ display: "none" }}>
                <LookbookGenerator
                  tier="mid"
                  items={tierItemsFor("mid")}
                  totalExpectedItems={items.filter((i: any) => i.tiers != null ? i.tiers.mid != null : i.products == null).length}
                  gender={detectOutfitGender(items)}
                  detectedGender={detectedGender}
                  isGuest={isGuest}
                  userId={userId}
                  onRequestSignup={onRequestSignup}
                  mode={mode}
                  outfitId={outfitId}
                  variant="primary"
                  renderMode="headless"
                  triggerNonce={firstGenNonce}
                  onImageGenerated={(url) => handleLookbookGenerated(url)}
                  isSwapping={isSwapping}
                  onLoadingChange={setPrimaryLoading}
                  onConflictDetected={setGenderConflict}
                  hasSensitiveItems={hasSensitiveItems}
                  modelProfile={modelProfile}
                />
              </div>
            </>
          )}
        </div>
        {isGuest && guestRegenStatus === "success" && lb1Url && (
          <button
            type="button"
            onClick={() => {
              trackEvent("guest_regen_inline_note_tapped", { outfit_id: outfitId });
              onRequestSignup("customise_model");
            }}
            style={{
              display: "block",
              width: "100%",
              marginTop: 8,
              background: "transparent",
              border: "none",
              padding: 0,
              textAlign: "left",
              fontSize: 12,
              color: "var(--color-text-secondary, var(--muted-foreground))",
              textDecoration: "underline",
              textUnderlineOffset: 2,
              cursor: "pointer",
              fontFamily: "var(--font-body)",
            }}
          >
            Want it to look exactly like you? Add skin tone, hair and more →
          </button>
        )}
        {lb1Url && genderConflict && (
          <div
            style={{
              margin: "8px 0",
              padding: "10px 14px",
              background: "var(--l2f-cream)",
              borderRadius: "var(--border-radius-lg)",
              border: "0.5px solid var(--l2f-danger-border)",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: "10px",
            }}
            role="status"
          >
            <p style={{ fontSize: 13, color: "var(--l2f-danger-ink)", margin: 0, lineHeight: 1.5, flex: 1 }}>
              We noticed this outfit may be styled for{" "}
              {genderConflict.detectedGender === "women" ? "women" : "men"}. Want to use{" "}
              {genderConflict.detectedGender === "women" ? "women's" : "men's"} model and sizing
              for this outfit?
            </p>
            <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
              <button
                onClick={() => setGenderConflict(null)}
                style={{
                  fontSize: 12,
                  padding: "5px 10px",
                  borderRadius: 999,
                  border: "0.5px solid #F0997B",
                  background: "white",
                  color: "var(--l2f-danger-ink-soft)",
                  cursor: "pointer",
                }}
              >
                Keep my model
              </button>
              <button
                onClick={() => {
                  const det = genderConflict.detectedGender;
                  setGenderConflict(null);
                  setTemporaryGenderOverride(det);
                  setSwitchGenderNonce((n) => n + 1);
                  // Persist override on outfits row for auth users.
                  // Optimistically update local state so the confidence card
                  // refetches immediately, then write to DB. Errors are
                  // logged but do not block (override still applies in-session).
                  if (!isGuest && userId && outfitId) {
                    setOutfitEffectiveGender(det);
                    void supabase
                      .from("outfits")
                      .update({ effective_gender: det })
                      .eq("id", outfitId)
                      .then(({ error }) => {
                        if (error) {
                          console.warn(
                            "[switch-model] failed to persist effective_gender",
                            error,
                          );
                        }
                      });
                  }
                }}

                style={{
                  fontSize: 12,
                  padding: "5px 10px",
                  borderRadius: 999,
                  border: "none",
                  background: "var(--l2f-coral)",
                  color: "white",
                  cursor: "pointer",
                  fontWeight: 500,
                }}
              >
                Switch model
              </button>
            </div>
          </div>
        )}
        {lb1Url && (
          <p className="text-xs font-medium mt-4 mb-2" style={{ color: "var(--l2f-ink)" }}>
            Now find every piece at your budget →
          </p>
        )}
      </div>

      {/* Headless regenerator for LB1 */}
      {lb1Url && (
        <LookbookGenerator
          tier="mid"
          items={tierItemsFor("mid")}
          totalExpectedItems={items.filter((i: any) => i.tiers != null ? i.tiers.mid != null : i.products == null).length}
          gender={detectOutfitGender(items)}
          detectedGender={detectedGender}
          isGuest={isGuest}
          userId={userId}
          onRequestSignup={onRequestSignup}
          mode={mode}
          outfitId={outfitId}
          renderMode="headless"
          triggerNonce={regenerateNonce + switchGenderNonce}
          onImageGenerated={handleLookbookGenerated}
          isSwapping={isSwapping}
          onLoadingChange={setHeadlessLoading}
          temporaryGenderOverride={temporaryGenderOverride}
          hasSensitiveItems={hasSensitiveItems}
          modelProfile={modelProfile}
          guestModelSetupOverride={guestModelSetupOverride}
        />
      )}

      {/* LB2 — Styled for you */}
      <div
        className="mt-6"
        style={{
          background: "#FFFCF8",
          border: "1.5px solid #C84B2F",
          borderRadius: 20,
          margin: "0 0 12px",
          padding: 16,
          overflow: "hidden",
        }}
      >
        <div className="flex items-start justify-between mb-2">
          <div>
            <div className="flex items-center gap-2">
              <p
                style={{
                  fontSize: 11,
                  textTransform: "uppercase",
                  letterSpacing: "0.08em",
                  fontWeight: 500,
                  color: "#C84B2F",
                  margin: 0,
                }}
              >
                STYLED FOR YOU
              </p>
              <span
                style={{
                  background: "#FAECE7",
                  color: "#C84B2F",
                  fontSize: 10,
                  fontWeight: 500,
                  borderRadius: 10,
                  padding: "2px 7px",
                }}
              >
                NEW
              </span>
            </div>
            <p style={{ fontSize: 13, color: "#4A4540", marginTop: 2 }}>
              Same vibe, different pieces
            </p>
          </div>
          <div className="flex gap-2 items-center">
            {!isGuest && (
              <button
                onClick={() => regenerateLb2Only({ isManualRetry: true })}
                disabled={lb2Loading}
                className="w-8 h-8 rounded-full flex items-center justify-center"
                style={{ background: "rgba(255,92,92,0.1)" }}
                aria-label="Regenerate styled look"
              >
                <RefreshCw size={14} style={{ color: "var(--l2f-coral)" }} />
              </button>
            )}
            {/* PQ-1: floating ShareLookbookButton removed — per-card share lives in Lb2Card */}
          </div>
        </div>
        <div className="relative w-full" style={{ aspectRatio: "3 / 4" }}>
          {lb2Url ? (
            <>
              <img
                src={lb2Url}
                alt="AI styled lookbook"
                className="absolute inset-0 w-full h-full object-cover"
                style={{ borderRadius: "var(--radius)" }}
              />
              <span
                className="absolute bottom-2 right-2 text-xs"
                style={{
                  background: "rgba(44,40,32,0.85)",
                  color: "#F5F0EB",
                  borderRadius: 20,
                  padding: "4px 10px",
                  fontSize: 10,
                  fontWeight: 500,
                }}
              >
                Like2Fit · Styled for you
              </span>
              {!isSample && (
                <LookbookActionButtons
                  imageUrl={lb2Url}
                  lookbook="LB2"
                  badgeText="Like2Fit · Styled for you"
                  filename="like2fit-styled-for-you.jpg"
                  outfitId={outfitId}
                />
              )}
            </>

          ) : lb2Loading ? (
            <div className="absolute inset-0">
              <LookbookLoadingSilhouette
                modelProfile={modelProfile}
                label="Styled for you"
                messages={[
                  "Exploring your style world...",
                  "Finding alternatives...",
                  "Styling another way...",
                  "Almost ready...",
                ]}
              />
            </div>
          ) : (
            <div
              className="absolute inset-0 flex flex-col items-center justify-center gap-2 px-6 text-center"
              style={{
                background: "var(--secondary)",
                borderRadius: "var(--radius)",
              }}
            >
              {lb2TimedOut ? (
                <>
                  <Info size={22} className="text-muted-foreground" />
                  <p className="text-sm text-muted-foreground">
                    Couldn't generate this variation
                  </p>
                  <button
                    onClick={() => regenerateLb2Only({ isManualRetry: true })}
                    className="flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium"
                    style={{ background: "var(--l2f-coral)", color: "white" }}
                  >
                    <RefreshCw size={14} />
                    Try again
                  </button>
                </>
              ) : (
                <div className="absolute inset-0">
                  <LookbookLoadingSilhouette
                    modelProfile={modelProfile}
                    label="Styled for you"
                    messages={[
                      "Styling a look just for you...",
                      "Matching your proportions...",
                      "Finding the right pieces...",
                      "Almost ready...",
                    ]}
                  />
                </div>
              )}
            </div>
          )}

        </div>
      </div>

      {/* LB3 — Styled with what you own */}
      {(() => {
        const lb3State = resolveLb3State({
          isGuest,
          wardrobeEligibleCount: wardrobeEligibleCount ?? 0,
        });
        const heroCategory = (heroItem?.category ?? "look").toLowerCase();
        const ownedNames = lb3OwnedItems
          .map((i) => (i as { name?: string })?.name)
          .filter(Boolean)
          .join(", ");
        const coralCtaBtn: React.CSSProperties = {
          background: "#C84B2F",
          color: "#fff",
          borderRadius: 20,
          padding: "10px 20px",
          fontSize: 14,
          fontWeight: 600,
          border: "none",
          cursor: "pointer",
          marginTop: 14,
        };
        const headerBlock = (
          <div style={{ padding: "12px 14px 8px" }}>
            <div
              style={{
                fontSize: 11,
                textTransform: "uppercase",
                color: "#C84B2F",
                letterSpacing: "0.08em",
                fontWeight: 500,
              }}
            >
              STYLED WITH WHAT YOU OWN
            </div>
            {lb3State === "auth-ready" && (
              <div style={{ fontSize: 13, color: "#4A4540", marginTop: 2 }}>
                Styled with {wardrobeEligibleCount ?? 0} piece
                {(wardrobeEligibleCount ?? 0) === 1 ? "" : "s"} you already own
              </div>
            )}
          </div>
        );

        // Sample mode: LB3 is "Does it fit my life?" — only meaningful for
        // the user's own outfits. Render the locked card for everyone
        // (guest + authed) when viewing the sample.
        if (isSample) {
          return (
            <section
              aria-label="Available with your own outfits"
              style={{
                background: "linear-gradient(135deg, #FAECE7 0%, #FFFCF8 100%)",
                border: "1.5px dashed #C84B2F",
                borderRadius: 20,
                margin: "0 0 12px",
                padding: 20,
                textAlign: "center",
                overflow: "hidden",
              }}
            >
              <div
                style={{ display: "inline-flex", gap: 4, marginBottom: 12 }}
                aria-hidden="true"
              >
                <div style={{ width: 36, height: 48, background: "#EDE8E2", borderRadius: 4, transform: "rotate(-6deg)" }} />
                <div style={{ width: 36, height: 48, background: "#C4A887", borderRadius: 4 }} />
                <div style={{ width: 36, height: 48, background: "#6B4423", borderRadius: 4, transform: "rotate(6deg)" }} />
              </div>
              <div style={{ fontSize: 11, textTransform: "uppercase", color: "#C84B2F", letterSpacing: "0.07em", fontWeight: 500 }}>
                STYLED WITH WHAT YOU OWN
              </div>
              <div style={{ fontSize: 16, fontWeight: 600, color: "#2C2820", marginTop: 4 }}>
                Available with your own outfits
              </div>
              <div style={{ fontSize: 13, color: "#6B6560", lineHeight: 1.5, marginTop: 6, maxWidth: 320, marginLeft: "auto", marginRight: "auto" }}>
                Upload your own outfit to see it styled with the wardrobe pieces you already own.
              </div>
            </section>
          );
        }

        if (lb3State === "guest") {
          return (

            <section
              aria-label="Sign up to see this look with your wardrobe"
              style={{
                background: "linear-gradient(135deg, #FAECE7 0%, #FFFCF8 100%)",
                border: "1.5px dashed #C84B2F",
                borderRadius: 20,
                margin: "0 0 12px",
                padding: 20,
                textAlign: "center",
                overflow: "hidden",
              }}
            >
              <div
                style={{ display: "inline-flex", gap: 4, marginBottom: 12 }}
                aria-hidden="true"
              >
                <div style={{ width: 36, height: 48, background: "#EDE8E2", borderRadius: 4, transform: "rotate(-6deg)" }} />
                <div style={{ width: 36, height: 48, background: "#C4A887", borderRadius: 4 }} />
                <div style={{ width: 36, height: 48, background: "#6B4423", borderRadius: 4, transform: "rotate(6deg)" }} />
              </div>
              <div style={{ fontSize: 11, textTransform: "uppercase", color: "#C84B2F", letterSpacing: "0.07em", fontWeight: 500 }}>
                STYLED WITH WHAT YOU OWN
              </div>
              <div style={{ fontSize: 16, fontWeight: 600, color: "#2C2820", marginTop: 4 }}>
                See it with your wardrobe
              </div>
              <div style={{ fontSize: 13, color: "#6B6560", lineHeight: 1.5, marginTop: 6, maxWidth: 320, marginLeft: "auto", marginRight: "auto" }}>
                Sign up to see this {heroCategory} worn with items you already own.
              </div>
              <button
                type="button"
                style={coralCtaBtn}
                onClick={() => onRequestSignup("lb3_guest_cta" as SignupTrigger)}
              >
                Sign up free
              </button>
              <p className="text-xs text-center text-muted-foreground mt-2">Takes 10 seconds</p>
            </section>
          );
        }

        if (lb3State === "auth-empty") {
          return (
            <section
              aria-label="Add wardrobe items to unlock"
              style={{
                background: "#FFFCF8",
                border: "1.5px dashed #C84B2F",
                borderRadius: 20,
                margin: "0 0 12px",
                padding: 20,
                textAlign: "center",
                overflow: "hidden",
              }}
            >
              <i
                className="ti ti-hanger"
                style={{ fontSize: 32, color: "#C84B2F" }}
                aria-hidden="true"
              />
              <div style={{ fontSize: 11, textTransform: "uppercase", color: "#C84B2F", letterSpacing: "0.07em", fontWeight: 500, marginTop: 8 }}>
                STYLED WITH WHAT YOU OWN
              </div>
              <div style={{ fontSize: 16, fontWeight: 600, color: "#2C2820", marginTop: 4 }}>
                Unlock wardrobe styling
              </div>
              <div style={{ fontSize: 13, color: "#6B6560", lineHeight: 1.5, marginTop: 6, maxWidth: 320, marginLeft: "auto", marginRight: "auto" }}>
                Add at least {LB3_WARDROBE_THRESHOLD} wardrobe items to see this {heroCategory} styled with what you own.
              </div>
              <button
                type="button"
                style={coralCtaBtn}
                onClick={() => {
                  onOpenWardrobe?.();
                }}
              >
                Add wardrobe items →
              </button>
            </section>
          );
        }

        // auth-ready
        return (
          <section
            aria-label="Styled with your wardrobe"
            style={{
              background: "#FFFCF8",
              border: "0.5px solid #E8E2D9",
              borderRadius: 20,
              margin: "0 0 12px",
              overflow: "hidden",
            }}
          >
            {headerBlock}
            <div className="relative w-full" style={{ aspectRatio: "3 / 4" }}>
              {lb3Url ? (
                <>
                  <img
                    src={lb3Url}
                    alt="Styled with your wardrobe"
                    className="absolute inset-0 w-full h-full object-cover"
                  />
                  {ownedNames && (
                    <div
                      style={{
                        position: "absolute",
                        bottom: 8,
                        left: 8,
                        maxWidth: "60%",
                        background: "rgba(44,40,32,0.85)",
                        color: "#F5F0EB",
                        borderRadius: 12,
                        padding: "6px 10px",
                        fontSize: 10,
                        fontWeight: 500,
                        zIndex: 2,
                      }}
                    >
                      <div style={{ opacity: 0.7, fontSize: 9, letterSpacing: "0.06em" }}>
                        FROM YOUR WARDROBE
                      </div>
                      <div style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", marginTop: 1 }}>
                        + {ownedNames}
                      </div>
                    </div>
                  )}
                  <span
                    style={{
                      position: "absolute",
                      bottom: 8,
                      right: 8,
                      background: "rgba(44,40,32,0.85)",
                      color: "#F5F0EB",
                      borderRadius: 20,
                      padding: "4px 10px",
                      fontSize: 10,
                      fontWeight: 500,
                      zIndex: 2,
                    }}
                  >
                    Like2Fit · Your wardrobe
                  </span>
                  {!isSample && (
                    <LookbookActionButtons
                      imageUrl={lb3Url}
                      lookbook="LB3"
                      badgeText="Like2Fit · From your wardrobe"
                      filename="like2fit-from-your-wardrobe.jpg"
                      outfitId={outfitId}
                    />
                  )}
                </>
              ) : lb3Loading ? (
                <div className="absolute inset-0">
                  <LookbookLoadingSilhouette
                    modelProfile={modelProfile}
                    label="Style with your wardrobe"
                    messages={[
                      "Checking your wardrobe...",
                      "Styling with what you own...",
                      "Finding what you need...",
                      "Almost ready...",
                    ]}
                  />
                </div>
              ) : lb3TimedOut ? (
                <div
                  className="absolute inset-0 flex flex-col items-center justify-center gap-3 px-6 text-center"
                  style={{ background: "var(--secondary)" }}
                >
                  <p className="text-sm text-muted-foreground">Couldn't style this with your wardrobe right now.</p>
                  <button
                    onClick={() => {
                      void regenerateLb3Only();
                    }}
                    className="px-5 py-2 rounded-full text-sm font-medium border"
                  >
                    Try again
                  </button>
                </div>
              ) : (
                <div className="absolute inset-0">
                  <LookbookLoadingSilhouette
                    modelProfile={modelProfile}
                    label="Style with your wardrobe"
                    messages={[
                      "Checking your wardrobe...",
                      "Styling with what you own...",
                      "Finding what you need...",
                      "Almost ready...",
                    ]}
                  />
                </div>
              )}
            </div>
            {lb3Url && lb3Outcome && (
              <div style={{ padding: "12px 0" }}>
                <Lb3ShoppingList
                  outfitId={outfitId ?? ""}
                  outcome={lb3Outcome}
                  ownedItems={lb3OwnedItems}
                  gapItems={lb3GapItems}
                  activeSizeProfile={activeSizeProfile}
                  onOpenWardrobeItem={() => onOpenWardrobe?.()}
                  onEnlargeImage={openLightbox}
                  offlineShoppingActive={showShoppingUx && shoppingChipMode === "nearby"}
                  getGapItemOfflineShopping={(gap) => {
                    // Best-effort match: find a detected_items row whose
                    // category matches this gap item. Gap items aren't
                    // detected_items themselves, so the mapping is fuzzy;
                    // return null when nothing matches → row shows no badge.
                    if (!gap.category) return null;
                    const row = items.find(
                      (it) => it.category === gap.category,
                    );
                    if (!row) return null;
                    return {
                      offline_shopping_tier_1: row.offline_shopping_tier_1 ?? null,
                      offline_shopping_tier_2: row.offline_shopping_tier_2 ?? null,
                      offline_shopping_source: row.offline_shopping_source ?? null,
                      offline_shopping_brand: row.offline_shopping_brand ?? null,
                      offline_shopping_resolved_at:
                        row.offline_shopping_resolved_at ?? null,
                    };
                  }}
                  onOpenOfflineStores={(gap, offlineItem) =>
                    setStoresSheetItem({
                      item: offlineItem,
                      itemName: gap.name ?? null,
                      brand: offlineItem.offline_shopping_brand ?? null,
                    })
                  }
                />

              </div>
            )}
            {lb3Url && (
              <div style={{ padding: "0 0 14px" }}>
                <button
                  onClick={() => onOpenWardrobe?.()}
                  className="px-5 py-2 rounded-full text-sm font-medium border"
                >
                  View my wardrobe
                </button>
              </div>
            )}
          </section>
        );
      })()}
      {/* F30.5 — Versatility sibling card after LB3 */}
      <VersatilityCard
        heroItem={heroItem}
        wardrobeItems={wardrobeItems}
        isGuest={isGuest}
      />
      {/* Single post-LB3 Save surface (auth) / EmailCaptureBanner (guest) */}
      {outfitId && !isGuest && (
        <button
          type="button"
          onClick={() => {
            trackEvent("save_button_clicked", {
              outfit_id: outfitId,
              placement: "lb3_section",
              is_guest: false,
            });
            onSave();
          }}
          className="mt-4 w-full px-5 py-3 rounded-full text-sm font-semibold text-white flex items-center justify-center gap-2"
          style={{ background: "var(--l2f-coral)" }}
        >
          <Check size={16} /> Save this look
        </button>
      )}
      {outfitId && isGuest && (
        <EmailCaptureBanner
          outfitId={outfitId}
          lookbookImageUrl={lb3Url ?? lb2Url ?? lb1Url ?? null}
          guestSessionId={guestSessionId}
          onRequestSignup={onRequestSignup}
        />
      )}
      {/* Shop This Look */}
      <div className="mt-8">
        <h3 className="l2f-section-label">
          {(() => {
            if (mode !== "style_item") return "Shop this look";
            const ownedItemsList = items.filter((i) => i.item_type === "owned");
            if (ownedItemsList.length > 1 || images.length > 1) return "Complete this look";
            const sole = ownedItemsList[0];
            return sole?.name ? `Pair with your ${sole.name.toLowerCase()}` : "Shop this look";
          })()}
        </h3>
      </div>

      {(userId || isGuest) && (
        <SizePromptBanner
          userId={userId ?? null}
          sizeProfile={sizeProfile}
          isGuest={isGuest}
          guestSizeProfile={guestSizeProfile}
          onRequestSignup={onRequestSignup}
          onSizeSet={(guestSize) => {
            if (isGuest && guestSize) {
              try {
                sessionStorage.setItem("l2f_guest_size", JSON.stringify(guestSize));
              } catch { /* ignore */ }
              setGuestSizeProfile(guestSize);
            } else {
              void loadSizeProfile();
            }
          }}
        />
      )}




      {(() => {
        const getCategoryBudget = (category: string | null | undefined): number => {
          const cat = (category ?? "").toLowerCase().trim();
          if (EXCLUDED_CATEGORIES.has(cat)) return 0;
          if (cat === "dress") return (budgetTop ?? 0) + (budgetBottom ?? 0);
          if (
            cat === "top" || cat === "shirt" || cat === "blouse" ||
            cat === "jumper" || cat === "sweater" || cat === "hoodie" ||
            cat === "outerwear" || cat === "jacket" || cat === "coat"
          ) return budgetTop ?? 0;
          if (
            cat === "bottom" || cat === "trousers" || cat === "jeans" ||
            cat === "skirt" || cat === "shorts" || cat === "leggings"
          ) return budgetBottom ?? 0;
          if (
            cat === "shoes" || cat === "trainers" || cat === "boots" ||
            cat === "heels" || cat === "sandals" || cat === "sneakers" ||
            cat === "footwear"
          ) return budgetShoes ?? 0;
          return 0;
        };
        const displayEntries = tierItemsForDisplay(activeTier);
        const baseOutfitTotal = displayEntries
          .filter((e) => !isExcludedFromBudget(e.item?.category))
          .reduce((sum, e) => sum + (e.product?.extracted_price ?? 0), 0);
        if (baseOutfitTotal <= 0) return null;
        const effectiveOutfitTotal = cheaperApplied && cheaperTotal != null
          ? cheaperTotal
          : Math.round(baseOutfitTotal);
        const hasAnyCategoryBudget =
          (budgetTop ?? 0) > 0 ||
          (budgetBottom ?? 0) > 0 ||
          (budgetShoes ?? 0) > 0;
        const outfitBudgetTotal = displayEntries
          .filter((e) => !isExcludedFromBudget(e.item?.category))
          .reduce((sum, e) => sum + getCategoryBudget(e.item?.category), 0);
        const showBudgetCompare = hasAnyCategoryBudget && outfitBudgetTotal > 0;
        const isOverBudget = showBudgetCompare && effectiveOutfitTotal > outfitBudgetTotal;
        const overBudgetBy = showBudgetCompare
          ? Math.round(effectiveOutfitTotal - outfitBudgetTotal)
          : 0;

        const saved = cheaperApplied && originalTotal != null && cheaperTotal != null
          ? originalTotal - cheaperTotal
          : 0;
        return (
          <div className="mt-3">
            {/* Wave 3B — Commit 3j: chip toggle sits above the LB1 shopping
                list — the actual decision surface for the Mall use case. */}
            {showShoppingUx && (
              <div style={{ margin: "0 0 8px", display: "flex", justifyContent: "flex-start" }}>
                <OfflineShoppingChip
                  mode={shoppingChipMode}
                  isFresh={shoppingLoc.isFresh}
                  onChange={setShoppingChipMode}
                />
              </div>
            )}
            <div
              style={{
                background: "#FFFCF8",
                border: "0.5px solid #E8E2D9",
                borderBottom: "none",
                borderRadius: "20px 20px 0 0",
                margin: "0 0 0",
                padding: "12px 14px 8px",
                display: "flex",
                alignItems: "flex-end",
                justifyContent: "space-between",
                gap: 8,
              }}
            >
              <div style={{ minWidth: 0 }}>
                <div
                  style={{
                    fontSize: 11,
                    fontWeight: 500,
                    letterSpacing: "0.07em",
                    textTransform: "uppercase",
                    color: "#9B958E",
                  }}
                >
                  {cheaperApplied ? "Cheaper version" : "This look costs approximately"}
                </div>
                <div style={{ display: "flex", alignItems: "baseline", gap: 8, flexWrap: "wrap", marginTop: 4 }}>
                  <div
                    style={{
                      fontFamily: "var(--font-serif, 'Instrument Serif', serif)",
                      fontStyle: "italic",
                      fontSize: 22,
                      fontWeight: 500,
                      lineHeight: 1.1,
                      color: cheaperApplied ? CORAL : "#2C2820",
                    }}
                  >
                    £{effectiveOutfitTotal}
                  </div>

                  {cheaperApplied && originalTotal != null && originalTotal > effectiveOutfitTotal && (
                    <span
                      style={{
                        fontSize: 14,
                        color: "var(--muted-foreground)",
                        textDecoration: "line-through",
                      }}
                    >
                      £{originalTotal}
                    </span>
                  )}
                </div>
                {cheaperApplied && saved > 0 && (
                  <div
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 4,
                      marginTop: 6,
                      background: "#E8F5E9",
                      color: "#1B5E20",
                      borderRadius: 999,
                      padding: "3px 9px",
                      fontSize: 11,
                      fontWeight: 700,
                      letterSpacing: "0.02em",
                    }}
                  >
                    <Sparkles size={11} /> −£{saved} saved
                  </div>
                )}
              </div>
              {!isSample && (!cheaperApplied ? (
                <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
                  {cheaperNoneFound && (
                    <span
                      style={{
                        flexShrink: 0,
                        fontSize: 12,
                        fontStyle: "italic",
                        color: "var(--muted-foreground)",
                        alignSelf: "center",
                      }}
                    >
                      Already the best prices we found
                    </span>
                  )}
                  <button
                    type="button"
                    onClick={() => {
                      void triggerHaptic("success");
                      trackEvent(RESULTS_ANALYTICS_EVENTS.FIND_CHEAPER_TAPPED, {
                        outfit_id: outfitId,
                      });
                      const filteredEntries = displayEntries
                        .filter((e) => !isExcludedFromBudget(e.item?.category));
                      const filteredItems = filteredEntries
                        .map((e) => e.item)
                        .filter((it): it is DetectedItem => !!it);

                      const result = findCheaperProducts(filteredItems, null);

                      const hasAnyCheaper = Object.values(result.overrides)
                        .some((p) => p != null);

                      if (!hasAnyCheaper) {
                        setCheaperNoneFound(true);
                        trackEvent("results_find_cheaper_none_found", { outfit_id: outfitId });
                        return;
                      }

                      const filteredOriginal = filteredItems.reduce(
                        (s, it) => s + (it.tiers?.mid?.extracted_price ?? 0),
                        0,
                      );
                      const filteredCheaper = filteredItems.reduce((s, it) => {
                        const ov = result.overrides[it.id];
                        const mid = it.tiers?.mid?.extracted_price ?? 0;
                        if (ov === undefined || ov === null) return s + mid;
                        return s + (ov.extracted_price ?? mid);
                      }, 0);

                      setCheaperOverrides(result.overrides);
                      setOriginalTotal(Math.round(filteredOriginal));
                      setCheaperTotal(Math.round(filteredCheaper));
                      setCheaperApplied(true);
                      setCheaperBannerDismissed(false);
                      trackEvent("results_find_cheaper_applied", {
                        outfit_id: outfitId,
                        savings_amount: Math.max(0, Math.round(filteredOriginal - filteredCheaper)),
                      });
                    }}
                    style={{
                      flexShrink: 0,
                      background: "transparent",
                      border: `1.5px solid ${CORAL}`,
                      color: CORAL,
                      borderRadius: 20,
                      padding: "6px 12px",
                      fontSize: 12,
                      fontWeight: 500,
                      cursor: "pointer",
                    }}
                  >
                    Find cheaper
                  </button>

                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => {
                    setCheaperApplied(false);
                    setCheaperOverrides({});
                    setCheaperTotal(null);
                    setOriginalTotal(null);
                    setCheaperBannerDismissed(false);
                  }}
                  style={{
                    flexShrink: 0,
                    background: "transparent",
                    border: "1px solid var(--border)",
                    color: "var(--muted-foreground)",
                    borderRadius: 999,
                    padding: "8px 12px",
                    fontSize: 12,
                    fontWeight: 600,
                    cursor: "pointer",
                  }}
                >
                  Show original
                </button>
              ))}
            </div>
            {showBudgetCompare && (
              <div
                className={`mt-2 ${isOverBudget ? "l2f-toast-error" : "l2f-toast-success"}`}
                style={{ justifyContent: "space-between" }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  {isOverBudget ? (
                    <AlertCircle size={13} style={{ color: "#854F0B" }} />
                  ) : (
                    <Check size={13} style={{ color: "var(--l2f-success-ink-soft)" }} />
                  )}
                  <p style={{ fontSize: 13, fontWeight: 500, margin: 0 }}>
                    {isOverBudget ? `£${overBudgetBy} over budget` : "Within your budget"}
                  </p>
                </div>
                <p style={{ fontSize: 13, fontWeight: 500, margin: 0 }}>
                  £{effectiveOutfitTotal} / £{outfitBudgetTotal}
                </p>
              </div>
            )}
          </div>
        );
      })()}

      {cheaperApplied && isGuest && !cheaperBannerDismissed && originalTotal != null && cheaperTotal != null && originalTotal > cheaperTotal && (
        <div
          className="mt-3"
          style={{
            position: "relative",
            background: "linear-gradient(135deg, var(--l2f-cream) 0%, #FFF6F2 100%)",
            border: `1px solid ${CORAL}33`,
            borderRadius: "var(--radius)",
            padding: "14px 16px 14px 16px",
          }}
        >
          <button
            type="button"
            onClick={() => setCheaperBannerDismissed(true)}
            aria-label="Dismiss"
            style={{
              position: "absolute",
              top: 8,
              right: 8,
              background: "transparent",
              border: "none",
              color: "var(--muted-foreground)",
              cursor: "pointer",
              padding: 4,
              lineHeight: 0,
            }}
          >
            <X size={14} />
          </button>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <Sparkles size={16} style={{ color: CORAL, flexShrink: 0 }} />
            <div
              style={{
                fontFamily: "var(--font-display)",
                fontStyle: "italic",
                fontSize: 20,
                lineHeight: 1.1,
                color: "var(--l2f-ink)",
              }}
            >
              You just saved £{originalTotal - cheaperTotal}
            </div>
          </div>
          <p style={{ fontSize: 12, color: "var(--muted-foreground)", margin: "6px 0 10px", paddingRight: 18 }}>
            Sign up to save this cheaper version permanently.
          </p>
          <button
            type="button"
            onClick={() => onRequestSignup("save_outfit")}
            style={{
              width: "100%",
              background: CORAL,
              color: "white",
              border: "none",
              borderRadius: 999,
              padding: "10px 14px",
              fontSize: 13,
              fontWeight: 700,
              cursor: "pointer",
            }}
          >
            Save this cheaper version — sign up free
          </button>
        </div>
      )}

      <div
        className="space-y-2"
        style={{
          background: "#FFFCF8",
          border: "0.5px solid #E8E2D9",
          borderTop: "none",
          borderRadius: "0 0 20px 20px",
          margin: "0 0 12px",
          padding: "6px 14px 6px",
        }}
      >
        {tiersLoading && tierItemsFor("mid").length === 0 && (
          <div className="space-y-2" aria-label="Loading items" role="status">
            {[0, 1, 2].map((i) => (
              <div
                key={i}
                style={{
                  background: "var(--secondary)",
                  border: "0.5px solid var(--border)",
                  borderRadius: "var(--radius)",
                  padding: "10px 12px",
                }}
              >
                <div className="flex items-center gap-3">
                  <Skeleton className="shrink-0" style={{ width: 48, height: 48, borderRadius: 8 }} />
                  <div className="flex-1 min-w-0 space-y-2">
                    <Skeleton style={{ height: 12, width: "70%", borderRadius: 4 }} />
                    <Skeleton style={{ height: 10, width: "40%", borderRadius: 4 }} />
                  </div>
                  <Skeleton className="shrink-0" style={{ width: 56, height: 28, borderRadius: 999 }} />
                </div>
              </div>
            ))}
          </div>
        )}
        {!tiersLoading && tierItemsFor(activeTier).length === 0 && (
          <p className="text-xs text-muted-foreground text-center py-4">No matches in this tier.</p>
        )}
        {!tiersLoading && (() => {
          const midCount = tierItemsFor("mid").length;
          const activeCount = tierItemsFor(activeTier).length;
          if (activeTier === "mid" || activeCount === 0 || activeCount >= midCount) return null;
          return (
            <p className="text-xs text-muted-foreground text-center pt-2 pb-1">
              Some items don't have a {activeTier} option — showing what's available
            </p>
          );
        })()}
        {tierItemsForDisplay(activeTier)
          .map(({ item, product, overBudget, overBudgetBy, onlineFallback }, idx) => {
          const swap = swapState[item.id];
          const isSwapPanelOpen = !!swap;
          const swapLoading = swap?.loading ?? false;
          const swapOpts = SWAP_OPTIONS[item.category.toLowerCase()] ?? [];
          const canSwap = mode === "style_item" && item.item_type === "pairing" && swapOpts.length > 0;
          const override = itemColourOverrides[item.id];
          const cheaperProduct =
            cheaperApplied && item.id in cheaperOverrides
              ? cheaperOverrides[item.id]
              : undefined;
          const displayProduct: ShoppingProduct =
            cheaperProduct !== undefined
              ? (cheaperProduct ?? (product as ShoppingProduct))
              : (override?.product ?? (product as ShoppingProduct));
          const originalMidPrice = item.tiers?.mid?.extracted_price ?? null;
          const cheaperFound = cheaperApplied && cheaperProduct != null;
          const cheaperNotFound =
            cheaperApplied && cheaperProduct === null;
          const perItemSaving =
            cheaperFound && originalMidPrice != null
              ? Math.max(
                  0,
                  Math.round(
                    originalMidPrice -
                      (displayProduct.extracted_price ?? originalMidPrice),
                  ),
                )
              : 0;
          const itemColour = item.color_hex ? hexToColourName(item.color_hex) : null;
          const colourOptions = (() => {
            const others = STYLE_COLOUR_OPTIONS.filter((c) => c.name !== itemColour).slice(
              0,
              itemColour ? 4 : 5,
            );
            if (itemColour) {
              const current =
                STYLE_COLOUR_OPTIONS.find((c) => c.name === itemColour) ?? {
                  name: itemColour,
                  hex: item.color_hex ?? "#888888",
                };
              return [current, ...others];
            }
            return others;
          })();
          const showColourDots = mode === "style_item" && colourOptions.length > 0;
          const overrideErrored =
            !!override && !override.loading && override.product == null;
          if (product === null) return null;
          return (
            <div key={item.id} ref={idx === 0 ? lb1FirstRowRef : undefined}>
              <div
                className="relative"
                style={{ opacity: 1 }}
              >
                <div
                  style={{
                    background: "var(--secondary)",
                    border: "0.5px solid var(--border)",
                    borderRadius: "var(--radius)",
                    padding: "10px 12px",
                    opacity: swapLoading ? 0.6 : 1,
                    transition: "opacity 150ms",
                  }}
                >
                  {/* Wave 3B — Commit 3h: per-item offline-shopping badge for the LB1 "This look costs approximately" row (Mall use case). */}
                  {(() => {
                    if (isGuest) return null;
                    const offlineActive = showShoppingUx && shoppingChipMode === "nearby";
                    if (!offlineActive) return null;
                    const offlineItem: Partial<OutfitItemOfflineShopping> = {
                      offline_shopping_tier_1: item.offline_shopping_tier_1 ?? null,
                      offline_shopping_tier_2: item.offline_shopping_tier_2 ?? null,
                      offline_shopping_source: item.offline_shopping_source ?? null,
                      offline_shopping_brand: item.offline_shopping_brand ?? null,
                      offline_shopping_resolved_at: item.offline_shopping_resolved_at ?? null,
                    };
                    const showOfflineBadge = hasOfflineShoppingData(offlineItem);
                    const src = offlineItem.offline_shopping_source ?? null;
                    const resolverRan =
                      !!src && src !== "skipped" && src !== "no_location";
                    const showOfflineFallback = !showOfflineBadge && resolverRan;
                    if (!showOfflineBadge && !showOfflineFallback) return null;
                    return (
                      <div style={{ marginBottom: 6, display: "flex", gap: 6 }}>
                        {showOfflineBadge ? (
                          <OfflineShoppingBadge
                            item={offlineItem}
                            onTap={() =>
                              setStoresSheetItem({
                                item: offlineItem,
                                itemName: item.name ?? null,
                                brand: offlineItem.offline_shopping_brand ?? null,
                              })
                            }
                            compact
                          />
                        ) : (
                          <span
                            style={{
                              fontSize: 11,
                              fontStyle: "italic",
                              color: "#9A9490",
                              lineHeight: 1,
                            }}
                          >
                            Online only
                          </span>
                        )}
                      </div>
                    );
                  })()}
                  <div className="flex items-center gap-3">

                    <button
                      type="button"
                      onClick={() => openLightbox(displayProduct.thumbnail, displayProduct.title, "shop_this_look")}
                      aria-label={`Enlarge ${displayProduct.title}`}
                      className="relative shrink-0 p-0 border-0 bg-transparent cursor-zoom-in"
                      style={{ width: 48, height: 48 }}
                    >
                      <img
                        src={displayProduct.thumbnail}
                        alt={displayProduct.title}
                        className="object-cover bg-secondary"
                        style={{ width: 48, height: 48, borderRadius: 8 }}
                        loading="lazy"
                      />
                      {override?.loading && (
                        <div
                          className="absolute inset-0 flex items-center justify-center"
                          style={{ background: "rgba(255,255,255,0.6)", borderRadius: 8 }}
                        >
                          <Loader2 size={16} className="animate-spin" style={{ color: CORAL }} />
                        </div>
                      )}
                    </button>
                    <a
                      href={displayProduct.link}
                      target={swapLoading ? undefined : "_blank"}
                      rel="noopener noreferrer"
                      onClick={(e) => {
                        if (swapLoading) { e.preventDefault(); return; }
                        trackProductClick(item, displayProduct);
                        trackEvent(RESULTS_ANALYTICS_EVENTS.SHOPPING_ROW_TAPPED, {
                          item_id: item.id,
                          retailer: displayProduct.source ?? null,
                          outfit_id: outfitId,
                          view_mode: "online",
                        });
                      }}
                      aria-disabled={swapLoading}
                      aria-label={`Buy ${displayProduct.title}${displayProduct.source ? ` from ${displayProduct.source}` : ""}`}
                      className="flex-1 min-w-0 flex items-center gap-2"
                      style={{
                        pointerEvents: swapLoading ? "none" : undefined,
                        opacity: swapLoading ? 0.6 : 1,
                        color: "inherit",
                        textDecoration: "none",
                      }}
                    >
                    <div className="flex-1 min-w-0">
                      <p className="truncate" style={{ fontSize: 13, color: "var(--foreground)" }}>
                        {displayProduct.title}
                      </p>
                      <p className="truncate" style={{ fontSize: 12, color: "var(--muted-foreground)" }}>
                        {displayProduct.source}
                        {displayProduct.price ? (
                          <>
                            {" · "}
                            <span style={{ fontWeight: cheaperFound ? 700 : 400, color: cheaperFound ? CORAL : "inherit" }}>
                              {displayProduct.price}
                            </span>
                            {cheaperFound && item.tiers?.mid?.price && (
                              <span style={{ marginLeft: 6, textDecoration: "line-through", color: "var(--muted-foreground)" }}>
                                {item.tiers.mid.price}
                              </span>
                            )}
                          </>
                        ) : null}
                      </p>
                      {cheaperFound && perItemSaving > 0 && (
                        <span
                          style={{
                            display: "inline-block",
                            marginTop: 4,
                            marginRight: 4,
                            fontSize: 10,
                            fontWeight: 700,
                            letterSpacing: "0.04em",
                            padding: "2px 7px",
                            borderRadius: 999,
                            background: "#E8F5E9",
                            color: "#1B5E20",
                            border: "0.5px solid #1B5E2033",
                          }}
                        >
                          −£{perItemSaving}
                        </span>
                      )}
                      {cheaperNotFound && (
                        <span
                          style={{
                            display: "inline-block",
                            marginTop: 4,
                            marginRight: 4,
                            fontSize: 10,
                            fontWeight: 600,
                            letterSpacing: "0.04em",
                            padding: "2px 7px",
                            borderRadius: 999,
                            background: "#FFF4E0",
                            color: "#B45309",
                            border: "0.5px solid #F59E0B33",
                          }}
                        >
                          Best price we found
                        </span>
                      )}
                      {(() => {
                        const info = getSizeLabelForItem(item, activeSizeProfile);
                        if (info.status === "none") return null;
                        const isConfirmed = info.status === "confirmed";
                        return (
                          <span
                            style={{
                              display: "inline-block",
                              marginTop: 4,
                              fontSize: 10,
                              fontWeight: 600,
                              letterSpacing: "0.04em",
                              padding: "2px 7px",
                              borderRadius: 999,
                              background: isConfirmed ? "var(--l2f-cream)" : "#FFF4E0",
                              color: isConfirmed ? CORAL : "#B45309",
                              border: `0.5px solid ${isConfirmed ? CORAL + "33" : "#F59E0B33"}`,
                              maxWidth: "100%",
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }}
                          >
                            {info.label}
                          </span>
                        );
                      })()}
                      </div>
                      <ChevronRight
                        size={16}
                        className="flex-shrink-0"
                        style={{ color: "var(--muted-foreground)" }}
                        aria-hidden="true"
                      />
                    </a>

                    {canSwap && (
                      <button
                        onClick={() => openSwap(item)}
                        disabled={swapLoading}
                        className="l2f-btn-secondary shrink-0"
                        style={{
                          width: "auto",
                          opacity: swapLoading ? 0.6 : 1,
                          pointerEvents: swapLoading ? "none" : undefined,
                        }}
                      >
                        Swap
                      </button>
                    )}
                  </div>
                  {showColourDots && (
                    <div
                      style={{
                        borderTop: "0.5px solid var(--border)",
                        marginTop: 6,
                        paddingTop: 6,
                        display: "flex",
                        alignItems: "center",
                        gap: 6,
                      }}
                    >
                      <p
                        style={{
                          fontSize: 11,
                          color: "var(--muted-foreground)",
                          margin: 0,
                          flexShrink: 0,
                        }}
                      >
                        Colours:
                      </p>
                      <div style={{ display: "flex", gap: 5, alignItems: "center" }}>
                        {colourOptions.map((colour) => {
                          const isSelected = override
                            ? override.colourName === colour.name
                            : colour.name === itemColour;
                          const isLoading =
                            !!override?.loading && override.colourName === colour.name;
                          return (
                            <button
                              key={colour.name}
                              onClick={() => {
                                if (colour.name === itemColour) {
                                  if (override) {
                                    setItemColourOverrides((prev) => {
                                      const next = { ...prev };
                                      delete next[item.id];
                                      return next;
                                    });
                                  }
                                  return;
                                }
                                void fetchColourVariant(item, colour.name);
                              }}
                              title={colourNameToDisplay(colour.name)}
                              aria-label={colourNameToDisplay(colour.name)}
                              style={{
                                width: 16,
                                height: 16,
                                borderRadius: "50%",
                                background: colour.hex,
                                border: isSelected
                                  ? `1.5px solid ${CORAL}`
                                  : "1.5px solid var(--border)",
                                cursor: "pointer",
                                padding: 0,
                                position: "relative",
                                flexShrink: 0,
                                opacity: isLoading ? 0.5 : 1,
                              }}
                            />
                          );
                        })}
                      </div>
                    </div>
                  )}
                  {overrideErrored && (
                    <p
                      style={{
                        fontSize: 11,
                        color: "var(--muted-foreground)",
                        margin: "4px 0 0",
                      }}
                    >
                      No {colourNameToDisplay(override!.colourName)} option found
                    </p>
                  )}
                </div>
                {swapLoading && (
                  <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                    <Loader2 size={20} className="animate-spin" style={{ color: CORAL }} />
                  </div>
                )}
              </div>

              {isSwapPanelOpen && (
                <div
                  className="mt-2 rounded-2xl p-3"
                  style={{ border: `1.5px solid ${CORAL}`, background: "var(--background)" }}
                >
                  <p className="text-[12px] font-medium uppercase tracking-wider" style={{ color: CORAL }}>
                    {item.category} — swap for?
                  </p>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {swapOpts.map((opt) => {
                      const selected = swap.selected === opt;
                      return (
                        <button
                          key={opt}
                          onClick={() => selectSwap(item.id, opt)}
                          disabled={swap.loading}
                          className="rounded-full transition-colors"
                          style={{
                            background: selected ? CORAL : "#FFFFFF",
                            color: selected ? "#FFFFFF" : "#111827",
                            border: selected ? `1px solid ${CORAL}` : "1px solid #D1D5DB",
                            fontSize: 12,
                            padding: "5px 10px",
                          }}
                        >
                          {opt}
                        </button>
                      );
                    })}
                  </div>
                  <button
                    onClick={() => confirmSwap(item)}
                    disabled={swap.loading || !swap.selected}
                    className="mt-3 w-full py-2.5 rounded-full text-sm font-medium text-white flex items-center justify-center gap-2 disabled:opacity-60"
                    style={{ background: CORAL }}
                  >
                    {swap.loading && <Loader2 size={14} className="animate-spin" />}
                    {swap.loading ? `Finding ${pluralize(swap.selected)}…` : `Find ${pluralize(swap.selected)} →`}
                  </button>
                  {!swap.loading && (
                    <button
                      onClick={() => cancelSwap(item.id)}
                      className="mt-2 w-full text-xs text-muted-foreground"
                    >
                      Cancel
                    </button>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

        {(() => {
          if (!lb2Url) return null;
          const breakdown = getStyledCostsBreakdown();
          if (breakdown.items.length === 0) {
            return (
              <p className="text-xs text-center text-muted-foreground mt-2 italic">
                Personalised shopping for this look coming soon
              </p>
            );
          }

          // TODO: extract shared ShopRow component used by main shopping list +
          // styled-costs + lb3 gap rows. Currently duplicating to ship the visual fix.
          const renderRow = (entry: StyledCostsItem) => {
            const { result, category, isHero, itemRow } = entry;
            const { product: displayProduct } = result;
            const info = getSizeLabelForItem({ category }, activeSizeProfile);
            const showSize = info.status !== "none";
            const isConfirmed = info.status === "confirmed";
            const rowKey = `${isHero ? "hero" : "lb2"}-${displayProduct.link ?? displayProduct.title}`;
            // Wave 3B — Commit 3h: per-item offline-shopping badge for the
            // LB2 "Styled for you costs" row (Mall use case).
            const offlineActive =
              !isGuest && showShoppingUx && shoppingChipMode === "nearby";
            const offlineItem: Partial<OutfitItemOfflineShopping> | null =
              offlineActive && itemRow
                ? {
                    offline_shopping_tier_1: itemRow.offline_shopping_tier_1 ?? null,
                    offline_shopping_tier_2: itemRow.offline_shopping_tier_2 ?? null,
                    offline_shopping_source: itemRow.offline_shopping_source ?? null,
                    offline_shopping_brand: itemRow.offline_shopping_brand ?? null,
                    offline_shopping_resolved_at:
                      itemRow.offline_shopping_resolved_at ?? null,
                  }
                : null;
            const showOfflineBadge =
              !!offlineItem && hasOfflineShoppingData(offlineItem);
            const lb2Src = offlineItem?.offline_shopping_source ?? null;
            const lb2ResolverRan =
              !!lb2Src && lb2Src !== "skipped" && lb2Src !== "no_location";
            const showOfflineFallback =
              !!offlineItem && !showOfflineBadge && lb2ResolverRan;
            return (
              <div key={rowKey}>
                <div
                  className="relative"
                  style={{ opacity: 1 }}
                >
                  <div
                    style={{
                      background: "var(--secondary)",
                      border: "0.5px solid var(--border)",
                      borderRadius: "var(--radius)",
                      padding: "10px 12px",
                    }}
                  >
                    {(showOfflineBadge || showOfflineFallback) && offlineItem && (
                      <div style={{ marginBottom: 6, display: "flex", gap: 6 }}>
                        {showOfflineBadge ? (
                          <OfflineShoppingBadge
                            item={offlineItem}
                            onTap={() =>
                              setStoresSheetItem({
                                item: offlineItem,
                                itemName: displayProduct.title ?? null,
                                brand: offlineItem.offline_shopping_brand ?? null,
                              })
                            }
                            compact
                          />
                        ) : (
                          <span
                            style={{
                              fontSize: 11,
                              fontStyle: "italic",
                              color: "#9A9490",
                              lineHeight: 1,
                            }}
                          >
                            Online only
                          </span>
                        )}
                      </div>
                    )}
                    <div className="flex items-center gap-3">

                      <button
                        type="button"
                        onClick={() =>
                          openLightbox(
                            displayProduct.thumbnail,
                            displayProduct.title,
                            "styled_for_you_costs",
                          )
                        }
                        aria-label={`Enlarge ${displayProduct.title}`}
                        className="relative shrink-0 p-0 border-0 bg-transparent cursor-zoom-in"
                        style={{ width: 48, height: 48 }}
                      >
                        <img
                          src={displayProduct.thumbnail}
                          alt={displayProduct.title}
                          className="object-cover bg-secondary"
                          style={{ width: 48, height: 48, borderRadius: 8 }}
                          loading="lazy"
                        />
                      </button>
                      <a
                        href={displayProduct.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => {
                          const price =
                            typeof displayProduct.extracted_price === "number"
                              ? displayProduct.extracted_price
                              : null;
                          const utms = getStoredUtms();
                          pushDataLayer(
                            price != null
                              ? { event: "buy_tap", value: price, currency: "GBP", ...utms }
                              : { event: "buy_tap", ...utms },
                          );
                          trackEvent("buy_button_clicked", {
                            outfit_id: outfitId,
                            retailer: displayProduct.source ?? null,
                            product_url: displayProduct.link ?? null,
                            price,
                            source: "styled_for_you_costs",
                            is_hero: isHero,
                          });
                        }}
                        aria-label={`Buy ${displayProduct.title}${displayProduct.source ? ` from ${displayProduct.source}` : ""}`}
                        className="flex-1 min-w-0 flex items-center gap-2"
                        style={{ color: "inherit", textDecoration: "none" }}
                      >
                        <div className="flex-1 min-w-0">
                          <p
                            className="truncate"
                            style={{ fontSize: 13, color: "var(--foreground)" }}
                          >
                            {displayProduct.title}
                          </p>
                          <p
                            className="truncate"
                            style={{ fontSize: 12, color: "var(--muted-foreground)" }}
                          >
                            {displayProduct.source}
                            {displayProduct.price ? ` · ${displayProduct.price}` : null}
                          </p>
                          {showSize && (
                            <span
                              style={{
                                display: "inline-block",
                                marginTop: 4,
                                fontSize: 10,
                                fontWeight: 600,
                                letterSpacing: "0.04em",
                                padding: "2px 7px",
                                borderRadius: 999,
                                background: isConfirmed ? "var(--l2f-cream)" : "#FFF4E0",
                                color: isConfirmed ? CORAL : "#B45309",
                                border: `0.5px solid ${isConfirmed ? CORAL + "33" : "#F59E0B33"}`,
                                maxWidth: "100%",
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                              }}
                            >
                              {info.label}
                            </span>
                          )}
                        </div>
                        <ChevronRight
                          size={16}
                          className="flex-shrink-0"
                          style={{ color: "var(--muted-foreground)" }}
                          aria-hidden="true"
                        />
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            );
          };

          return (
            <section className="styled-for-you-costs mt-4">
              <div
                className="flex items-center justify-between px-4 py-3 rounded-xl"
                style={{ background: "var(--secondary)", marginBottom: 12 }}
              >
                <div>
                  <p className="text-xs uppercase tracking-widest text-muted-foreground font-medium">
                    Styled for you costs
                  </p>
                  <p className="text-2xl font-serif">£{breakdown.total.toFixed(0)}</p>
                </div>
              </div>

              <div
                style={{
                  background: "#E6F1FB",
                  border: "0.5px solid #B5D4F4",
                  borderRadius: 8,
                  padding: 10,
                  display: "flex",
                  flexDirection: "column",
                  gap: 8,
                }}
              >
                {breakdown.items.map(renderRow)}
              </div>
            </section>
          );
        })()}




      {/* Regenerate CTA is now in the sticky bottom bar below */}
      {mode === "find_look" && !crossSellDismissed && onGoToStyle && (
        <button
          onClick={() => {
            setCrossSellDismissed(true);
            onGoToStyle();
          }}
          className="mt-6 w-full flex flex-row items-center gap-2.5 text-left active:scale-[0.99] transition-transform"
          style={{
            background: "var(--secondary)",
            border: "0.5px solid var(--border)",
            borderRadius: 14,
            padding: "12px 14px",
          }}
        >
          <Shirt size={20} style={{ color: CORAL, flexShrink: 0 }} />
          <div className="flex-1 min-w-0">
            <div style={{ fontSize: 12, fontWeight: 500, color: "var(--foreground)" }}>
              Own something similar?
            </div>
            <div style={{ fontSize: 12, color: "var(--muted-foreground)", marginTop: 1 }}>
              Style it with your wardrobe →
            </div>
          </div>
        </button>
      )}

    </div>
    {isSample && (
      <div
        style={{
          position: "fixed",
          left: 0,
          right: 0,
          bottom: "calc(49px + var(--safe-bottom, 0px))",
          background: "#FFFCF8",
          borderTop: "0.5px solid #E8E2D9",
          padding: "14px 18px 14px",
          zIndex: 60,
        }}
      >
        <button
          type="button"
          onClick={() => onSampleExit?.()}
          style={{
            width: "100%",
            background: "#C84B2F",
            color: "#FFFCF8",
            border: "none",
            borderRadius: 14,
            padding: 14,
            fontSize: 15,
            fontWeight: 500,
            cursor: "pointer",
          }}
        >
          Get my own outfit
        </button>
      </div>
    )}
    {lightboxImage && (
      <EnhancedImageViewer
        images={[{ src: lightboxImage.src, alt: lightboxImage.alt }]}
        onClose={() => setLightboxImage(null)}
      />
    )}
    {/* Wave 3B — Commit 3d: expanded stores sheet triggered by any badge tap. */}
    <StoresExpandedSheet
      open={!!storesSheetItem}
      onClose={() => setStoresSheetItem(null)}
      itemName={storesSheetItem?.itemName ?? null}
      brand={storesSheetItem?.brand ?? null}
      item={storesSheetItem?.item ?? null}
    />
    </>
  );
}

function Saved({ userId, onView }: { userId: string; onView: (o: OutfitRow) => void }) {
  const [items, setItems] = useState<OutfitRow[] | null>(null);
  const [signedUrls, setSignedUrls] = useState<Record<string, string>>({});
  const [deleting, setDeleting] = useState<OutfitRow | null>(null);
  const [removingId, setRemovingId] = useState<string | null>(null);
  const [deletingNow, setDeletingNow] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data, error } = await (supabase
        .from("outfits") as any)
        .select("id, image_url, image_path, image_paths, created_at, analysis_mode, occasion, style_summary, title, is_saved")
        .eq("user_id", userId)
        .eq("is_saved", true)
        .order("created_at", { ascending: false });
      if (cancelled) return;
      if (error) { toast.error("Couldn't load saved outfits."); setItems([]); return; }
      const rows = (data ?? []) as OutfitRow[];
      setItems(rows);

      const entries = await Promise.all(
        rows.map(async (r) => [r.id, (await signImage(r.image_path)) ?? ""] as const),
      );
      if (cancelled) return;
      setSignedUrls(Object.fromEntries(entries.filter(([, u]) => u)));
    })();
    return () => { cancelled = true; };
  }, [userId]);

  const formatDate = (iso: string) => {
    try {
      return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
    } catch { return ""; }
  };

  const confirmDelete = async () => {
    if (!deleting) return;
    setDeletingNow(true);
    const target = deleting;
    const paths = (target.image_paths && target.image_paths.length > 0)
      ? target.image_paths
      : (target.image_path ? [target.image_path] : []);
    try {
      if (paths.length > 0) {
        await supabase.storage.from("outfits").remove(paths);
      }
      const { error } = await supabase.from("outfits").delete().eq("id", target.id);
      if (error) throw error;
      setRemovingId(target.id);
      setDeleting(null);
      setTimeout(() => {
        setItems((prev) => (prev ?? []).filter((it) => it.id !== target.id));
        setRemovingId(null);
      }, 300);
    } catch (e) {
      console.error(e);
      toast.error("Couldn't delete outfit.");
    } finally {
      setDeletingNow(false);
    }
  };

  return (
    <div className="flex-1 flex flex-col px-6 pt-12">
      <h2 className="text-2xl font-extrabold tracking-tight">My Wardrobe</h2>
      <p className="text-muted-foreground text-sm mt-1">Your style library</p>

      {items === null ? (
        <div className="flex-1 flex items-center justify-center pb-32">
          <Loader2 size={28} className="animate-spin text-muted-foreground" />
        </div>
      ) : items.length === 0 ? (
        <div className="flex-1 flex flex-col items-center justify-center text-center pb-32">
          <div className="w-20 h-20 rounded-3xl bg-secondary flex items-center justify-center mb-4">
            <Bookmark size={28} className="text-muted-foreground" />
          </div>
          <p className="font-medium">No saved outfits yet.</p>
          <p className="text-sm text-muted-foreground mt-1">Tap Save Outfit on a result to keep it here.</p>
        </div>
      ) : (
        <div className="mt-6 grid grid-cols-2 gap-3">
          {items.map((it) => (
            <div
              key={it.id}
              className="rounded-2xl overflow-hidden bg-card border border-border shadow-sm transition-opacity duration-300"
              style={{ opacity: removingId === it.id ? 0 : 1 }}
            >
              <div className="aspect-square bg-secondary relative">
                {signedUrls[it.id] && (
                  <img  loading="lazy" decoding="async"src={signedUrls[it.id]} alt={it.title ?? "Saved outfit"} className="w-full h-full object-cover" />
                )}
              </div>
              <div className="p-3">
                <p className="text-sm font-medium text-foreground truncate">
                  {it.title ?? "Untitled outfit"}
                </p>
                <div className="flex items-center gap-2 mt-1">
                  {it.analysis_mode === "style_item" && (
                    <span className="text-[12px] font-medium px-2 py-0.5 rounded-full bg-secondary text-muted-foreground">
                      👗 Styled
                    </span>
                  )}
                  <span className="text-[12px] text-muted-foreground">{formatDate(it.created_at)}</span>
                </div>
                <div className="flex items-center justify-between mt-3">
                  <button
                    onClick={() => onView(it)}
                    className="text-xs font-medium px-4 py-1.5 rounded-full"
                    style={{ background: CORAL, color: "white" }}
                  >
                    View
                  </button>
                  <button
                    onClick={() => setDeleting(it)}
                    aria-label="Delete outfit"
                    className="p-1.5 rounded-full hover:bg-secondary"
                  >
                    <Trash2 size={20} style={{ color: "#9CA3AF" }} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {deleting && (
        <div className="fixed inset-0 z-50 flex items-end justify-center" onClick={() => !deletingNow && setDeleting(null)}>
          <div className="absolute inset-0 bg-black/40" />
          <div
            className="relative w-full max-w-md bg-background rounded-t-3xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mx-auto mt-3 h-1.5 w-12 rounded-full bg-muted" />
            <div className="px-6 pt-5 pb-6">
              <div className="flex items-center gap-3">
                <div className="w-[60px] h-[60px] rounded-xl overflow-hidden bg-secondary shrink-0">
                  {signedUrls[deleting.id] && (
                    <img  loading="lazy" decoding="async"src={signedUrls[deleting.id]} alt="" className="w-full h-full object-cover" />
                  )}
                </div>
                <p className="font-medium text-base truncate flex-1">{deleting.title ?? "Untitled outfit"}</p>
              </div>
              <p className="font-medium mt-5">Delete this outfit?</p>
              <p className="text-sm text-muted-foreground mt-1">This cannot be undone.</p>
              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => setDeleting(null)}
                  disabled={deletingNow}
                  className="flex-1 py-3 rounded-full font-medium border border-border text-muted-foreground"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDelete}
                  disabled={deletingNow}
                  className="flex-1 py-3 rounded-full font-medium text-white"
                  style={{ background: CORAL }}
                >
                  {deletingNow ? "Deleting…" : "Delete"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── F32.1 Account chrome tokens ─────────────────────────────────────
const ACC_BG = "#E8E0D5";
const ACC_CARD = "#FFFCF8";
const ACC_BORDER = "#E8E2D9";
const ACC_DIVIDER = "#F1EFE8";
const ACC_INK = "#1A1814";
const ACC_MUTED = "#5F5A52";
const ACC_SUMMARY = "#4A453E";
const ACC_CORAL = "#C84B2F";
const APP_VERSION = "1.0.0";

function AccountEyebrow({ children }: { children: React.ReactNode }) {
  return (
    <p
      className="l2f-type-section-header"
      style={{
        color: ACC_MUTED,
        padding: "0 4px 8px",
        margin: 0,
      }}
    >
      {children}
    </p>
  );
}

function AccountCard({
  children,
  noPaddingX,
}: {
  children: React.ReactNode;
  noPaddingX?: boolean;
}) {
  return (
    <div
      style={{
        background: ACC_CARD,
        border: `0.5px solid ${ACC_BORDER}`,
        borderRadius: 20,
        padding: noPaddingX ? 14 : "0 14px",
        marginBottom: 14,
      }}
    >
      {children}
    </div>
  );
}

function AccountFlatRow({
  title,
  summary,
  rightValue,
  onClick,
  isLast,
}: {
  title: string;
  summary?: string;
  rightValue?: string;
  onClick?: () => void;
  isLast?: boolean;
}) {
  const tappable = !!onClick;
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!tappable}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 12,
        padding: "16px 4px",
        minHeight: 56,
        width: "100%",
        textAlign: "left",
        background: "transparent",
        border: "none",
        borderBottom: isLast ? "none" : `0.5px solid ${ACC_DIVIDER}`,
        cursor: tappable ? "pointer" : "default",
      }}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        <p
          className="l2f-type-editorial-label"
          style={{
            color: ACC_INK,
            margin: 0,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {title}
        </p>
        {summary && (
          <p
            className="l2f-type-secondary"
            style={{
              color: ACC_SUMMARY,
              marginTop: 2,
              margin: 0,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {summary}
          </p>
        )}
      </div>
      {rightValue !== undefined && (
        <span className="l2f-type-editorial-info" style={{ color: ACC_MUTED }}>{rightValue}</span>
      )}
      {tappable && (
        <ChevronRight size={14} style={{ color: ACC_MUTED, flexShrink: 0 }} />
      )}
    </button>
  );
}

function AccountToggleRow({
  title,
  summary,
  on,
  onChange,
  isLast,
}: {
  title: string;
  summary?: string;
  on: boolean;
  onChange: (v: boolean) => void;
  isLast?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={() => onChange(!on)}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 12,
        padding: "16px 4px",
        minHeight: 56,
        width: "100%",
        textAlign: "left",
        background: "transparent",
        border: "none",
        borderBottom: isLast ? "none" : `0.5px solid ${ACC_DIVIDER}`,
        cursor: "pointer",
      }}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        <p
          className="l2f-type-editorial-label"
          style={{
            color: ACC_INK,
            margin: 0,
          }}
        >
          {title}
        </p>
        {summary && (
          <p className="l2f-type-secondary" style={{ color: ACC_SUMMARY, marginTop: 2, margin: 0 }}>
            {summary}
          </p>
        )}
      </div>
      <div
        style={{
          width: 36,
          height: 20,
          background: on ? ACC_CORAL : "#D3D1C7",
          borderRadius: 10,
          position: "relative",
          flexShrink: 0,
          transition: "background 0.18s ease",
        }}
      >
        <div
          style={{
            width: 16,
            height: 16,
            background: "#fff",
            borderRadius: "50%",
            position: "absolute",
            top: 2,
            left: on ? 18 : 2,
            transition: "left 0.18s ease",
          }}
        />
      </div>
    </button>
  );
}

async function openExternalLink(path: string) {
  const url =
    typeof window !== "undefined" && window.location?.origin
      ? `${window.location.origin}${path}`
      : `https://like2fit.com${path}`;
  if (Capacitor.isNativePlatform()) {
    try {
      const { InAppBrowser } = await import("@capacitor/inappbrowser");
      await InAppBrowser.openInExternalBrowser({ url });
      return;
    } catch (err) {
      console.error("[Account] InAppBrowser.openInExternalBrowser failed", err);
    }
  }
  if (typeof window !== "undefined") window.open(url, "_blank", "noopener,noreferrer");
}


function Account({
  email, userId, typicalBudget, budgetTop, budgetBottom, budgetShoes, onBudgetSaved, onLogout, focusRequest, onShowIntroAgain,
}: {
  email: string;
  userId: string;
  typicalBudget: number | null;
  budgetTop: number | null;
  budgetBottom: number | null;
  budgetShoes: number | null;
  onBudgetSaved: (b: number, perCat?: PerCategoryBudgets) => void;
  onLogout: () => void;
  focusRequest?: { section: "brand_fit" | "size" | "model" | "personalisation"; nonce: number } | null;
  onShowIntroAgain?: () => void;
}) {
  const initial = (email[0] ?? "F").toUpperCase();
  const navigate = useNavigate();
  const [sheetOpen, setSheetOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [confirmLogout, setConfirmLogout] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState("");
  const [deleting, setDeleting] = useState(false);
  

  const handleDeleteAccount = async () => {
    if (deleteConfirmText.trim() !== "DELETE") return;
    setDeleting(true);
    try {
      const { error } = await supabase.functions.invoke("delete-account", { body: {} });
      if (error) throw error;
      trackEvent("account_deleted", { user_id: userId });
      setConfirmDelete(false);
      setDeleteConfirmText("");
      await supabase.auth.signOut();
      toast.success("Your account has been deleted. Check your email for confirmation.", {
        duration: 8000,
      });
      onLogout();
    } catch (err) {
      console.error("[Account] delete failed", err);
      toast.error("Couldn't delete account. Please try again or email support@like2fit.com.");
    } finally {
      setDeleting(false);
    }
  };


  // Per-row auto-open nonces derived from focusRequest.
  const lastFocusNonceRef = useRef<number | null>(null);
  const [modelOpenNonce, setModelOpenNonce] = useState<number | undefined>(undefined);
  const [sizeOpenNonce, setSizeOpenNonce] = useState<number | undefined>(undefined);
  const [brandFitOpenNonce, setBrandFitOpenNonce] = useState<number | undefined>(undefined);
  const [personalisationOpenNonce, setPersonalisationOpenNonce] = useState<number | undefined>(undefined);
  const [pulseSection, setPulseSection] = useState<"brand_fit" | "size" | "model" | "personalisation" | null>(null);

  useEffect(() => {
    if (!focusRequest || focusRequest.nonce === lastFocusNonceRef.current) return;
    lastFocusNonceRef.current = focusRequest.nonce;
    const { section, nonce } = focusRequest;
    if (section === "model") setModelOpenNonce(nonce);
    else if (section === "size") setSizeOpenNonce(nonce);
    else if (section === "brand_fit") setBrandFitOpenNonce(nonce);
    else if (section === "personalisation") setPersonalisationOpenNonce(nonce);
    setPulseSection(section);
    requestAnimationFrame(() => {
      const el = document.getElementById(`account-row-${section}`);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
    });
    const t = setTimeout(() => setPulseSection(null), 1400);
    return () => clearTimeout(t);
  }, [focusRequest]);

  useEffect(() => {
    debugLog("[Account] mounted. user:", { userId, email, typicalBudget });
  }, [userId, email, typicalBudget]);




  const savePerCategory = async (perCat: PerCategoryBudgets) => {
    setSaving(true);
    const values = [perCat.top, perCat.bottom, perCat.shoes].filter((v) => v !== -1);
    const typical =
      values.length === 0
        ? -1
        : Math.round(values.reduce((a, b) => a + b, 0) / values.length);
    const { error } = await (supabase.from("profiles") as any)
      .update({
        budget_top: perCat.top,
        budget_bottom: perCat.bottom,
        budget_shoes: perCat.shoes,
        typical_budget: typical,
      })
      .eq("id", userId);
    setSaving(false);
    setSheetOpen(false);
    if (error) {
      toast.error("Couldn't save budget.");
      return;
    }
    onBudgetSaved(typical, perCat);
    toast.success("Budget updated");
  };

  const formatCat = (v: number | null) => {
    if (v === -1) return "No limit";
    if (v == null) return "Set";
    return `£${v}`;
  };



  const initialChar = (email[0] ?? "F").toUpperCase();
  void initial;
  const isAdmin = userId === "8a4181be-7247-42aa-b69c-64f5bd5a25e1";

  return (
    <div
      style={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        background: ACC_BG,
        minHeight: "100dvh",
        paddingTop: "var(--safe-top)",
        paddingBottom: "calc(var(--safe-bottom) + 100px)",
        paddingLeft: 14,
        paddingRight: 14,
      }}
    >
      {/* Header */}
      <div style={{ marginBottom: 16, padding: "16px 4px 0" }}>
        <p
          className="l2f-type-section-header"
          style={{
            color: ACC_CORAL,
            margin: 0,
          }}
        >
          LIKE2FIT · THE PROFILE
        </p>
        <h1
          className="l2f-type-h1"
          style={{
            color: ACC_INK,
            marginTop: 2,
            margin: 0,
          }}
        >
          Account
        </h1>
      </div>

      {/* Identity card */}
      <div
        style={{
          background: ACC_CARD,
          border: `0.5px solid ${ACC_BORDER}`,
          borderRadius: 20,
          padding: 14,
          marginBottom: 14,
          display: "flex",
          alignItems: "center",
          gap: 12,
        }}
      >
        <div
          style={{
            width: 44,
            height: 44,
            borderRadius: "50%",
            background: ACC_CORAL,
            color: "#fff",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontFamily: "var(--font-serif, var(--font-display))",
            fontStyle: "italic",
            fontSize: 17,
            flexShrink: 0,
          }}
        >
          {initialChar}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <p
            className="l2f-type-editorial-info"
            style={{
              color: ACC_INK,
              margin: 0,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {email || "—"}
          </p>
          <p
            className="l2f-type-section-header"
            style={{
              color: ACC_MUTED,
              marginTop: 2,
              margin: 0,
            }}
          >
            SIGNED IN
          </p>
        </div>
      </div>

      {/* MY PREFERENCES */}
      <AccountEyebrow>MY PREFERENCES</AccountEyebrow>
      <AccountCard>
        <AccountFlatRow
          title="Budgets"
          summary={`Top ${formatCat(budgetTop)} · Bottom ${formatCat(budgetBottom)} · Shoes ${formatCat(budgetShoes)}`}
          onClick={() => setSheetOpen(true)}
        />
        <GenderDisplayRow userId={userId} />
        <div id="account-row-model" className={pulseSection === "model" ? "l2f-focus-pulse" : undefined}>
          <LookbookModelRow userId={userId} autoOpenNonce={modelOpenNonce} />
        </div>
        <div id="account-row-personalisation" className={pulseSection === "personalisation" ? "l2f-focus-pulse" : undefined}>
          <PersonalisationRow userId={userId} autoOpenNonce={personalisationOpenNonce} />
        </div>
        <div id="account-row-size" className={pulseSection === "size" ? "l2f-focus-pulse" : undefined}>
          <MySizeRow userId={userId} autoOpenNonce={sizeOpenNonce} />
        </div>
        <div id="account-row-brand_fit" className={pulseSection === "brand_fit" ? "l2f-focus-pulse" : undefined}>
          <BrandFitRow userId={userId} autoOpenNonce={brandFitOpenNonce} />
        </div>
      </AccountCard>




      {/* SUPPORT */}
      <AccountEyebrow>SUPPORT</AccountEyebrow>
      <AccountCard>
        <AccountFlatRow
          title="Help & support"
          summary="How Like2Fit works, troubleshooting, contact"
          onClick={() => navigate({ to: "/help" })}
          isLast
        />
      </AccountCard>

      {/* ADMIN */}
      {isAdmin && (
        <>
          <AccountEyebrow>ADMIN</AccountEyebrow>
          <AccountCard noPaddingX>
            <button
              onClick={async () => {
                try {
                  const { data, error } = await supabase.functions.invoke("backfill-wardrobe-items");
                  if (error) throw error;
                  alert(JSON.stringify(data, null, 2));
                } catch (err) {
                  alert("Error: " + err);
                }
              }}
              className="l2f-type-label"
              style={{
                width: "100%",
                padding: 12,
                background: "transparent",
                border: `1px solid ${ACC_CORAL}`,
                color: ACC_CORAL,
                borderRadius: 22,
                cursor: "pointer",
              }}
            >
              Run wardrobe backfill
            </button>
          </AccountCard>
        </>
      )}

      {/* ABOUT */}
      <AccountEyebrow>ABOUT</AccountEyebrow>
      <AccountCard>
        <AccountFlatRow title="About Like2Fit" onClick={() => navigate({ to: "/about" })} />
        {onShowIntroAgain && (
          <AccountFlatRow
            title="Show intro again"
            summary="Replay the 3-screen walkthrough"
            onClick={() => {
              onShowIntroAgain();
              toast.success("Intro will show next time you open the app.");
            }}
          />
        )}
        <AccountFlatRow title="Privacy policy" onClick={() => navigate({ to: "/privacy" })} />
        <AccountFlatRow title="Terms of service" onClick={() => navigate({ to: "/terms" })} isLast />
      </AccountCard>

      {/* Account actions */}
      <AccountCard noPaddingX>
        <button
          onClick={() => setConfirmLogout(true)}
          className="l2f-type-label"
          style={{
            width: "100%",
            padding: 12,
            background: "transparent",
            border: `1px solid ${ACC_CORAL}`,
            color: ACC_CORAL,
            borderRadius: 22,
            cursor: "pointer",
          }}
        >
          Log out
        </button>
        <div style={{ textAlign: "center", marginTop: 10 }}>
          <button
            onClick={() => setConfirmDelete(true)}
            className="l2f-type-secondary"
            style={{
              color: ACC_CORAL,
              textDecoration: "underline",
              background: "transparent",
              border: "none",
              cursor: "pointer",
              padding: 0,
            }}
          >
            Delete account
          </button>
          <div>
            <button
              onClick={() => openExternalLink("/account-deletion")}
              className="l2f-type-caption"
              style={{
                color: ACC_MUTED,
                marginTop: 2,
                background: "transparent",
                border: "none",
                cursor: "pointer",
                padding: 0,
              }}
            >
              Learn more about account deletion
            </button>
          </div>
        </div>
      </AccountCard>

      {/* Version stamp */}
      <div style={{ textAlign: "center", padding: "12px 0 4px" }}>
        <span className="l2f-type-caption" style={{ color: ACC_MUTED }}>
          Like2Fit · v{APP_VERSION}
        </span>
      </div>


      {sheetOpen && (
        <BudgetCategorySheet
          initialTop={budgetTop}
          initialBottom={budgetBottom}
          initialShoes={budgetShoes}
          saving={saving}
          onClose={() => setSheetOpen(false)}
          onSave={savePerCategory}
        />
      )}

      {confirmLogout && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center px-6"
          onClick={() => setConfirmLogout(false)}
        >
          <div className="absolute inset-0 bg-black/40" />
          <div
            className="relative w-full max-w-sm rounded-3xl p-5 shadow-xl"
            style={{ background: ACC_CARD, maxWidth: 320 }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3
              className="l2f-type-h1"
              style={{
                textAlign: "center",
                color: "var(--l2f-ink)",
              }}
            >
              Are you sure you want to log out?
            </h3>
            <div className="mt-6 flex gap-3">
              <button
                onClick={() => setConfirmLogout(false)}
                className="flex-1 py-3 rounded-full text-sm font-semibold border-2 border-border text-muted-foreground"
              >
                Cancel
              </button>
              <button
                onClick={() => { setConfirmLogout(false); onLogout(); }}
                className="flex-1 py-3 rounded-full text-sm font-semibold text-white"
                style={{ background: CORAL }}
              >
                Log Out
              </button>
            </div>
          </div>
        </div>
      )}

      {confirmDelete && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center px-6"
          onClick={() => !deleting && setConfirmDelete(false)}
        >
          <div className="absolute inset-0 bg-black/50" />
          <div
            className="relative w-full max-w-sm rounded-3xl p-5 shadow-xl"
            style={{ background: ACC_CARD, maxWidth: 320 }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3
              className="l2f-type-h1"
              style={{
                textAlign: "center",
                color: ACC_CORAL,
                margin: 0,
              }}
            >
              Delete your account?
            </h3>
            <p className="mt-4 text-sm text-muted-foreground leading-relaxed">
              This deletes your profile, wardrobe, saved looks and outfit history.
              You have <strong>30 days</strong> to change your mind — email{" "}
              <span style={{ color: "var(--l2f-ink)" }}>support@like2fit.com</span>{" "}
              from this address to restore. After 30 days everything is permanently
              erased.
            </p>
            <p className="mt-4 text-xs font-semibold text-muted-foreground">
              Type DELETE to confirm
            </p>
            <input
              type="text"
              value={deleteConfirmText}
              onChange={(e) => setDeleteConfirmText(e.target.value)}
              disabled={deleting}
              autoCapitalize="characters"
              autoCorrect="off"
              spellCheck={false}
              placeholder="DELETE"
              className="mt-2 w-full px-4 py-3 rounded-xl border-2 border-border bg-background text-sm font-mono tracking-widest"
            />
            <div className="mt-6 flex gap-3">
              <button
                onClick={() => { setConfirmDelete(false); setDeleteConfirmText(""); }}
                disabled={deleting}
                className="flex-1 py-3 rounded-full text-sm font-semibold border-2 border-border text-muted-foreground disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteAccount}
                disabled={deleting || deleteConfirmText.trim() !== "DELETE"}
                className="flex-1 py-3 rounded-full text-sm font-semibold text-white disabled:opacity-40"
                style={{ background: "#C0392B" }}
              >
                {deleting ? "Deleting..." : "Delete Account"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>

  );
}

const INSPO_SECTIONS: Record<Gender | "default", { title: string; query: string }[]> = {
  female: [
    { title: "leather jacket street", query: "leather-jacket" },
    { title: "coquette bows", query: "coquette" },
    { title: "ballet flats", query: "ballet-flats" },
    { title: "quiet luxury looks", query: "quiet-luxury" },
    { title: "parisian chic", query: "parisian-style" },
  ],
  male: [
    { title: "old money tailoring", query: "mens-tailoring" },
    { title: "streetwear staples", query: "mens-streetwear" },
    { title: "minimalist basics", query: "mens-minimal" },
    { title: "techwear fits", query: "techwear" },
    { title: "smart casual", query: "mens-smart-casual" },
  ],
  other: [
    { title: "androgynous looks", query: "androgynous-fashion" },
    { title: "y2k revival", query: "y2k-fashion" },
    { title: "monochrome minimal", query: "monochrome-outfit" },
    { title: "vintage thrift", query: "vintage-thrift" },
    { title: "avant-garde", query: "avant-garde-fashion" },
  ],
  default: [
    { title: "trending now", query: "fashion" },
    { title: "street style", query: "street-style" },
    { title: "minimalist wardrobe", query: "minimal-fashion" },
    { title: "vintage finds", query: "vintage-fashion" },
  ],
};

function Inspo({ gender: _gender }: { gender: Gender | null }) {
  return (
    <div className="flex-1 flex flex-col items-center justify-center px-8 text-center">
      <div
        className="w-20 h-20 rounded-full flex items-center justify-center mb-6"
        style={{ background: "var(--l2f-cream)" }}
      >
        <Sparkles size={36} style={{ color: CORAL }} />
      </div>
      <p
        className="l2f-section-label"
        style={{ color: "var(--l2f-coral)", marginBottom: 8 }}
      >
        LIKE2FIT · THE EDIT
      </p>
      <h2 className="l2f-empty-state-title">Inspo is coming soon</h2>
      <p className="mt-3 text-sm text-muted-foreground max-w-xs leading-relaxed">
        We're curating the best outfit inspiration for you. Check back soon.
      </p>
    </div>
  );
}

type TabKey = "find" | "style" | "saved" | "account";

type CentreButtonConfig = {
  state: "first-gen" | "regenerate" | "loading" | "swapping" | "done" | "locked" | "upload" | "disabled" | "waiting";
  label: string;
  onTap: () => void;
  disabled?: boolean;
};

const CENTRE_BUTTON_ANIMATION_CSS = `
  @keyframes expandRight {
    0%   { width: 40px; opacity: 0; }
    15%  { opacity: 1; }
    40%  { width: 170px; opacity: 1; }
    70%  { width: 170px; opacity: 1; }
    90%  { width: 40px; opacity: 0; }
    100% { width: 40px; opacity: 0; }
  }
  @keyframes textFade {
    0%   { opacity: 0; }
    20%  { opacity: 1; }
    70%  { opacity: 1; }
    88%  { opacity: 0; }
    100% { opacity: 0; }
  }
  @keyframes doneFloat {
    0%   { opacity: 0; transform: translateX(-50%) translateY(0px); }
    20%  { opacity: 1; transform: translateX(-50%) translateY(-5px); }
    70%  { opacity: 1; transform: translateX(-50%) translateY(-5px); }
    100% { opacity: 0; transform: translateX(-50%) translateY(-12px); }
  }
`;

function CentreButton({ config }: { config: CentreButtonConfig }) {
  const { state, onTap, disabled } = config;
  const isLoading = state === "loading" || state === "swapping";
  const isDone = state === "done";

  let bg = CORAL;
  let opacity = 1;
  let iconColor = "white";
  let borderColor = "var(--foreground)";
  let icon: React.ReactNode = <Sparkles size={14} color={iconColor} />;

  if (state === "first-gen" || state === "regenerate") {
    icon = <Sparkles size={14} color="white" />;
  } else if (isLoading) {
    opacity = 0.5;
    icon = <Loader2 size={14} color="white" className="animate-spin" />;
  } else if (isDone) {
    bg = "var(--secondary)";
    borderColor = "var(--foreground)";
    iconColor = "var(--muted-foreground)";
    icon = <Check size={14} style={{ color: "var(--muted-foreground)" }} />;
  } else if (state === "locked") {
    icon = <Lock size={14} color="white" />;
  } else if (state === "upload") {
    icon = <Plus size={16} color="white" />;
  } else if (state === "disabled") {
    bg = "transparent";
    borderColor = "rgba(255,255,255,0.15)";
    icon = <Plus size={14} color="rgba(255,255,255,0.2)" />;
  } else if (state === "waiting") {
    bg = "var(--color-background-secondary, var(--secondary))";
    borderColor = "rgba(255,255,255,0.15)";
    icon = <Sparkles size={14} color="rgba(255,255,255,0.25)" />;
  }

  return (
    <button
      onClick={() => { if (!disabled) onTap(); }}
      disabled={disabled}
      aria-label={config.label}
      className="flex items-center justify-center active:scale-95 transition-transform"
      style={{
        width: 40,
        height: 40,
        borderRadius: "50%",
        border: `${state === "disabled" || state === "waiting" ? 1.5 : 2.5}px solid ${borderColor}`,
        background: bg,
        opacity,
        marginTop: -12,
        cursor: disabled ? "default" : "pointer",
        pointerEvents: disabled ? "none" : undefined,
        flexShrink: 0,
        position: "relative",
        zIndex: 22,
      }}
    >
      {icon}
    </button>
  );
}

function HangerIcon({ size = 24, className }: { size?: number; className?: string }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
    >
      <path d="M12 6V3" />
      <path d="M3 21h18l-4-8H7L3 21z" />
    </svg>
  );
}

function TabBar({
  current,
  onChange,
  centreButton,
}: {
  current: TabKey;
  onChange: (s: TabKey) => void;
  centreButton?: CentreButtonConfig;
}) {
  const tabs: { key: TabKey; label: string; icon: React.ComponentType<{ size?: number; className?: string }> }[] = [
    { key: "find", label: "Get Look", icon: Search },
    { key: "style", label: "Style", icon: Shirt },
    { key: "saved", label: "Wardrobe", icon: HangerIcon },
    { key: "account", label: "Account", icon: User },
  ];
  const tabsDisabled =
    centreButton?.state === "loading" || centreButton?.state === "swapping";

  const [pillAnimKey, setPillAnimKey] = useState(0);
  const [doneAnimKey, setDoneAnimKey] = useState(0);
  const centreState = centreButton?.state;

  useEffect(() => {
    const style = document.createElement("style");
    style.textContent = CENTRE_BUTTON_ANIMATION_CSS;
    document.head.appendChild(style);
    return () => { document.head.removeChild(style); };
  }, []);

  useEffect(() => {
    const coralStates = ["first-gen", "regenerate", "locked"];
    if (coralStates.includes(centreState ?? "")) {
      setPillAnimKey((k) => k + 1);
    }
    if (centreState === "done") {
      setDoneAnimKey((k) => k + 1);
    }
  }, [centreState]);

  let pillText: string | null = null;
  if (centreState === "first-gen") pillText = "Tap to generate your look ✨";
  else if (centreState === "regenerate") pillText = "Tap to regenerate ✨";
  else if (centreState === "locked") pillText = "Sign up to generate ✨";

  const renderTab = (key: TabKey, label: string, Icon: React.ComponentType<{ size?: number; className?: string }>) => {
    const active = current === key;
    return (
      <button
        key={key}
        onClick={() => { if (!tabsDisabled) onChange(key); }}
        disabled={tabsDisabled}
        aria-current={active ? "page" : undefined}
        aria-label={label}
        className="flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-full transition-all duration-200 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--l2f-coral)] focus-visible:ring-offset-2 focus-visible:ring-offset-[#0A0A0A]"
        style={{
          ...(active ? { background: CORAL, boxShadow: "0 4px 14px -6px rgba(255,92,92,0.55)" } : {}),
          opacity: tabsDisabled ? 0.3 : 1,
          pointerEvents: tabsDisabled ? "none" : undefined,
          cursor: tabsDisabled ? "default" : "pointer",
          minHeight: 44,
        }}
      >
        <Icon size={18} className="text-white" />
        {active && <span className="text-white text-xs font-medium">{label}</span>}
      </button>
    );
  };

  return (
    <nav className="fixed bottom-0 left-1/2 -translate-x-1/2 w-full max-w-md px-4 pb-4 pt-2 bg-gradient-to-t from-background via-background to-background/80">
      <div
        className="bg-foreground rounded-full flex items-center justify-around py-2 px-2 shadow-xl"
        style={{ overflow: "visible" }}
      >
        {renderTab(tabs[0].key, tabs[0].label, tabs[0].icon)}
        {renderTab(tabs[1].key, tabs[1].label, tabs[1].icon)}
        {centreButton && (
          <div
            className="flex items-center justify-center"
            style={{ flexShrink: 0, width: 48, position: "relative" }}
          >
            {pillText && (
              <span
                key={`pill-${centreState}-${pillAnimKey}`}
                style={{
                  position: "absolute",
                  top: "50%",
                  left: 4,
                  marginTop: -28,
                  height: 32,
                  background: CORAL,
                  borderRadius: 9999,
                  display: "flex",
                  alignItems: "center",
                  paddingLeft: 44,
                  paddingRight: 14,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  pointerEvents: "none",
                  zIndex: 21,
                  width: 40,
                  opacity: 0,
                  animation: "expandRight 2.2s ease-in-out 3",
                }}
              >
                <span
                  style={{
                    fontSize: 12,
                    fontWeight: 500,
                    color: "white",
                    opacity: 0,
                    animation: "textFade 2.2s ease-in-out 3",
                  }}
                >
                  {pillText}
                </span>
              </span>
            )}
            {centreState === "done" && (
              <span
                key={`done-${doneAnimKey}`}
                style={{
                  position: "absolute",
                  bottom: 48,
                  left: "50%",
                  transform: "translateX(-50%)",
                  whiteSpace: "nowrap",
                  fontSize: 12,
                  fontWeight: 500,
                  color: "white",
                  zIndex: 22,
                  pointerEvents: "none",
                  animation: "doneFloat 2s ease-out 1 forwards",
                }}
              >
                ✓ Done
              </span>
            )}
            <CentreButton config={centreButton} />
          </div>
        )}
        {renderTab(tabs[2].key, tabs[2].label, tabs[2].icon)}
        {renderTab(tabs[3].key, tabs[3].label, tabs[3].icon)}
      </div>
    </nav>
  );
}


function OnboardBrands({ userId, onContinue }: { userId: string; onContinue: () => void }) {
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);

  const toggle = (b: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(b) ? next.delete(b) : next.add(b);
      return next;
    });

  const submit = async () => {
    setLoading(true);
    await supabase
      .from("profiles")
      .update({
        favorite_brands: Array.from(selected),
      })
      .eq("id", userId);
    setLoading(false);
    onContinue();
  };

  return (
    <div className="flex-1 flex flex-col px-6 pt-16 pb-8">
      <div className="text-center">
        <p
          className="l2f-section-label"
          style={{ color: "var(--l2f-coral)", marginBottom: 10 }}
        >
          CHAPTER ONE · THE BRANDS
        </p>
        <h2 className="l2f-type-h1" style={{ color: "var(--l2f-ink)" }}>
          Curate your style
        </h2>
        <p className="mt-2 text-muted-foreground l2f-onb-helper">Select favorite brands to personalize your experience.</p>
      </div>

      <div className="mt-8 grid grid-cols-3 gap-4 flex-1 overflow-y-auto pb-4" role="group" aria-label="Favorite brands">
        {BRANDS.map((b) => {
          const active = selected.has(b);
          return (
            <button
              key={b}
              onClick={() => toggle(b)}
              aria-pressed={active}
              aria-label={`${b}${active ? ", selected" : ""}`}
              className="l2f-onb-toggle aspect-square rounded-full border-2 flex items-center justify-center text-center text-[12px] font-medium leading-tight px-2 transition"
              style={
                active
                  ? { background: CORAL, color: "white", borderColor: CORAL }
                  : { borderColor: "var(--border)", color: "var(--foreground)" }
              }
            >
              {b}
            </button>
          );
        })}
      </div>

      <button
        onClick={submit}
        disabled={loading}
        aria-busy={loading}
        className="l2f-cta-coral mt-4 w-full py-3.5 rounded-full font-medium text-white shadow-lg shadow-[color:var(--l2f-coral)]/30 disabled:opacity-40"
        style={{ background: CORAL }}
      >
        {loading ? "Saving…" : selected.size > 0 ? `Continue (${selected.size})` : "Skip"}
      </button>
    </div>
  );
}

function BudgetSheet({
  current, onClose, onSelect,
}: {
  current: number | null;
  onClose: () => void;
  onSelect: (value: number) => void;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center" onClick={onClose}>
      <div className="absolute inset-0 bg-black/40" />
      <div
        className="relative w-full max-w-md bg-background rounded-t-3xl flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mx-auto mt-3 h-1.5 w-12 rounded-full bg-muted" />
        <div className="px-6 pt-4 pb-2">
          <p
            className="l2f-section-label"
            style={{ color: "var(--l2f-coral)", marginBottom: 6 }}
          >
            THE BUDGET
          </p>
          <h3
            style={{
              fontFamily: "var(--font-display)",
              fontStyle: "italic",
              fontWeight: 400,
              fontSize: 26,
              lineHeight: 1.1,
              color: "var(--l2f-ink)",
            }}
          >
            Choose your budget
          </h3>
          <p className="text-xs text-muted-foreground mt-1">
            Used to personalise your shopping results
          </p>
        </div>
        <div className="px-6 pb-6 pt-3 grid grid-cols-2 gap-2.5">
          {BUDGET_OPTIONS.map((opt) => {
            const active = current === opt.value;
            return (
              <button
                key={opt.value}
                onClick={() => onSelect(opt.value)}
                className="py-3 rounded-full text-sm font-medium border-2 transition"
                style={
                  active
                    ? { background: CORAL, color: "white", borderColor: CORAL }
                    : { background: "var(--secondary)", borderColor: "transparent", color: "var(--foreground)" }
                }
              >
                {opt.label}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

type PerCategoryBudgets = { top: number; bottom: number; shoes: number };

function CategoryBudgetRow({
  label,
  placeholder,
  value,
  noLimit,
  onChangeValue,
  onChangeNoLimit,
}: {
  label: string;
  placeholder: string;
  value: number | null;
  noLimit: boolean;
  onChangeValue: (v: number | null) => void;
  onChangeNoLimit: (v: boolean) => void;
}) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const checkboxId = `nolimit-${label.replace(/\s+/g, "-").toLowerCase()}`;
  return (
    <div>
      <p
        className="l2f-section-label"
        style={{ color: "var(--l2f-ink)", opacity: 0.55, marginBottom: 8 }}
      >
        {label}
      </p>
      <div className="flex items-center gap-3">
        <div
          className="flex-1 flex items-center rounded-2xl border-2 px-4 transition"
          style={{
            background: noLimit ? "var(--secondary)" : "white",
            borderColor: noLimit
              ? "transparent"
              : value !== null && value > 0
                ? CORAL
                : "hsl(var(--border))",
            opacity: noLimit ? 0.5 : 1,
          }}
        >
          <span
            className="text-base font-medium pr-1"
            style={{ color: "var(--l2f-ink)" }}
          >
            £
          </span>
          <input
            ref={inputRef}
            type="number"
            min="1"
            max="9999"
            step="1"
            pattern="[0-9]*"
            inputMode="numeric"
            disabled={noLimit}
            value={value ?? ""}
            placeholder={placeholder}
            onKeyDown={(e) => {
              if ([".", ",", "e", "E", "-", "+"].includes(e.key)) {
                e.preventDefault();
              }
            }}
            onChange={(e) => {
              const v = e.target.value;
              if (v === "") {
                onChangeValue(null);
                return;
              }
              const n = parseInt(v, 10);
              onChangeValue(Number.isFinite(n) ? n : null);
            }}
            className="flex-1 bg-transparent py-3.5 text-base font-medium outline-none disabled:cursor-not-allowed"
            style={{ color: "var(--l2f-ink)" }}
          />
        </div>
        <label
          htmlFor={checkboxId}
          className="flex items-center gap-2 cursor-pointer select-none px-1 py-2"
          style={{ color: "var(--l2f-ink)" }}
        >
          <input
            id={checkboxId}
            type="checkbox"
            checked={noLimit}
            onChange={(e) => {
              const checked = e.target.checked;
              onChangeNoLimit(checked);
              if (checked) {
                onChangeValue(null);
              } else {
                setTimeout(() => inputRef.current?.focus(), 0);
              }
            }}
            style={{ width: 18, height: 18, accentColor: CORAL }}
          />
          <span className="text-xs font-medium">No limit</span>
        </label>
      </div>
    </div>
  );
}

function BudgetCategorySheet({
  initialTop, initialBottom, initialShoes, saving, onClose, onSave,
}: {
  initialTop: number | null;
  initialBottom: number | null;
  initialShoes: number | null;
  saving: boolean;
  onClose: () => void;
  onSave: (perCat: PerCategoryBudgets) => void;
}) {
  const [topBudget, setTopBudget] = useState<number | null>(
    initialTop === -1 ? null : initialTop,
  );
  const [topNoLimit, setTopNoLimit] = useState(initialTop === -1);
  const [bottomBudget, setBottomBudget] = useState<number | null>(
    initialBottom === -1 ? null : initialBottom,
  );
  const [bottomNoLimit, setBottomNoLimit] = useState(initialBottom === -1);
  const [shoesBudget, setShoesBudget] = useState<number | null>(
    initialShoes === -1 ? null : initialShoes,
  );
  const [shoesNoLimit, setShoesNoLimit] = useState(initialShoes === -1);

  const canSave =
    (topNoLimit || (topBudget !== null && topBudget > 0)) &&
    (bottomNoLimit || (bottomBudget !== null && bottomBudget > 0)) &&
    (shoesNoLimit || (shoesBudget !== null && shoesBudget > 0));

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center" onClick={onClose}>
      <div className="absolute inset-0 bg-black/40" />
      <div
        className="relative w-full max-w-md bg-background rounded-t-3xl flex flex-col max-h-[90vh]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mx-auto mt-3 h-1.5 w-12 rounded-full bg-muted" />
        <div className="px-6 pt-4 pb-2">
          <p
            className="l2f-section-label"
            style={{ color: "var(--l2f-coral)", marginBottom: 6 }}
          >
            THE BUDGET
          </p>
          <h3
            style={{
              fontFamily: "var(--font-display)",
              fontStyle: "italic",
              fontWeight: 400,
              fontSize: 26,
              lineHeight: 1.1,
              color: "var(--l2f-ink)",
            }}
          >
            What's the most you'd spend on each?
          </h3>
          <p className="text-xs text-muted-foreground mt-2">
            You'll see everything at or below your limit — bargains included.
          </p>
        </div>
        <div className="px-6 pb-4 pt-4 space-y-5 overflow-y-auto">
          <CategoryBudgetRow
            label="A top or shirt"
            placeholder="e.g. 40"
            value={topBudget}
            noLimit={topNoLimit}
            onChangeValue={setTopBudget}
            onChangeNoLimit={setTopNoLimit}
          />
          <CategoryBudgetRow
            label="Trousers or a skirt"
            placeholder="e.g. 60"
            value={bottomBudget}
            noLimit={bottomNoLimit}
            onChangeValue={setBottomBudget}
            onChangeNoLimit={setBottomNoLimit}
          />
          <CategoryBudgetRow
            label="A pair of shoes"
            placeholder="e.g. 100"
            value={shoesBudget}
            noLimit={shoesNoLimit}
            onChangeValue={setShoesBudget}
            onChangeNoLimit={setShoesNoLimit}
          />
        </div>
        <div className="px-6 pt-2 pb-6 border-t border-border">
          <button
            onClick={() => {
              if (!canSave || saving) return;
              onSave({
                top: topNoLimit ? -1 : topBudget!,
                bottom: bottomNoLimit ? -1 : bottomBudget!,
                shoes: shoesNoLimit ? -1 : shoesBudget!,
              });
            }}
            disabled={!canSave || saving}
            className="w-full py-3.5 rounded-full font-medium text-white transition"
            style={{
              background: canSave ? CORAL : "var(--secondary)",
              color: canSave ? "white" : "var(--muted-foreground)",
              opacity: canSave ? 1 : 0.6,
              cursor: canSave ? "pointer" : "not-allowed",
            }}
          >
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}



function OnboardBudget({
  userId, onContinue,
}: {
  userId: string;
  onContinue: (budget: number, perCat: PerCategoryBudgets) => void;
}) {
  const [topBudget, setTopBudget] = useState<number | null>(null);
  const [topNoLimit, setTopNoLimit] = useState(false);
  const [bottomBudget, setBottomBudget] = useState<number | null>(null);
  const [bottomNoLimit, setBottomNoLimit] = useState(false);
  const [shoesBudget, setShoesBudget] = useState<number | null>(null);
  const [shoesNoLimit, setShoesNoLimit] = useState(false);
  const [loading, setLoading] = useState(false);

  const canContinue =
    (topNoLimit || (topBudget !== null && topBudget > 0)) &&
    (bottomNoLimit || (bottomBudget !== null && bottomBudget > 0)) &&
    (shoesNoLimit || (shoesBudget !== null && shoesBudget > 0));

  const save = async () => {
    if (!canContinue || loading) return;
    setLoading(true);
    const finalTop = topNoLimit ? -1 : topBudget!;
    const finalBottom = bottomNoLimit ? -1 : bottomBudget!;
    const finalShoes = shoesNoLimit ? -1 : shoesBudget!;
    const values = [finalTop, finalBottom, finalShoes].filter((v) => v !== -1);
    const typicalBudget =
      values.length === 0
        ? -1
        : Math.round(values.reduce((a, b) => a + b, 0) / values.length);

    await (supabase.from("profiles") as any)
      .update({
        budget_top: finalTop,
        budget_bottom: finalBottom,
        budget_shoes: finalShoes,
        typical_budget: typicalBudget,
        onboarded_at: new Date().toISOString(),
      })
      .eq("id", userId);
    setLoading(false);
    onContinue(typicalBudget, {
      top: finalTop,
      bottom: finalBottom,
      shoes: finalShoes,
    });
  };

  return (
    <div className="flex-1 flex flex-col px-6 pt-16 pb-8">
      <p
        className="l2f-section-label"
        style={{ color: "var(--l2f-coral)", marginBottom: 10 }}
      >
        CHAPTER THREE · THE BUDGET
      </p>
      <h2 className="l2f-type-h1" style={{ color: "var(--l2f-ink)" }}>
        What's the most you'd spend on each?
      </h2>
      <p className="mt-3 text-muted-foreground l2f-onb-helper">
        You'll see everything at or below your limit — bargains included.
        You can update these anytime in your account settings.
      </p>

      <div className="mt-8 space-y-6">
        <CategoryBudgetRow
          label="A top or shirt"
          placeholder="e.g. 40"
          value={topBudget}
          noLimit={topNoLimit}
          onChangeValue={setTopBudget}
          onChangeNoLimit={setTopNoLimit}
        />
        <CategoryBudgetRow
          label="Trousers or a skirt"
          placeholder="e.g. 60"
          value={bottomBudget}
          noLimit={bottomNoLimit}
          onChangeValue={setBottomBudget}
          onChangeNoLimit={setBottomNoLimit}
        />
        <CategoryBudgetRow
          label="A pair of shoes"
          placeholder="e.g. 100"
          value={shoesBudget}
          noLimit={shoesNoLimit}
          onChangeValue={setShoesBudget}
          onChangeNoLimit={setShoesNoLimit}
        />
      </div>

      <div className="mt-auto pt-8">
        <button
          onClick={save}
          disabled={!canContinue || loading}
          aria-busy={loading}
          className="l2f-cta-coral w-full py-3.5 rounded-full font-medium text-white shadow-lg shadow-[color:var(--l2f-coral)]/30 transition disabled:shadow-none"
          style={{
            background: canContinue ? CORAL : "var(--secondary)",
            color: canContinue ? "white" : "var(--muted-foreground)",
            opacity: canContinue ? 1 : 0.6,
            cursor: canContinue ? "pointer" : "not-allowed",
          }}
        >
          {loading ? "Saving…" : "Continue"}
        </button>
        <p
          className="l2f-type-caption text-center mt-3"
          style={{
            color: "var(--muted-foreground)",
            opacity: 0.7,
          }}
        >
          You can update these anytime in your account settings.
        </p>
      </div>
    </div>
  );
}

function OnboardingTour({
  step,
  onNext,
  onFinish,
}: {
  step: number;
  onNext: () => void;
  onFinish: () => void;
}) {
  const steps = [
    {
      emoji: "📸",
      text: "Upload any outfit screenshot",
      cta: "Next →",
      // Highlight Analyse button area (mid-screen) since they're already on Upload tab
      tooltipStyle: { top: "30%", left: "50%", transform: "translate(-50%, -50%)" },
      highlight: { left: "16px", right: "16px", bottom: "88px", height: "60px" },
    },
    {
      emoji: "✨",
      text: "AI identifies every clothing piece",
      cta: "Next →",
      tooltipStyle: { bottom: "180px", left: "50%", transform: "translateX(-50%)" },
      highlight: { left: "16px", right: "16px", bottom: "88px", height: "60px" },
    },
    {
      emoji: "🛍",
      text: "Shop and save looks you love",
      cta: "Got it →",
      tooltipStyle: { bottom: "110px", left: "50%", transform: "translateX(-50%)" },
      // Saved tab is 2nd of 4 tabs in bottom nav
      highlight: { left: "calc(16px + 25%)", width: "25%", bottom: "16px", height: "56px" },
    },
  ];
  const current = steps[step - 1];
  if (!current) return null;

  const isLast = step === steps.length;
  const handleClick = isLast ? onFinish : onNext;

  return (
    <div className="fixed inset-0 z-50 pointer-events-auto">
      {/* Dark overlay */}
      <div className="absolute inset-0 bg-black/70" onClick={(e) => e.stopPropagation()} />

      {/* Highlight ring */}
      <div
        className="absolute rounded-2xl pointer-events-none"
        style={{
          ...current.highlight,
          boxShadow: `0 0 0 3px ${CORAL}, 0 0 0 9999px rgba(0,0,0,0.7)`,
          border: `2px solid ${CORAL}`,
        }}
      />

      {/* Skip button */}
      <button
        onClick={onFinish}
        className="absolute text-white/90 px-3 py-1.5 rounded-full bg-white/10 backdrop-blur inline-flex items-center justify-center"
        style={{ top: "calc(var(--safe-top) + 16px)", right: 16, fontSize: 15, fontWeight: 500, minHeight: 44, minWidth: 44 }}
      >
        Skip
      </button>

      {/* Tooltip card */}
      <div
        className="absolute max-w-[280px] w-[80%] rounded-2xl p-5 shadow-2xl"
        style={{ ...current.tooltipStyle, background: CORAL, color: "white" }}
      >
        <p className="l2f-type-body mb-4" style={{ fontWeight: 500, lineHeight: 1.4 }}>
          <span className="mr-1">{current.emoji}</span>
          {current.text}
        </p>
        <div className="flex items-center justify-between">
          <span className="l2f-type-section-header text-white/80">{step} / {steps.length}</span>
          <button
            onClick={handleClick}
            className="bg-white rounded-full px-4 py-2 inline-flex items-center justify-center"
            style={{ color: CORAL, fontSize: 15, fontWeight: 600, minHeight: 44 }}
          >
            {current.cta}
          </button>
        </div>
      </div>

    </div>
  );
}


function GuestSavedEmpty({ onSignup }: { onSignup: () => void }) {
  return (
    <div className="flex-1 flex flex-col items-center justify-center px-8 text-center">
      <div className="w-20 h-20 rounded-full flex items-center justify-center mb-6" style={{ background: `${CORAL}1A` }}>
        <Bookmark size={36} style={{ color: CORAL }} />
      </div>
      <h2 className="text-2xl font-extrabold tracking-tight">Your style library</h2>
      <p className="mt-3 text-sm text-muted-foreground max-w-xs leading-relaxed">
        Create an account to save your favourite looks and build your personal style wardrobe.
      </p>
      <div className="mt-8 w-full max-w-xs space-y-3">
        <button
          onClick={onSignup}
          className="w-full py-3.5 rounded-full font-medium text-white shadow-lg shadow-[color:var(--l2f-coral)]/30"
          style={{ background: CORAL }}
        >
          Create Account
        </button>
        <button
          onClick={onSignup}
          className="w-full py-3.5 rounded-full font-medium border-2 border-foreground"
        >
          Log In
        </button>
      </div>
    </div>
  );
}



// ─────────────────────────────────────────────────────────────────────
// AI Lookbook generator inside the Shop This Look bottom sheet
// ─────────────────────────────────────────────────────────────────────
function detectOutfitGender(items: DetectedItem[]): "men" | "women" | "unisex" {
  let men = 0, women = 0;
  for (const it of items) {
    const hay = [it.name ?? "", ...(it.style_tags ?? [])].join(" ").toLowerCase();
    if (/\bwomen\b|\bwoman\b|\bfemale\b/.test(hay)) women++;
    else if (/\bmen\b|\bman\b|\bmale\b/.test(hay)) men++;
  }
  if (women > men) return "women";
  if (men > women) return "men";
  return "unisex";
}

type HeightKey = "under_5_4" | "5_4_to_5_7" | "over_5_7";
type BodyShapeKey =
  | "straight"
  | "hourglass"
  | "pear"
  | "athletic"
  | "lean"
  | "broad"
  | "regular";
type VariationKey = "editorial_studio" | "street_style" | "lifestyle";
type HairKey =
  | "short_dark"
  | "short_light"
  | "long_dark"
  | "long_light"
  | "natural"
  | "medium_wavy"
  | "bald"
  | "curly_afro"
  | "medium_length"
  | "locs_braids";
type ModelForKey = "myself" | "other";
type ModelGenderKey = "woman" | "man" | "non-binary";
type BodySizeKey =
  | "slim"
  | "average"
  | "curvy"
  | "plus_size"
  | "slim_lean"
  | "stocky"
  | "curvy_stocky";
type GenderKind = "female" | "male" | "non-binary";

const VARIATION_DESCRIPTIONS: Record<VariationKey, string> = {
  editorial_studio:
    "Clean white studio background, editorial fashion photography style, model facing forward",
  street_style:
    "Urban street background, natural daylight, candid fashion photography style, model at a slight angle",
  lifestyle:
    "Soft natural indoor setting, warm lighting, lifestyle fashion photography, model in relaxed pose",
};
const VARIATION_LABELS: Record<VariationKey, string> = {
  editorial_studio: "Editorial Studio",
  street_style: "Street Style",
  lifestyle: "Lifestyle",
};

type ModelSetupPayload = {
  height?: string;
  body_shape?: string;
  body_size?: string;
  skin_tone: string;
  hair?: string | null;
  variation?: VariationKey;
  variation_description: string;
};

type SavedModelProfile = {
  model_height: string | null;
  model_body_shape: string | null;
  model_body_size: string | null;
  model_skin_tone: string | null;
  model_hair: string | null;
  model_gender: string | null;
  model_preferred_variation: VariationKey | null;
};

type StyleContext = {
  owned_image_urls: string[];
  owned_items: Array<{
    name: string;
    category: string;
    color_hex: string | null;
    style_tags: string[] | null;
    material: string | null;
    pattern: string | null;
  }>;
};

type LookbookItemPayload = {
  items: { item: DetectedItem; product: ShoppingProduct }[];
  tier: TierKey;
  gender: "men" | "women" | "unisex";
  isGuest: boolean;
  modelSetup?: ModelSetupPayload | null;
  styleContext?: StyleContext | null;
  outfitId?: string | null;
  userId?: string | null;
  guestSessionId?: string | null;
};

async function fetchStyleContext(outfitId: string, isGuest: boolean = false): Promise<StyleContext | null> {
  try {
    let outfit: { image_paths?: string[] | null; image_path?: string | null } | null = null;
    const directOutfit = await supabase
      .from("outfits")
      .select("image_paths, image_path")
      .eq("id", outfitId)
      .maybeSingle();
    outfit = (directOutfit.data as typeof outfit) ?? null;
    if (!outfit && isGuest) {
      const { data: guestRows } = await (supabase as any).rpc("get_guest_outfit", {
        p_id: outfitId,
      });
      outfit = Array.isArray(guestRows) && guestRows.length > 0 ? guestRows[0] : null;
    }
    const paths: string[] =
      (outfit?.image_paths && outfit.image_paths.length > 0)
        ? outfit.image_paths
        : (outfit?.image_path ? [outfit.image_path] : []);
    const signed = await Promise.all(
      paths.map(async (p) => {
        const { data } = await supabase.storage
          .from("outfits")
          .createSignedUrl(p, 60 * 10);
        return data?.signedUrl ?? null;
      }),
    );
    const owned_image_urls = signed.filter((u): u is string => !!u);

    let ownedItems: StyleContext["owned_items"] | null = null;
    const directItems = await supabase
      .from("detected_items")
      .select("name, category, color_hex, style_tags, material, pattern")
      .eq("outfit_id", outfitId)
      .eq("item_type", "owned");
    ownedItems = (directItems.data as StyleContext["owned_items"] | null) ?? null;
    if ((!ownedItems || ownedItems.length === 0) && isGuest) {
      const { data: guestItems } = await (supabase as any).rpc("get_guest_outfit_items", {
        p_outfit_id: outfitId,
      });
      ownedItems = Array.isArray(guestItems)
        ? (guestItems as Array<{ name: string; category: string; color_hex: string | null; style_tags: string[] | null; material: string | null; pattern: string | null; item_type: string }>)
            .filter((r) => r.item_type === "owned")
            .map((r) => ({ name: r.name, category: r.category, color_hex: r.color_hex, style_tags: r.style_tags, material: r.material ?? null, pattern: r.pattern ?? null }))
        : null;
    }

    return {
      owned_image_urls,
      owned_items: (ownedItems ?? []) as StyleContext["owned_items"],
    };
  } catch (e) {
    console.error("fetchStyleContext failed", e);
    return null;
  }
}

async function invokeGenerateLookbook({
  items,
  tier,
  gender,
  isGuest,
  modelSetup,
  styleContext,
  outfitId,
  userId,
  guestSessionId,
}: LookbookItemPayload) {
  let guestUsed = false;
  if (typeof window !== "undefined") {
    const today = new Date().toISOString().slice(0, 10);
    const storedDate = localStorage.getItem("lookbook_guest_date");
    const storedCount = parseInt(localStorage.getItem("lookbook_guest_count") ?? "0", 10);
    const count = storedDate === today ? storedCount : 0;
    guestUsed = count >= 10;
  }
  const mappedItems = items.map(({ item, product }) => ({
    name: item.name,
    category: item.category,
    color_hex: item.color_hex,
    material: (item as { material?: string | null }).material ?? null,
    pattern: (item as { pattern?: string | null }).pattern ?? null,
    style_tags: item.style_tags ?? [],
    product: {
      title: product?.title,
      thumbnail: product?.thumbnail,
      price: product?.price,
      source: product?.source,
    },
  }));
  const payload: Record<string, unknown> = {
    items: mappedItems,
    tier,
    gender,
    is_guest: isGuest,
    guest_used: guestUsed,
  };
  if (modelSetup) payload.model_setup = modelSetup;
  if (styleContext) {
    payload.is_style_my_item = true;
    payload.owned_image_urls = styleContext.owned_image_urls;
    payload.owned_items = styleContext.owned_items;
  }
  if (outfitId) payload.outfit_id = outfitId;
  if (userId) payload.user_id = userId;
  if (guestSessionId) payload.guest_session_id = guestSessionId;
  debugLog('[LB1 invoke] calling generate-lookbook1', { tier, gender, outfitId, items: mappedItems.length });
  const res = await supabase.functions.invoke("generate-lookbook1", { body: payload });
  debugLog('[LB1 invoke] response', { data: res.data, error: res.error });
  return res;
}

const LOOKBOOK_STEPS = [
  { at: 0, emoji: "✨", text: "Creating your look...", sub: "Setting up your personalised model" },
  { at: 6000, emoji: "👗", text: "Styling the outfit...", sub: "Placing each item on your model" },
  { at: 14000, emoji: "🎨", text: "Adding the finishing touches...", sub: "Perfecting colours, lighting and detail" },
  { at: 22000, emoji: "📸", text: "Almost there...", sub: "Your fashion editorial is nearly ready" },
];

const LB3_STEPS = [
  { at: 0, emoji: "👕", text: "Checking your wardrobe...", sub: "Looking at what you own" },
  { at: 5000, emoji: "👗", text: "Styling with what you own...", sub: "Matching your pieces together" },
  { at: 12000, emoji: "🛍️", text: "Finding what you need...", sub: "Filling gaps with suggested items" },
  { at: 18000, emoji: "✨", text: "Building your outfit...", sub: "Composing the final look" },
  { at: 24000, emoji: "📸", text: "Almost there...", sub: "Your personalised look is nearly ready" },
];

function LookbookLoadingOverlay({ previousImageUrl, steps = LOOKBOOK_STEPS }: { previousImageUrl: string | null; steps?: typeof LOOKBOOK_STEPS }) {
  const [stepIndex, setStepIndex] = useState(0);
  const [progress, setProgress] = useState(0);
  const [visibleStep, setVisibleStep] = useState(0);
  const [stepOpacity, setStepOpacity] = useState(1);

  useEffect(() => {
    const start = Date.now();
    const timers = steps.slice(1).map((s, i) =>
      setTimeout(() => setStepIndex(i + 1), s.at),
    );
    const progInterval = setInterval(() => {
      const elapsed = Date.now() - start;
      const pct = Math.min(85, (elapsed / 25000) * 85);
      setProgress(pct);
      if (elapsed >= 25000) clearInterval(progInterval);
    }, 100);
    return () => {
      timers.forEach(clearTimeout);
      clearInterval(progInterval);
    };
  }, []);

  useEffect(() => {
    if (stepIndex === visibleStep) return;
    setStepOpacity(0);
    const t = setTimeout(() => {
      setVisibleStep(stepIndex);
      setStepOpacity(1);
    }, 250);
    return () => clearTimeout(t);
  }, [stepIndex, visibleStep]);

  const step = steps[visibleStep];
  const textShadow = "0px 1px 4px rgba(0, 0, 0, 0.8)";

  return (
    <div
      className="relative w-full h-full overflow-hidden rounded-xl"
      style={{
        aspectRatio: "3 / 4",
        background: previousImageUrl
          ? `url(${previousImageUrl}) center/cover no-repeat`
          : "linear-gradient(135deg, var(--l2f-coral) 0%, #cc3333 100%)",
      }}
    >
      <div
        className="absolute inset-0 flex flex-col items-center justify-center px-4"
        style={{ background: "rgba(0, 0, 0, 0.45)" }}
      >
        <div
          style={{
            width: "60%",
            height: 4,
            borderRadius: 9999,
            background: "rgba(255, 255, 255, 0.3)",
            boxShadow: "0px 1px 3px rgba(0, 0, 0, 0.5)",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              width: `${progress}%`,
              height: "100%",
              background: "#FFFFFF",
              borderRadius: 9999,
              transition: "width 0.3s ease",
            }}
          />
        </div>
        <div
          style={{
            marginTop: 16,
            textAlign: "center",
            opacity: stepOpacity,
            transition: "opacity 0.5s ease",
          }}
        >
          <div
            style={{
              fontSize: 28,
              lineHeight: 1,
              filter: "drop-shadow(0px 1px 3px rgba(0, 0, 0, 0.6))",
            }}
          >
            {step.emoji}
          </div>
          <div
            style={{
              marginTop: 8,
              fontSize: 16,
              fontWeight: 500,
              color: "#fff",
              textShadow,
            }}
          >
            {step.text}
          </div>
          <div
            style={{
              marginTop: 4,
              fontSize: 13,
              color: "#fff",
              opacity: 0.8,
              textShadow,
            }}
          >
            {step.sub}
          </div>
        </div>
        <div
          style={{
            marginTop: 16,
            fontSize: 12,
            color: "#fff",
            opacity: 0.6,
            textShadow,
            textAlign: "center",
          }}
        >
          This usually takes 20-30 seconds
        </div>
      </div>
    </div>
  );
}

function LookbookGenerator({
  tier,
  items,
  gender,
  detectedGender = null,
  isGuest,
  userId,
  onRequestSignup,
  mode = "find_look",
  outfitId = null,
  variant = "outline",
  renderMode = "button",
  triggerNonce = 0,
  onImageGenerated,
  isSwapping = false,
  onLoadingChange,
  onConflictDetected,
  temporaryGenderOverride = null,
  hasSensitiveItems = false,
  totalExpectedItems,
  modelProfile = null,
  guestModelSetupOverride = null,
}: {
  tier: TierKey;
  items: { item: DetectedItem; product: ShoppingProduct }[];
  gender: "men" | "women" | "unisex";
  detectedGender?: "men" | "women" | "unisex" | null;
  isGuest: boolean;
  userId: string | null;
  onRequestSignup: (trigger: SignupTrigger) => void;
  mode?: AnalysisMode;
  outfitId?: string | null;
  variant?: "outline" | "primary";
  renderMode?: "button" | "headless";
  triggerNonce?: number;
  onImageGenerated?: (url: string) => void;
  isSwapping?: boolean;
  onLoadingChange?: (loading: boolean) => void;
  onConflictDetected?: (conflict: { detectedGender: "men" | "women" | "unisex" }) => void;
  temporaryGenderOverride?: "men" | "women" | "unisex" | null;
  hasSensitiveItems?: boolean;
  totalExpectedItems?: number;
  modelProfile?: {
    model_gender?: string | null;
    model_height?: string | null;
    model_body_shape?: string | null;
    model_body_size?: string | null;
    model_skin_tone?: string | null;
    model_hair?: string | null;
  } | null;
  guestModelSetupOverride?: Record<string, unknown> | null;
}) {

  // Priority: profile model_gender (auth) > detected_gender (from GPT) > heuristic > "women".
  // `gender` prop is already the heuristic result.
  const outfitGender: "men" | "women" | "unisex" = detectedGender ?? gender;
  type State =
    | { kind: "idle" }
    | { kind: "loading" }
    | { kind: "image"; url: string }
    | { kind: "error" }
    | { kind: "daily" }
    | { kind: "guest" }
    | { kind: "guest-dismissed" };

  const [state, setState] = useState<State>({ kind: "idle" });
  const [setupOpen, setSetupOpen] = useState(false);
  const [lastImageUrl, setLastImageUrl] = useState<string | null>(null);

  // Always hold the latest items value to avoid stale-closure reads in generate().
  const latestItemsRef = useRef(items);
  useEffect(() => {
    latestItemsRef.current = items;
  }, [items]);

  const isStyleMyItem = mode === "style_item";

  useEffect(() => {
    setState({ kind: "idle" });
  }, [tier]);

  useEffect(() => {
    onLoadingChange?.(state.kind === "loading");
  }, [state.kind, onLoadingChange]);


  const runSingleGenerationWithGender = useCallback(
    async (modelSetup: ModelSetupPayload | null, effectiveGender: "men" | "women" | "unisex") => {
      setState({ kind: "loading" });
      try {
        const styleContext =
          isStyleMyItem && outfitId ? await fetchStyleContext(outfitId, isGuest) : null;
        const guestSessionId =
          typeof window !== "undefined" ? localStorage.getItem("guest_session_id") : null;
        const { data, error } = await invokeGenerateLookbook({
          items: latestItemsRef.current, tier, gender: effectiveGender, isGuest, modelSetup, styleContext,
          outfitId, userId, guestSessionId,
        });
        if (error) { setState({ kind: "error" }); return; }
        const d = data as { image_url?: string; error?: string; status?: string; outfit_id?: string };
        if (d?.error === "daily_limit_reached") { setState({ kind: "daily" }); return; }
        if (d?.error === "guest_limit_reached") {
          setSignupTrigger("lookbook");
          trackEvent("signup_prompted", { trigger: "lookbook" });
          if (isGuest && typeof window !== "undefined") {
            const today = new Date().toISOString().slice(0, 10);
            const storedDate = localStorage.getItem("lookbook_guest_date");
            const storedCount = parseInt(localStorage.getItem("lookbook_guest_count") ?? "0", 10);
            const count = storedDate === today ? storedCount : 0;
            trackEvent("lookbook_limit_reached", {
              is_guest: true,
              guest_session_id: localStorage.getItem("guest_session_id"),
              generations_used: count,
            });
          }
          setState({ kind: "guest" });
          return;
        }

        // Async-job branch: edge function accepted the work and is generating
        // in the background. Wait for the outfits row to flip to ready/failed
        // via Realtime + a 3s poll backstop.
        let finalImageUrl: string | null = null;
        if (d?.status === "pending" && d?.outfit_id) {
          const pendingOutfitId = d.outfit_id;
          debugLog('[LB1 poll] starting', { pendingOutfitId });
          type PollResult =
            | { ok: true; url: string }
            | { ok: false; reason: "content_policy" | "generic" };
          const result = await new Promise<PollResult>((resolve) => {
            let done = false;
            const finish = (r: PollResult) => {
              if (done) return;
              done = true;
              clearInterval(interval);
              clearTimeout(timeout);
              supabase.removeChannel(channel);
              resolve(r);
            };
            const handleRow = (row: {
              lookbook_status?: string | null;
              lookbook_image_url?: string | null;
              lookbook_error?: string | null;
            } | null) => {
              if (!row) return;
              if (row.lookbook_status === "ready" && row.lookbook_image_url) {
                finish({ ok: true, url: row.lookbook_image_url });
              } else if (row.lookbook_status === "failed") {
                finish({
                  ok: false,
                  reason: row.lookbook_error === "content_policy" ? "content_policy" : "generic",
                });
              }
            };
            const channel = supabase
              .channel(`lookbook:${pendingOutfitId}`)
              .on(
                "postgres_changes",
                {
                  event: "UPDATE",
                  schema: "public",
                  table: "outfits",
                  filter: `id=eq.${pendingOutfitId}`,
                },
                (payload) => handleRow(payload.new as {
                  lookbook_status?: string | null;
                  lookbook_image_url?: string | null;
                  lookbook_error?: string | null;
                }),
              )
              .subscribe();
            const interval = setInterval(async () => {
              let row: { lookbook_status?: string | null; lookbook_image_url?: string | null; lookbook_error?: string | null } | null = null;
              const direct = await supabase
                .from("outfits")
                .select("lookbook_status, lookbook_image_url, lookbook_error")
                .eq("id", pendingOutfitId)
                .maybeSingle();
              row = (direct.data as typeof row) ?? null;
              if (!row) {
                // Guest fallback: row exists but anon SELECT is locked; fetch via guest RPC.
                const { data: guestRows } = await (supabase as any).rpc("get_guest_outfit", {
                  p_id: pendingOutfitId,
                });
                if (Array.isArray(guestRows) && guestRows.length > 0) {
                  const g = guestRows[0] as { lookbook_status?: string | null; lookbook_image_url?: string | null; lookbook_error?: string | null };
                  row = {
                    lookbook_status: g.lookbook_status ?? null,
                    lookbook_image_url: g.lookbook_image_url ?? null,
                    lookbook_error: g.lookbook_error ?? null,
                  };
                }
              }
              handleRow(row);
            }, 3000);
            // Hard cap: 3 minutes — well beyond the ~84s typical generation.
            const timeout = setTimeout(() => finish({ ok: false, reason: "generic" }), 180000);
          });
          if (!result.ok) {
            setState({ kind: "error" });
            return;
          }
          finalImageUrl = result.url;
        } else if (d?.image_url) {
          // Legacy sync path (no outfit_id was sent).
          finalImageUrl = d.image_url;
        } else if (d?.error) {
          setState({ kind: "error" });
          return;
        } else {
          setState({ kind: "error" });
          return;
        }

        if (isGuest && typeof window !== "undefined") {
          const today = new Date().toISOString().slice(0, 10);
          const storedDate = localStorage.getItem("lookbook_guest_date");
          const storedCount = parseInt(localStorage.getItem("lookbook_guest_count") ?? "0", 10);
          const count = storedDate === today ? storedCount : 0;
          localStorage.setItem("lookbook_guest_count", String(count + 1));
          localStorage.setItem("lookbook_guest_date", today);
        }
        trackEvent("lookbook_generated", {
          tier,
          mode,
          has_model_setup: !!modelSetup,
        });
        setLastImageUrl(finalImageUrl);
        if (onImageGenerated) {
          onImageGenerated(finalImageUrl);
          setState({ kind: "idle" });
        } else {
          setState({ kind: "image", url: finalImageUrl });
        }
      } catch (e) {
        console.error(e);
        setState({ kind: "error" });
      }
    },
    [items, tier, isGuest, isStyleMyItem, outfitId, userId, mode, onImageGenerated],
  );

  const runSingleGeneration = useCallback(
    (modelSetup: ModelSetupPayload | null) => runSingleGenerationWithGender(modelSetup, outfitGender),
    [runSingleGenerationWithGender, outfitGender],
  );

  const generate = useCallback(async () => {
    debugLog('[LB1 generate] invoked', { isSwapping, itemsLen: latestItemsRef.current.length, isGuest, userId, outfitId });
    if (isSwapping) {
      debugLog("[LB1 generate] blocked: swap in progress");
      return;
    }
    if (latestItemsRef.current.length === 0) { debugLog('[LB1 generate] blocked: no items'); return; }
    // Guests: skip setup, single generation as before
    if (isGuest || !userId) {
      debugLog(
        `Lookbook gender: profile=null detected=${detectedGender} heuristic=${gender} final=${outfitGender}`,
      );
      await runSingleGeneration(
        (guestModelSetupOverride as ModelSetupPayload | null) ?? null,
      );
      return;
    }
    // Check profile for model setup
    const { data: prof } = await supabase
      .from("profiles")
      .select(
        "model_setup_complete, model_height, model_body_shape, model_body_size, model_skin_tone, model_preferred_variation, model_hair, model_gender, model_for, size_gender",
      )
      .eq("id", userId)
      .maybeSingle();
    if (prof?.model_setup_complete) {
      const p = prof as Record<string, unknown>;
      const variation = (p.model_preferred_variation as VariationKey | null) ?? "editorial_studio";
      const variationDescription = VARIATION_DESCRIPTIONS[variation];
      const modelFor = (p.model_for as string | null) ?? "myself";
      const savedGender = (p.model_gender as string | null) ?? null;
      const sizeGenderFallback = (p.size_gender as string | null) ?? null;
      const savedBodySize = (p.model_body_size as string | null) ?? null;
      const savedHeight = (p.model_height as string | null) ?? null;
      const savedBodyShape = (p.model_body_shape as string | null) ?? null;
      const savedSkinTone = (p.model_skin_tone as string | null) ?? null;
      const savedHair = (p.model_hair as string | null) ?? null;

      const modelSetup: ModelSetupPayload | null =
        savedHeight && savedBodyShape && savedSkinTone
          ? {
              height: savedHeight,
              body_shape: savedBodyShape,
              body_size: savedBodySize ?? undefined,
              skin_tone: savedSkinTone,
              hair: savedHair,
              variation,
              variation_description: variationDescription,
            }
          : null;
      const normalizeGender = (g: string | null): "men" | "women" | "unisex" | null => {
        if (!g) return null;
        if (g === "man" || g === "men") return "men";
        if (g === "woman" || g === "women") return "women";
        if (g === "non-binary") return "unisex";
        return null;
      };
      const profileGender =
        normalizeGender(savedGender) ?? normalizeGender(sizeGenderFallback);
      // Profile gender is the single source of truth. detectedGender no longer overrides it.
      const effectiveGender: "men" | "women" | "unisex" =
        temporaryGenderOverride ?? profileGender ?? "women";
      debugLog(
        "Invoking lookbook with gender:",
        effectiveGender,
        "model_gender:",
        savedGender,
        "size_gender:",
        sizeGenderFallback,
      );
      debugLog(
        `Lookbook gender: profile=${savedGender} detected=${detectedGender} heuristic=${gender} override=${temporaryGenderOverride} final=${effectiveGender}`,
      );

      // Gender conflict detection (only when model_for === "myself" and not a temporary override).
      // Compare saved model gender against GPT-detected outfit gender.
      if (!temporaryGenderOverride) {
        const savedIsFemale = savedGender === "woman" || savedGender === "non-binary";
        const savedIsMale = savedGender === "man";
        const detectedIsFemale = detectedGender === "women";
        const detectedIsMale = detectedGender === "men";
        const hasConflict =
          modelFor === "myself" &&
          !!savedGender &&
          detectedGender !== null &&
          ((savedIsMale && detectedIsFemale) || (savedIsFemale && detectedIsMale));
        debugLog(
          `Conflict check: saved=${savedGender} detected=${detectedGender} hasConflict=${hasConflict}`,
        );
        if (hasConflict) {
          onConflictDetected?.({
            detectedGender: detectedIsMale ? "men" : "women",
          });
        }
      }

      await runSingleGenerationWithGender(modelSetup, effectiveGender);
      return;
    }
    // Not set up — open setup flow
    setSetupOpen(true);
  }, [items, isGuest, userId, gender, detectedGender, outfitGender, runSingleGeneration, runSingleGenerationWithGender, isSwapping, temporaryGenderOverride, onConflictDetected, guestModelSetupOverride]);

  // External trigger: parent bumps triggerNonce to start a generation.
  const generatedForNonce = useRef<number>(0);

  useEffect(() => {
    const allItems = latestItemsRef.current;
    debugLog('[LB1 gate] effect fired', {
      triggerNonce,
      itemsLength: allItems.length,
      itemsPropLength: items.length,
      totalExpectedItems,
      generatedNonce: generatedForNonce.current,
      isSwapping,
      outfitId,
    });
    if (triggerNonce <= 0) { debugLog('[LB1 gate] bail: triggerNonce<=0'); return; }
    if (generatedForNonce.current === triggerNonce) { debugLog('[LB1 gate] bail: already-generated for nonce', triggerNonce); return; }
    if (allItems.length === 0) { debugLog('[LB1 gate] bail: no items'); return; }
    if (
      totalExpectedItems != null &&
      totalExpectedItems > 0 &&
      allItems.length < totalExpectedItems
    ) { debugLog('[LB1 gate] bail: items', allItems.length, '< expected', totalExpectedItems); return; }
    const missingTier = allItems.filter((i) => i.item.tiers?.mid == null);
    if (missingTier.length > 0) { debugLog('[LB1 gate] bail: missing tiers.mid for', missingTier.map((i) => i.item.name)); return; }
    const missingTitle = allItems.filter((i) => !i.item.tiers?.mid?.title);
    if (missingTitle.length > 0) { debugLog('[LB1 gate] bail: missing mid.title for', missingTitle.map((i) => i.item.name)); return; }
    if (isSwapping) { debugLog('[LB1 gate] bail: isSwapping'); return; }
    debugLog('[LB1 gate] ALL PASSED, calling generate()');
    generatedForNonce.current = triggerNonce;
    void generate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [triggerNonce, items]);




  const handleSave = (url: string) => {
    const a = document.createElement("a");
    a.href = url;
    a.download = "like2fit-look.png";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const handleShare = async () => {
    const shareData = {
      title: "My Like2Fit Look",
      text: "I found this outfit on Like2Fit! 👗",
      url: "https://www.like2fit.com",
    };
    if (typeof navigator !== "undefined" && (navigator as Navigator & { share?: (d: ShareData) => Promise<void> }).share) {
      try {
        await (navigator as Navigator & { share: (d: ShareData) => Promise<void> }).share(shareData);
        return;
      } catch {
        // fallthrough
      }
    }
    try {
      await navigator.clipboard.writeText(shareData.url);
      toast.success("Link copied!");
    } catch {
      toast.error("Couldn't share. Please try again.");
    }
  };

  if (state.kind === "loading") {
    return (
      <LookbookLoadingSilhouette
        modelProfile={modelProfile}
        label="The look"
        messages={[
          "Finding your exact look...",
          "Matching each piece...",
          "Styling your model...",
          "Almost ready...",
        ]}
      />
    );
  }

  if (state.kind === "image") {
    return (
      <div className="w-full">
        <div className="relative w-full" style={{ aspectRatio: "3 / 4" }}>
          <img
            src={state.url}
            alt="AI-generated lookbook"
            className="absolute inset-0 w-full h-full object-cover rounded-xl"
          />
          {/* PQ-1: floating ShareLookbookButton removed — share covered by per-card buttons */}
          <span
            className="absolute bottom-2 right-2 text-white rounded-xl"
            style={{
              fontSize: 12,
              padding: "7px 10px",
              background: "rgba(31,41,55,0.75)",
            }}
          >
            Like2Fit · On you
          </span>
        </div>
        {/* Conflict prompt now lives in Results, rendered below the lookbook image */}

        <div className="flex gap-2 mt-3">
          <button
            onClick={() => handleSave(state.url)}
            className="flex-1 py-3 rounded-full text-sm font-medium border border-border"
          >
            📸 Save Image
          </button>
          <button
            onClick={handleShare}
            className="flex-1 py-3 rounded-full text-sm font-medium border border-border"
          >
            🔗 Share This Look
          </button>
        </div>
        <p className="text-center italic mt-2" style={{ fontSize: 12, color: "#9CA3AF" }}>
          AI-generated for inspiration only. Actual products may vary.
        </p>
      </div>
    );
  }

  if (state.kind === "daily") {
    return (
      <div className="w-full py-4 text-center">
        <p className="text-sm" style={{ color: "#6B7280" }}>Something went wrong. Please try again.</p>
        <button
          onClick={generate}
          className="mt-3 w-full py-3 rounded-full text-sm font-medium bg-white"
          style={{ border: "2px solid var(--l2f-coral)", color: "var(--l2f-coral)" }}
        >
          Try Again
        </button>
      </div>
    );
  }

  if (state.kind === "guest") {
    return (
      <div className="w-full py-6 text-center">
        <div className="text-4xl">👗</div>
        <p className="font-medium mt-2">Want more AI looks?</p>
        <p className="text-sm mt-1" style={{ color: "#6B7280" }}>
          You've used your 10 free looks today. Sign up for unlimited AI-generated looks — free forever.
        </p>
        <button
          onClick={() => onRequestSignup("lookbook_limit")}
          className="mt-4 w-full py-3 rounded-full text-sm font-medium text-white"
          style={{ background: CORAL }}
        >
          Create Account
        </button>
        <button
          onClick={() => setState({ kind: "guest-dismissed" })}
          className="mt-2 text-xs underline"
          style={{ color: "#9CA3AF" }}
        >
          Maybe Later
        </button>
      </div>
    );
  }

  if (state.kind === "error") {
    return (
      <div className="w-full py-4 text-center">
        {hasSensitiveItems ? (
          <>
            <p className="text-sm" style={{ color: "#6B7280" }}>
              Lookbook generation isn't available for this outfit type due to content guidelines.
            </p>
            <p className="text-sm mt-1" style={{ color: "#6B7280" }}>
              You can still browse and buy all the items below ↓
            </p>
          </>
        ) : (
          <>
            <p className="text-sm" style={{ color: "#6B7280" }}>Couldn't generate this look.</p>
            <button
              onClick={generate}
              className="mt-3 w-full py-3 rounded-full text-sm font-medium bg-white"
              style={{ border: "2px solid var(--l2f-coral)", color: "var(--l2f-coral)" }}
            >
              Try Again
            </button>
          </>
        )}
      </div>
    );
  }

  // idle or guest-dismissed
  const disabled = state.kind === "guest-dismissed" || items.length === 0 || isSwapping;
  const hideIdleButton = renderMode === "headless";
  return (
    <>
      {!hideIdleButton && (
        <div className="w-full">
          <button
            onClick={generate}
            disabled={disabled}
            className={
              variant === "primary"
                ? "w-full text-white disabled:opacity-50 active:scale-[0.98] transition-transform flex items-center justify-center gap-2"
                : "w-full py-4 rounded-full text-sm font-medium bg-white disabled:opacity-50"
            }
            style={
              variant === "primary"
                ? { background: CORAL, fontSize: 16, fontWeight: 500, padding: 16, borderRadius: 999, pointerEvents: isSwapping ? "none" : undefined }
                : { border: "2px solid var(--l2f-coral)", color: "var(--l2f-coral)", pointerEvents: isSwapping ? "none" : undefined }
            }
          >
            {isSwapping ? (
              <><Loader2 size={16} className="animate-spin" /> Updating outfit…</>
            ) : variant === "primary"
              ? <>✨ See This Outfit on a Model</>
              : isStyleMyItem
                ? "✨ See Your Items Styled Together"
                : "✨ Generate This Look on a Model"}
          </button>
          {variant !== "primary" && (
            <p className="text-center mt-1.5" style={{ fontSize: 12, color: "#9CA3AF" }}>
              {state.kind === "guest-dismissed"
                ? "Sign up for more generations"
                : isStyleMyItem
                  ? "Your items + these pairings on a model · Takes ~15 seconds"
                  : "AI-generated styling · Takes ~15 seconds"}
            </p>
          )}
        </div>
      )}
      {setupOpen && userId && (
        <LookbookModelSetup
          userId={userId}
          items={items}
          tier={tier}
          gender={gender}
          mode={mode}
          outfitId={outfitId}
          onClose={() => setSetupOpen(false)}
          onComplete={(url) => {
            setSetupOpen(false);
            trackEvent("lookbook_generated", {
              tier,
              mode,
              has_model_setup: true,
            });
            setLastImageUrl(url);
            if (onImageGenerated) {
              onImageGenerated(url);
              setState({ kind: "idle" });
            } else {
              setState({ kind: "image", url });
            }
          }}
        />
      )}
    </>
  );
}

// ============================================================
// Personal Style Model setup flow
// ============================================================

const HEIGHT_OPTIONS: { key: HeightKey; label: string }[] = [
  { key: "under_5_4", label: "Under 5'4\"" },
  { key: "5_4_to_5_7", label: "5'4\" – 5'7\"" },
  { key: "over_5_7", label: "5'7\"+" },
];

type BodyShapeOption = { key: BodyShapeKey; label: string; description: string; svg: React.ReactNode };

const BODY_SHAPES_FEMALE: BodyShapeOption[] = [
  {
    key: "straight",
    label: "Straight",
    description: "Similar width shoulders, waist and hips",
    svg: (
      <svg viewBox="0 0 60 100" className="w-12 h-20" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M22 12 Q22 6 30 6 Q38 6 38 12 Q38 18 30 20 Q22 18 22 12 Z" />
        <path d="M22 22 L20 50 L22 80 L26 92 M38 22 L40 50 L38 80 L34 92" />
        <path d="M22 22 L38 22 M20 50 L40 50 M22 80 L38 80" />
      </svg>
    ),
  },
  {
    key: "hourglass",
    label: "Hourglass",
    description: "Defined waist, balanced shoulders and hips",
    svg: (
      <svg viewBox="0 0 60 100" className="w-12 h-20" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M22 12 Q22 6 30 6 Q38 6 38 12 Q38 18 30 20 Q22 18 22 12 Z" />
        <path d="M18 24 L26 50 L18 78 L24 92 M42 24 L34 50 L42 78 L36 92" />
        <path d="M18 24 L42 24 M26 50 L34 50 M18 78 L42 78" />
      </svg>
    ),
  },
  {
    key: "pear",
    label: "Pear",
    description: "Hips wider than shoulders",
    svg: (
      <svg viewBox="0 0 60 100" className="w-12 h-20" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M22 12 Q22 6 30 6 Q38 6 38 12 Q38 18 30 20 Q22 18 22 12 Z" />
        <path d="M24 24 L26 50 L16 80 L22 92 M36 24 L34 50 L44 80 L38 92" />
        <path d="M24 24 L36 24 M26 50 L34 50 M16 80 L44 80" />
      </svg>
    ),
  },
  {
    key: "athletic",
    label: "Athletic",
    description: "Toned, broad shoulders",
    svg: (
      <svg viewBox="0 0 60 100" className="w-12 h-20" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M22 12 Q22 6 30 6 Q38 6 38 12 Q38 18 30 20 Q22 18 22 12 Z" />
        <path d="M16 24 L24 50 L26 80 L28 92 M44 24 L36 50 L34 80 L32 92" />
        <path d="M16 24 L44 24 M24 50 L36 50 M26 80 L34 80" />
      </svg>
    ),
  },
];

const BODY_SHAPES_MALE: BodyShapeOption[] = [
  {
    key: "lean",
    label: "Lean",
    description: "Slim build, narrow shoulders and hips",
    svg: (
      <svg viewBox="0 0 60 100" className="w-12 h-20" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M24 12 Q24 6 30 6 Q36 6 36 12 Q36 18 30 20 Q24 18 24 12 Z" />
        <path d="M24 22 L24 50 L24 80 L26 92 M36 22 L36 50 L36 80 L34 92" />
        <path d="M24 22 L36 22 M24 50 L36 50 M24 80 L36 80" />
      </svg>
    ),
  },
  {
    key: "athletic",
    label: "Athletic",
    description: "Broad shoulders, narrow waist",
    svg: (
      <svg viewBox="0 0 60 100" className="w-12 h-20" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M24 12 Q24 6 30 6 Q36 6 36 12 Q36 18 30 20 Q24 18 24 12 Z" />
        <path d="M14 24 L22 50 L24 80 L26 92 M46 24 L38 50 L36 80 L34 92" />
        <path d="M14 24 L46 24 M22 50 L38 50 M24 80 L36 80" />
      </svg>
    ),
  },
  {
    key: "broad",
    label: "Broad",
    description: "Wide shoulders and chest, stocky build",
    svg: (
      <svg viewBox="0 0 60 100" className="w-12 h-20" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M24 12 Q24 6 30 6 Q36 6 36 12 Q36 18 30 20 Q24 18 24 12 Z" />
        <path d="M12 24 L18 50 L20 80 L22 92 M48 24 L42 50 L40 80 L38 92" />
        <path d="M12 24 L48 24 M18 50 L42 50 M20 80 L40 80" />
      </svg>
    ),
  },
  {
    key: "regular",
    label: "Regular",
    description: "Average proportions, balanced build",
    svg: (
      <svg viewBox="0 0 60 100" className="w-12 h-20" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M24 12 Q24 6 30 6 Q36 6 36 12 Q36 18 30 20 Q24 18 24 12 Z" />
        <path d="M20 22 L22 50 L22 80 L24 92 M40 22 L38 50 L38 80 L36 92" />
        <path d="M20 22 L40 22 M22 50 L38 50 M22 80 L38 80" />
      </svg>
    ),
  },
];

function bodyShapesFor(g: GenderKind): BodyShapeOption[] {
  return g === "male" ? BODY_SHAPES_MALE : BODY_SHAPES_FEMALE;
}

const BODY_SIZE_FEMALE: { key: BodySizeKey; label: string }[] = [
  { key: "slim", label: "Slim" },
  { key: "average", label: "Average" },
  { key: "curvy", label: "Curvy" },
  { key: "plus_size", label: "Plus size" },
];
const BODY_SIZE_MALE: { key: BodySizeKey; label: string }[] = [
  { key: "slim_lean", label: "Slim/Lean" },
  { key: "average", label: "Average" },
  { key: "stocky", label: "Stocky" },
  { key: "plus_size", label: "Plus size" },
];
const BODY_SIZE_NB: { key: BodySizeKey; label: string }[] = [
  { key: "slim", label: "Slim" },
  { key: "average", label: "Average" },
  { key: "curvy_stocky", label: "Curvy/Stocky" },
  { key: "plus_size", label: "Plus size" },
];
function bodySizesFor(g: GenderKind): { key: BodySizeKey; label: string }[] {
  if (g === "male") return BODY_SIZE_MALE;
  if (g === "non-binary") return BODY_SIZE_NB;
  return BODY_SIZE_FEMALE;
}

const SKIN_TONES = ["#F5D5B8", "#E8B89A", "#D4956A", "#B5723A", "#8B4513", "#4A2410"];

type HairOption = { key: HairKey; label: string; svg: React.ReactNode };

const HAIR_OPTIONS_FEMALE: HairOption[] = [
  {
    key: "short_dark",
    label: "Short Dark",
    svg: (
      <svg viewBox="0 0 60 60" className="w-12 h-12" fill="none">
        <circle cx="30" cy="32" r="14" fill="#F5D5B8" stroke="#9CA3AF" strokeWidth="1.5" />
        <path d="M16 28 Q16 14 30 14 Q44 14 44 28 Q40 22 30 22 Q20 22 16 28 Z" fill="#1F2937" />
      </svg>
    ),
  },
  {
    key: "short_light",
    label: "Short Light",
    svg: (
      <svg viewBox="0 0 60 60" className="w-12 h-12" fill="none">
        <circle cx="30" cy="32" r="14" fill="#F5D5B8" stroke="#9CA3AF" strokeWidth="1.5" />
        <path d="M16 28 Q16 14 30 14 Q44 14 44 28 Q40 22 30 22 Q20 22 16 28 Z" fill="#E8C77A" />
      </svg>
    ),
  },
  {
    key: "long_dark",
    label: "Long Dark",
    svg: (
      <svg viewBox="0 0 60 60" className="w-12 h-12" fill="none">
        <circle cx="30" cy="30" r="14" fill="#F5D5B8" stroke="#9CA3AF" strokeWidth="1.5" />
        <path d="M14 28 Q14 12 30 12 Q46 12 46 28 L46 50 Q42 36 38 36 L22 36 Q18 36 14 50 Z" fill="#1F2937" />
      </svg>
    ),
  },
  {
    key: "long_light",
    label: "Long Light",
    svg: (
      <svg viewBox="0 0 60 60" className="w-12 h-12" fill="none">
        <circle cx="30" cy="30" r="14" fill="#F5D5B8" stroke="#9CA3AF" strokeWidth="1.5" />
        <path d="M14 28 Q14 12 30 12 Q46 12 46 28 L46 50 Q42 36 38 36 L22 36 Q18 36 14 50 Z" fill="#E8C77A" />
      </svg>
    ),
  },
  {
    key: "natural",
    label: "Natural",
    svg: (
      <svg viewBox="0 0 60 60" className="w-12 h-12" fill="none">
        <circle cx="30" cy="34" r="12" fill="#F5D5B8" stroke="#9CA3AF" strokeWidth="1.5" />
        <circle cx="30" cy="26" r="18" fill="#3B2417" />
        <circle cx="30" cy="34" r="12" fill="#F5D5B8" />
      </svg>
    ),
  },
  {
    key: "medium_wavy",
    label: "Medium Wavy",
    svg: (
      <svg viewBox="0 0 60 60" className="w-12 h-12" fill="none">
        <circle cx="30" cy="30" r="14" fill="#F5D5B8" stroke="#9CA3AF" strokeWidth="1.5" />
        <path d="M15 30 Q15 14 30 14 Q45 14 45 30 Q45 38 42 44 Q40 38 38 42 Q36 38 34 42 Q32 38 30 42 Q28 38 26 42 Q24 38 22 42 Q20 38 18 44 Q15 38 15 30 Z" fill="#6B4423" />
      </svg>
    ),
  },
];

const HAIR_OPTIONS_MALE: HairOption[] = [
  {
    key: "short_dark",
    label: "Short Dark",
    svg: (
      <svg viewBox="0 0 60 60" className="w-12 h-12" fill="none">
        <circle cx="30" cy="32" r="14" fill="#F5D5B8" stroke="#9CA3AF" strokeWidth="1.5" />
        <path d="M16 28 Q16 14 30 14 Q44 14 44 28 Q40 22 30 22 Q20 22 16 28 Z" fill="#1F2937" />
      </svg>
    ),
  },
  {
    key: "short_light",
    label: "Short Light",
    svg: (
      <svg viewBox="0 0 60 60" className="w-12 h-12" fill="none">
        <circle cx="30" cy="32" r="14" fill="#F5D5B8" stroke="#9CA3AF" strokeWidth="1.5" />
        <path d="M16 28 Q16 14 30 14 Q44 14 44 28 Q40 22 30 22 Q20 22 16 28 Z" fill="#E8C77A" />
      </svg>
    ),
  },
  {
    key: "bald",
    label: "Shaved/Bald",
    svg: (
      <svg viewBox="0 0 60 60" className="w-12 h-12" fill="none">
        <circle cx="30" cy="30" r="15" fill="#F5D5B8" stroke="#9CA3AF" strokeWidth="1.5" />
      </svg>
    ),
  },
  {
    key: "curly_afro",
    label: "Curly/Afro",
    svg: (
      <svg viewBox="0 0 60 60" className="w-12 h-12" fill="none">
        <circle cx="30" cy="34" r="12" fill="#F5D5B8" stroke="#9CA3AF" strokeWidth="1.5" />
        <circle cx="30" cy="24" r="17" fill="#1F2937" />
        <circle cx="30" cy="34" r="12" fill="#F5D5B8" />
      </svg>
    ),
  },
  {
    key: "medium_length",
    label: "Medium Length",
    svg: (
      <svg viewBox="0 0 60 60" className="w-12 h-12" fill="none">
        <circle cx="30" cy="32" r="14" fill="#F5D5B8" stroke="#9CA3AF" strokeWidth="1.5" />
        <path d="M16 30 Q16 12 30 12 Q44 12 44 30 Q44 36 42 40 L40 32 Q38 38 36 32 Q34 38 32 32 Q30 38 28 32 Q26 38 24 32 Q22 38 20 32 L18 40 Q16 36 16 30 Z" fill="#3B2417" />
      </svg>
    ),
  },
  {
    key: "locs_braids",
    label: "Locs/Braids",
    svg: (
      <svg viewBox="0 0 60 60" className="w-12 h-12" fill="none">
        <circle cx="30" cy="30" r="14" fill="#F5D5B8" stroke="#9CA3AF" strokeWidth="1.5" />
        <path d="M16 28 Q16 12 30 12 Q44 12 44 28 Z" fill="#1F2937" />
        <path d="M18 28 L17 52 M23 30 L22 54 M28 31 L28 55 M33 31 L33 55 M38 30 L39 54 M43 28 L44 52" stroke="#1F2937" strokeWidth="2.5" strokeLinecap="round" />
      </svg>
    ),
  },
];

function hairOptionsFor(g: GenderKind): HairOption[] {
  return g === "male" ? HAIR_OPTIONS_MALE : HAIR_OPTIONS_FEMALE;
}

const SAMPLE_LOOKBOOK_URL =
  "https://images.unsplash.com/photo-1490481651871-ab68de25d43d?auto=format&fit=crop&w=800&q=80";

function LookbookModelSetup({
  userId,
  items,
  tier,
  gender,
  onClose,
  onComplete,
  startStep = "preview",
  accountMode = false,
  initial,
  mode = "find_look",
  outfitId = null,
}: {
  userId: string;
  items?: { item: DetectedItem; product: ShoppingProduct }[];
  tier?: TierKey;
  gender?: "men" | "women" | "unisex";
  onClose: () => void;
  onComplete?: (url: string) => void;
  startStep?: "preview" | "collect";
  accountMode?: boolean;
  initial?: {
    modelFor?: ModelForKey | null;
    modelGender?: ModelGenderKey | null;
    height?: HeightKey | null;
    bodyShape?: BodyShapeKey | null;
    bodySize?: BodySizeKey | null;
    skinTone?: string | null;
    hair?: HairKey | null;
  };
  mode?: AnalysisMode;
  outfitId?: string | null;
}) {
  const isStyleMyItem = mode === "style_item";
  type Step = "preview" | "collect" | "generating" | "select" | "saving";
  const [step, setStep] = useState<Step>(startStep);
  const [modelFor, setModelFor] = useState<ModelForKey | null>(initial?.modelFor ?? null);
  const [modelGender, setModelGender] = useState<ModelGenderKey | null>(initial?.modelGender ?? null);
  const [height, setHeight] = useState<HeightKey | null>(initial?.height ?? null);
  const [bodyShape, setBodyShape] = useState<BodyShapeKey | null>(initial?.bodyShape ?? null);
  const [bodySize, setBodySize] = useState<BodySizeKey | null>(initial?.bodySize ?? null);
  const [skinTone, setSkinTone] = useState<string | null>(initial?.skinTone ?? null);
  const [hair, setHair] = useState<HairKey | null>(initial?.hair ?? null);
  const [generated, setGenerated] = useState<{ variation: VariationKey; url: string }[]>([]);
  const [selected, setSelected] = useState<VariationKey | null>(null);

  // Effective gender for dynamic options.
  const effectiveGenderKind: GenderKind =
    modelGender === "man"
      ? "male"
      : modelGender === "non-binary"
        ? "non-binary"
        : "female";

  const currentBodyShapes = bodyShapesFor(effectiveGenderKind);
  const currentBodySizes = bodySizesFor(effectiveGenderKind);
  const currentHairOptions = hairOptionsFor(effectiveGenderKind);

  // When gender changes, clear selections that no longer belong to the new option set.
  useEffect(() => {
    if (bodyShape && !currentBodyShapes.some((s) => s.key === bodyShape)) {
      setBodyShape(null);
    }
    if (bodySize && !currentBodySizes.some((s) => s.key === bodySize)) {
      setBodySize(null);
    }
    if (hair && !currentHairOptions.some((h) => h.key === hair)) {
      setHair(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveGenderKind]);

  const genderForGeneration: "men" | "women" | "unisex" =
    modelGender === "man"
      ? "men"
      : modelGender === "woman"
        ? "women"
        : modelGender === "non-binary"
          ? "unisex"
          : (gender ?? "unisex");

  const canContinue =
    !!modelFor &&
    !!modelGender &&
    !!height &&
    !!bodySize &&
    !!bodyShape &&
    !!skinTone &&
    !!hair;

  const skip = async () => {
    await supabase.from("profiles").update({ model_setup_complete: true }).eq("id", userId);
    if (accountMode || !items || !tier || !gender || !onComplete) {
      onClose();
      return;
    }
    // Trigger a default generation
    setStep("generating");
    const styleContext =
      isStyleMyItem && outfitId ? await fetchStyleContext(outfitId) : null;
    const { data, error } = await invokeGenerateLookbook({
      items, tier, gender, isGuest: false, modelSetup: null, styleContext,
    });
    if (error || (data as { error?: string })?.error || !(data as { image_url?: string })?.image_url) {
      toast.error("Generation failed. Please try again.");
      onClose();
      return;
    }
    onComplete((data as { image_url: string }).image_url);
  };

  const saveProfileOnly = async () => {
    if (!canContinue || !height || !bodyShape || !bodySize || !skinTone || !hair || !modelFor) return;
    setStep("saving");
    await supabase
      .from("profiles")
      .update({
        model_for: modelFor,
        model_gender: modelFor === "myself" ? modelGender : null,
        model_height: height,
        model_body_shape: bodyShape,
        model_body_size: bodySize,
        model_skin_tone: skinTone,
        model_hair: hair,
        model_setup_complete: true,
      })
      .eq("id", userId);
    setTimeout(() => {
      onClose();
    }, 1200);
  };

  const startGeneration = async () => {
    if (!canContinue || !height || !bodyShape || !bodySize || !skinTone || !hair) return;
    if (accountMode || !items || !tier) {
      await saveProfileOnly();
      return;
    }
    setStep("generating");
    const variations: VariationKey[] = ["editorial_studio", "street_style", "lifestyle"];
    const styleContext =
      isStyleMyItem && outfitId ? await fetchStyleContext(outfitId) : null;
    const results = await Promise.all(
      variations.map((v) =>
        invokeGenerateLookbook({
          items, tier, gender: genderForGeneration, isGuest: false,
          modelSetup: {
            height, body_shape: bodyShape, body_size: bodySize, skin_tone: skinTone, hair,
            variation: v, variation_description: VARIATION_DESCRIPTIONS[v],
          },
          styleContext,
        }),
      ),
    );
    const successes: { variation: VariationKey; url: string }[] = [];
    results.forEach((r, i) => {
      const d = r.data as { image_url?: string; error?: string } | null;
      if (!r.error && d?.image_url) successes.push({ variation: variations[i], url: d.image_url });
    });
    if (successes.length === 0) {
      toast.error("Generation failed. Please try again.");
      onClose();
      return;
    }
    setGenerated(successes);
    setStep("select");
  };

  const saveModel = async () => {
    if (!selected || !height || !bodyShape || !bodySize || !skinTone || !hair || !modelFor) return;
    setStep("saving");
    await supabase
      .from("profiles")
      .update({
        model_for: modelFor,
        model_gender: modelFor === "myself" ? modelGender : null,
        model_height: height,
        model_body_shape: bodyShape,
        model_body_size: bodySize,
        model_skin_tone: skinTone,
        model_hair: hair,
        model_preferred_variation: selected,
        model_setup_complete: true,
      })
      .eq("id", userId);
    const chosen = generated.find((g) => g.variation === selected);
    setTimeout(() => {
      if (chosen && onComplete) onComplete(chosen.url);
      else onClose();
    }, 1500);
  };

  return (
    <div className="fixed inset-0 z-[60] bg-background overflow-y-auto">
      <div className="max-w-md mx-auto min-h-full flex flex-col px-6 pt-10 pb-10">
        {step === "preview" && (
          <>
            <div className="text-center">
              <div
                className="mx-auto w-20 h-20 rounded-full flex items-center justify-center"
                style={{ background: "var(--l2f-cream)" }}
              >
                <Sparkles size={36} style={{ color: CORAL }} />
              </div>
              <p
                className="l2f-section-label"
                style={{ color: "var(--l2f-coral)", marginTop: 16, marginBottom: 8 }}
              >
                LIKE2FIT · THE MODEL
              </p>
              <h2
                style={{
                  fontFamily: "var(--font-display)",
                  fontStyle: "italic",
                  fontWeight: 400,
                  fontSize: 30,
                  lineHeight: 1.1,
                  color: "var(--l2f-ink)",
                }}
              >
                Your personal style model
              </h2>
              <p className="mt-2 text-sm" style={{ color: "#6B7280" }}>
                We'll create an AI model that represents you in every lookbook generation
              </p>
            </div>
            <div className="mt-6">
              <img
                src={SAMPLE_LOOKBOOK_URL}
                alt="Sample lookbook"
                className="w-full rounded-2xl object-cover"
                style={{ aspectRatio: "1/1" }}
              />
              <p className="text-center mt-2 text-xs font-medium" style={{ color: CORAL }}>
                Your lookbooks will look like this — but styled to represent you
              </p>
            </div>
            <div className="mt-auto pt-8">
              <button
                onClick={() => setStep("collect")}
                className="w-full py-4 rounded-full text-sm font-medium text-white"
                style={{ background: CORAL }}
              >
                Create My Model →
              </button>
              <button
                onClick={skip}
                className="mt-3 w-full text-xs underline"
                style={{ color: "#9CA3AF" }}
              >
                Skip — use default model
              </button>
            </div>
          </>
        )}

        {step === "collect" && (
          <>
            <div className="flex items-center justify-between">
              <button onClick={onClose} aria-label="Back">
                <ArrowLeft size={22} />
              </button>
              <div className="flex gap-1.5">
                <span className="w-2 h-2 rounded-full" style={{ background: CORAL }} />
                <span className="w-2 h-2 rounded-full" style={{ background: "#E5E7EB" }} />
              </div>
            </div>
            <p
              className="l2f-section-label"
              style={{ color: "var(--l2f-coral)", marginTop: 18, marginBottom: 8 }}
            >
              STEP ONE · ABOUT YOU
            </p>
            <h2
              style={{
                fontFamily: "var(--font-display)",
                fontStyle: "italic",
                fontWeight: 400,
                fontSize: 30,
                lineHeight: 1.1,
                color: "var(--l2f-ink)",
              }}
            >
              Tell us about you
            </h2>
            <p className="mt-1 text-sm" style={{ color: "#6B7280" }}>
              This helps us generate a model that looks like you
            </p>

            <div className="mt-6">
              <p className="text-sm font-medium">Who is this model for?</p>
              <div className="mt-3 grid grid-cols-2 gap-2">
                {([
                  { key: "myself" as const, label: "👤 Myself" },
                  { key: "other" as const, label: "👥 Someone else" },
                ]).map((o) => {
                  const active = modelFor === o.key;
                  return (
                    <button
                      key={o.key}
                      onClick={() => setModelFor(o.key)}
                      className="py-3 rounded-full text-sm font-medium border"
                      style={{
                        background: active ? CORAL : "#fff",
                        color: active ? "#fff" : "#374151",
                        borderColor: active ? CORAL : "#E5E7EB",
                      }}
                    >
                      {o.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {modelFor && (
              <div className="mt-6">
                <p className="text-sm font-medium">
                  {modelFor === "myself" ? "Your gender" : "Their gender"}
                </p>
                <div className="mt-3 flex flex-wrap gap-2">
                  {([
                    { key: "woman" as const, label: "Woman" },
                    { key: "man" as const, label: "Man" },
                    { key: "non-binary" as const, label: "Non-binary" },
                  ]).map((o) => {
                    const active = modelGender === o.key;
                    return (
                      <button
                        key={o.key}
                        onClick={() => setModelGender(o.key)}
                        className="px-4 py-2 rounded-full text-sm font-medium border"
                        style={{
                          background: active ? CORAL : "#fff",
                          color: active ? "#fff" : "#374151",
                          borderColor: active ? CORAL : "#E5E7EB",
                        }}
                      >
                        {o.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            <div className="mt-6">
              <p className="text-sm font-medium">Your height</p>
              <div className="mt-3 flex flex-wrap gap-2">
                {HEIGHT_OPTIONS.map((o) => {
                  const active = height === o.key;
                  return (
                    <button
                      key={o.key}
                      onClick={() => setHeight(o.key)}
                      className="px-4 py-2 rounded-full text-sm font-medium border"
                      style={{
                        background: active ? CORAL : "#fff",
                        color: active ? "#fff" : "#374151",
                        borderColor: active ? CORAL : "#E5E7EB",
                      }}
                    >
                      {o.label}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="mt-6">
              <p className="text-sm font-medium">Your body size</p>
              <p className="text-xs mt-0.5" style={{ color: "#9CA3AF" }}>
                Select the closest match
              </p>
              <div className="mt-3 flex flex-wrap gap-2">
                {currentBodySizes.map((o) => {
                  const active = bodySize === o.key;
                  return (
                    <button
                      key={o.key}
                      onClick={() => setBodySize(o.key)}
                      className="px-4 py-2 rounded-full text-sm font-medium border"
                      style={{
                        background: active ? CORAL : "#fff",
                        color: active ? "#fff" : "#374151",
                        borderColor: active ? CORAL : "#E5E7EB",
                      }}
                    >
                      {o.label}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="mt-6">
              <p className="text-sm font-medium">Your body shape</p>
              <p className="text-xs mt-0.5" style={{ color: "#9CA3AF" }}>
                Select the silhouette that most resembles yours
              </p>
              <div className="mt-3 grid grid-cols-2 gap-3">
                {currentBodyShapes.map((s) => {
                  const active = bodyShape === s.key;
                  return (
                    <button
                      key={s.key}
                      onClick={() => setBodyShape(s.key)}
                      className="relative rounded-2xl p-3 flex flex-col items-center text-center bg-white"
                      style={{
                        border: active ? `2px solid ${CORAL}` : "1px solid #E5E7EB",
                      }}
                    >
                      {active && (
                        <span
                          className="absolute top-2 right-2 w-5 h-5 rounded-full flex items-center justify-center text-white text-xs"
                          style={{ background: CORAL }}
                        >
                          ✓
                        </span>
                      )}
                      <div style={{ color: "#374151" }}>{s.svg}</div>
                      <p className="mt-2 text-sm font-medium">{s.label}</p>
                      <p className="text-[12px] mt-0.5 leading-tight" style={{ color: "#9CA3AF" }}>
                        {s.description}
                      </p>
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="mt-6">
              <p className="text-sm font-medium">Your skin tone</p>
              <p className="text-xs mt-0.5" style={{ color: "#9CA3AF" }}>
                Select the closest match
              </p>
              <div className="mt-3 flex justify-between items-center">
                {SKIN_TONES.map((c) => {
                  const active = skinTone === c;
                  return (
                    <button
                      key={c}
                      onClick={() => setSkinTone(c)}
                      aria-label={`Skin tone ${c}`}
                      className="rounded-full flex items-center justify-center"
                      style={{
                        width: 60,
                        height: 60,
                        padding: 3,
                        border: active ? `3px solid ${CORAL}` : "3px solid transparent",
                      }}
                    >
                      <span
                        className="rounded-full block"
                        style={{ width: 48, height: 48, background: c }}
                      />
                    </button>
                  );
                })}
              </div>
            </div>


            <div className="mt-6">
              <p className="text-sm font-medium">Your hair</p>
              <p className="text-xs mt-0.5" style={{ color: "#9CA3AF" }}>
                Select the closest match
              </p>
              <div className="mt-3 grid grid-cols-3 gap-3">
                {currentHairOptions.map((h) => {
                  const active = hair === h.key;
                  return (
                    <button
                      key={h.key}
                      onClick={() => setHair(h.key)}
                      className="relative rounded-2xl p-3 flex flex-col items-center text-center bg-white"
                      style={{
                        border: active ? `2px solid ${CORAL}` : "1px solid #E5E7EB",
                      }}
                    >
                      {active && (
                        <span
                          className="absolute top-1.5 right-1.5 w-5 h-5 rounded-full flex items-center justify-center text-white text-xs"
                          style={{ background: CORAL }}
                        >
                          ✓
                        </span>
                      )}
                      {h.svg}
                      <p className="mt-1 text-xs font-medium">{h.label}</p>
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="mt-auto pt-8">
              <button
                onClick={startGeneration}
                disabled={!canContinue}
                className="w-full py-4 rounded-full text-sm font-medium text-white disabled:opacity-50"
                style={{ background: CORAL }}
              >
                Continue →
              </button>
            </div>
          </>
        )}

        {step === "generating" && (
          <div className="flex-1 flex flex-col items-center justify-center text-center">
            <div
              className="w-full rounded-2xl py-16 px-6 animate-pulse"
              style={{
                background: "linear-gradient(120deg, #FFE4E4, var(--l2f-coral), #FFE4E4)",
                backgroundSize: "200% 200%",
              }}
            >
              <div className="text-5xl">✨</div>
              <p
                className="mt-4 text-white"
                style={{
                  fontFamily: "var(--font-display)",
                  fontStyle: "italic",
                  fontWeight: 400,
                  fontSize: 26,
                  lineHeight: 1.15,
                }}
              >
                Creating your models…
              </p>
              <p className="mt-1 text-sm text-white/90">Generating 3 options for you to choose from</p>
              <p className="mt-1 text-xs text-white/80">This takes about 30 seconds</p>
              <div className="flex gap-1.5 justify-center mt-4">
                <span className="w-2 h-2 bg-white rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
                <span className="w-2 h-2 bg-white rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
                <span className="w-2 h-2 bg-white rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
              </div>
            </div>
          </div>
        )}

        {step === "select" && (
          <>
            <p
              className="l2f-section-label"
              style={{ color: "var(--l2f-coral)", marginBottom: 8 }}
            >
              STEP TWO · THE EDIT
            </p>
            <h2
              style={{
                fontFamily: "var(--font-display)",
                fontStyle: "italic",
                fontWeight: 400,
                fontSize: 30,
                lineHeight: 1.1,
                color: "var(--l2f-ink)",
              }}
            >
              Choose your model
            </h2>
            <p className="mt-1 text-sm" style={{ color: "#6B7280" }}>
              Select the one that best represents you
            </p>

            <div className="mt-4 flex flex-col gap-4">
              {generated.map((g) => {
                const active = selected === g.variation;
                return (
                  <button
                    key={g.variation}
                    onClick={() => setSelected(g.variation)}
                    className="relative w-full text-left"
                  >
                    <div
                      className="relative w-full overflow-hidden"
                      style={{
                        aspectRatio: "1/1",
                        borderRadius: 12,
                        border: active ? `3px solid ${CORAL}` : "1px solid #E5E7EB",
                      }}
                    >
                      <img  loading="lazy" decoding="async"src={g.url} alt={VARIATION_LABELS[g.variation]} className="w-full h-full object-cover" />
                      {active && (
                        <span
                          className="absolute top-2 right-2 w-7 h-7 rounded-full flex items-center justify-center text-white"
                          style={{ background: CORAL }}
                        >
                          ✓
                        </span>
                      )}
                    </div>
                    <p className="mt-2 text-sm font-medium">{VARIATION_LABELS[g.variation]}</p>
                  </button>
                );
              })}
            </div>

            <div className="mt-auto pt-8">
              <button
                onClick={saveModel}
                disabled={!selected}
                className="w-full py-4 rounded-full text-sm font-medium text-white disabled:opacity-50"
                style={{ background: CORAL }}
              >
                Save This Model →
              </button>
              <p className="text-center text-xs mt-2" style={{ color: "#9CA3AF" }}>
                You can generate new options anytime from your Account page
              </p>
            </div>
          </>
        )}

        {step === "saving" && (
          <div className="flex-1 flex flex-col items-center justify-center text-center">
            <div
              className="w-20 h-20 rounded-full flex items-center justify-center"
              style={{ background: "var(--l2f-cream)" }}
            >
              <Check size={40} style={{ color: CORAL }} strokeWidth={2.5} />
            </div>
            <p
              className="mt-4"
              style={{
                fontFamily: "var(--font-display)",
                fontStyle: "italic",
                fontWeight: 400,
                fontSize: 26,
                lineHeight: 1.15,
                color: "var(--l2f-ink)",
              }}
            >
              Your model is saved
            </p>
            <p className="mt-1 text-sm" style={{ color: "#6B7280" }}>
              It will be used in all your future lookbook generations
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// LookbookModelRow — Account page model editor, aligned with GRWM onboarding
// vocabulary. Replaces the legacy LookbookModelSetup sheet on the Account page.
// ============================================================================

type GrwmGender = "women" | "men" | "non-binary";

const GRWM_SKIN_TONES: { key: string; hex: string }[] = [
  { key: "light", hex: "#FDDBB4" },
  { key: "light_medium", hex: "#F5C18A" },
  { key: "medium", hex: "#D4956A" },
  { key: "medium_deep", hex: "#B07346" },
  { key: "deep", hex: "#7B4F2E" },
  { key: "very_deep", hex: "#4A2910" },
];

const GRWM_SKIN_HEX: Record<string, string> = Object.fromEntries(
  GRWM_SKIN_TONES.map((s) => [s.key, s.hex]),
);

function grwmHeightOptions(g: GrwmGender): { value: string; label: string }[] {
  if (g === "men") {
    return [
      { value: "short", label: "Under 5'7" },
      { value: "average_short", label: "5'7 to 5'10" },
      { value: "average_tall", label: "5'10 to 6'2" },
      { value: "tall", label: "Over 6'2" },
    ];
  }
  if (g === "non-binary") {
    return [
      { value: "short", label: "Under 5'3" },
      { value: "average_short", label: "5'3 to 5'6" },
      { value: "average_tall", label: "5'6 to 5'9" },
      { value: "tall", label: "Over 5'9" },
    ];
  }
  return [
    { value: "short", label: "Under 5'2" },
    { value: "average_short", label: "5'2 to 5'5" },
    { value: "average_tall", label: "5'5 to 5'8" },
    { value: "tall", label: "Over 5'8" },
  ];
}

function grwmBodyShapeOptions(g: GrwmGender): { value: string; label: string }[] {
  if (g === "men") {
    return [
      { value: "slim_lean", label: "Slim / lean" },
      { value: "regular_average", label: "Regular / average" },
      { value: "athletic_broad", label: "Athletic / broad" },
      { value: "stocky", label: "Stocky" },
      { value: "plus_size", label: "Plus size" },
    ];
  }
  return [
    { value: "hourglass", label: "Hourglass" },
    { value: "pear", label: "Pear" },
    { value: "athletic", label: "Athletic" },
    { value: "curvy", label: "Curvy" },
    { value: "petite", label: "Petite" },
  ];
}

function grwmBodySizeOptions(): { value: string; label: string }[] {
  return [
    { value: "slim", label: "Slim" },
    { value: "average", label: "Average" },
    { value: "fuller", label: "Fuller" },
    { value: "plus_size", label: "Plus size" },
  ];
}

function grwmHairOptions(g: GrwmGender): { value: string; label: string }[] {
  if (g === "men") {
    return [
      { value: "short_dark", label: "Short dark" },
      { value: "short_light", label: "Short light" },
      { value: "bald_shaved", label: "Bald / shaved" },
      { value: "natural_afro", label: "Natural / afro" },
      { value: "locs_braids", label: "Locs or braids" },
      { value: "medium_length", label: "Medium length" },
      { value: "curly", label: "Curly" },
    ];
  }
  return [
    { value: "short_dark", label: "Short dark" },
    { value: "short_light", label: "Short light" },
    { value: "long_dark", label: "Long dark" },
    { value: "long_light", label: "Long light / blonde" },
    { value: "natural_afro", label: "Natural / afro" },
    { value: "medium_wavy", label: "Medium wavy" },
    { value: "locs_braids", label: "Locs or braids" },
  ];
}

type GrwmModelInfo = {
  complete: boolean;
  gender: GrwmGender;
  height: string | null;
  body_shape: string | null;
  body_size: string | null;
  skin_tone: string | null;
  hair: string | null;
};

function LookbookModelRow({ userId, autoOpenNonce }: { userId: string; autoOpenNonce?: number }) {
  const [info, setInfo] = useState<GrwmModelInfo | null>(null);
  const [open, setOpen] = useState(false);
  const lastAutoOpenRef = useRef<number | null>(null);
  useEffect(() => {
    if (autoOpenNonce && autoOpenNonce !== lastAutoOpenRef.current) {
      lastAutoOpenRef.current = autoOpenNonce;
      setOpen(true);
    }
  }, [autoOpenNonce]);



  const load = useCallback(async () => {
    const { data } = await supabase
      .from("profiles")
      .select(
        "model_setup_complete, model_gender, gender, model_height, model_body_shape, model_body_size, model_skin_tone, model_hair",
      )
      .eq("id", userId)
      .maybeSingle();
    const d = data as Record<string, unknown> | null;
    const rawGender =
      (d?.model_gender as string | null) ?? (d?.gender as string | null) ?? null;
    const g: GrwmGender =
      rawGender === "men" || rawGender === "male"
        ? "men"
        : rawGender === "non-binary" || rawGender === "other"
          ? "non-binary"
          : "women";
    setInfo({
      complete: !!d?.model_setup_complete,
      gender: g,
      height: (d?.model_height as string | null) ?? null,
      body_shape: (d?.model_body_shape as string | null) ?? null,
      body_size: (d?.model_body_size as string | null) ?? null,
      skin_tone: (d?.model_skin_tone as string | null) ?? null,
      hair: (d?.model_hair as string | null) ?? null,
    });
  }, [userId]);

  useEffect(() => {
    void load();
  }, [load]);

  const isSetUp = !!info?.complete;
  const swatchHex = info?.skin_tone ? GRWM_SKIN_HEX[info.skin_tone] : undefined;

  void swatchHex;
  return (
    <>
      <AccountFlatRow
        title="My lookbook model"
        summary={isSetUp ? "Personalised to you" : "Not set up yet"}
        onClick={() => setOpen(true)}
      />
      {open && info && (
        <GrwmModelEditor
          userId={userId}
          gender={info.gender}
          initial={{
            height: info.height,
            bodyShape: info.body_shape,
            bodySize: info.body_size,
            skinTone: info.skin_tone,
            hair: info.hair,
          }}
          onClose={() => {
            setOpen(false);
            void load();
          }}
        />
      )}
    </>
  );
}

function GrwmModelEditor({
  userId,
  gender,
  initial,
  onClose,
}: {
  userId: string;
  gender: GrwmGender;
  initial: {
    height: string | null;
    bodyShape: string | null;
    bodySize: string | null;
    skinTone: string | null;
    hair: string | null;
  };
  onClose: () => void;
}) {
  const [height, setHeight] = useState<string | null>(initial.height);
  const [bodyShape, setBodyShape] = useState<string | null>(initial.bodyShape);
  const [bodySize, setBodySize] = useState<string | null>(initial.bodySize);
  const [skinTone, setSkinTone] = useState<string | null>(initial.skinTone);
  const [hair, setHair] = useState<string | null>(initial.hair);
  const [saving, setSaving] = useState(false);

  const heightOpts = grwmHeightOptions(gender);
  const shapeOpts = grwmBodyShapeOptions(gender);
  const sizeOpts = grwmBodySizeOptions();
  const hairOpts = grwmHairOptions(gender);
  const showBodySize = gender !== "men";

  const canSave =
    !!height &&
    !!bodyShape &&
    (!showBodySize || !!bodySize) &&
    !!skinTone &&
    !!hair;

  const onSave = async () => {
    if (!canSave) return;
    setSaving(true);
    const patch: Record<string, unknown> = {
      model_height: height,
      model_body_shape: bodyShape,
      model_body_size: showBodySize ? bodySize : bodyShape,
      model_skin_tone: skinTone,
      model_hair: hair,
      model_setup_complete: true,
    };
    const { error } = await (supabase.from("profiles") as any)
      .update(patch)
      .eq("id", userId);
    setSaving(false);
    if (error) {
      console.error("[GrwmModelEditor] save failed", error);
      toast.error("Couldn't save model. Try again.");
      return;
    }
    toast.success("Model updated");
    onClose();
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center"
      onClick={onClose}
    >
      <div className="absolute inset-0 bg-black/40" />
      <div
        className="relative w-full max-w-md bg-background rounded-t-3xl sm:rounded-3xl shadow-xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 bg-background px-6 pt-6 pb-3 border-b border-border">
          <div className="flex items-center justify-between">
            <h3
              style={{
                fontFamily: "var(--font-display)",
                fontStyle: "italic",
                fontSize: 22,
                color: "var(--l2f-ink)",
              }}
            >
              My lookbook model
            </h3>
            <button
              onClick={onClose}
              className="text-sm text-muted-foreground"
              aria-label="Close"
            >
              <X size={20} />
            </button>
          </div>
        </div>

        <div className="px-6 py-5 space-y-6">
          <PillSection
            label="Height"
            options={heightOpts}
            value={height}
            onChange={setHeight}
          />
          <PillSection
            label={gender === "men" ? "Body build" : "Body shape"}
            options={shapeOpts}
            value={bodyShape}
            onChange={setBodyShape}
          />
          {showBodySize && (
            <PillSection
              label="Body size"
              options={sizeOpts}
              value={bodySize}
              onChange={setBodySize}
            />
          )}
          <div>
            <p
              className="l2f-section-label mb-3"
              style={{ color: "var(--l2f-coral)" }}
            >
              SKIN TONE
            </p>
            <div className="grid grid-cols-6 gap-2">
              {GRWM_SKIN_TONES.map((s) => (
                <button
                  key={s.key}
                  onClick={() => setSkinTone(s.key)}
                  className="aspect-square rounded-full border-2 transition"
                  style={{
                    background: s.hex,
                    borderColor: skinTone === s.key ? CORAL : "transparent",
                    transform: skinTone === s.key ? "scale(1.08)" : "scale(1)",
                  }}
                  aria-label={s.key}
                />
              ))}
            </div>
          </div>
          <PillSection
            label="Hair"
            options={hairOpts}
            value={hair}
            onChange={setHair}
          />
        </div>

        <div className="sticky bottom-0 bg-background px-6 py-4 border-t border-border">
          <button
            onClick={onSave}
            disabled={!canSave || saving}
            className="w-full py-3.5 rounded-full font-semibold text-white disabled:opacity-50"
            style={{ background: CORAL }}
          >
            {saving ? "Saving…" : "Save changes"}
          </button>
        </div>
      </div>
    </div>
  );
}

function PillSection({
  label,
  options,
  value,
  onChange,
}: {
  label: string;
  options: { value: string; label: string }[];
  value: string | null;
  onChange: (v: string) => void;
}) {
  return (
    <div>
      <p
        className="l2f-section-label mb-3"
        style={{ color: "var(--l2f-coral)" }}
      >
        {label.toUpperCase()}
      </p>
      <div className="flex flex-wrap gap-2">
        {options.map((o) => {
          const selected = value === o.value;
          return (
            <button
              key={o.value}
              onClick={() => onChange(o.value)}
              className="px-4 py-2 rounded-full text-sm border transition"
              style={{
                background: selected ? CORAL : "white",
                color: selected ? "white" : "var(--l2f-ink)",
                borderColor: selected ? CORAL : "var(--border)",
              }}
            >
              {o.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}


type SizeProfileRow = {
  size_gender: "women" | "men" | null;
  size_uk_number: string | null;
  size_letter: string | null;
  size_shoe: string | null;
  size_top_letter: string | null;
  /** Optional UK number size for women's tops (e.g. "10", "12"). */
  size_top_uk_number?: string | null;
  size_trouser_waist: string | null;
  size_trouser_length: string | null;
  size_shoe_mens: string | null;
  model_gender?: string | null;
  model_height?: string | null;
  model_body_shape?: string | null;
  model_body_size?: string | null;
};

type GuestSizeProfile = {
  gender: "women" | "men";
  uk_number: string | null;
  top_letter: string | null;
  /** Optional UK number top size — women only. */
  top_uk_number?: string | null;
  shoe: string | null;
  trouser_waist: string | null;
  trouser_length: string | null;
  shoe_mens: string | null;
};

// ActiveSize + getSizeLabelForItem moved to src/lib/sizeLabels.ts

function toActiveFromRow(row: SizeProfileRow | null): ActiveSize | null {
  if (!row?.size_gender) return null;
  return {
    gender: row.size_gender,
    uk_number: row.size_uk_number,
    top_letter: row.size_top_letter ?? row.size_letter,
    shoe: row.size_shoe,
    trouser_waist: row.size_trouser_waist,
    trouser_length: row.size_trouser_length,
    shoe_mens: row.size_shoe_mens,
  };
}

function toActiveFromGuest(g: GuestSizeProfile | null): ActiveSize | null {
  if (!g?.gender) return null;
  return { ...g };
}

function buildSizeSummary(p: ActiveSize): string {
  const parts: string[] = [];
  if (p.top_letter) parts.push(`Top: ${p.top_letter}`);
  if (p.gender === "women") {
    if (p.uk_number) parts.push(`Clothes: UK ${p.uk_number}`);
    if (p.shoe) parts.push(`Shoes: UK ${p.shoe}`);
  } else {
    if (p.trouser_waist) parts.push(`Waist: ${p.trouser_waist}"`);
    if (p.shoe_mens) parts.push(`Shoes: UK ${p.shoe_mens}`);
  }
  return parts.join(" · ");
}


const WOMEN_UK_NUMBERS = ["4", "6", "8", "10", "12", "14", "16", "18", "20", "22", "24"];
const LETTER_SIZES = ["XS", "S", "M", "L", "XL", "XXL"];
const WOMEN_SHOES = ["3", "3.5", "4", "4.5", "5", "5.5", "6", "6.5", "7", "7.5", "8", "8.5", "9"];
const MEN_WAISTS = ["28", "30", "32", "34", "36", "38", "40", "42", "44"];
const MEN_LENGTHS = ["28", "30", "32", "34", "36"];
const MEN_SHOES = ["6", "7", "8", "9", "10", "11", "12", "13", "14"];

function GenderDisplayRow({ userId }: { userId: string }) {
  const [gender, setGender] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data } = await (supabase.from("profiles") as any)
        .select("model_gender, size_gender, gender")
        .eq("id", userId)
        .maybeSingle();
      if (cancelled || !data) return;
      setGender(
        (data.model_gender as string | null) ??
          (data.size_gender as string | null) ??
          (data.gender as string | null) ??
          null,
      );
    })();
    return () => { cancelled = true; };
  }, [userId]);

  const label =
    gender === "men" || gender === "man"
      ? "Man"
      : gender === "women" || gender === "woman"
        ? "Woman"
        : gender === "non-binary"
          ? "Non-Binary"
          : "Not set";

  return (
    <AccountFlatRow
      title="Gender"
      summary="Set during onboarding"
      rightValue={label}
    />
  );
}

function MySizeRow({ userId, autoOpenNonce }: { userId: string; autoOpenNonce?: number }) {
  const [size, setSize] = useState<SizeProfileRow | null>(null);
  const [open, setOpen] = useState(false);
  const lastAutoOpenRef = useRef<number | null>(null);
  useEffect(() => {
    if (autoOpenNonce && autoOpenNonce !== lastAutoOpenRef.current) {
      lastAutoOpenRef.current = autoOpenNonce;
      setOpen(true);
    }
  }, [autoOpenNonce]);



  const load = useCallback(async () => {
    const { data } = await (supabase.from("profiles") as any)
      .select(
        "size_gender, size_uk_number, size_letter, size_shoe, size_top_letter, size_top_uk_number, size_trouser_waist, size_trouser_length, size_shoe_mens",
      )
      .eq("id", userId)
      .maybeSingle();
    setSize((data as SizeProfileRow | null) ?? null);
  }, [userId]);

  useEffect(() => { void load(); }, [load]);

  const isSet = !!size?.size_gender;
  const summary = (() => {
    if (!size?.size_gender) return "Not set up yet";
    const parts: string[] = [];
    if (size.size_gender === "women") {
      if (size.size_uk_number) parts.push(`UK ${size.size_uk_number}`);
      if (size.size_top_letter) parts.push(size.size_top_letter);
      if (size.size_shoe) parts.push(`shoe ${size.size_shoe}`);
    } else {
      if (size.size_top_letter) parts.push(size.size_top_letter);
      if (size.size_trouser_waist) {
        const len = size.size_trouser_length ? `/${size.size_trouser_length}` : "";
        parts.push(`W${size.size_trouser_waist}${len}`);
      }
      if (size.size_shoe_mens) parts.push(`shoe ${size.size_shoe_mens}`);
    }
    return parts.length ? parts.join(" · ") : "Tap to set sizes";
  })();

  void isSet;
  return (
    <>
      <AccountFlatRow
        title="My size"
        summary={summary}
        onClick={() => setOpen(true)}
      />
      {open && (
        <MySizeSheet
          userId={userId}
          initial={size}
          onClose={() => { setOpen(false); void load(); }}
        />
      )}
    </>
  );
}

/* ============================================================
   PERSONALISATION-1 — Selfie + age personalisation row/editor
   ============================================================
   Inserted between LookbookModelRow and MySizeRow in MY PREFERENCES.
   - Self-loads its profile data on mount (MySizeRow pattern).
   - Selfie path + timestamp persist to DB *at capture time* — the
     storage object is already collected data, no policy gate.
   - Toggle / age / GDPR Art. 7 consent persist via Save.
   - Consent checkbox is shown only when a selfie exists AND
     personalisation_consent_at is NULL; Save is blocked until ticked.
   - Delete handler NULLs consent_at so re-enabling re-prompts.
*/
function PersonalisationRow({ userId, autoOpenNonce }: { userId: string; autoOpenNonce?: number }) {
  const [open, setOpen] = useState(false);
  const [summary, setSummary] = useState<string>("Not set up yet");
  const [hasSelfie, setHasSelfie] = useState(false);
  const lastAutoOpenRef = useRef<number | null>(null);
  useEffect(() => {
    if (autoOpenNonce && autoOpenNonce !== lastAutoOpenRef.current) {
      lastAutoOpenRef.current = autoOpenNonce;
      setOpen(true);
    }
  }, [autoOpenNonce]);


  const load = useCallback(async () => {
    const { data } = await supabase
      .from("profiles")
      .select("personalisation_enabled, selfie_path, model_age")
      .eq("id", userId)
      .maybeSingle();
    const d = data as {
      personalisation_enabled?: boolean;
      selfie_path?: string | null;
      model_age?: number | null;
    } | null;
    const enabled = !!d?.personalisation_enabled;
    const selfie = !!d?.selfie_path;
    setHasSelfie(selfie);
    if (!selfie) {
      setSummary("Not set up yet");
    } else if (enabled) {
      setSummary(d?.model_age ? `On · age ${d.model_age}` : "On");
    } else {
      setSummary("Selfie saved · off");
    }
  }, [userId]);

  useEffect(() => { void load(); }, [load]);
  void hasSelfie;

  return (
    <>
      <AccountFlatRow
        title="Personalise lookbooks"
        summary={summary}
        onClick={() => setOpen(true)}
      />
      {open && (
        <PersonalisationEditor
          userId={userId}
          onClose={() => { setOpen(false); void load(); }}
        />
      )}
    </>
  );
}

function PersonalisationEditor({
  userId,
  onClose,
}: {
  userId: string;
  onClose: () => void;
}) {
  const [loading, setLoading] = useState(true);
  const [enabled, setEnabled] = useState(false);
  const [age, setAge] = useState<number | null>(null);
  const [selfiePath, setSelfiePath] = useState<string | null>(null);
  const [selfieUploadedAt, setSelfieUploadedAt] = useState<string | null>(null);
  const [selfieSignedUrl, setSelfieSignedUrl] = useState<string | null>(null);
  const [consentAt, setConsentAt] = useState<string | null>(null);
  const [consentChecked, setConsentChecked] = useState(false);
  const [busy, setBusy] = useState<"idle" | "capturing" | "uploading" | "saving" | "deleting">("idle");
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      const { data } = await supabase
        .from("profiles")
        .select("personalisation_enabled, model_age, selfie_path, selfie_uploaded_at, personalisation_consent_at")
        .eq("id", userId)
        .maybeSingle();
      if (!alive) return;
      const d = data as {
        personalisation_enabled?: boolean;
        model_age?: number | null;
        selfie_path?: string | null;
        selfie_uploaded_at?: string | null;
        personalisation_consent_at?: string | null;
      } | null;
      if (d) {
        setEnabled(!!d.personalisation_enabled);
        setAge(d.model_age ?? null);
        setSelfiePath(d.selfie_path ?? null);
        setSelfieUploadedAt(d.selfie_uploaded_at ?? null);
        setConsentAt(d.personalisation_consent_at ?? null);
        if (d.selfie_path) {
          const { data: signed } = await supabase.storage
            .from("selfies").createSignedUrl(d.selfie_path, 300);
          if (alive) setSelfieSignedUrl(signed?.signedUrl ?? null);
        }
      }
      if (alive) setLoading(false);
    })();
    return () => { alive = false; };
  }, [userId]);

  async function handleCaptureSelfie() {
    setBusy("capturing");
    try {
      const { Camera, CameraSource, CameraDirection, CameraResultType } =
        await import("@capacitor/camera");
      const status = await Camera.checkPermissions();
      if (status.camera === "prompt" || status.camera === "prompt-with-rationale") {
        const requested = await Camera.requestPermissions({ permissions: ["camera"] });
        if (requested.camera !== "granted" && requested.camera !== "limited") {
          toast.error("Camera access required. Enable in Settings → Like2Fit → Camera.");
          return;
        }
      } else if (status.camera === "denied") {
        toast.error("Camera access was denied. Enable in Settings → Like2Fit → Camera.");
        return;
      }
      const photo = await Camera.getPhoto({
        source: CameraSource.Camera,
        direction: CameraDirection.Front,
        resultType: CameraResultType.Uri,
        quality: 90,
        allowEditing: false,
      });
      if (!photo.webPath) return;

      setBusy("uploading");
      const resp = await fetch(photo.webPath);
      const blob = await resp.blob();
      const rawFile = new File([blob], "selfie.jpg", { type: blob.type || "image/jpeg" });
      const compressed = await compressImage(rawFile);
      const ts = Date.now();
      const path = `${userId}/${ts}.jpg`;

      const { error: upErr } = await supabase.storage
        .from("selfies")
        .upload(path, compressed, { contentType: "image/jpeg", upsert: false });
      if (upErr) {
        console.error("[personalisation] upload failed:", upErr);
        toast.error("Selfie upload failed. Try again.");
        return;
      }

      const previousPath = selfiePath;
      const nowIso = new Date(ts).toISOString();
      const { error: dbErr } = await supabase
        .from("profiles")
        .update({ selfie_path: path, selfie_uploaded_at: nowIso } as never)
        .eq("id", userId);
      if (dbErr) {
        console.error("[personalisation] profile write failed:", dbErr);
        toast.error("Couldn't save selfie. Try again.");
        // Roll back the just-uploaded object so we don't orphan it.
        await supabase.storage.from("selfies").remove([path]).catch(() => {});
        return;
      }

      if (previousPath && previousPath !== path) {
        await supabase.storage.from("selfies").remove([previousPath]).catch(() => {});
      }

      setSelfiePath(path);
      setSelfieUploadedAt(nowIso);
      const { data: signed } = await supabase.storage
        .from("selfies").createSignedUrl(path, 300);
      setSelfieSignedUrl(signed?.signedUrl ?? null);
      setDirty(true);
    } catch (e) {
      console.error("[personalisation] capture error:", e);
    } finally {
      setBusy("idle");
    }
  }

  async function handleDelete() {
    setBusy("deleting");
    try {
      if (selfiePath) {
        await supabase.storage.from("selfies").remove([selfiePath]).catch(() => {});
      }
      const { error } = await supabase
        .from("profiles")
        .update({
          selfie_path: null,
          selfie_uploaded_at: null,
          personalisation_enabled: false,
          personalisation_consent_at: null,
        } as never)
        .eq("id", userId);
      if (error) {
        console.error("[personalisation] delete failed:", error);
        toast.error("Couldn't remove selfie.");
        return;
      }
      setSelfiePath(null);
      setSelfieUploadedAt(null);
      setSelfieSignedUrl(null);
      setEnabled(false);
      setConsentAt(null);
      setConsentChecked(false);
      setDirty(false);
      onClose();
    } finally {
      setBusy("idle");
    }
  }

  const consentRequired = !!selfiePath && !consentAt;
  const saveDisabled =
    busy !== "idle" || !dirty || (consentRequired && !consentChecked);

  async function handleSave() {
    setBusy("saving");
    try {
      const consentTimestamp = !consentAt && consentChecked
        ? new Date().toISOString()
        : undefined;
      const patch: Record<string, unknown> = {
        personalisation_enabled: enabled,
        model_age: age ?? null,
      };
      if (consentTimestamp) patch.personalisation_consent_at = consentTimestamp;
      const { error } = await supabase
        .from("profiles")
        .update(patch as never)
        .eq("id", userId);
      if (error) {
        console.error("[personalisation] save failed:", error);
        toast.error("Couldn't save. Try again.");
        return;
      }
      if (consentTimestamp) setConsentAt(consentTimestamp);
      setDirty(false);
      onClose();
    } finally {
      setBusy("idle");
    }
  }

  void selfieUploadedAt;

  return (
    <div
      role="dialog"
      aria-modal="true"
      style={{
        position: "fixed", inset: 0, zIndex: 60,
        background: "rgba(0,0,0,0.45)",
        display: "flex", alignItems: "flex-end", justifyContent: "center",
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "100%", maxWidth: 520,
          background: "var(--background)",
          borderTopLeftRadius: 20, borderTopRightRadius: 20,
          maxHeight: "92vh", display: "flex", flexDirection: "column",
        }}
      >
        <div style={{ padding: "16px 20px", borderBottom: "0.5px solid var(--border)" }}>
          <div style={{ fontSize: 17, fontWeight: 600, color: "var(--foreground)" }}>
            Personalise lookbooks
          </div>
          <div style={{ fontSize: 13, color: "var(--muted-foreground)", marginTop: 4 }}>
            Use your face and age across all renders.
          </div>
        </div>

        <div style={{ overflowY: "auto", padding: "16px 20px", flex: 1 }}>
          {loading ? (
            <div style={{ color: "var(--muted-foreground)", fontSize: 14 }}>Loading…</div>
          ) : (
            <>
              {/* Selfie tile */}
              <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 18 }}>
                <div
                  style={{
                    width: 96, height: 96, borderRadius: 16,
                    background: "var(--secondary)",
                    border: "0.5px solid var(--border)",
                    overflow: "hidden", flexShrink: 0,
                    display: "flex", alignItems: "center", justifyContent: "center",
                  }}
                >
                  {selfieSignedUrl ? (
                    <img
                      src={selfieSignedUrl}
                      alt="Selfie"
                      style={{ width: "100%", height: "100%", objectFit: "cover" }}
                    />
                  ) : (
                    <span style={{ fontSize: 11, color: "var(--muted-foreground)" }}>No selfie</span>
                  )}
                </div>
                <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 8 }}>
                  <button
                    type="button"
                    disabled={busy === "capturing" || busy === "uploading"}
                    onClick={handleCaptureSelfie}
                    style={{
                      padding: "10px 14px", borderRadius: 12,
                      background: "var(--foreground)", color: "var(--background)",
                      fontSize: 14, fontWeight: 600,
                      border: "none", cursor: "pointer",
                      opacity: (busy === "capturing" || busy === "uploading") ? 0.6 : 1,
                    }}
                  >
                    {busy === "capturing" ? "Opening camera…"
                      : busy === "uploading" ? "Uploading…"
                      : selfiePath ? "Re-take selfie" : "Take selfie"}
                  </button>
                  {selfiePath && (
                    <button
                      type="button"
                      disabled={busy === "deleting"}
                      onClick={handleDelete}
                      style={{
                        padding: "8px 14px", borderRadius: 12,
                        background: "transparent",
                        color: "var(--destructive, #b00020)",
                        fontSize: 13, fontWeight: 500,
                        border: "0.5px solid var(--border)", cursor: "pointer",
                      }}
                    >
                      {busy === "deleting" ? "Deleting…" : "Delete selfie"}
                    </button>
                  )}
                </div>
              </div>

              {/* Toggle */}
              <label
                style={{
                  display: "flex", alignItems: "center", justifyContent: "space-between",
                  padding: "12px 0", borderTop: "0.5px solid var(--border)",
                  cursor: selfiePath ? "pointer" : "not-allowed",
                  opacity: selfiePath ? 1 : 0.5,
                }}
              >
                <span style={{ fontSize: 15, color: "var(--foreground)" }}>
                  Use my face in lookbooks
                </span>
                <input
                  type="checkbox"
                  checked={enabled}
                  disabled={!selfiePath}
                  onChange={(e) => { setEnabled(e.target.checked); setDirty(true); }}
                  style={{ width: 20, height: 20 }}
                />
              </label>

              {/* Age */}
              <div style={{ padding: "12px 0", borderTop: "0.5px solid var(--border)" }}>
                <label style={{ display: "block", fontSize: 15, color: "var(--foreground)", marginBottom: 6 }}>
                  My age (optional)
                </label>
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  value={age ?? ""}
                  onChange={(e) => {
                    const raw = e.target.value.replace(/[^0-9]/g, "");
                    setAge(raw === "" ? null : Number(raw));
                    setDirty(true);
                  }}
                  onBlur={() => {
                    if (age !== null) {
                      const clamped = Math.max(13, Math.min(100, age));
                      if (clamped !== age) setAge(clamped);
                    }
                  }}
                  placeholder="e.g. 28"
                  style={{
                    width: "100%", padding: "10px 12px",
                    borderRadius: 10, border: "0.5px solid var(--border)",
                    background: "var(--background)", color: "var(--foreground)",
                    fontSize: 15,
                  }}
                />

              </div>

              {/* Privacy note */}
              <div
                style={{
                  marginTop: 14, padding: "12px 14px",
                  background: "var(--secondary)", borderRadius: 12,
                  fontSize: 12, lineHeight: 1.5, color: "var(--muted-foreground)",
                }}
              >
                Your selfie is stored privately and only used to render your
                personalised lookbooks. Delete it any time to remove it from
                storage.
              </div>

              {/* Consent (first-time, only when a selfie exists and consent_at is null) */}
              {consentRequired && (
                <div style={{ marginTop: 12, padding: "12px 14px", border: "0.5px solid var(--border)", borderRadius: 12 }}>
                  <label style={{ display: "flex", alignItems: "flex-start", gap: 10, cursor: "pointer" }}>
                    <input
                      type="checkbox"
                      checked={consentChecked}
                      onChange={(e) => { setConsentChecked(e.target.checked); setDirty(true); }}
                      style={{ marginTop: 3, width: 18, height: 18, flexShrink: 0 }}
                    />
                    <span style={{ fontSize: 13, lineHeight: 1.45, color: "var(--foreground)" }}>
                      I&rsquo;m 18 or older and consent to my selfie being used
                      to render personalised looks.
                    </span>
                  </label>
                </div>
              )}
            </>
          )}
        </div>

        <div
          style={{
            padding: "12px 20px 20px",
            borderTop: "0.5px solid var(--border)",
            display: "flex", gap: 10,
          }}
        >
          <button
            type="button"
            onClick={onClose}
            style={{
              flex: 1, padding: "12px 16px", borderRadius: 12,
              background: "transparent", color: "var(--foreground)",
              border: "0.5px solid var(--border)",
              fontSize: 15, fontWeight: 500, cursor: "pointer",
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={saveDisabled}
            onClick={handleSave}
            style={{
              flex: 1, padding: "12px 16px", borderRadius: 12,
              background: "var(--foreground)", color: "var(--background)",
              border: "none", fontSize: 15, fontWeight: 600, cursor: "pointer",
              opacity: saveDisabled ? 0.5 : 1,
            }}
          >
            {busy === "saving" ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}



function SizePromptBanner({
  userId,
  sizeProfile,
  isGuest,
  guestSizeProfile,
  onSizeSet,
  onRequestSignup,
}: {
  userId: string | null;
  sizeProfile: SizeProfileRow | null;
  isGuest: boolean;
  guestSizeProfile: GuestSizeProfile | null;
  onSizeSet: (guestSize?: GuestSizeProfile) => void;
  onRequestSignup?: (trigger: SignupTrigger) => void;
}) {
  const [open, setOpen] = useState(false);
  const active = isGuest
    ? toActiveFromGuest(guestSizeProfile)
    : toActiveFromRow(sizeProfile);
  const isSet = !!active;

  // CONFIRMATION BAR (size already captured)
  if (isSet) {
    const summary = buildSizeSummary(active!);
    return (
      <>
        <div
          className="mt-3"
          style={{
            background: "var(--secondary)",
            border: "0.5px solid var(--border)",
            borderRadius: "var(--radius)",
            padding: "12px 14px",
            display: "flex",
            alignItems: "center",
            gap: 12,
          }}
        >
          <div
            style={{
              flexShrink: 0,
              width: 28,
              height: 28,
              borderRadius: 999,
              background: "var(--l2f-cream)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Check size={16} style={{ color: CORAL }} strokeWidth={3} />
          </div>
          <div style={{ minWidth: 0, flex: 1 }}>
            <div
              style={{
                fontSize: 11,
                fontWeight: 600,
                letterSpacing: "0.1em",
                textTransform: "uppercase",
                color: "var(--muted-foreground)",
              }}
            >
              Your sizes
            </div>
            <div
              style={{
                fontSize: 13,
                color: "var(--l2f-ink)",
                marginTop: 2,
                lineHeight: 1.3,
                wordBreak: "break-word",
              }}
            >
              {summary || "Saved"}
            </div>
          </div>
          {isGuest && onRequestSignup ? (
            <button
              type="button"
              onClick={() => onRequestSignup("account_tab")}
              style={{
                flexShrink: 0,
                background: CORAL,
                border: "none",
                color: "white",
                borderRadius: 999,
                padding: "8px 12px",
                fontSize: 12,
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              Sign up to save
            </button>
          ) : (
            <button
              type="button"
              onClick={() => {
                trackEvent("size_sheet_opened", { is_guest: isGuest, current_state: "size_saved" });
                setOpen(true);
              }}
              style={{
                flexShrink: 0,
                background: "transparent",
                border: "none",
                color: CORAL,
                fontSize: 12,
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              Edit
            </button>
          )}
        </div>
        {open && !isGuest && userId && (
          <MySizeSheet
            userId={userId}
            initial={sizeProfile}
            onClose={() => { setOpen(false); onSizeSet(); }}
          />
        )}
      </>
    );
  }

  // PROMPT BANNER (no size yet)
  return (
    <>
      <div
        className="mt-3"
        style={{
          background: "var(--l2f-cream)",
          border: `0.5px solid ${CORAL}33`,
          borderRadius: "var(--radius)",
          padding: "14px 16px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 12,
        }}
      >
        <div style={{ minWidth: 0 }}>
          <div
            style={{
              fontFamily: "var(--font-display)",
              fontStyle: "italic",
              fontSize: 20,
              lineHeight: 1.15,
              color: "var(--l2f-ink)",
            }}
          >
            Shop in your size
          </div>
          <p
            style={{
              fontSize: 12,
              color: "var(--muted-foreground)",
              margin: "3px 0 0",
              lineHeight: 1.35,
            }}
          >
            Tell us your size and we'll label every item below.
          </p>
        </div>
        <button
          type="button"
          onClick={() => {
            trackEvent("size_sheet_opened", { is_guest: isGuest, current_state: "no_size" });
            setOpen(true);
          }}
          style={{
            flexShrink: 0,
            background: CORAL,
            border: "none",
            color: "white",
            borderRadius: 999,
            padding: "9px 14px",
            fontSize: 13,
            fontWeight: 600,
            cursor: "pointer",
          }}
        >
          Set size
        </button>
      </div>
      {open && (
        <MySizeSheet
          userId={userId ?? ""}
          initial={sizeProfile}
          onClose={() => { setOpen(false); if (!isGuest) onSizeSet(); }}
          onSaveGuest={
            isGuest
              ? (data) => {
                  setOpen(false);
                  onSizeSet(data);
                }
              : undefined
          }
        />
      )}
    </>
  );
}

const Pill = ({ active, label, onClick }: { active: boolean; label: string; onClick: () => void }) => (
  <button
    onClick={onClick}
    className="px-3 py-2 rounded-full text-xs font-medium border transition-colors"
    style={{
      background: active ? CORAL : "transparent",
      color: active ? "white" : "inherit",
      borderColor: active ? CORAL : "hsl(var(--border))",
    }}
  >
    {label}
  </button>
);

function MySizeSheet({
  userId,
  initial,
  onClose,
  onSaveGuest,
}: {
  userId: string;
  initial: SizeProfileRow | null;
  onClose: () => void;
  onSaveGuest?: (data: GuestSizeProfile) => void;
}) {
  // Gender is the single source of truth from onboarding (model_gender).
  // MySizeSheet never lets the user change it — it only shows size fields.
  const [gender, setGender] = useState<"women" | "men" | null>(
    initial?.size_gender === "men" || initial?.size_gender === "women"
      ? initial.size_gender
      : null,
  );
  const [ukNumber, setUkNumber] = useState<string | null>(initial?.size_uk_number ?? null);
  const [shoeW, setShoeW] = useState<string | null>(initial?.size_shoe ?? null);
  const [topLetter, setTopLetter] = useState<string | null>(initial?.size_top_letter ?? null);
  const [topUkNumber, setTopUkNumber] = useState<string | null>(initial?.size_top_uk_number ?? null);
  const [waist, setWaist] = useState<string | null>(initial?.size_trouser_waist ?? null);
  const [length, setLength] = useState<string | null>(initial?.size_trouser_length ?? null);
  const [shoeM, setShoeM] = useState<string | null>(initial?.size_shoe_mens ?? null);
  const [saving, setSaving] = useState(false);
  const savedRef = useRef(false);
  const initialSnapshot = useRef({
    ukNumber: initial?.size_uk_number ?? null,
    shoeW: initial?.size_shoe ?? null,
    topLetter: initial?.size_top_letter ?? null,
    topUkNumber: initial?.size_top_uk_number ?? null,
    waist: initial?.size_trouser_waist ?? null,
    length: initial?.size_trouser_length ?? null,
    shoeM: initial?.size_shoe_mens ?? null,
  });
  const isGuestSheet = !!onSaveGuest;
  const handleDismiss = () => {
    if (savedRef.current) {
      onClose();
      return;
    }
    const hadChanges =
      ukNumber !== initialSnapshot.current.ukNumber ||
      shoeW !== initialSnapshot.current.shoeW ||
      topLetter !== initialSnapshot.current.topLetter ||
      topUkNumber !== initialSnapshot.current.topUkNumber ||
      waist !== initialSnapshot.current.waist ||
      length !== initialSnapshot.current.length ||
      shoeM !== initialSnapshot.current.shoeM;
    trackEvent("size_sheet_dismissed", { is_guest: isGuestSheet, had_changes: hadChanges });
    onClose();
  };

  // Load gender from profile (model_gender → size_gender fallback) for authed users.
  useEffect(() => {
    if (onSaveGuest || !userId) return;
    let cancelled = false;
    (async () => {
      const { data } = await (supabase.from("profiles") as any)
        .select("model_gender, size_gender")
        .eq("id", userId)
        .maybeSingle();
      if (cancelled || !data) return;
      const raw = (data.model_gender as string | null) ?? (data.size_gender as string | null) ?? null;
      const normalized: "women" | "men" | null =
        raw === "men" || raw === "man" || raw === "women" || raw === "woman" || raw === "non-binary"
          ? raw === "men" || raw === "man"
            ? "men"
            : "women" // non-binary defaults to women's sizing
          : null;
      if (normalized) setGender(normalized);
    })();
    return () => { cancelled = true; };
  }, [userId, onSaveGuest]);

  const save = async () => {
    if (!gender) {
      toast.error("Complete onboarding to set your sizing system.");
      return;
    }
    const savedFields: string[] = [];
    if (gender === "women") {
      if (ukNumber) savedFields.push("uk_number");
      if (shoeW) savedFields.push("shoe");
      if (topLetter) savedFields.push("top_letter");
    } else if (gender === "men") {
      if (topLetter) savedFields.push("top_letter");
      if (waist) savedFields.push("trouser_waist");
      if (length) savedFields.push("trouser_length");
      if (shoeM) savedFields.push("shoe_mens");
    }
    trackEvent("size_sheet_submitted", { is_guest: isGuestSheet, saved_fields: savedFields });
    // Guest path — intercept before any DB write and hand data back to caller.
    if (onSaveGuest) {
      savedRef.current = true;
      onSaveGuest({
        gender,
        uk_number: gender === "women" ? ukNumber : null,
        top_letter: topLetter,
        top_uk_number: gender === "women" ? topUkNumber : null,
        shoe: gender === "women" ? shoeW : null,
        trouser_waist: gender === "men" ? waist : null,
        trouser_length: gender === "men" ? length : null,
        shoe_mens: gender === "men" ? shoeM : null,
      });
      toast.success("Sizes saved");
      return;
    }
    setSaving(true);
    // Gender is owned by onboarding (model_gender). Do not write size_gender here.
    const payload = {
      size_uk_number: gender === "women" ? ukNumber : null,
      size_letter: gender === "women" ? (topLetter ?? null) : null,
      size_shoe: gender === "women" ? shoeW : null,
      size_top_letter: (gender === "women" || gender === "men") ? topLetter : null,
      // Numeric top size only applies to women — see Build B notes.
      size_top_uk_number: gender === "women" ? topUkNumber : null,
      size_trouser_waist: gender === "men" ? waist : null,
      size_trouser_length: gender === "men" ? length : null,
      size_shoe_mens: gender === "men" ? shoeM : null,
    };
    const { error } = await (supabase.from("profiles") as any)
      .update(payload)
      .eq("id", userId);
    setSaving(false);
    if (error) {
      toast.error("Couldn't save sizes.");
      return;
    }
    savedRef.current = true;
    toast.success("Sizes saved");
    onClose();
  };


  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center" onClick={handleDismiss}>
      <div className="absolute inset-0 bg-black/40" />
      <div
        className="relative w-full max-w-md bg-background rounded-t-3xl max-h-[85vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mx-auto mt-3 h-1.5 w-12 rounded-full bg-muted" />
        <div className="px-6 pt-4 pb-2">
          <h3 className="text-xl font-extrabold tracking-tight">My size</h3>
          <p className="text-xs text-muted-foreground mt-1">
            Used to prioritise shopping matches in your size.
          </p>
        </div>
        <div className="flex-1 overflow-y-auto px-6 pb-6 pt-2 space-y-5">
          {!gender && (
            <p className="text-xs text-muted-foreground">
              Your sizing system is set during onboarding.
            </p>
          )}
          {gender === "women" && (
            <>
              <div>
                <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-1">UK Clothing Size</p>
                <p className="text-xs text-muted-foreground mb-2">For dresses, skirts and trousers</p>
                <div className="flex flex-wrap gap-2">
                  {WOMEN_UK_NUMBERS.map((n) => (
                    <Pill key={n} active={ukNumber === n} label={n} onClick={() => setUkNumber(ukNumber === n ? null : n)} />
                  ))}
                </div>
              </div>
              <div>
                <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-1">Top Size</p>
                <p className="text-xs text-muted-foreground mb-2">For tops, shirts and jackets</p>
                <div className="flex flex-wrap gap-2">
                  {LETTER_SIZES.map((n) => (
                    <Pill key={n} active={topLetter === n} label={n} onClick={() => setTopLetter(topLetter === n ? null : n)} />
                  ))}
                </div>
              </div>
              <div>
                <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-1">Top — UK number (optional)</p>
                <p className="text-xs text-muted-foreground mb-2">Some brands like M&amp;S and John Lewis use numbers. Add yours for more specific recommendations.</p>
                <div className="flex flex-wrap gap-2">
                  {["6", "8", "10", "12", "14", "16", "18", "20"].map((n) => (
                    <Pill key={n} active={topUkNumber === n} label={n} onClick={() => setTopUkNumber(topUkNumber === n ? null : n)} />
                  ))}
                </div>
              </div>
              <div>
                <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-2">UK shoe size</p>
                <div className="flex flex-wrap gap-2">
                  {WOMEN_SHOES.map((n) => (
                    <Pill key={n} active={shoeW === n} label={n} onClick={() => setShoeW(shoeW === n ? null : n)} />
                  ))}
                </div>
              </div>
            </>
          )}

          {gender === "men" && (
            <>
              <div>
                <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-2">Top size</p>
                <div className="flex flex-wrap gap-2">
                  {LETTER_SIZES.map((n) => (
                    <Pill key={n} active={topLetter === n} label={n} onClick={() => setTopLetter(topLetter === n ? null : n)} />
                  ))}
                </div>
              </div>
              <div>
                <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-2">Trouser waist (in)</p>
                <div className="flex flex-wrap gap-2">
                  {MEN_WAISTS.map((n) => (
                    <Pill key={n} active={waist === n} label={n} onClick={() => setWaist(waist === n ? null : n)} />
                  ))}
                </div>
              </div>
              <div>
                <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-2">Trouser length (in)</p>
                <div className="flex flex-wrap gap-2">
                  {MEN_LENGTHS.map((n) => (
                    <Pill key={n} active={length === n} label={n} onClick={() => setLength(length === n ? null : n)} />
                  ))}
                </div>
              </div>
              <div>
                <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-2">UK shoe size</p>
                <div className="flex flex-wrap gap-2">
                  {MEN_SHOES.map((n) => (
                    <Pill key={n} active={shoeM === n} label={n} onClick={() => setShoeM(shoeM === n ? null : n)} />
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
        <div className="px-6 pt-2 pb-6 border-t border-border">
          <button
            onClick={save}
            disabled={!gender || saving}
            className="w-full py-3.5 rounded-full font-medium text-white disabled:opacity-50"
            style={{ background: CORAL }}
          >
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}

const OnboardPill = ({ active, label, onClick }: { active: boolean; label: string; onClick: () => void }) => (
  <button
    onClick={onClick}
    aria-pressed={active}
    className="l2f-onb-toggle px-3 py-2 rounded-full text-xs font-medium border transition-colors"
    style={{
      background: active ? CORAL : "transparent",
      color: active ? "white" : "inherit",
      borderColor: active ? CORAL : "hsl(var(--border))",
    }}
  >
    {label}
  </button>
);

function OnboardSize({ userId, onContinue }: { userId: string; onContinue: () => void; }) {
  const [gender, setGender] = useState<"women" | "men" | null>(null);
  const [ukNumber, setUkNumber] = useState<string | null>(null);
  const [shoeW, setShoeW] = useState<string | null>(null);
  const [topLetter, setTopLetter] = useState<string | null>(null);
  const [waist, setWaist] = useState<string | null>(null);
  const [length, setLength] = useState<string | null>(null);
  const [shoeM, setShoeM] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    const payload = {
      size_gender: gender,
      size_uk_number: gender === "women" ? ukNumber : null,
      size_letter: gender === "women" ? (topLetter ?? null) : null,
      size_shoe: gender === "women" ? shoeW : null,
      size_top_letter: (gender === "women" || gender === "men") ? topLetter : null,
      size_trouser_waist: gender === "men" ? waist : null,
      size_trouser_length: gender === "men" ? length : null,
      size_shoe_mens: gender === "men" ? shoeM : null,
    };
    const { error } = await (supabase.from("profiles") as any)
      .update(payload)
      .eq("id", userId);
    setSaving(false);
    if (error) {
      toast.error("Couldn't save sizes.");
      return;
    }
    onContinue();
  };

  return (
    <div className="flex-1 flex flex-col px-6 pt-10 pb-6">
      <p
        className="l2f-section-label"
        style={{ color: "var(--l2f-coral)", marginBottom: 10 }}
      >
        CHAPTER TWO · THE FIT
      </p>
      <h2 className="l2f-type-h1" style={{ color: "var(--l2f-ink)" }}>
        What's your size?
      </h2>
      <p className="text-sm text-muted-foreground mt-2 l2f-onb-helper">
        We'll use this to find clothes that fit you across different retailers
      </p>
      <div className="flex-1 overflow-y-auto mt-6 space-y-5">
        <div>
          <p className="l2f-type-section-header text-muted-foreground mb-2">
            Sizing system
          </p>
          <div className="flex gap-2">
            <OnboardPill active={gender === "women"} label="Women's" onClick={() => setGender("women")} />
            <OnboardPill active={gender === "men"} label="Men's" onClick={() => setGender("men")} />
          </div>
        </div>

        {gender === "women" && (
          <>
            <div>
              <p className="l2f-type-section-header text-muted-foreground mb-1">UK Clothing Size</p>
              <p className="text-xs text-muted-foreground mb-2">For dresses, skirts and trousers</p>
              <div className="flex flex-wrap gap-2">
                {WOMEN_UK_NUMBERS.map((n) => (
                  <OnboardPill key={n} active={ukNumber === n} label={n} onClick={() => setUkNumber(ukNumber === n ? null : n)} />
                ))}
              </div>
            </div>
            <div>
              <p className="l2f-type-section-header text-muted-foreground mb-1">Top Size</p>
              <p className="text-xs text-muted-foreground mb-2">For tops, shirts and jackets</p>
              <div className="flex flex-wrap gap-2">
                {LETTER_SIZES.map((n) => (
                  <OnboardPill key={n} active={topLetter === n} label={n} onClick={() => setTopLetter(topLetter === n ? null : n)} />
                ))}
              </div>
            </div>
            <div>
              <p className="l2f-type-section-header text-muted-foreground mb-2">UK shoe size</p>
              <div className="flex flex-wrap gap-2">
                {WOMEN_SHOES.map((n) => (
                  <OnboardPill key={n} active={shoeW === n} label={n} onClick={() => setShoeW(shoeW === n ? null : n)} />
                ))}
              </div>
            </div>
          </>
        )}

        {gender === "men" && (
          <>
            <div>
              <p className="l2f-type-section-header text-muted-foreground mb-2">Top size</p>
              <div className="flex flex-wrap gap-2">
                {LETTER_SIZES.map((n) => (
                  <OnboardPill key={n} active={topLetter === n} label={n} onClick={() => setTopLetter(topLetter === n ? null : n)} />
                ))}
              </div>
            </div>
            <div>
              <p className="l2f-type-section-header text-muted-foreground mb-2">Trouser waist (in)</p>
              <div className="flex flex-wrap gap-2">
                {MEN_WAISTS.map((n) => (
                  <OnboardPill key={n} active={waist === n} label={n} onClick={() => setWaist(waist === n ? null : n)} />
                ))}
              </div>
            </div>
            <div>
              <p className="l2f-type-section-header text-muted-foreground mb-2">Trouser length (in)</p>
              <div className="flex flex-wrap gap-2">
                {MEN_LENGTHS.map((n) => (
                  <OnboardPill key={n} active={length === n} label={n} onClick={() => setLength(length === n ? null : n)} />
                ))}
              </div>
            </div>
            <div>
              <p className="l2f-type-section-header text-muted-foreground mb-2">UK shoe size</p>
              <div className="flex flex-wrap gap-2">
                {MEN_SHOES.map((n) => (
                  <OnboardPill key={n} active={shoeM === n} label={n} onClick={() => setShoeM(shoeM === n ? null : n)} />
                ))}
              </div>
            </div>
          </>
        )}
      </div>
      <div className="pt-4 space-y-3">
        <button
          onClick={save}
          disabled={!gender || saving}
          aria-busy={saving}
          className="l2f-cta-coral w-full py-3.5 rounded-full font-medium text-white disabled:opacity-50"
          style={{ background: CORAL }}
        >
          {saving ? "Saving…" : "Continue →"}
        </button>
        <button
          onClick={onContinue}
          className="w-full text-xs text-muted-foreground hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-[color:var(--l2f-coral)] focus-visible:outline-offset-2 rounded-full py-2"
        >
          Skip for now
        </button>
      </div>
    </div>
  );
}

/* ============================================================================
   Brand fits — row + editor sheet (Account screen).
   Mirrors MySizeRow / LookbookModelRow structure.
   ========================================================================= */

type BrandVerdict = "true_to_size" | "runs_small" | "runs_large";

const VERDICT_LABEL: Record<BrandVerdict, string> = {
  true_to_size: "Fits true to size",
  runs_small: "Runs small",
  runs_large: "Runs large",
};

const VERDICT_OPTIONS: { value: BrandVerdict; label: string }[] = [
  { value: "true_to_size", label: "Fits true to size" },
  { value: "runs_small", label: "Runs small" },
  { value: "runs_large", label: "Runs large" },
];

function defaultBrandsForGender(gender: "women" | "men" | "non-binary"): string[] {
  if (gender === "men") return ["Zara", "ASOS", "H&M", "Next"];
  if (gender === "non-binary") return ["Zara", "ASOS", "H&M", "COS"];
  return ["Zara", "ASOS", "H&M", "& Other Stories"];
}

function BrandFitRow({ userId, autoOpenNonce }: { userId: string; autoOpenNonce?: number }) {
  const [tops, setTops] = useState<BrandFitEntry[]>([]);
  const [bottoms, setBottoms] = useState<BrandFitEntry[]>([]);
  const [gender, setGender] = useState<"women" | "men" | "non-binary">("women");
  const [open, setOpen] = useState(false);
  const lastAutoOpenRef = useRef<number | null>(null);

  useEffect(() => {
    if (autoOpenNonce && autoOpenNonce !== lastAutoOpenRef.current) {
      lastAutoOpenRef.current = autoOpenNonce;
      setOpen(true);
    }
  }, [autoOpenNonce]);

  const load = useCallback(async () => {
    const { data } = await (supabase.from("profiles") as any)
      .select("brand_fit_tops, brand_fit_bottoms, model_gender, gender")
      .eq("id", userId)
      .maybeSingle();
    if (!data) return;
    const t = (data as any).brand_fit_tops;
    const b = (data as any).brand_fit_bottoms;
    setTops(Array.isArray(t) ? (t as BrandFitEntry[]) : []);
    setBottoms(Array.isArray(b) ? (b as BrandFitEntry[]) : []);
    const rawG = ((data as any).model_gender ?? (data as any).gender ?? "women") as string;
    setGender(
      rawG === "men" || rawG === "male" ? "men"
        : rawG === "non-binary" || rawG === "other" ? "non-binary"
          : "women",
    );
  }, [userId]);

  useEffect(() => { void load(); }, [load]);

  const total = tops.length + bottoms.length;
  const summary = total === 0
    ? "Tell us how brands fit you"
    : `${total} ${total === 1 ? "brand" : "brands"} saved`;

  void total;
  return (
    <>
      <AccountFlatRow
        title="Brand fits"
        summary={summary}
        onClick={() => setOpen(true)}
        isLast
      />
      {open && (
        <BrandFitSheet
          userId={userId}
          gender={gender}
          initialTops={tops}
          initialBottoms={bottoms}
          onClose={() => { setOpen(false); void load(); }}
        />
      )}
    </>
  );
}

function BrandFitSheet({
  userId,
  gender,
  initialTops,
  initialBottoms,
  onClose,
}: {
  userId: string;
  gender: "women" | "men" | "non-binary";
  initialTops: BrandFitEntry[];
  initialBottoms: BrandFitEntry[];
  onClose: () => void;
}) {
  const [tops, setTops] = useState<BrandFitEntry[]>(initialTops);
  const [bottoms, setBottoms] = useState<BrandFitEntry[]>(initialBottoms);
  const [saving, setSaving] = useState(false);
  const defaults = useMemo(() => defaultBrandsForGender(gender), [gender]);

  const save = async () => {
    setSaving(true);
    const { error } = await (supabase.from("profiles") as any)
      .update({ brand_fit_tops: tops, brand_fit_bottoms: bottoms })
      .eq("id", userId);
    setSaving(false);
    if (error) {
      console.error("[BrandFitSheet] save failed", error);
      toast.error("Couldn't save brand fits");
      return;
    }
    toast.success("Brand fits saved");
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center" onClick={onClose}>
      <div className="absolute inset-0 bg-black/40" />
      <div
        className="relative w-full max-w-md bg-background rounded-t-3xl flex flex-col max-h-[90vh]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mx-auto mt-3 h-1.5 w-12 rounded-full bg-muted" />
        <div className="px-6 pt-4 pb-2 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="l2f-section-label" style={{ color: "var(--l2f-coral)", marginBottom: 6 }}>
              THE FIT
            </p>
            <h3
              style={{
                fontFamily: "var(--font-display)",
                fontStyle: "italic",
                fontWeight: 400,
                fontSize: 26,
                lineHeight: 1.1,
                color: "var(--l2f-ink)",
              }}
            >
              Brand fits
            </h3>
            <p className="text-xs text-muted-foreground mt-2">
              Tell us how brands fit you — we'll use this to improve recommendations.
            </p>
          </div>
          <button onClick={onClose} aria-label="Close" className="text-muted-foreground shrink-0 mt-1">
            <X size={20} />
          </button>
        </div>

        <div className="px-6 pb-4 pt-4 space-y-6 overflow-y-auto flex-1">
          <BrandFitCategorySection
            label="Tops"
            category="tops"
            entries={tops}
            onChange={setTops}
            defaults={defaults}
          />
          <BrandFitCategorySection
            label="Bottoms"
            category="bottoms"
            entries={bottoms}
            onChange={setBottoms}
            defaults={defaults}
          />
        </div>

        <div className="px-6 pt-2 pb-6 border-t border-border">
          <button
            onClick={save}
            disabled={saving}
            className="w-full py-3.5 rounded-full font-medium text-white transition"
            style={{ background: CORAL, opacity: saving ? 0.6 : 1, cursor: saving ? "not-allowed" : "pointer" }}
          >
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}

function BrandFitCategorySection({
  label,
  category,
  entries,
  onChange,
  defaults,
}: {
  label: string;
  category: "tops" | "bottoms";
  entries: BrandFitEntry[];
  onChange: (next: BrandFitEntry[]) => void;
  defaults: string[];
}) {
  const [adding, setAdding] = useState(false);
  const [brandPick, setBrandPick] = useState<string | null>(null);
  const [otherText, setOtherText] = useState("");
  const [verdict, setVerdict] = useState<BrandVerdict | null>(null);
  const [confirming, setConfirming] = useState<number | null>(null);

  const resetAdd = () => {
    setAdding(false);
    setBrandPick(null);
    setOtherText("");
    setVerdict(null);
  };

  const submitAdd = () => {
    let brand: string | null = brandPick;
    if (brandPick === "__other__") {
      const norm = normaliseBrand(otherText.trim());
      brand = norm ?? (otherText.trim() || null);
    }
    if (!brand || !verdict) return;
    const exists = entries.some(
      (e) => normaliseBrand(e.brand)?.toLowerCase() === normaliseBrand(brand!)?.toLowerCase(),
    );
    if (exists) {
      toast.error("Brand already added — delete the existing entry first");
      return;
    }
    onChange([...entries, { brand, verdict, category }]);
    resetAdd();
  };

  const remove = (idx: number) => {
    onChange(entries.filter((_, i) => i !== idx));
    setConfirming(null);
  };

  const canConfirm =
    !!verdict && !!brandPick && (brandPick !== "__other__" || otherText.trim().length > 0);

  return (
    <div>
      <p className="l2f-section-label mb-3" style={{ color: "var(--l2f-coral)" }}>
        {label.toUpperCase()}
      </p>

      {entries.length === 0 && !adding && (
        <p className="text-xs text-muted-foreground mb-3">No brand fits saved yet.</p>
      )}

      <div className="space-y-2">
        {entries.map((e, idx) => (
          <div
            key={`${e.brand}-${idx}`}
            className="flex items-center justify-between gap-3 rounded-xl border border-border bg-card px-3 py-2.5"
          >
            <div className="min-w-0">
              <p className="text-sm font-medium truncate">{e.brand}</p>
              <p className="text-xs text-muted-foreground">
                {VERDICT_LABEL[e.verdict as BrandVerdict] ?? e.verdict}
              </p>
            </div>
            {confirming === idx ? (
              <div className="flex items-center gap-2 shrink-0">
                <button
                  onClick={() => setConfirming(null)}
                  className="text-xs text-muted-foreground px-2 py-1"
                >
                  Cancel
                </button>
                <button
                  onClick={() => remove(idx)}
                  className="text-xs text-white px-3 py-1 rounded-full"
                  style={{ background: CORAL }}
                >
                  Remove
                </button>
              </div>
            ) : (
              <button
                onClick={() => setConfirming(idx)}
                aria-label={`Remove ${e.brand}`}
                className="text-muted-foreground shrink-0 p-1"
              >
                <X size={16} />
              </button>
            )}
          </div>
        ))}
      </div>

      {adding ? (
        <div className="mt-3 rounded-xl border border-border bg-secondary/40 p-3 space-y-3">
          <div>
            <p className="text-xs font-medium text-muted-foreground mb-2">Brand</p>
            <div className="flex flex-wrap gap-2">
              {defaults.map((b) => (
                <button
                  key={b}
                  onClick={() => { setBrandPick(b); setOtherText(""); }}
                  className="px-3 py-1.5 rounded-full text-xs font-medium border transition"
                  style={{
                    background: brandPick === b ? CORAL : "transparent",
                    color: brandPick === b ? "white" : "inherit",
                    borderColor: brandPick === b ? CORAL : "hsl(var(--border))",
                  }}
                >
                  {b}
                </button>
              ))}
              <button
                onClick={() => setBrandPick("__other__")}
                className="px-3 py-1.5 rounded-full text-xs font-medium border transition"
                style={{
                  background: brandPick === "__other__" ? CORAL : "transparent",
                  color: brandPick === "__other__" ? "white" : "inherit",
                  borderColor: brandPick === "__other__" ? CORAL : "hsl(var(--border))",
                }}
              >
                Other…
              </button>
            </div>
            {brandPick === "__other__" && (
              <input
                value={otherText}
                onChange={(e) => setOtherText(e.target.value)}
                placeholder="Type a brand name"
                className="mt-2 w-full rounded-full border border-border bg-background px-3 py-2 text-sm outline-none focus:border-[color:var(--l2f-coral)]"
              />
            )}
          </div>
          <div>
            <p className="text-xs font-medium text-muted-foreground mb-2">How does it fit?</p>
            <div className="flex flex-wrap gap-2">
              {VERDICT_OPTIONS.map((v) => (
                <button
                  key={v.value}
                  onClick={() => setVerdict(v.value)}
                  className="px-3 py-1.5 rounded-full text-xs font-medium border transition"
                  style={{
                    background: verdict === v.value ? CORAL : "transparent",
                    color: verdict === v.value ? "white" : "inherit",
                    borderColor: verdict === v.value ? CORAL : "hsl(var(--border))",
                  }}
                >
                  {v.label}
                </button>
              ))}
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-1">
            <button onClick={resetAdd} className="text-xs text-muted-foreground px-3 py-1.5">
              Cancel
            </button>
            <button
              onClick={submitAdd}
              disabled={!canConfirm}
              className="text-xs text-white px-4 py-1.5 rounded-full"
              style={{
                background: canConfirm ? CORAL : "var(--secondary)",
                color: canConfirm ? "white" : "var(--muted-foreground)",
                opacity: canConfirm ? 1 : 0.6,
                cursor: canConfirm ? "pointer" : "not-allowed",
              }}
            >
              Confirm
            </button>
          </div>
        </div>
      ) : (
        <button
          onClick={() => setAdding(true)}
          className="mt-3 w-full text-sm font-medium rounded-full border border-dashed border-border py-2.5 flex items-center justify-center gap-1"
          style={{ color: CORAL }}
        >
          <Plus size={16} /> Add a brand fit
        </button>
      )}
    </div>
  );
}
